/**
 * MarketStore — Zustand store fed by HydraClient
 *
 * Hydra sends two ticker formats:
 *   Kalshi:      KXNBAGAME-26APR04DETPHI-DET  (eventTicker: KXNBAGAME-26APR04DETPHI)
 *   Polymarket:  PM-nba-det-phi-2026-04-04     (moneyline)
 *                PM-nba-det-phi-2026-04-04-spread-home-1pt5
 *                PM-nba-det-phi-2026-04-04-total-139pt5
 *
 * Performance: Maps are mutated in-place. A `version` counter is bumped on a
 * RAF-throttled schedule so React re-renders at ~display refresh rate, not per-message.
 */

import { CELL_BOOK_VIEW_SUFFIX } from './bookViewIds';
import { kalshiTotalLineFromStrike } from '../lib/kalshiTicker/totalLine';
import { retainSourceAge, sourceAgeNow, type SourceAge } from '../lib/sourceAge';
import { FeedSourceAuthority, type SourceProof, type SourceObservation, type SourceVerdict } from '../lib/gameState/sourceEvidence';
import { declareFeedNeeds } from './feedNeeds';
import type { SourceContinuity, SourceLane } from '../lib/gameState/sourceContinuity';
import { create } from 'zustand';
import { UsBoardQuotes, UsSlugIdentity, type UsBrowserBoardQuote, type UsBrowserLineQuote } from './usBoardQuotes';
import { buildUsGatewayQuoteIndex, type UsGatewayQuoteIndex, type UsGatewayQuoteInput } from './usGatewayQuotes';
import { mergeUsStandaloneLines } from './usStandaloneLines';
import { US_GAME_SPORTS, usGameSportForBoard, normalizeUsGameKey, type UsGameSport } from '../lib/polymarketUs/sports';
import { FEED_VENUES, feedPairKey, selectPairOwner, type FeedPair, type FeedVenue } from './feedPairOwnership';
export type { FeedVenue } from './feedPairOwnership';
import { golfMatchIdentity, type GolfMatchIdentity } from '../lib/golfMatch';
import { advancePromotes, advancePromotionEvidence } from '../lib/advancePromotion';
import type { HydraConsensus, HydraMarket, HydraOpMarket, HydraOpOdds, HydraOrderbook, HydraPriceLevel } from '../clients/HydraClient';
import { getTeamDisplayName } from '../lib/kalshiTicker/teamNames';
import { isDrawOutcome, formatPlayerName } from '../lib/kalshiTicker/helpers';
import { classifyMlbExtendedTicker, isMlbExtendedSeriesTicker, MLB_PROP_STATS } from '../lib/kalshiTicker/sports/mlb';
import {
  classifyNflExtendedTicker,
  isNflExtendedSeriesTicker,
  NFL_PROP_STATS,
  NFL_PROP_DST_PLAYER_NAME,
  NFL_PROP_NONE_PLAYER_NAME,
  type NflPropStat,
} from '../lib/kalshiTicker/sports/nfl';
import { isNflPropStat, isSoccerPropStat, propBinaryThreshold, type PropSport } from '../lib/sport/propStat';
import {
  classifySoccerPropTicker,
  isSoccerPropSeriesTicker,
  soccerLeagueLabel,
  SOCCER_PROP_NONE_PLAYER_NAME,
  SOCCER_PROP_STATS,
  type SoccerPropStat,
} from '../lib/kalshiTicker/sports/soccer';
import { classifyPolyExtendedTicker, slugifyPlayerName, titleCasePolySlug } from '../lib/kalshiTicker/polyPropsEvents';
import type { MlbPropStat } from '../lib/kalshiTicker/sports/mlb';
import type { GameRow, VenueBook, Sport, MarketType, BookLevel, SeriesEvent, SeriesOutcome, MultiOutcomeEvent, DateFilter, StatusFilter, GameEventRow, SpreadLine, TotalLine, PeriodMoneyline, Period, MlbPropStatGroup, MlbEventMarkets, PlayerPropLadder, TeamTotalLadder, FutureBoard, FutureLeg, CfbEventMarkets, UfcFightProps, PolyFightPropRow, CfbEventEnumGroup, CfbEventOutcome, CfbTeamStatLadder, CfbTotalLadder, CfbOuLine, PlayerPropStatGroup, RaceToLine, RaceToOutcome } from '../views/ValueDashboard/types';
import { classifyCfbWireMarket, CFB_ENUM_GROUPS, CFB_TOTAL_LABELS, cfbTeamStatLabel, cfbTeamStatRank } from '../lib/cfbWireMarkets';
import { MULTI_OUTCOME_SPORTS, SPORT_TABS, STANDALONE_SPORTS, displaySportOf } from '../views/ValueDashboard/types';
import { selectUfcFightProps } from '../lib/ufc/fightPropSelection';
import { polyFightSideNames } from '../lib/ufc/polyFightTitle';
import { classifyPolyFightProp } from '../lib/kalshiTicker/polyFightProps';
import { containerTitle, humanizeSlug, parseFutureSlug } from '../lib/futures/parseFutureSlug';
import type { LivePlayerPropMarket, MarketReadState } from '../views/PropWorkbench/liveSlateAdapter';
import { matchPolySpread } from './spreadMatch';
import { foldGameKeySport, parseGameKeyV2, promoteToGameKeyV2, toHydraGameKeyLive } from '../services/analyticsGameKey';
import { useGameStatesStore } from './gameStatesStore';
import { primaryGameStateEnabled, readPrimaryGameState } from '@/services/primaryGameState';
import { useCoreGameStatesStore } from '@/services/coreGameStates';
import {
  ingestConsensusTrendFrame,
  trendFrameFromConsensus,
  type ConsensusTrendState,
} from '../lib/consensusTrend';
import { depthKey, pruneDepthVelocity, recordDepthSample } from '../components/cards/bookDepthVelocity';
import { midCents, mirrorCents } from '../lib/pricing/subCent';
import {
  OP_LANE_STALE_MS,
  opLaneStatusFromKey,
  opLaneStatusKey,
  opTeamTotalTeamCode,
  selectOpAnchorBook,
  type OpFeedHealth,
  type OpLaneStatus,
  type OpLastAnchorFair,
} from '../lib/opOdds';
import {
  GAME_FINAL_GRACE_MS,
  SPORT_GAME_DURATION_MS,
  TERMINAL_STATUSES,
  TRADABLE_STATUSES,
} from '../lib/sport/marketLifecycle';
import { classifyMarketFreshness, type MarketFreshness } from '../lib/sport/marketFreshness';
import { gatewayHealth } from '../lib/feed/gatewayHealthMeter';
import { countShownBook } from '../lib/telemetry/feedCounters';
import {
  SPORT_NORMALIZE,
  coerceCanonicalTeams,
  marketSportKeys,
  normalizeOutcomeCode,
  normalizeSport,
  normalizeFeedSport,
  baseSportOf,
  altTokenFor,
  isAltFeedSport,
  type FeedSport,
  parseTicker,
  resolveMarketPeriod,
  polyCanonicalOutcome,
  sportFromMarket,
  type ParsedTicker,
} from './marketFrameNormalization';
export { normalizeSport, sportForMarketFrame } from './marketFrameNormalization';
export type { FeedSport } from './marketFrameNormalization';
import { decideMarketFrameAdmission } from './marketFrameAdmission';
import { opProbeKey, probeFeedFlush, probeFeedIngest } from '../lib/trading/feedLatencyProbe';
import { noteMarketIdentity, noteMarketIdentityRemoved } from './accountMarketIdentity';
import { DEFAULT_EXCLUDED_CLASSES, rowPassesFilter, type FilterScope, type HydraFilterAckMsg, type MarketFilterEffective } from '../workers/feedFilter';
import {
  observeKalshiExchangeIndex,
  readKalshiExchangeIndex,
  type KalshiExchangeIndexDiagnostic,
} from '../lib/kalshiExchangeIndex';

// NOTE: the markets feed is deliberately BROAD — the per-view sport-needs
// narrowing (setSportNeeds / ALL_SPORTS / sportToHydraCode) was removed
// 2026-06-12: it made every sport-chip toggle pay a wire round trip +
// snapshot for data the store didn't hold. See workers/sportFilter.ts.

// ── Ticker parsing ───────────────────────────────────────────────


interface SeriesScoreTickerInfo {
  teamCode: string;
  winnerWins: number;
  loserWins: number;
  round?: number;
  year: string;
  teamChunk: string;
}

function inferSeriesRoundFromChunk(teamChunk: string): number | undefined {
  const roundMatch = teamChunk.match(/R(\d+)$/);
  if (roundMatch) return Number(roundMatch[1]);
  if (/(?:ECF|WCF|ALCS|NLCS|LCS)$/.test(teamChunk)) return 3;
  if (/(?:FIN|WS)$/.test(teamChunk)) return 4;
  return undefined;
}

function stripSeriesStageSuffix(teamChunk: string): string {
  const roundStripped = teamChunk.replace(/R\d+$/, '');
  if (roundStripped !== teamChunk) return roundStripped;
  const suffixes = ['ALCS', 'NLCS', 'ALDS', 'NLDS', 'WCF', 'ECF', 'FIN', 'EAST', 'WEST', 'LCS', 'LDS', 'WC'];
  for (const suffix of suffixes) {
    if (teamChunk.endsWith(suffix)) return teamChunk.slice(0, -suffix.length);
  }
  return teamChunk;
}

function orderedSeriesTeamsFromScoreTicker(
  teamChunk: string,
  teams: readonly [string, string],
): [string, string] | null {
  const stripped = stripSeriesStageSuffix(teamChunk);
  const [a, b] = teams;
  if (stripped === `${a}${b}`) return [a, b];
  if (stripped === `${b}${a}`) return [b, a];
  return null;
}

function parseKalshiSeriesScoreTicker(ticker: string): SeriesScoreTickerInfo | null {
  const match = ticker.match(
    /^KX(NBA|WNBA|NHL|MLB)SERIESSCORE-(\d{2})([A-Z0-9]+)-([A-Z]+)(\d)(\d)$/,
  );
  if (!match) return null;
  const [, , year, teamChunk, teamCode, winnerWinsRaw, loserWinsRaw] = match;
  const winnerWins = Number(winnerWinsRaw);
  const loserWins = Number(loserWinsRaw);
  if (!Number.isFinite(winnerWins) || !Number.isFinite(loserWins)) return null;
  return {
    teamCode,
    winnerWins,
    loserWins,
    round: inferSeriesRoundFromChunk(teamChunk),
    year,
    teamChunk,
  };
}

function seriesScoreIsCurrentClincher(
  score: SeriesScoreTickerInfo,
  yesTeam: string,
  seriesTeams: readonly [string, string],
  seriesWins: Record<string, number> | undefined,
  winsToClinch: number | undefined,
): boolean {
  if (!seriesWins || winsToClinch == null) return false;
  const opponent = seriesTeams[0] === yesTeam ? seriesTeams[1] : seriesTeams[1] === yesTeam ? seriesTeams[0] : null;
  if (!opponent) return false;
  return (
    score.winnerWins === winsToClinch
    && score.loserWins === (seriesWins[opponent] ?? -1)
    && (seriesWins[yesTeam] ?? -1) === winsToClinch - 1
  );
}

function escapeRegExp(raw: string): string {
  return raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isDirectKalshiTeamSeriesMarket(market: HydraMarket): boolean {
  if (market.source !== 'kalshi' || !market.canonicalYesTeam) return false;
  const seriesTicker = market.seriesTicker ?? market.ticker.split('-')[0];
  if (!seriesTicker) return false;
  return new RegExp(`^${escapeRegExp(seriesTicker)}-\\d{2}-[A-Z0-9]+$`).test(market.ticker);
}

/**
 * Pull the canonical line value out of a Kalshi market subtitle. Hydra
 * forwards Kalshi's `yes_sub_title` on every spread + total frame; it
 * reads either "Montreal wins by over 2.5 goals" (spread) or "Over
 * 203.5 points scored" (total). One regex covers both shapes. Falls
 * back to `title` for the spread case to tolerate pre-subtitle hydra
 * builds during deploy. Returns null when nothing parseable is present.
 */
export function parseLineFromSubtitle(subtitle: string | null | undefined): number | null {
  if (!subtitle) return null;
  // Spread / total form: "... over N (runs|points|goals|games|rounds)".
  // Anchoring to the unit avoids accidental matches against arbitrary
  // "over N" text fragments elsewhere in the subtitle (e.g. a total
  // subtitle accidentally attached to a spread market, or "over $N
  // wagered" promo copy).
  const overUnder = subtitle.match(
    /\b(?:over|under) (\d+(?:\.\d+)?) (?:runs?|points?|goals?|games?|rounds?)\b/i,
  );
  if (overUnder) {
    const n = Number(overUnder[1]);
    return Number.isFinite(n) ? n : null;
  }
  // MLB-style spread copy: "wins by N or more runs". "N or more" with
  // the .5-line convention means the team covers a line of N − 0.5
  // (winning by 2 covers 1.5, winning by 3 covers 2.5). Returning the
  // adjusted value keeps us consistent with the bucket-fallback math
  // (bucket-0.5) that runs when no subtitle is present at all.
  const orMore = subtitle.match(
    /\bby (\d+) or more (?:runs?|points?|goals?|games?|rounds?)\b/i,
  );
  if (orMore) {
    const n = Number(orMore[1]);
    return Number.isFinite(n) ? n - 0.5 : null;
  }
  // Soccer spread copy: "New York City wins by more than 2.5 goals", and the
  // World Cup's prefixed variant "Goal Diff Reg Time: England wins by more
  // than 5.5 goals". Already a .5 line, so it is the line verbatim — unlike
  // the "N or more" form above. This branch never matched before, so soccer
  // spreads fell through to bucket math and were only accidentally right;
  // `floorStrike` now front-runs it, and this keeps OG-hydra frames (which
  // carry no floorStrike) correct rather than lucky.
  const moreThan = subtitle.match(
    /\bby more than (\d+(?:\.\d+)?) (?:runs?|points?|goals?|games?|rounds?)\b/i,
  );
  if (moreThan) {
    const n = Number(moreThan[1]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Sports with no fixed roster: the "team" is one athlete, there is no team-name
 * registry, and hydra's canonical codes are full Title-cased surnames rather
 * than franchise abbreviations (`tennis|2026-07-30|Cerundolo|Gea`,
 * `ufc|2026-08-01|Prepolec|Rebecki`, `boxing|2026-08-01|Conwell|Kroll`).
 * Everything positional that hydra ships for these — `canonicalAwayCode` /
 * `canonicalHomeCode` — is still derived from the LOSSY ticker pack (Kalshi's
 * first-3 / ≤6-char surname truncation), so display and URLs have to be
 * re-expressed in the canonical code space. See `rosterlessDisplayCodes`.
 */
const ROSTERLESS_SPORTS = new Set<Sport>(['atp', 'wta', 'ufc', 'boxing']);

/** A Poly game slug whose competitor segments carry a digit (#1090). */
const POLY_DIGIT_COMPETITOR_RE =
  /^PM-[a-z]+-(?:[a-z0-9]*\d[a-z0-9]*-[a-z0-9]+|[a-z0-9]+-[a-z0-9]*\d[a-z0-9]*)-\d{4}-\d{2}-\d{2}/;

/** Case-insensitive "these two codes name the same competitor" test, tolerant
 *  of one being an abbreviation of the other (`RIB` ↔ `RIBEIRO`, `CONWEL` ↔
 *  `CONWELL`). Used only to line an abbreviated positional code up against
 *  hydra's canonical pair — never as a pairing/join rule. */
function competitorCodeMatches(code: string, canonical: string): boolean {
  if (!code || !canonical) return false;
  return code === canonical || canonical.startsWith(code) || code.startsWith(canonical);
}

/**
 * Re-express a roster-less row's positional (away, home) codes in hydra's
 * canonical full-surname code space, preserving the away/home ORDER that only
 * the Kalshi ticker knows.
 *
 * `canonicalTeams` is alpha-sorted, so it alone can't say which fighter is
 * listed first; `canonicalAwayCode`/`canonicalHomeCode` know the order but
 * carry the abbreviated pack. Matching one against the other recovers both.
 * Resolve on whichever side is unambiguous — matching exactly one canonical
 * name pins the other by elimination. Returns null (caller keeps the
 * abbreviated codes) when the frame carries no canonical pair, when the codes
 * are already canonical, or when neither side resolves uniquely — a
 * same-prefix pair (Silva vs Silvarez packed as SIL/SIL) must degrade to the
 * status quo, never to a guess.
 */
function rosterlessDisplayCodes(
  teams: readonly [string, string] | undefined,
  awayCode: string,
  homeCode: string,
): [string, string] | null {
  if (!teams || teams.length !== 2) return null;
  const [t0, t1] = teams;
  const u0 = t0.toUpperCase();
  const u1 = t1.toUpperCase();
  const a = awayCode.toUpperCase();
  const h = homeCode.toUpperCase();
  if ((a === u0 && h === u1) || (a === u1 && h === u0)) return null; // already canonical
  const awayIs0 = competitorCodeMatches(a, u0);
  const awayIs1 = competitorCodeMatches(a, u1);
  if (awayIs0 !== awayIs1) return awayIs0 ? [t0, t1] : [t1, t0];
  const homeIs0 = competitorCodeMatches(h, u0);
  const homeIs1 = competitorCodeMatches(h, u1);
  if (homeIs0 !== homeIs1) return homeIs0 ? [t1, t0] : [t0, t1];
  return null;
}

/**
 * Readable name for one side of a game row.
 *
 * Franchise sports resolve through the team-name registry ("BOS" → "Boston
 * Celtics"). Roster-less sports have no registry, so the name comes from that
 * competitor's OWN Kalshi market: `yes_sub_title` (hydra's `subtitle`) carries
 * the complete athlete name — "Charles Conwell", not the ticker's `CONWEL` —
 * and a 1v1 event lists one market per competitor, so a row holding both Kalshi
 * sides holds both names. Canonical code last; ticker fragments never (a
 * title-cased pack reads "Conwel", and on a mis-split pack, "Djos").
 *
 * The subtitle is read through `stripSubtitleScope`, so a scope-qualified leg
 * ("Reg Time: Spain") names the club and not the market's scope. Unlike
 * `clubNameFrom` it keeps a name that differs from the code only in case —
 * "Army" is the label a reader wants for ARMY, not a subtitle echoing the
 * code back.
 */
function competitorDisplayName(
  code: string,
  sport: Sport,
  kalshiMkt: HydraMarket | null,
): string {
  const registryName = getTeamDisplayName(code, sport);
  if (registryName !== code) return registryName;
  const subtitle = kalshiMkt?.subtitle?.trim();
  return (subtitle ? stripSubtitleScope(subtitle) : '') || code;
}

/**
 * Which side of the row a Polymarket binary's YES token belongs to.
 *
 * A Poly game/match/fight is a SINGLE binary whose YES = the slug-first
 * competitor, and hydra names that competitor in `canonicalYesTeam` (or, for
 * the soccer "To Advance" 2-way, `yesOutcome` — passed in by the caller).
 * Hydra's contract is explicit that consumers must orient off this field and
 * never off name order, so the only question here is which of the row's two
 * codes it names.
 *
 * Previously this was a tennis-only trust guard that DISCARDED a stamp naming
 * neither row code and fell back to YES=away — a coin flip whenever the row
 * still carried abbreviated codes (and, off the tennis branch, an inverted coin
 * flip: an unequal compare silently meant "home"). With roster-less row codes
 * now converged onto hydra's canonical space (`rosterlessDisplayCodes`) the
 * stamp matches exactly; the abbreviation-tolerant compare below is the seam
 * for any frame that still arrives half-converged, and it only fires when
 * exactly one side matches.
 *
 * The nullable resolver reports an unresolvable identity (no stamp, or a stamp
 * matching neither/both sides) as null so consumers can choose their failure
 * mode. `yesSideForRow` keeps the documented legacy default (slug-first =
 * away) — never a silent flip — for every caller except getGames' stamped
 * tennis rows, which fail closed instead (#92: no silent side inversion when
 * canonical orientation is unavailable).
 */
function yesSideForRowOrNull(
  yesTeam: string | null | undefined,
  awayCode: string,
  homeCode: string | null,
): 'away' | 'home' | null {
  if (yesTeam == null) return null;
  const y = yesTeam.toUpperCase();
  const a = awayCode.toUpperCase();
  const h = (homeCode ?? '').toUpperCase();
  if (y === a) return 'away';
  if (y === h) return 'home';
  const nearAway = competitorCodeMatches(y, a);
  const nearHome = competitorCodeMatches(y, h);
  if (nearAway && !nearHome) return 'away';
  if (nearHome && !nearAway) return 'home';
  return null;
}

function yesSideForRow(
  yesTeam: string | null | undefined,
  awayCode: string,
  homeCode: string | null,
): 'away' | 'home' {
  return yesSideForRowOrNull(yesTeam, awayCode, homeCode) ?? 'away';
}

function codesFromCanonicalGameKey(key: string | null | undefined): {
  date: string | null;
  awayCode: string;
  homeCode: string;
} | null {
  if (!key) return null;
  const parts = key.split('|');
  if (parts.length < 4) return null;
  return {
    date: parts[1] || null,
    awayCode: parts[2]?.toUpperCase() ?? '',
    homeCode: parts[3]?.toUpperCase() ?? '',
  };
}

// ── MLB player-prop / event-market classification ─────────────────────
// Fail-safe wrapper around classifyMlbExtendedTicker. Callers pre-gate with
// isMlbExtendedSeriesTicker, so a null here means the series segment IS one of
// the 11 prop/event series but the market segments don't match the published
// anatomy (e.g. a player-code variant) — drop it and log once, never guess.
const _loggedBadMlbExtended = new Set<string>();
function classifyMlbExtended(ticker: string): ReturnType<typeof classifyMlbExtendedTicker> {
  const cls = classifyMlbExtendedTicker(ticker);
  if (!cls && !_loggedBadMlbExtended.has(ticker)) {
    _loggedBadMlbExtended.add(ticker);
    // eslint-disable-next-line no-console
    console.warn('[marketStore] MLB prop/event ticker failed structural parse — dropped', ticker);
  }
  return cls;
}

// ── NFL player-prop classification — wire class first, ticker second ─────
// Core stamps `class: 'player-prop'` + `stat` on every NFL prop row (and on
// MLB's, since 2026-09-03); the class is what SELECTS a row. The ticker
// classifier supplies what the wire does not yet carry — the player segment
// (team code + player id + jersey) and the ladder threshold — and is the
// whole fallback for a frame from an engine that has not forwarded the class.
// `player` / `playerTeam` (core-stamped, edge forward pending) are preferred
// over the subtitle / ticker derivations whenever present.
const _loggedBadNflExtended = new Set<string>();
function classifyNflExtended(ticker: string): ReturnType<typeof classifyNflExtendedTicker> {
  const cls = classifyNflExtendedTicker(ticker);
  if (!cls && !_loggedBadNflExtended.has(ticker)) {
    _loggedBadNflExtended.add(ticker);
    // eslint-disable-next-line no-console
    console.warn('[marketStore] NFL prop ticker failed structural parse — wire identity only', ticker);
  }
  return cls;
}

/** op-player-props (spec DNL-002, ATOMIC migration): the ladder identity is
 *  core's `playerKey` (canonical TEAM+CODE+jersey — alias-safe, jersey
 *  verbatim) whenever the Kalshi row carries it; the ticker-derived key
 *  survives ONLY for rows without it. The same string keys the OP
 *  `player-prop` buckets, so the ladder key and the bucket key move
 *  together and never disagree. Null ⇒ fall back to the ticker. */
function wirePlayerKey(m: HydraMarket): string | null {
  return typeof m.playerKey === 'string' && m.playerKey.trim() !== '' ? m.playerKey.trim() : null;
}

interface NflPropFrame {
  stat: NflPropStat;
  /** '' on the NONE / D/ST legs. */
  teamCode: string;
  playerId: string;
  jerseyNumber?: number;
  /** Ladder identity: the ticker's WHOLE player segment (`SFBPURDY13` —
   *  team + initial + surname + jersey). The jersey is the discriminator
   *  Kalshi itself relies on: two same-team players sharing an initial and
   *  surname token are different segments only by it (Danielle R1 DNL-001).
   *  `NONE` / `DST` on the team-less legs. */
  playerKey: string;
  /** The WIRE playerKey when the row carried one (then `playerKey` above
   *  EQUALS it); null on ticker-derived identities and the NONE / D/ST legs.
   *  What the OP `player-prop` bucket is keyed by. */
  opPlayerKey: string | null;
  /** Ladder rung, or the binary stat's fixed identity threshold. */
  threshold: number;
  /** Display name — wire `player`, else the subtitle, else null (the caller
   *  falls back to the formatted ticker id). */
  playerName: string | null;
}

/** Kalshi prop subtitle → player name: "Brock Purdy: 350+" → "Brock Purdy";
 *  a touchdown binary's subtitle IS the name ("Sam Darnold"). */
function nflPlayerNameFromSubtitle(subtitle: string | null | undefined): string | null {
  const s = subtitle?.trim();
  if (!s) return null;
  const i = s.indexOf(':');
  const name = i > 0 ? s.slice(0, i).trim() : s;
  return name || null;
}

function classifyNflPropFrame(m: HydraMarket): NflPropFrame | null {
  if (m.source !== 'kalshi') return null;
  // The wire class is authoritative when present (rule 8): a row core
  // classes as anything else is not a prop, whatever its ticker looks like.
  if (m.class != null && m.class !== 'player-prop') return null;
  const wireStat = m.class === 'player-prop' && typeof m.stat === 'string' && isNflPropStat(m.stat) ? m.stat : null;
  const cls = isNflExtendedSeriesTicker(m.ticker) ? classifyNflExtended(m.ticker) : null;
  if (!wireStat && !cls) return null;
  // Class present but a stat this build does not know: ignore, never guess
  // the family off the ticker (rule 8 — additive vocabulary).
  if (m.class === 'player-prop' && !wireStat) return null;
  const stat: NflPropStat = wireStat ?? cls!.stat;
  const binary = propBinaryThreshold(stat);
  const wirePlayer = typeof m.player === 'string' && m.player.trim() ? m.player.trim() : null;
  const wireTeam = typeof m.playerTeam === 'string' && m.playerTeam.trim() ? m.playerTeam.trim().toUpperCase() : null;
  const subtitleName = nflPlayerNameFromSubtitle(m.subtitle);
  if (cls) {
    if (cls.leg === 'none') {
      return { stat, teamCode: '', playerId: cls.playerId, playerKey: cls.playerId, opPlayerKey: null, threshold: binary ?? 1, playerName: subtitleName ?? NFL_PROP_NONE_PLAYER_NAME };
    }
    if (cls.leg === 'dst') {
      return { stat, teamCode: '', playerId: cls.playerId, playerKey: cls.playerId, opPlayerKey: null, threshold: binary ?? 1, playerName: NFL_PROP_DST_PLAYER_NAME };
    }
    const threshold = binary ?? cls.threshold;
    if (threshold == null) return null;
    const wireKey = wirePlayerKey(m);
    return {
      stat,
      teamCode: wireTeam ?? cls.teamCode,
      playerId: cls.playerId,
      jerseyNumber: cls.jerseyNumber,
      // Identity: core's wire `playerKey` when the row carries it (spec
      // DNL-002 atomic migration — the OP bucket is keyed by the same
      // string); otherwise the TICKER's whole segment (team as Kalshi
      // packed it, jersey included), unchanged from before the field.
      playerKey: wireKey ?? `${cls.teamCode}${cls.playerId}${cls.jerseyNumber ?? ''}`,
      opPlayerKey: wireKey,
      threshold,
      playerName: wirePlayer ?? subtitleName,
    };
  }
  // Wire-only identity: the class says prop, the ticker grammar is one this
  // build cannot split. Name from the wire, threshold from the strike.
  const name = wirePlayer ?? subtitleName;
  if (!name) return null;
  const threshold = binary ?? (typeof m.floorStrike === 'number' && Number.isFinite(m.floorStrike) ? m.floorStrike + 0.5 : null);
  if (threshold == null) return null;
  const playerId = slugifyPlayerName(name) || name;
  const wireKey = wirePlayerKey(m);
  return { stat, teamCode: wireTeam ?? '', playerId, playerKey: wireKey ?? `${wireTeam ?? ''}${playerId}`, opPlayerKey: wireKey, threshold, playerName: name };
}

// ── Soccer goalscorer classification (soccer-goalscorer-props, 2026-09-09) ──
// Same discipline as the NFL classifier above: the wire class selects the
// row (`class: 'player-prop'` + stat `goal` | `first-goal`, stamped by core),
// the ticker classifier supplies the player segment / rung, and the wire
// `player` / `playerTeam` win over subtitle / ticker derivations.
interface SoccerPropFrame {
  stat: SoccerPropStat;
  teamCode: string;
  playerId: string;
  jerseyNumber?: number;
  playerKey: string;
  threshold: number;
  playerName: string | null;
}

function classifySoccerPropFrame(m: HydraMarket): SoccerPropFrame | null {
  if (m.source !== 'kalshi') return null;
  if (m.class != null && m.class !== 'player-prop') return null;
  const wireStat = m.class === 'player-prop' && typeof m.stat === 'string' && isSoccerPropStat(m.stat) ? m.stat : null;
  const cls = isSoccerPropSeriesTicker(m.ticker) ? classifySoccerPropTicker(m.ticker) : null;
  if (!wireStat && !cls) return null;
  if (m.class === 'player-prop' && !wireStat) return null;
  const stat: SoccerPropStat = wireStat ?? cls!.stat;
  const binary = propBinaryThreshold(stat);
  const wirePlayer = typeof m.player === 'string' && m.player.trim() ? m.player.trim() : null;
  const wireTeam = typeof m.playerTeam === 'string' && m.playerTeam.trim() ? m.playerTeam.trim().toUpperCase() : null;
  const subtitleName = nflPlayerNameFromSubtitle(m.subtitle);
  if (cls) {
    if (cls.leg === 'none') {
      return { stat, teamCode: '', playerId: cls.playerId, playerKey: cls.playerId, threshold: binary ?? 1, playerName: SOCCER_PROP_NONE_PLAYER_NAME };
    }
    const threshold = binary ?? cls.threshold;
    if (threshold == null) return null;
    return {
      stat,
      teamCode: wireTeam ?? cls.teamCode,
      playerId: cls.playerId,
      jerseyNumber: cls.jerseyNumber,
      playerKey: `${cls.teamCode}${cls.playerId}${cls.jerseyNumber ?? ''}`,
      threshold,
      playerName: wirePlayer ?? subtitleName,
    };
  }
  const name = wirePlayer ?? subtitleName;
  if (!name) return null;
  const threshold = binary ?? (typeof m.floorStrike === 'number' && Number.isFinite(m.floorStrike) ? m.floorStrike + 0.5 : null);
  if (threshold == null) return null;
  const playerId = slugifyPlayerName(name) || name;
  return { stat, teamCode: wireTeam ?? '', playerId, playerKey: `${wireTeam ?? ''}${playerId}`, threshold, playerName: name };
}

/** VenueBook for a Poly props/events frame with the empty-side sentinel
 *  guard applied (DNL-G2). Poly prop books are STATIC (Gamma snapshot, never
 *  WS-subscribed) and frequently one-sided; frames encode an empty side as
 *  bid 0 / ask 100, so the unguarded (bid+ask)/2 mid fabricates a price —
 *  50¢ from a fully-empty 0/100 book, (0+12)/2=6 from a one-sided one — and
 *  unlike a WS market the bad mark is PERMANENT, not a transient frame.
 *  Same strict-zero/strict-100 guard as getMarketPrice (real prices live
 *  strictly inside (0,100); Poly sub-cent longshots must pass): a book
 *  without two real sides marks at last trade, else "—". */
function buildGuardedPolyBook(
  m: HydraMarket,
  book: HydraOrderbook | null,
  freshness: MarketFreshness = 'unknown',
): VenueBook {
  const vb = buildVenueBookFromMarket(m, book, freshness);
  const bid = m.yesBid;
  const ask = m.yesAsk;
  const twoSided = bid != null && bid > 0 && ask != null && ask > 0 && ask < 100;
  if (!twoSided) {
    vb.priceCents = m.lastPrice != null && m.lastPrice > 0 ? m.lastPrice : null;
  }
  return vb;
}

/** Kalshi prop subtitle "Fernando Tatis Jr.: 2+" → "Fernando Tatis Jr.".
 *  Null when the subtitle is absent or carries no name segment. */
function playerNameFromSubtitle(subtitle: string | null | undefined): string | null {
  if (!subtitle) return null;
  const i = subtitle.indexOf(':');
  const name = i > 0 ? subtitle.slice(0, i).trim() : '';
  return name || null;
}

/** Wire status → slate read state. Terminal statuses read settled; only the
 *  known-open 'active' reads working; everything else (missing, novel) stays
 *  unknown — an unrecognized status must never present as a working market. */
function readStateForMarket(m: HydraMarket): MarketReadState {
  if (TERMINAL_STATUSES.has(m.status)) return 'settled';
  if (m.status === 'active') return 'working';
  return 'unknown';
}

function soccerPolyOutcomeFromTicker(ticker: string): string | null {
  const match = ticker.match(/^PM-[a-z0-9]+-[a-z0-9]+-[a-z0-9]+-\d{4}-\d{2}-\d{2}-(?:moneyline-)?([a-z0-9]+)$/i);
  return normalizeOutcomeCode(match?.[1]);
}

/**
 * Doubleheader-safe grouping/row key for a market. Prefer hydra's
 * `canonicalGameKeyV2` — the uniform 5-seg `sport|date|gameNo|LO|HI` key that
 * disambiguates the two games of a doubleheader that share one
 * `canonicalGameKey`. Fall back to the legacy key (or the call site's parsed
 * fallback) PROMOTED to the gameNo=1 form, so a normal single game keys
 * identically whether or not v2 has arrived yet; only a real doubleheader
 * (gameNo>1, carried only on the v2 field) splits into a second row. Every game
 * selector keys on this so its rows, the per-game URL, and the per-game odds
 * stream all address one specific game. See HYDRA_API.md "canonicalGameKeyV2".
 * `legacyBaseKey` is the call site's existing choice (`market.canonicalGameKey ??
 * "sport|date|away|home"` fallback).
 */
function gameKeyV2(market: HydraMarket, legacyBaseKey: string): string | null {
  if (isDhAmbiguous(market)) return null;
  return promoteToGameKeyV2(market.canonicalGameKeyV2 ?? legacyBaseKey);
}

// ── Doubleheader ambiguity (DNL-001, fail-closed identity) ───────────
//
// `promoteToGameKeyV2` turns a legacy 4-seg key into the gameNo=1 form. That
// is exactly right for an ordinary single game — and exactly WRONG for a frame
// on a doubleheader day whose game hydra could not determine: it silently
// attaches the frame to GAME 1. Live 2026-07-19 (LAD/NYY): Poly's opener slug
// carried a stale 7/18-evening Gamma start; promoting it put that start — and
// the Poly book, i.e. trade context — on the G1 card of a game that actually
// started 16:35Z. An unpaired row beats a wrong-game row.
//
// Two ambiguity signals, checked in order:
//
//   1. `dhAmbiguous: true` — the producer's explicit verdict (hydraedge
//      Danielle-bug/doubleheader-identity). Authoritative; the v2 key is
//      absent by producer invariant whenever it is set.
//   2. Belt-and-braces for mixed-version / OG-firehose frames that predate the
//      field: a LEGACY-ONLY frame whose base key has a v2 sibling with
//      gameNo ≥ 2 in the store. A gameNo ≥ 2 stamp is proof the base key is a
//      doubleheader day, so a legacy-only frame on it could be either game.
//      The same-game highlight resolves through this rule too
//      (getCanonicalGameKey); gameHighlight.nhl.test.ts pins it against the
//      real store ("a legacy-only frame on a day with a witnessed game 2
//      claims NEITHER game").
//
// Base keys with no DH evidence keep today's behavior EXACTLY — a single game
// whose v2 stamp simply hasn't arrived still promotes and still groups. That
// scoping is what keeps this from silently changing every sport.
//
// Sticky by design (DNL-002): once a base key is witnessed as a doubleheader
// it stays witnessed for the session. Un-witnessing on a frame that merely
// stopped carrying gameNo 2 would re-open the promotion hole mid-session. The
// reverse transition costs nothing: a frame that regains its v2 stamp is
// unambiguous by rule 1/2 regardless of what this set remembers.
const _dhBaseKeys = new Set<string>();

/** `sport|date|LO|HI` for either key shape (the gameNo segment dropped), or
 *  null for non-canonical strings (event tickers, synthetic panel keys). */
function dhBaseOf(key: string | null | undefined): string | null {
  const parts = parseGameKeyV2(key);
  if (!parts || !parts.sport) return null;
  return `${parts.sport}|${parts.date}|${parts.teamLo}|${parts.teamHi}`;
}

/** Record a frame's doubleheader evidence. Called on every ingest, before any
 *  key is derived, so ingest ORDER cannot change the verdict for frames that
 *  arrive after the evidence — and the getGames result cache (version-stamped,
 *  cleared wholesale on flush) re-derives rows once it does. */
function witnessDhEvidence(market: HydraMarket): boolean {
  const v2 = market.canonicalGameKeyV2;
  if (!v2) return false;
  const parts = parseGameKeyV2(v2);
  if (!parts || parts.gameNo < 2) return false;
  const base = dhBaseOf(v2);
  if (!base || _dhBaseKeys.has(base)) return false;
  _dhBaseKeys.add(base);
  return true; // NEW evidence — it can flip other tickers' verdicts
}

/** True when this frame changes the ticker's doubleheader identity relative to
 *  the record it replaces. The `getGames` result cache is stamped by store
 *  `version`, which only bumps on the throttled flush — so without an explicit
 *  drop, an unambiguous → ambiguous transition could keep serving the cached
 *  row (with the ticker still seated on GAME 1) until the next flush. Bounded,
 *  but a fail-closed identity contract cannot have a wrong-game window at all
 *  (DNL-002). Identity changes are rare, so the extra rebuild is not hot-path
 *  relevant. */
function dhIdentityChanged(prev: HydraMarket | undefined, next: HydraMarket): boolean {
  if (!prev) return false;
  return prev.canonicalGameKeyV2 !== next.canonicalGameKeyV2
    || prev.canonicalGameKey !== next.canonicalGameKey
    || (prev.dhAmbiguous === true) !== (next.dhAmbiguous === true);
}

function isDhAmbiguous(market: HydraMarket): boolean {
  if (market.dhAmbiguous === true) return true;
  if (market.canonicalGameKeyV2) return false;
  const base = dhBaseOf(market.canonicalGameKey);
  return base != null && _dhBaseKeys.has(base);
}

// ── Helpers ──────────────────────────────────────────────────────

/** Format an epoch ms to "H:MMa" / "H:MMp" in the **browser's local
 *  timezone**. Empty string when the input is null/non-finite. The narrow
 *  Date column on GameTable can't fit "AM/PM" without wrapping, so the
 *  single-char p/a form is used. The user's browser zone is the right
 *  default for a personal trading UI. */
const _LOCAL_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const _LOCAL_DATE_FMT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function parseStartTimeMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function formatStartDateLocal(ms: number | null | undefined): string | null {
  if (ms == null || !Number.isFinite(ms)) return null;
  const parts = _LOCAL_DATE_FMT.formatToParts(new Date(ms));
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : null;
}

export function formatStartTimeLocal(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  const parts = _LOCAL_TIME_FMT.formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const dayPeriod = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toLowerCase();
  const ampm = dayPeriod.startsWith('p') ? 'p' : 'a';
  return hour && minute ? `${hour}:${minute}${ampm}` : '';
}

function toBookLevels(levels: HydraPriceLevel[]): BookLevel[] {
  return levels
    .map((l) => ({ priceCents: l.price, size: l.size }))
    .sort((a, b) => b.priceCents - a.priceCents); // descending: best bid first
}

// Canonical status sets live in lib/sport/marketLifecycle.ts (shared with the
// LT2 line-availability classifier); re-exported here for existing consumers.
export { TERMINAL_STATUSES } from '../lib/sport/marketLifecycle';

export interface DerivedGameState {
  gameState: 'live' | 'pre' | 'final';
  authority: 'venue' | 'schedule' | 'heuristic' | 'unknown';
}

export function deriveGameStateWithAuthority(
  statuses: string[],
  isSeries: boolean,
  startTimeMs: number | null,
  nowMs: number,
  sport: string | null = null,
): DerivedGameState {
  const scheduledPregame = startTimeMs != null && nowMs < startTimeMs;
  if (statuses.length === 0) {
    return {
      gameState: 'pre',
      authority: scheduledPregame ? 'schedule' : 'unknown',
    };
  }
  // All non-empty statuses terminal → final. Series rows never reach
  // final because the underlying tickers transition independently from
  // the row's lifetime; treat any non-terminal status as keeping the
  // row live/pre.
  if (!isSeries && statuses.every((s) => TERMINAL_STATUSES.has(s))) {
    return { gameState: 'final', authority: 'venue' };
  }
  // ANY-terminal acceleration: Polymarket typically resolves before
  // Kalshi finalizes (Kalshi waits on staff settlement). If even one
  // venue has marked the market terminal AND we've crossed the
  // scheduled event start, trust that signal — the game is over. Gate on
  // startTimeMs to avoid false-positives from pre-game stale 'closed'
  // states (e.g. a series-clinched team's losing ticker). When start
  // time is unknown, we DON'T fire this — fall through to the time
  // and all-terminal checks instead.
  if (!isSeries && startTimeMs != null && nowMs >= startTimeMs) {
    if (statuses.some((s) => TERMINAL_STATUSES.has(s))) {
      return { gameState: 'final', authority: 'venue' };
    }
  }
  // Time-based final coercion: Kalshi often leaves yesterday's games
  // in 'open' for hours past their actual end. Past sport duration +
  // grace → trust the clock.
  if (!isSeries && startTimeMs != null && sport) {
    const dur = SPORT_GAME_DURATION_MS[sport];
    if (dur != null && nowMs - startTimeMs > dur + GAME_FINAL_GRACE_MS) {
      return { gameState: 'final', authority: 'heuristic' };
    }
  }
  if (statuses.some((s) => TRADABLE_STATUSES.has(s))) {
    // 'open'/'active' means the book is available for trading, not that the
    // game is in progress. Only call it 'live' once we've crossed the known
    // start time. Unknown startTimeMs defaults to 'pre' so the live glow
    // doesn't false-positive on every tradeable book.
    if (startTimeMs != null && nowMs >= startTimeMs) {
      return { gameState: 'live', authority: 'heuristic' };
    }
    return {
      gameState: 'pre',
      authority: scheduledPregame ? 'schedule' : 'unknown',
    };
  }
  return {
    gameState: 'pre',
    authority: scheduledPregame ? 'schedule' : 'unknown',
  };
}

export function deriveGameState(
  statuses: string[],
  isSeries: boolean,
  startTimeMs: number | null,
  nowMs: number,
  sport: string | null = null,
): 'live' | 'pre' | 'final' {
  return deriveGameStateWithAuthority(statuses, isSeries, startTimeMs, nowMs, sport).gameState;
}

/** Ms since the freshest evidence for a ticker — the OLDER of two independent
 *  stamps, so either one going cold is enough to age the market:
 *
 *  - `_lastReceiveMs`, the browser observation clock. Catches a market that
 *    falls silent mid-session, but reads 0 for every market in hydra's opening
 *    snapshot, including one that died days ago.
 *  - the producer's own `lastUpdateMs`. Catches a market that is already cold
 *    when it arrives, which is the failure #319 measured.
 *
 *  Null when neither stamp exists. `ingestOrderbook` overwrites `lastUpdateMs`
 *  with a browser timestamp, which can only make the producer arm look younger,
 *  never older — so that clock mixing is fail-open here. */
// Legacy age diagnostic only. Negotiated Core/Edge feeds use exact source
// continuity below: change/publication stamps do not decide their freshness.
function marketEvidenceAgeMs(
  ticker: string,
  mkt: HydraMarket | null | undefined,
  nowMs: number,
): number | null {
  const observedAt = _lastReceiveMs.get(ticker);
  const producedAt = mkt?.lastUpdateMs;
  if (observedAt == null && producedAt == null) return null;
  const ages: number[] = [];
  if (observedAt != null) ages.push(nowMs - observedAt);
  if (producedAt != null) ages.push(nowMs - producedAt);
  // Clamped: a producer clock running ahead of the browser is skew, not youth.
  return Math.max(0, ...ages);
}

function freshnessForMarket(
  mkt: HydraMarket | null,
  sport: Sport | string | null,
  gameState?: 'live' | 'pre' | 'final',
): MarketFreshness {
  if (!mkt) return 'unknown';
  const qualified = governingPriceVerdict(mkt.ticker);
  if (qualified) return qualified === 'continuous' ? 'fresh' : 'stale';
  const nowMs = Date.now();
  const state = gameState ?? deriveGameState(
    mkt.status ? [mkt.status] : [],
    false,
    parseStartTimeMs(mkt.eventStartTime),
    nowMs,
    sport,
  );
  return classifyMarketFreshness({
    ageMs: marketEvidenceAgeMs(mkt.ticker, mkt, nowMs),
    sport,
    gameState: state,
  });
}

/** Final-game winner derivation from price collapse to 100¢/0¢. Each Kalshi
 *  market is single-team-YES so its lastPrice tells you that team directly;
 *  Polymarket is single-market-YES with the side identified by `yesIsAway`.
 *  Returns null when no source has a definitive 99-or-1¢ resolution
 *  (in-flight finalization, missing lastPrice). */
function deriveFinalWinnerSide(
  kAwayMkt: HydraMarket | null,
  kHomeMkt: HydraMarket | null,
  polyMkt: HydraMarket | null,
  yesIsAwayForPoly: boolean,
  /** Polymarket US (F1): same single-YES shape and the SAME rank as intl
   *  Poly — a 100¢ last on the US book is winner evidence, consulted after
   *  intl so a disagreement between the two Poly venues resolves in intl's
   *  favour (the older, deeper book). */
  polyUsMkt: HydraMarket | null = null,
  yesIsAwayForPolyUs = true,
): 'away' | 'home' | null {
  if (kAwayMkt?.lastPrice != null) {
    if (kAwayMkt.lastPrice >= 99) return 'away';
    if (kAwayMkt.lastPrice <= 1) return 'home';
  }
  if (kHomeMkt?.lastPrice != null) {
    if (kHomeMkt.lastPrice >= 99) return 'home';
    if (kHomeMkt.lastPrice <= 1) return 'away';
  }
  if (polyMkt?.lastPrice != null) {
    if (polyMkt.lastPrice >= 99) return yesIsAwayForPoly ? 'away' : 'home';
    if (polyMkt.lastPrice <= 1) return yesIsAwayForPoly ? 'home' : 'away';
  }
  if (polyUsMkt?.lastPrice != null) {
    if (polyUsMkt.lastPrice >= 99) return yesIsAwayForPolyUs ? 'away' : 'home';
    if (polyUsMkt.lastPrice <= 1) return yesIsAwayForPolyUs ? 'home' : 'away';
  }
  return null;
}

/**
 * Pick the highest-price bid level on a side, filtered to "real" bids:
 * size ≥ 1 contracts AND price within `slack`¢ of the last trade. Mirrors
 * the ladder/helpers.bestBidDepthForSide sanity check so depth-velocity
 * samples (and any future consumer here) read the same level the K Q /
 * P Q column does. Operates on the raw HydraPriceLevel shape (`price` /
 * `size`) since this runs at ingest, before VenueBook construction.
 */
function bestRealBidLevel(
  levels: HydraPriceLevel[] | undefined,
  lastTradeCents: number | null,
  slack = 15,
): HydraPriceLevel | null {
  if (!levels || levels.length === 0) return null;
  let best: HydraPriceLevel | null = null;
  for (const lvl of levels) {
    if (lvl.size < 1) continue;
    if (lastTradeCents != null && lvl.price > lastTradeCents + slack) continue;
    if (!best || lvl.price > best.price) best = lvl;
  }
  return best;
}

/**
 * Build a `VenueBook` from a raw `HydraMarket` + optional orderbook frame.
 * Liquidity counts both bid and ask depth so ladder pills can scale against
 * the total $ in book. Volume falls back to summed depth when hydra hasn't
 * sent a market frame yet.
 */
export function buildVenueBookFromMarket(
  mkt: HydraMarket | null,
  book: HydraOrderbook | null,
  freshness: MarketFreshness = 'unknown',
): VenueBook {
  // A2 — `sourceValidity` is the verdict of the proof that GOVERNS the price:
  // the book's whenever a usable Kalshi book governs the bid/ask, else the
  // price proof's. One meaning; it never encodes ticker-scalar observation.
  const sourceValidity = mkt ? governingPriceVerdict(mkt.ticker) : undefined;
  if (sourceValidity) freshness = sourceValidity === 'continuous' ? 'fresh' : 'stale';
  const display = mkt ? readBookDisplay(mkt.ticker) : null;
  // Present only while a book proof is HELD for the row: `continuous` when
  // that book is usable, `unconfirmed` when it is withdrawn (gap, resync,
  // pendingResync, retired feed). A bookless row carries no book verdict —
  // its line follows the row rule (freshness / sourceValidity) exactly as
  // before, which is what keeps bookless Kalshi markets' behaviour unchanged.
  const bookSourceValidity = mkt && _bookSourceProof.has(mkt.ticker)
    ? (sourceBookUsable(mkt.ticker) ? 'continuous' as const : 'unconfirmed' as const) : undefined;
  if (mkt && !sourceBookUsable(mkt.ticker)) book = null;
  // Ticker-scalar authority (Kalshi only): lastPrice / volume / openInterest
  // are ticker/trade-channel values, proven only by a market frame's own
  // evidence. While unconfirmed they are presented as not-yet-observed — a
  // dash, never a muted historical value that can look actionable.
  const tickerSourceValidity = mkt?.source === 'kalshi' ? sourceTickerVerdict(mkt.ticker) : undefined;
  const tickerScalarsObserved = tickerSourceValidity !== 'unconfirmed';
  const exchangeIndexDiagnostic =
    mkt?.source === 'kalshi'
      ? (_kalshiExchangeIndexDiagnostics.get(mkt.ticker) ?? readKalshiExchangeIndex(mkt))
      : undefined;
  // Sentinel guard — same rule as getMarketPrice below. Kalshi encodes an
  // EMPTY book side as bid 0 / ask 100; real prices only exist strictly
  // inside (0, 100). Averaging against an empty side craters the mid or
  // spikes it toward 100 — midCents(99, 100) rounds UP to exactly 100, and
  // with both moneyline legs stale the Marty card rendered `100¢ · 100¢`,
  // an impossible pair (2026-07-23). A one-sided book marks at last trade.
  // Strict inequalities: Polymarket sub-cent prices (0.5¢) must pass.
  const bid = mkt?.yesBid;
  const ask = mkt?.yesAsk;
  const last = tickerScalarsObserved ? mkt?.lastPrice : undefined;
  const price =
    bid != null && bid > 0 && ask != null && ask > 0 && ask < 100
      ? midCents(bid, ask)
      : last != null && last > 0
        ? last
        : null;
  const bids = book?.yes ?? [];
  const asks = book?.no ?? [];
  const bidLiq = bids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const askLiq = asks.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const liq = bidLiq + askLiq;
  // Per-side resting depth: SpreadsTotalsTable splits its K Vol / P Vol
  // column into O / U (for totals) or Y / N (for spreads) so traders can
  // see liquidity tilt at a glance. Hydra doesn't send traded volume split
  // by side, so depth is the best signal available here. Undefined when
  // there's no orderbook frame yet.
  const yesDepth = book ? bids.reduce((s, l) => s + l.size, 0) : undefined;
  const noDepth = book ? asks.reduce((s, l) => s + l.size, 0) : undefined;
  const bookDepth = (yesDepth ?? 0) + (noDepth ?? 0);
  const vol = mkt?.volume ?? bookDepth;
  // #154: when the market frame carries no cumulative TRADED volume, `vol` is the
  // YES+NO resting-depth fallback above — NOT executed volume. Flag it so the
  // renderer can mark it as "resting depth" and not conflate it with real
  // traded volume (attribute 4). `mkt?.volume == null` also covers mkt === null.
  const volumeIsDepthDerived = mkt?.volume == null;
  // Forward polymarket per-outcome tokens when present. Kalshi markets
  // leave these undefined, which is fine — the trade ticket
  // (GameDetailPanel) already skips poly-tokenId resolution for kalshi
  // venues. Used by synthesize.ts's
  // spread/total rows where away/home share one underlying book and the
  // YES vs NO split has to come from within the book (the opposite-book
  // moneyline trick doesn't apply when both slots point at the same
  // VenueBook reference).
  return {
    marketTicker: mkt?.ticker ?? '',
    ...(exchangeIndexDiagnostic
      ? {
          exchangeIndexState: exchangeIndexDiagnostic.state,
          ...(exchangeIndexDiagnostic.state === 'valid'
            ? { exchangeIndex: exchangeIndexDiagnostic.exchangeIndex }
            : {}),
        }
      : {}),
    freshness,
    ...(sourceValidity ? { sourceValidity } : {}),
    ...(bookSourceValidity ? { bookSourceValidity } : {}),
    ...(tickerSourceValidity ? { tickerSourceValidity } : {}),
    ...(display?.retained && display.book ? { retainedDepth: {
      yes: toBookLevels(display.book.yes), no: toBookLevels(display.book.no),
      liquidity: Math.round([...display.book.yes, ...display.book.no].reduce((sum, level) => sum + level.price * level.size / 100, 0)),
      confirmedAtMs: display.confirmedAtMs!,
    } } : {}),
    status: mkt?.status,
    priceCents: price,
    lastPriceCents: tickerScalarsObserved ? mkt?.lastPrice ?? null : null,
    // Frame BBO scalars, sentinel-guarded (bid 0 / ask 100 = empty side —
    // same strict guard as getMarketPrice). The pill layer's fallback for
    // BOOKLESS markets (static Poly props have no orderbook and often no
    // last trade — without these every price mode rendered "—"). Levels
    // always win when present; see pillPriceCents.
    yesBidCents: mkt?.yesBid != null && mkt.yesBid > 0 ? mkt.yesBid : null,
    yesAskCents:
      mkt?.yesAsk != null && mkt.yesAsk > 0 && mkt.yesAsk < 100 ? mkt.yesAsk : null,
    volume: vol,
    volumeIsDepthDerived,
    yesVolume: yesDepth,
    noVolume: noDepth,
    liquidity: Math.round(liq),
    yes: toBookLevels(bids),
    no: toBookLevels(asks),
    tokenId: mkt?.yesTokenId,
    noTokenId: mkt?.noTokenId,
    conditionId: mkt?.conditionId,
    negRisk: mkt?.negRisk,
    tickSize: mkt?.tickSize,
    priceRanges: mkt?.priceRanges,
  };
}

/**
 * A venue book that quotes SOMETHING — a mid inside (0, 100) or a last trade.
 * False for the bookless listing: no bid, no ask, no trade, which is what a
 * freshly listed market looks like before anyone quotes it. The soccer "To
 * Advance" selectors gate on this so a listing never displaces a live 1X2.
 */
export function venueBookHasPrice(b: VenueBook | null | undefined): boolean {
  return b?.priceCents != null;
}

/** A Kalshi full-game moneyline frame (a soccer 1X2 leg) carrying a price —
 *  the "result Kalshi quoted" clause of the advance-promotion evidence. Same
 *  period/type classification getMultiOutcomeEvents applies to its legs. */
function isQuotedKalshiFullMoneyline(m: HydraMarket, parsed: ParsedTicker | null): boolean {
  if (m.source !== 'kalshi') return false;
  if (parsed && parsed.marketType !== 'moneyline') return false;
  if (resolveMarketPeriod(m, parsed) !== 'full') return false;
  return venueBookHasPrice(buildVenueBookFromMarket(m, null, 'unknown'));
}

/**
 * Project a single Polymarket binary moneyline (one slug) onto away/home
 * VenueBooks. The slug's YES outcome is one team (per `canonicalYesTeam`, else
 * default YES=away); that team gets the native book, the other the inverse
 * (price 100−yes, yes/no ladders swapped, outcome tokens swapped). Shared by
 * getGames' full-game moneyline (buildPolyBooks) and getPeriodMoneyline's 1H
 * winner pairing so both invert identically.
 *
 * Exported for #154's focused projection/arithmetic/shared-aggregate test.
 */
export function buildPolyMoneylinePair(
  polyMkt: HydraMarket,
  polyBook: HydraOrderbook | null,
  awayCode: string,
  /** Row home code — the other candidate `canonicalYesTeam` can name (see
   *  `yesSideForRow`); safe to pass null where it isn't in scope. */
  homeCode: string | null,
  /** Explicit YES-side team code override. The 1X2 / NBA moneyline path reads
   *  `canonicalYesTeam`, but the soccer "To Advance" binary carries the YES team
   *  in `yesOutcome` instead — getAdvanceGame passes it here so the native/inverted
   *  split keys off the right team. Falls back to `canonicalYesTeam` when omitted. */
  yesTeamOverride?: string | null,
  freshness: MarketFreshness = 'unknown',
): { away: VenueBook; home: VenueBook } {
  if (!sourceBookUsable(polyMkt.ticker)) polyBook = null;
  const sourceValidity = sourcePriceVerdict(polyMkt.ticker);
  if (sourceValidity) freshness = sourceValidity === 'continuous' ? 'fresh' : 'stale';
  // Hydra stamps canonicalYesTeam with the code of the title/slug-first
  // competitor (Poly's YES = outcomes[0]) in the SAME code space as the key it
  // ships with — a full surname for tennis/UFC/boxing, a 3-char team code
  // elsewhere. Orient off it, never off name order: without the stamp,
  // Poly-only tennis swapped the two players whenever the title-first player
  // wasn't alpha-first (the 2026-07-10 Poling/Miyoshi inversion), and a fight's
  // venues genuinely disagree on order (Kalshi `TUCRIB`, Poly "Ribeiro vs.
  // Tuchalov").
  const yesTeamRaw = yesTeamOverride ?? polyMkt.canonicalYesTeam;
  const yesIsAway = yesSideForRow(yesTeamRaw, awayCode, homeCode) === 'away';
  const slugYesPrice = polyMkt.yesBid != null && polyMkt.yesAsk != null
    ? midCents(polyMkt.yesBid, polyMkt.yesAsk) : polyMkt.lastPrice ?? null;
  const slugNoPrice = polyMkt.noBid != null && polyMkt.noAsk != null
    ? midCents(polyMkt.noBid, polyMkt.noAsk)
    : (slugYesPrice != null ? mirrorCents(slugYesPrice) : null);
  // #154: the market-wide traded volume AND liquidity are deliberately SHARED
  // across both complementary team rows (no per-team split exists on a single
  // binary moneyline). Both books below carry the same `vol` and `totalLiq`;
  // `isSharedBinaryBook` flags it so the renderer cues BOTH the VOL and LIQ
  // cells and the two rows don't read as independent per-team totals (attr 5 /
  // D3 rework — the liquidity cue was the gap Brendan flagged).
  const vol = polyMkt.volume ?? 0;
  const slugYesBids = polyBook?.yes ?? [];
  const slugNoBids = polyBook?.no ?? [];
  const bidLiq = slugYesBids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const askLiq = slugNoBids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
  const totalLiq = Math.round(bidLiq + askLiq);
  const yesTeamBook: VenueBook = {
    ...(sourceValidity ? { sourceValidity } : {}),
    marketTicker: polyMkt.ticker, priceCents: slugYesPrice,
    nativeSideInverted: false,
    freshness,
    lastPriceCents: polyMkt.lastPrice ?? null,
    volume: vol, isSharedBinaryBook: true, liquidity: totalLiq,
    yes: toBookLevels(slugYesBids), no: toBookLevels(slugNoBids),
    tokenId: polyMkt.yesTokenId, noTokenId: polyMkt.noTokenId,
    conditionId: polyMkt.conditionId, negRisk: polyMkt.negRisk, tickSize: polyMkt.tickSize,
  };
  const otherTeamBook: VenueBook = {
    ...(sourceValidity ? { sourceValidity } : {}),
    marketTicker: polyMkt.ticker, priceCents: slugNoPrice,
    nativeSideInverted: true,
    freshness,
    lastPriceCents: polyMkt.lastPrice != null ? mirrorCents(polyMkt.lastPrice) : null,
    volume: vol, isSharedBinaryBook: true, liquidity: totalLiq,
    yes: toBookLevels(slugNoBids), no: toBookLevels(slugYesBids),
    tokenId: polyMkt.noTokenId, noTokenId: polyMkt.yesTokenId,
    conditionId: polyMkt.conditionId, negRisk: polyMkt.negRisk, tickSize: polyMkt.tickSize,
  };
  return yesIsAway
    ? { away: yesTeamBook, home: otherTeamBook }
    : { away: otherTeamBook, home: yesTeamBook };
}

/** The venue's decimal tick (`0.005`, `'0.001'`) as the dollar-decimal string
 *  the store already carries in `VenueBook.tickSize` (`'0.005'` = 0.5¢).
 *  Undefined stays undefined — never invent a grid. */
export function normalizePmusTickSize(raw: number | string | null | undefined): string | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? String(raw) : undefined;
  const t = raw.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? String(n) : undefined;
}

function pmusNumber(raw: number | string | null | undefined): number | undefined {
  if (raw == null) return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Polymarket US (F1): one binary instrument per game, projected onto away/home
 * exactly like the intl slot — `buildPolyMoneylinePair` verbatim (same
 * `canonicalYesTeam` orientation, inverted complement, shared volume/liquidity
 * cue) — then decorated with the venue's own identity: `marketSlug` (what the
 * relay addresses orders by), `minimumTradeQty`, `feeCoefficient`, and the
 * tick from `orderPriceMinTickSize` (falls back to hydra's generic `tickSize`).
 * No tokens, no conditionId, no negRisk: the venue has none.
 */
export function buildPolyUsMoneylinePair(
  mkt: HydraMarket,
  book: HydraOrderbook | null,
  awayCode: string,
  homeCode: string | null,
  yesTeamOverride?: string | null,
  freshness: MarketFreshness = 'unknown',
): { away: VenueBook; home: VenueBook } {
  const pair = buildPolyMoneylinePair(mkt, book, awayCode, homeCode, yesTeamOverride, freshness);
  const decoration: Partial<VenueBook> = {
    marketSlug: mkt.pmusMarketSlug,
    minimumTradeQty: pmusNumber(mkt.minimumTradeQty),
    feeCoefficient: pmusNumber(mkt.feeCoefficient),
    tickSize: normalizePmusTickSize(mkt.orderPriceMinTickSize) ?? mkt.tickSize,
  };
  return {
    away: { ...pair.away, ...decoration },
    home: { ...pair.home, ...decoration },
  };
}

// ── Feed log (ring buffer, last 100 pricing updates) ─────────────

export type FeedLogSource = 'kalshi' | 'polymarket' | 'polymarket_us' | 'unabated' | 'oddspapi';

export interface FeedLogEntry {
  ts: number;          // Date.now()
  ticker: string;      // unabated/oddspapi/gamestate entries: canonical game key (no venue ticker exists)
  /** 'gamestate' rows live in stores/gameStateTape, never in this store's log:
   *  a game-state change must not publish the market store. */
  source: FeedLogSource | 'gamestate';
  sport: string;
  type: 'market' | 'orderbook' | 'consensus' | 'opOdds' | 'gameState';
  /** Display label of the ENGINE that delivered this row (OG / e2 / hc1 …) —
   *  the FeedTape source column. Absent on rows logged before the fleet
   *  attaches or from paths with no feed context. */
  feedLabel?: string;
  yesBid?: number;
  yesAsk?: number;
  volume?: number;     // market volume (from HydraMarket.volume)
  bookLevels?: number; // total yes+no levels in orderbook
  // Consensus-only — full-game probability cents for each team, plus the
  // periodTypeId so future in-period updates can be distinguished in the log.
  awayProbCents?: number;
  homeProbCents?: number;
  // Tenth-of-a-cent resolution (0–1000, HYDRA_API "Unabated consensus
  // update"). The tape shows THESE when present: frames now emit on
  // tenth-only moves, so an integer-only row would render as a duplicate of
  // its predecessor and the reason the frame exists would be invisible.
  // Absent on pre-rollout hydra (renderer falls back to the cents).
  awayProbTenths?: number;
  homeProbTenths?: number;
  drawProbTenths?: number;
  // Sharp-anchor flag — a flip alone triggers a frame with zero numeric
  // change, so the tape marks it or the row looks like a no-op re-emit.
  sharpBookUsed?: boolean;
  periodTypeId?: number;
  // OddsPapi-only (type 'opOdds'). The OP tape reuses the *ProbTenths/
  // *ProbCents fields above for the full-game moneyline de-vig consensus
  // when the frame carries one.
  bookCount?: number;      // books across the frame's markets
  opTombstone?: boolean;   // markets:[] no-coverage tombstone (rule 1)
  opSnapshotGames?: number; // snapshot commit summary row: game count
  // Game-state-only (type 'gameState') — see stores/gameStateTape.
  gsLane?: 'poll' | 'core'; // the tape names the lane's engine at render
  gsStatusId?: number;     // 1 scheduled, 2 live, 3 final
  awayScore?: number | null;
  homeScore?: number | null;
  gsPeriodLabel?: string | null;
  gsClock?: string | null;
  gsProvider?: string;     // Core lane's selected provider; unset on the poll lane
  gsMatchup?: string;      // AWAY@HOME when the record names its sides
  gsNewGames?: number;     // summary row: games this lane started reporting
}

const FEED_LOG_MAX = 100;
const _feedLogKalshi: FeedLogEntry[] = [];
const _feedLogPoly: FeedLogEntry[] = [];
const _feedLogPolyUs: FeedLogEntry[] = [];
const _feedLogUnabated: FeedLogEntry[] = [];
const _feedLogOp: FeedLogEntry[] = [];

function pushFeedLog(entry: FeedLogEntry) {
  // #1124: a book logged here got past the no-view bail, so it reached a view.
  if (entry.type === 'orderbook') countShownBook(entry.source);
  const log =
    entry.source === 'kalshi' ? _feedLogKalshi :
    entry.source === 'polymarket' ? _feedLogPoly :
    entry.source === 'polymarket_us' ? _feedLogPolyUs :
    entry.source === 'oddspapi' ? _feedLogOp :
    _feedLogUnabated;
  log.push(entry);
  if (log.length > FEED_LOG_MAX) log.shift();
}

// ── Venue fence (F0, Polymarket US) ──────────────────────────────
//
// The wire is untyped. Before F0 every `source !== 'kalshi'` read meant
// "intl Polymarket", so a frame with any other source — the `polymarket_us`
// venue hydra is about to emit, or garbage — would have merged into the intl
// Poly slot of the same canonicalGameKey row, its feed log and its start-time
// cache. `feedVenueOf` is the one place the wire value is admitted; an
// unknown source is dropped at ingest and counted, never coerced.

const KNOWN_VENUES: ReadonlySet<string> = new Set(['kalshi', 'polymarket', 'polymarket_us']);

function feedVenueOf(source: unknown): FeedVenue | null {
  return typeof source === 'string' && KNOWN_VENUES.has(source) ? (source as FeedVenue) : null;
}

/** Venue from the ticker namespace alone — for paths (orderbook frames) that
 *  carry no `source`. `PMUS-` is checked before `PM-`; an unrecognized prefix
 *  is null and the caller skips venue-keyed writes. */
function prefixVenue(ticker: string): FeedVenue | null {
  if (ticker.startsWith('KX')) return 'kalshi';
  if (ticker.startsWith('PMUS-')) return 'polymarket_us';
  if (ticker.startsWith('PM-')) return 'polymarket';
  return null;
}

let _droppedUnknownSourceFrames = 0;
/** Market frames refused at ingest because `source` was outside the known
 *  venue set. Diagnostic only; a non-zero value on a box means hydra is
 *  emitting a venue this build does not know. */
export function getDroppedUnknownSourceFrameCount(): number {
  return _droppedUnknownSourceFrames;
}
/** Test seam. */
export function _resetDroppedUnknownSourceFrameCountForTests(): void {
  _droppedUnknownSourceFrames = 0;
}

// ── Mutable data layer (not in Zustand state — no copy overhead) ─

const _markets = new Map<string, HydraMarket>();
/** Per-ticker shard evidence. Contradictions are sticky for this process. */
const _kalshiExchangeIndexDiagnostics = new Map<string, KalshiExchangeIndexDiagnostic>();
// Polymarket conditionId → slug ticker. The relay's poly position/fills
// ledger keys rows by CONDITION ID while _markets keys frames by the PM-
// slug ticker — without this index a getMarket(conditionId) is a guaranteed
// miss (which is why resolvePolyCloseTarget could never enable Cash Out).
const _tickerByConditionId = new Map<string, string>();
// Polymarket outcome tokenId → slug ticker. Open orders and audit rows key
// by TOKEN id; the game-row sweeps only cover moneyline pairs (+ soccer
// outcomes), so spread/total/anything-else tokens need a direct route back
// to their market frame for labels + tickSize/negRisk.
const _tickerByTokenId = new Map<string, string>();
// Polymarket US market slug → PMUS- ticker (F1). The relay's US ledger keys
// orders/fills/positions by SLUG; frames key by ticker. Kept by
// deleteMarketRecord like its siblings: identity outlives the row.
const _tickerByPmusSlug = new Map<string, string>();
const _orderbooks = new Map<string, HydraOrderbook>();

// ── Sport index ─────────────────────────────────────────────────
// `_markets` holds every sport's markets in one map. The list/game selectors
// (getGames, getGameEvents, getMultiOutcomeEvents, …) only ever care about one
// sport at a time, but historically each walked the *entire* map per recompute
// — so adding markets to one sport (a busy MLB slate of spread/total/F5
// tickers) slowed the recompute for *every* sport's tab. This index keys the
// markets by NORMALIZED sport so a selector scans only its bucket(s).
//
// A market can land in MULTIPLE buckets (see `marketSportKeys`): series rows
// are keyed by the sport encoded in `canonicalSeriesKey`, not `market.sport`;
// and ambiguous tennis frames are indexed under both atp and wta. The buckets
// are a *pre-filter only* — every selector keeps its own exact sport check, so
// OVER-including a market in a bucket is harmless (a few wasted iterations)
// while UNDER-including would drop a row. When in doubt, `marketSportKeys`
// over-includes. Maintained exclusively by `indexMarketBySport` (called from
// ingestMarket); `_markets` is never deleted today, so this is set-only aside
// from re-ingest reclassification.
const _marketsBySport = new Map<Sport, Map<string, HydraMarket>>();
// ticker → the buckets it is currently filed under, so a re-ingest whose
// canonical keys / sport changed can be pulled out of now-stale buckets.
const _marketSportKeys = new Map<string, Sport[]>();
const _EMPTY_MARKETS: ReadonlyArray<HydraMarket> = [];
// Cache membership only: selector reads still use current market/book/proof data.
// Rebuild at identity boundaries, including sport-bucket reclassification.
let _mlbGameTickers: Map<string, string[]> | null = null;

/** (Re)file a market into its sport bucket(s), pulling it out of any bucket it
 *  no longer belongs to (canonical keys can refine across frames). */
function indexMarketBySport(market: HydraMarket): void {
  const ticker = market.ticker;
  const next = marketSportKeys(market);
  const prev = _marketSportKeys.get(ticker);
  if (Boolean(prev?.includes('mlb')) !== next.includes('mlb')) _mlbGameTickers = null;
  if (prev) {
    for (const s of prev) {
      if (!next.includes(s)) _marketsBySport.get(s)?.delete(ticker);
    }
  }
  for (const s of next) {
    let bucket = _marketsBySport.get(s);
    if (!bucket) { bucket = new Map(); _marketsBySport.set(s, bucket); }
    bucket.set(ticker, market); // always overwrite so the bucket holds the latest object
  }
  _marketSportKeys.set(ticker, next);
  // Same lockstep discipline for the futures index: a bucket this ticker left
  // must not keep it on a board.
  if (prev) deindexFuture(ticker, prev.filter((s) => !next.includes(s)));
  indexMarketAsFuture(market, next);
  const golf = golfMatchIdentity(market);
  const previousGolf = _golfMatches.get(ticker);
  if (golf) _golfMatches.set(ticker, golf);
  else _golfMatches.delete(ticker);
  if (golf?.identity !== previousGolf?.identity) _golfMatchList = null;
}

/** Markets filed under a sport — the per-sport replacement for `_markets`
 *  iteration in the list/game selectors. */
function marketsForSport(sport: Sport): Iterable<HydraMarket> {
  return _marketsBySport.get(sport)?.values() ?? _EMPTY_MARKETS;
}

/** Markets to scan for a per-game selector keyed by a canonicalGameKey
 *  (`{sport}|…`). Falls back to the full map when the prefix doesn't normalize
 *  to a known sport (e.g. the tour-less `tennis|…`) — rare, and those games
 *  have no period markets so the selector bails fast anyway. */
function marketsForGameKey(gameKey: string): Iterable<HydraMarket> {
  const i = gameKey.indexOf('|');
  const sport = i > 0 ? SPORT_NORMALIZE[gameKey.slice(0, i)] : undefined;
  return sport ? marketsForSport(sport) : _markets.values();
}

function* mlbMarketsForGameKey(gameKey: string): Iterable<HydraMarket> {
  const i = gameKey.indexOf('|');
  if (i <= 0 || SPORT_NORMALIZE[gameKey.slice(0, i)] !== 'mlb') {
    yield* marketsForGameKey(gameKey);
    return;
  }
  if (!_mlbGameTickers) {
    const index = new Map<string, string[]>();
    for (const market of marketsForSport('mlb')) {
      const key = gameKeyV2(market, market.canonicalGameKey ?? '');
      if (!key) continue;
      const tickers = index.get(key);
      if (tickers) tickers.push(market.ticker);
      else index.set(key, [market.ticker]);
    }
    _mlbGameTickers = index;
  }
  for (const ticker of _mlbGameTickers.get(gameKey) ?? []) {
    const market = _markets.get(ticker);
    if (market) yield market;
  }
}

// Browser-console diagnostic: call `__dumpKalshiSpreads('TBNYY')` (or any
// matchup substring) to list every Kalshi spread/total market currently
// in the store whose ticker contains the substring, along with the
// parser's interpretation of it. Helps figure out why a particular
// magnitude / side isn't showing in the chip strip — surfaces whether
// hydra is even sending the ticker we expect, and what marketStore
// thinks the (team, bucket, lineMag, favored) reads as.
if (typeof window !== 'undefined') {
  // @ts-expect-error attaching debug helper to window
  window.__dumpKalshiSpreads = (matchup: string) => {
    const lower = matchup.toUpperCase();
    const rows: Array<Record<string, unknown>> = [];
    for (const [, m] of _markets) {
      if (!m.ticker.startsWith('KX')) continue;
      if (!m.ticker.includes(lower)) continue;
      const parsed = parseTicker(m);
      const subtitleParse = parseLineFromSubtitle(m.subtitle);
      const titleParse = parseLineFromSubtitle(m.title);
      rows.push({
        ticker: m.ticker,
        status: m.status,
        subtitle: m.subtitle,
        title: m.title,
        canonicalGameKey: m.canonicalGameKey,
        parsedMarketType: parsed?.marketType,
        parsedSpreadTeamCode: parsed?.spreadTeamCode,
        parsedSpreadBucket: parsed?.spreadBucket,
        parsedAwayCode: parsed?.awayCode,
        parsedHomeCode: parsed?.homeCode,
        subtitleLine: subtitleParse,
        titleLine: titleParse,
        bucketFallbackLine: parsed?.spreadBucket != null
          ? parsed.spreadBucket - 0.5
          : null,
      });
    }
    // eslint-disable-next-line no-console
    console.table(rows);
    return rows;
  };
}
// Cache of canonicalGameKey → eventStartTime epoch ms. `eventStartTime`
// flows in from Polymarket Gamma, Kalshi milestones, and the Unabated
// game-state poll. Every sibling market sharing the canonical key
// (moneyline legs, spreads, totals, …) reads back the same value.
// Used by lookups like `getEventStartTimeMsByTicker` to give related
// markets an event start without an O(N) scan per query.
//
// Update policy (`updateStartTimeCache` is the only mutator):
//   • Values are stored PER SOURCE per gameKey; reads resolve by SOURCE
//     PRECEDENCE, never last-write-wins between venues — that was the
//     A-Fire flicker (Gitea #54): a postponed game (MIL/PIT Jul-10, live
//     repro) keeps both venues streaming — Kalshi milestones carry the
//     rescheduled start while Poly Gamma keeps the original — so the
//     resolved value ping-ponged on every alternating frame and the 4 Hz
//     throttled sort ticks on PositionTracker / MyTrades re-shuffled
//     cards continuously until the game cleared.
//   • Unabated outranks the venues only once it says the game has STARTED
//     (phase in-progress/final AND unabated ≤ now): live-delay timing must
//     win post-start, but a PRE-start unabated seed pinning the board
//     would block venue schedule corrections (postponements) on views
//     that never mount useGameStatesPoll and so never refresh the
//     unabated slot. Pre-start order: kalshi > unabated > polymarket.
//   • WITHIN one source, last-write-wins stays: schedule corrections from
//     the same feed propagate immediately.
//   • Returns true only when the RESOLVED value changed — the signal call
//     sites use to bump the store version. Resolution is computed at
//     write time; the pre→post-start flip lands with the next write for
//     the gameKey (the games-state poll re-seeds unabated every cycle
//     where it's mounted, and venue frames stream continuously).
// `polymarket_us` sits LAST in both precedence chains: a US frame never
// overrides a Kalshi or intl-Poly start (F0-a).
type StartTimeSource = 'kalshi' | 'polymarket' | 'polymarket_us' | 'unabated';
type StartTimeSlots = Partial<Record<StartTimeSource, number>> & {
  gameStateStatus?: 1 | 2 | 3;
};
const _startTimeSlotsByGameKey = new Map<string, StartTimeSlots>();
// Resolved view (precedence applied at write time) so the hot readers
// stay a single O(1) map hit per lookup.
const _startTimeMsByGameKey = new Map<string, number>();

function resolveStartTime(slots: StartTimeSlots): number | undefined {
  // Passing a scheduled timestamp is not evidence that the game started.
  // Keep the phase beside its timestamp so later venue frames cannot promote
  // an old scheduled value just because the wall clock crossed it (#1253).
  if ((slots.gameStateStatus === 2 || slots.gameStateStatus === 3)
    && slots.unabated != null && slots.unabated <= Date.now()) return slots.unabated;
  return slots.kalshi ?? slots.unabated ?? slots.polymarket ?? slots.polymarket_us;
}

// Resolved SCHEDULE view — the same precedence minus the post-start Unabated
// override. Unabated outranks the venues once a game has started because
// live-delay timing must drive the displayed clock; but Unabated is also the
// source observed flapping live, and a flapping value must never decide which
// calendar DAY a row belongs to. Since the day filter became a per-row test
// (rainout makeup fix), the resolved start decides whether a row RENDERS at
// all — so a first-pitch flip to a stale/flapping Unabated value could drop a
// live game off a day-filtered board and strand it behind the All toggle.
// Day bucketing reads this; every user-facing clock keeps reading the resolved
// value, so live-delay accuracy is unchanged.
const _scheduleStartMsByGameKey = new Map<string, number>();

function resolveScheduleStartTime(slots: StartTimeSlots): number | undefined {
  return slots.kalshi ?? slots.unabated ?? slots.polymarket ?? slots.polymarket_us;
}

// ── Subtitle-named matchups (#685) ───────────────────────────────────
//
// Some sports have no team-code registry at all: `TEAM_NAMES_BY_SPORT`
// (lib/kalshiTicker/teamNames.ts) covers nba/wnba/nhl/nfl/mlb and nothing
// else, so `getTeamDisplayName` hands a soccer code straight back. Soccer's
// display name therefore rides the LIVE FEED — Kalshi's per-market
// `yes_sub_title`, which hydra forwards as `subtitle` and which names the
// club that market's YES side pays out on ("New York City", "Switzerland").
// Contract settled with core 2026-08-22; deliberately NOT a static club-code
// table, because the codes move (56 of 62 changed in one week: fcc→CIN,
// sdg→SD, …) and a table is born stale while the subtitle rides the feed.
//
// This index is that authority, held ONCE for the whole app. Before #685 it
// existed only as a local accumulator inside getMultiOutcomeEvents, so the
// dashboard row was named while every card, and the knockout Advance rows,
// still showed codes for the same fixture.
//
// Keyed by the doubleheader-safe game key, so EVERY market family on a
// fixture resolves through it — 1X2, draw, spread, total, BTTS, To Advance —
// since hydra stamps them all with one `canonicalGameKey`. Values carry the
// oriented pair as well as the names, because Polymarket frames carry
// `canonicalGameKey` but NOT canonicalAway/HomeCode (Kalshi-only, see the
// HydraMarket contract), so a poly card has no other way to learn which club
// is away.
/** What a subtitle-named fixture is currently CALLED, oriented away → home.
 *  Names are pre-floored to the raw code, so a consumer never has to decide
 *  what to render when the producer hasn't named a club yet. */
export interface SubtitleMatchupView {
  awayCode: string;
  homeCode: string;
  awayName: string;
  homeName: string;
}

interface SubtitleMatchup {
  awayCode: string;
  homeCode: string;
  /** outcome code → the club name its moneyline subtitle carries. Absent for
   *  a code whose frame shipped without a subtitle — readers floor to the
   *  code, never to empty text. */
  names: Map<string, string>;
}
const _subtitleMatchupByGameKey = new Map<string, SubtitleMatchup>();

/** Sports whose display name comes from the feed rather than a code registry.
 *  The mechanism is general — CFB, tennis, UFC and cricket are equally
 *  registry-less — but enabling one is a naming-policy decision with its own
 *  acceptance, and #685 is scoped to soccer ("existing CFB sorting and
 *  non-soccer card naming remain unchanged"). Adding a sport here is a
 *  one-line change plus its own test. */
const SUBTITLE_NAMED_SPORTS: ReadonlySet<Sport> = new Set<Sport>(['soccer']);

/** Does this frame's subtitle name a CLUB, rather than describe a bet?
 *
 *  ONE predicate, consulted by both the write and the clear path below. They
 *  were separate checks until the #685 review: the clear path re-tested only a
 *  subset, so any frame `namesAClub` rejected for a reason the clear path
 *  didn't know about DELETED the club name instead of leaving it alone. A
 *  soccer 1H moneyline shares the fixture's canonical key, so it wiped the
 *  full-game name on every tick for as long as Game Lines were listed.
 *
 *  Authority is the TICKER PARSE, with the frame fields as corroboration:
 *
 *   • `parsed.marketType === 'moneyline'` — derived from the series suffix, so
 *     it excludes SPREAD / TOTAL / BTTS / ADVANCE by construction. This is the
 *     guard that matters: `HydraMarket.marketType` is documented as "absent on
 *     older builds — consumers must tolerate that and fall back to the parsed
 *     type", and an advance frame without it sails past a field-only check and
 *     writes "Switzerland advances" as a club name.
 *   • `parsed.period === 'full'` — excludes the 1H/2H moneylines, which are
 *     3-way and DO carry a `yesOutcome`, from touching the full-game name.
 *   • the frame fields (`floorStrike`, `yesOutcome`, prose) then corroborate:
 *     either source says "not a full-game moneyline" and we neither write nor
 *     clear.
 *
 *  This is the same dual-check discipline `getMultiOutcomeEvents` and
 *  `getAdvanceGame` already apply (`m.marketType !== 'advance' &&
 *  parsed?.marketType !== 'advance'`) — the index was the only soccer
 *  classifier in this store trusting the hydra field on its own.
 */
function isClubNamingFrame(market: HydraMarket, parsed: ParsedTicker | null): boolean {
  if (market.source !== 'kalshi') return false;
  if (!parsed) return false;
  if (parsed.marketType !== 'moneyline') return false;
  if (resolveMarketPeriod(market, parsed) !== 'full') return false;
  if (market.marketType && NON_MONEYLINE_TYPES.has(market.marketType)) return false;
  return market.floorStrike == null;
}

/** Field-only prefilter for `isClubNamingFrame`, so a caller sweeping every
 *  market of a sport can skip the ticker parse for frames that cannot name a
 *  club. NEVER the authority: anything that passes here still goes through
 *  `isClubNamingFrame`, which is where the policy lives. Kept beside it so the
 *  two can't drift into disagreeing about what a naming frame is. */
function mayNameAClub(market: HydraMarket): boolean {
  return (
    market.source === 'kalshi' &&
    market.floorStrike == null &&
    (market.period == null || market.period === 'full')
  );
}

/** Non-moneyline hydra market types. Corroboration only — see isClubNamingFrame
 *  for why the parse, not this field, is the authority. */
const NON_MONEYLINE_TYPES: ReadonlySet<string> = new Set([
  'advance', 'btts', 'spread', 'total', 'series', 'future', 'prop',
]);

/** The club name a naming frame carries, or null when it carries none.
 *
 *  Kalshi scope-qualifies World Cup subtitles — the 2026 final's ESP leg reads
 *  "Reg Time: Spain", not "Spain" (verified against prod hydra 2026-07-18; the
 *  club leagues do not qualify, so this is a no-op for MLS/UEL/UCL). The
 *  qualifier is dropped for the same reason `describeMarket.stripOutcomeQualifier`
 *  drops it: a scope belongs to the MARKET, not to the club's name, and
 *  borrowing "Reg Time" for the To Advance row — which settles INCLUDING extra
 *  time and penalties — labels it with the opposite of what it settles on.
 *
 *  Also rejects line prose outright, which catches pre-`floorStrike` frames
 *  that carry neither structured signal.
 */
function clubNameFrom(subtitle: string | null | undefined, code: string): string | null {
  const raw = subtitle?.trim();
  if (!raw) return null;
  if (/\b(?:over|under)\s+\d|\bwins? by\b|\bgoals? scored\b/i.test(raw)) return null;
  const cleaned = stripSubtitleScope(raw);
  return cleaned && cleaned.toUpperCase() !== code.toUpperCase() ? cleaned : null;
}

/** A subtitle with its market-scope qualifier removed — "Reg Time: Spain" →
 *  "Spain". ONE cut, shared with `competitorDisplayName`, because the two
 *  disagree only about what to do with the remainder: the index rejects a
 *  subtitle that merely echoes the code, the display name keeps it. */
function stripSubtitleScope(subtitle: string): string {
  return (subtitle.includes(': ') ? subtitle.slice(subtitle.lastIndexOf(': ') + 2) : subtitle).trim();
}

/** Event-ticker key for a frame hydra hasn't stamped with a canonical game key
 *  yet. Mirrors getMultiOutcomeEvents' own eventKey fallback exactly — the two
 *  must agree or the row loses its names on pre-canonical frames. */
function subtitleFallbackKey(market: HydraMarket): string | null {
  return market.eventTicker || market.ticker.replace(/-[^-]+$/, '') || null;
}

/** Every key this frame's fixture may be indexed under, most authoritative
 *  first. ONE derivation shared by the writer and both readers — they drifted
 *  in the first cut of #685 (the writer gated on `canonicalGameKey` while the
 *  row readers accept `canonicalGameKeyV2 || canonicalGameKey`), which loses a
 *  v2-only frame's names entirely.
 *
 *  The event-ticker fallback covers the window before hydra stamps a fixture:
 *  the row already names those games off its own parse, so without the same
 *  fallback here the cards would show codes beside a named row — #685's exact
 *  symptom, reproduced by the fix meant to remove it. */
function subtitleKeysForMarket(market: HydraMarket): string[] {
  const keys: string[] = [];
  const canonical = market.canonicalGameKeyV2 || market.canonicalGameKey
    ? gameKeyV2(market, market.canonicalGameKey ?? '')
    : null;
  if (canonical) keys.push(canonical);
  const fallback = subtitleFallbackKey(market);
  if (fallback && fallback !== canonical) keys.push(fallback);
  return keys;
}

/** Record what this frame proves about its fixture's display names.
 *
 *  Called from ingestMarket for every frame of a subtitle-named sport, so the
 *  index is always warm and every read is a single map hit — `describeMarket`
 *  runs during render for every position, order and fill on the dashboard and
 *  must never reach for a selector (see its own O(1) warning).
 *
 *  A frame that is not a club-naming frame is a NO-OP for the NAMES: it neither
 *  writes nor clears. Only a full-game moneyline leg has anything to say about
 *  what a club is called, so only it may change the answer. (Orientation is
 *  different — any frame of the fixture knows who is at home.)
 *
 *  Within that shape the write follows ingestMarket's wholesale-replace
 *  discipline: the latest moneyline frame for a code wins, INCLUDING when it
 *  arrives carrying no usable subtitle. A producer that stops naming a club
 *  must surface as the code floor rather than be masked by a value we happen to
 *  still be holding. */
function indexSubtitleMatchup(market: HydraMarket): void {
  const sport = SPORT_NORMALIZE[market.canonicalGameKey?.split('|')[0] ?? '']
    ?? (market.sport ? SPORT_NORMALIZE[market.sport] : undefined);
  if (!sport || !SUBTITLE_NAMED_SPORTS.has(sport)) return;
  const key = subtitleKeysForMarket(market)[0];
  if (!key) return;

  let entry = _subtitleMatchupByGameKey.get(key);
  if (!entry) {
    entry = { awayCode: '', homeCode: '', names: new Map() };
    _subtitleMatchupByGameKey.set(key, entry);
  }
  // Orientation: Kalshi-only canonical fields, and the only source of away/home
  // for the poly frames that share this key.
  if (market.canonicalAwayCode && market.canonicalHomeCode) {
    entry.awayCode = market.canonicalAwayCode.toUpperCase();
    entry.homeCode = market.canonicalHomeCode.toUpperCase();
  }

  if (!isClubNamingFrame(market, parseTicker(market))) return;
  const code = normalizeOutcomeCode(market.yesOutcome);
  if (!code || isDrawOutcome(code)) return;
  const name = clubNameFrom(market.subtitle, code);
  if (name) entry.names.set(code, name);
  else entry.names.delete(code);
}

/** The oriented, feed-named matchup for a game key, or null when this fixture
 *  has no oriented entry (not a subtitle-named sport, nothing ingested, or
 *  hydra has not stamped away/home yet). Names are already floored to the raw
 *  code, so callers get a non-empty string or nothing at all. */
function subtitleMatchupForKey(gameKey: string | null): SubtitleMatchupView | null {
  if (!gameKey) return null;
  const entry = _subtitleMatchupByGameKey.get(gameKey);
  if (!entry || !entry.awayCode || !entry.homeCode) return null;
  return {
    awayCode: entry.awayCode,
    homeCode: entry.homeCode,
    awayName: entry.names.get(entry.awayCode) || entry.awayCode,
    homeName: entry.names.get(entry.homeCode) || entry.homeCode,
  };
}

/** The feed's name for one outcome code, or undefined when it has none — for
 *  callers that have their own next fallback (a code registry). */
function subtitleNameHint(gameKey: string | null, code: string): string | undefined {
  if (!gameKey) return undefined;
  return _subtitleMatchupByGameKey.get(gameKey)?.names.get(code);
}

/** The name this fixture gives one outcome code, floored to the code itself. */
function subtitleNameForCode(gameKey: string | null, code: string): string {
  return subtitleNameHint(gameKey, code) || code;
}

function updateStartTimeCache(
  gameKey: string,
  ms: number,
  source: StartTimeSource,
  gameStateStatus?: 1 | 2 | 3,
): boolean {
  if (!Number.isFinite(ms)) return false;
  let slots = _startTimeSlotsByGameKey.get(gameKey);
  if (!slots) {
    slots = {};
    _startTimeSlotsByGameKey.set(gameKey, slots);
  }
  slots[source] = ms;
  if (source === 'unabated') slots.gameStateStatus = gameStateStatus;
  // Schedule view tracked alongside the resolved one. A schedule correction
  // that doesn't move the resolved value (a venue re-time arriving after
  // first pitch, when Unabated already owns the resolved slot) still has to
  // bump the store version — it can change which day a row buckets into.
  const schedule = resolveScheduleStartTime(slots);
  const scheduleChanged =
    schedule !== undefined && schedule !== _scheduleStartMsByGameKey.get(gameKey);
  if (scheduleChanged) _scheduleStartMsByGameKey.set(gameKey, schedule);
  const resolved = resolveStartTime(slots);
  if (resolved === undefined || resolved === _startTimeMsByGameKey.get(gameKey)) {
    return scheduleChanged;
  }
  _startTimeMsByGameKey.set(gameKey, resolved);
  return true;
}
// Cache of (canonicalGameKey × periodTypeId) → latest Unabated consensus
// snapshot. Fed by the `consensus` WS channel (HYDRA_API.md "Unabated
// consensus update"). Hydra emits on ≥1¢ shift per (game, period) and
// backfills the latest Full Game value per active game on subscribe, so
// `getConsensusFor` returns the live value as soon as the WS hand-shakes
// — no per-game REST poll needed.
//
// Keyed by `${canonicalGameKey}|${periodTypeId}` so in-period values
// (Q1 / inning / set, periodTypeId !== 1) don't clobber the Full Game
// read consumers show by default. `getConsensusFor` filters to
// periodTypeId=1 — when in-period views land, they'll select on the
// period they want from the same cache.
const _consensusByGameKeyAndPeriod = new Map<string, HydraConsensus>();

// Active-sport set from hydra's `activeSports` meta frame (normalized to g1
// Sport ids, unknown wire codes like NFL filtered out). Null until the first
// frame arrives. Holds the last NON-EMPTY set: hydra's cache is cold for
// ~1-2 min after its nightly restart and the frame arrives empty in that
// window — valid data, but blanking the toolbar mid-session helps nobody.
// Active-sport sets keyed by feed source id ('firehose', 'edge:nfl', …). Each
// feed announces its OWN activeSports meta frame; getActiveSports unions them
// so the chip row reflects every connected pipe. Single-set until the hydraedge
// fan-in (multi-upstream) made a per-source union necessary.
//
// Holds RAW FEED KEYS (main-alt-partition-he6-he7 D5.2): an ALT edge's
// announce is `CFBALT`, and the FeedTape chip must show exactly that. The
// sport-chip row (getActiveSports) folds each key to its display sport, so
// `cfbalt` never becomes a chip of its own.
const _activeSportsBySource = new Map<string, Set<FeedSport>>();
// Proposed filter axes each feed's engine advertised on its announce
// (`activeSports.filterAxes`, g1xl-v2#1053 Part 3). Last announce wins;
// cleared with the feed. Empty for every engine today.
const _filterAxesBySource = new Map<string, ReadonlySet<string>>();
const NO_AXES: ReadonlySet<string> = new Set();
// Bucket by (gameKey, periodTypeId, marketType). The soccer knockout "To
// Advance" consensus rides the SAME canonicalGameKey + periodTypeId (1) as that
// match's regulation result frame, so without the marketType segment the two
// overwrite each other. The result/1X2 frame omits marketType → 'result'.
const consensusCacheKey = (
  gameKey: string,
  periodTypeId: number,
  marketType?: HydraConsensus['marketType'],
) => `${gameKey}|${periodTypeId}|${marketType ?? 'result'}`;

// ── OddsPapi comparison odds (HYDRA_API.md §"OddsPapi odds") ─────────
// One bucket per (gameKeyV2, periodTypeId, marketType) — the SAME key
// vocabulary as the consensus cache but a SEPARATE map, never merged into
// `_consensusByGameKeyAndPeriod`: consensus ingest is not feed-gated, so a
// shared key would let last-write-wins clobber Unabated (the DNL-002 store
// hazard the architecture calls out). The stager (lib/opSnapshotStager)
// owns rev/epoch/atomic-commit semantics; by the time these setters run the
// change is already authenticated — this map is the render mirror.
export interface OpOddsSlice {
  /** Which comparison lane owns this slice — provenance for
   *  display/diagnostics, never a routing key inside the store. Only
   *  OddsPapi remains (the OpticOdds trial lane was removed 2026-09-06). */
  provider: 'oddspapi';
  canonicalGameKey: string;
  canonicalGameKeyV2: string;
  sport: string;
  /** Provider orientation in canonical codes — align to the row via
   *  lib/opOdds.alignOpSidesToRow (rule 4), never inline. */
  awayTeam: string;
  homeTeam: string;
  lastUpdateMs: number;
  market: HydraOpMarket;
}
const _opOddsByGameKey = new Map<string, OpOddsSlice>();

// OP consensus trend state (Dan 2026-08-03: OP/PNCL gets the U column's
// velocity arrows). SAME engine (lib/consensusTrend), SEPARATE map — fed
// exclusively by OP consensus tenths, never Unabated's, and vice versa.
// Feeding rule: a bucket's trend ingests only when its consensus CHANGED
// (opConsensusEqual); first sight — including every rebuild inside an
// atomic snapshot replace, which runs against a cleared slice map — seeds
// with a NULL arrival, so a reconnect's re-delivery of unchanged (or
// while-offline-moved) state can never false-light recency/arrows. The
// cost is that movement during an outage lands silently — matching what
// the ~20s arrow lifetime would show anyway. No sharp flag is fed: the
// sharp-anchor treatment stays Unabated vocabulary.
const _opTrendByBucket = new Map<string, ConsensusTrendState>();

// Last-known anchor fair values per bucket (phase 3) — the number the
// strikethrough is drawn over when the frame lists pinnacle in
// withdrawnBooks. Written whenever an anchor quote carries fair tenths;
// deliberately NOT cleared when the anchor vanishes (that persistence is
// the whole point) — only when the game's coverage itself goes. This
// caches a display VALUE for an EXPLICIT wire withdrawal; the withdrawal
// state itself is never inferred client-side (rule 12).
const _opLastAnchorFair = new Map<string, OpLastAnchorFair>();

function feedOpTrend(lane: OddsLaneNs, key: string, prevSlice: OpOddsSlice | undefined, slice: OpOddsSlice): void {
  const c = slice.market.consensus;
  if (c == null) return; // consensus dropped/absent — let arrows decay in place
  if (prevSlice != null && opConsensusEqual(prevSlice.market.consensus, c)) return;
  const now = Date.now();
  const prevT = lane.trendByBucket.get(key) ?? null;
  const nextT = ingestConsensusTrendFrame(
    prevT,
    trendFrameFromConsensus(c, prevSlice == null ? null : now),
    now,
  );
  if (nextT !== prevT) lane.trendByBucket.set(key, nextT);
}

/** Odds wire keys and route keys may spell the same sport differently
 * (ncaaf/CFB). Normalize only that namespace boundary; preserve the date,
 * game number, teams, period and exact strike, and keep wire IDs on slices. */
function oddsGameKeyV2(key: string | null | undefined): string {
  return foldGameKeySport(promoteToGameKeyV2(key));
}

const opOddsCacheKey = (
  gameKeyV2: string,
  /** Period discriminator: the numeric periodTypeId for full-game entries
   *  (1 — keeps every pre-B key BYTE-IDENTICAL, so reference stability
   *  and consumers survive the widening untouched), or the verbatim
   *  vendor `period` string for period-scoped entries (full-feed B —
   *  those carry no periodTypeId at all). Feature detection IS the key
   *  shape; no versioning. */
  periodKey: number | string,
  marketType: string,
  points?: number,
  /** op-player-props: the (stat, player) discriminator of a `player-prop`
   *  bucket — `who` is the wire `playerKey` when core paired the entry,
   *  else `pid<playerId>` (unpaired; g1 never pins those). Key shape:
   *  `${gameKeyV2}|${periodKey}|player-prop|${stat}|${who}|${points}`. */
  prop?: { stat: string; who: string },
) =>
  // Line markets are one-bucket-per-LINE — the line joins the key.
  // Non-line families keep the original 3-part key unchanged.
  prop !== undefined
    ? `${gameKeyV2}|${periodKey}|${marketType}|${prop.stat}|${prop.who}|${points}`
    : points === undefined
      ? `${gameKeyV2}|${periodKey}|${marketType}`
      : `${gameKeyV2}|${periodKey}|${marketType}|${points}`;

/** Known OP market types (contract rule 8: unknown values are IGNORED —
 *  that's what keeps the NEXT widening additive too). spread/total joined
 *  2026-08-11 (hydracore §"Spread & total markets"); full-feed B
 *  (2026-08-17) adds team totals, football totals specials and odd/even —
 *  note the WIRE spellings `total-touchdowns`/`total-fieldgoals`
 *  (hydracore resolver.ts normalizes the vendor's `totals-*`). Player
 *  props, 1x2 and N-way families are deliberately NOT served (phase C /
 *  model mismatch) — they stay unknown here until a contract addendum. */
const OP_KNOWN_MARKET_TYPES: ReadonlySet<string> = new Set([
  'moneyline', 'spread', 'total',
  'teamtotal-home', 'teamtotal-away',
  'total-touchdowns', 'total-fieldgoals',
  'oddeven', 'oddeven-home', 'oddeven-away',
  // op-player-props R1 (2026-09-08): per-player O/U ladder entries —
  // `stat` + `playerKey`/`playerId` discriminate the bucket (see
  // opOddsCacheKey). First-TD (`shape:'yes'`) is NOT served yet.
  'player-prop',
]);

/** Families that carry a line — `points` is REQUIRED on these (fail
 *  closed per entry when absent) and joins the cache key. Period
 *  moneylines and odd/even never carry points. */
const OP_LINE_MARKET_TYPES: ReadonlySet<string> = new Set([
  'spread', 'total',
  'player-prop',
  'teamtotal-home', 'teamtotal-away',
  'total-touchdowns', 'total-fieldgoals',
]);

/** Per-game line index for the spread/total ladders — the SpreadsTotals
 *  surfaces look up "OP consensus at THIS row's line" per render, and a
 *  prefix scan of the flat bucket map (~50 lines × ~30 games) per row per
 *  tick would be O(rows × buckets). Full-game (periodTypeId 1) only —
 *  the producer's phase-1 scope; later period phases extend the index
 *  key, not the shape. Values are the SAME slice objects as
 *  `_opOddsByGameKey` (reference-stable across byte-equal re-deliveries,
 *  so zustand consumers don't re-render on snapshot re-sends). */
interface OpLineIndexEntry {
  spreads: Map<number, OpOddsSlice>; // key: wire `points` (home handicap)
  totals: Map<number, OpOddsSlice>;  // key: wire `points` (O/U line)
}
const _opLinesByGame = new Map<string, OpLineIndexEntry>();

/** One comparison lane's whole store namespace. The OP lane aliases the
 *  original module maps (every existing selector keeps reading them
 *  untouched). The write paths below are lane-parameterized — the only
 *  behavioural difference between lanes is which namespace they touch and
 *  which provider tag lands on the slice. (The OpticOdds trial lane was a
 *  second instance of this shape; removed 2026-09-06 with the vendor.) */
interface OddsLaneNs {
  provider: 'oddspapi';
  byGameKey: Map<string, OpOddsSlice>;
  trendByBucket: Map<string, ConsensusTrendState>;
  lastAnchorFair: Map<string, OpLastAnchorFair>;
  linesByGame: Map<string, OpLineIndexEntry>;
  /** game → its bucket keys in `byGameKey`. A game's frame replaces that
   *  game's buckets, and finding them used to mean a prefix scan of EVERY
   *  bucket of EVERY game (~50 per game across the slate) on every OddsPapi
   *  frame. Maintained only where `byGameKey` gains or loses keys: the three
   *  functions below. */
  keysByGame: Map<string, Set<string>>;
}
const _opLane: OddsLaneNs = {
  provider: 'oddspapi',
  byGameKey: _opOddsByGameKey,
  trendByBucket: _opTrendByBucket,
  lastAnchorFair: _opLastAnchorFair,
  linesByGame: _opLinesByGame,
  keysByGame: new Map(),
};
/** One game's `[bucketKey, slice]` pairs, via the per-game key index — the
 *  readers below run per mounted ladder row per store update, and each used to
 *  walk every bucket of every game on the slate to find one game's prefix. */
function* opGameEntries(gameKeyV2: string): Generator<[string, OpOddsSlice]> {
  for (const key of _opLane.keysByGame.get(gameKeyV2) ?? []) {
    const slice = _opOddsByGameKey.get(key);
    if (slice) yield [key, slice];
  }
}

/** Reference strikes are exact contract identities. Any settlement-equivalent
 *  venue conversion belongs in its explicit caller (for example N+ to N-0.5),
 *  never in a nearest-strike fallback shared across families. */
function opLineLookup(lines: Map<number, OpOddsSlice> | undefined, target: number): OpOddsSlice | null {
  if (!Number.isFinite(target)) return null;
  return lines?.get(target) ?? null;
}

/** Split a game's absolute frame into bucket entries (known markets only)
 *  and write them, clearing the game's previous buckets first — the frame
 *  is ABSOLUTE per-game state (rule 1), so a market absent from it must
 *  drop. Returns true when anything changed (dedup: a byte-equal re-emit
 *  from a snapshot burst must not tick). */
function writeOddsGame(lane: OddsLaneNs, op: HydraOpOdds): boolean {
  const wireGameKeyV2 = promoteToGameKeyV2(op.canonicalGameKeyV2 || op.canonicalGameKey);
  const gameKeyV2 = oddsGameKeyV2(wireGameKeyV2);
  const next = new Map<string, OpOddsSlice>();
  for (const market of op.markets) {
    if (!OP_KNOWN_MARKET_TYPES.has(market.marketType)) continue;
    // Line markets without a line are contractually impossible — fail
    // closed on the entry (rule 8 posture), never invent a bucket.
    const isLine = OP_LINE_MARKET_TYPES.has(market.marketType);
    if (isLine && (typeof market.points !== 'number' || !Number.isFinite(market.points))) continue;
    // Full-feed B feature detection: a period entry carries `period` and
    // no periodTypeId; a full-game entry carries periodTypeId (1) and no
    // period. Either discriminates the bucket; an entry with NEITHER is
    // contractually impossible — fail closed on it.
    const periodKey = market.period ?? market.periodTypeId;
    if (periodKey == null) continue;
    // op-player-props: one bucket per (stat, player, line). `stat` and a
    // player identity are contractual on the entry — fail closed without
    // them (never a stat-less or anonymous bucket).
    let prop: { stat: string; who: string } | undefined;
    if (market.marketType === 'player-prop') {
      const stat = typeof market.stat === 'string' ? market.stat : '';
      const who =
        typeof market.playerKey === 'string' && market.playerKey !== ''
          ? market.playerKey
          : typeof market.playerId === 'number'
            ? `pid${market.playerId}`
            : '';
      if (!stat || !who) continue;
      prop = { stat, who };
    }
    next.set(
      opOddsCacheKey(gameKeyV2, periodKey, market.marketType, isLine ? market.points : undefined, prop),
      {
        provider: lane.provider,
        canonicalGameKey: op.canonicalGameKey,
        canonicalGameKeyV2: wireGameKeyV2,
        sport: op.sport,
        awayTeam: op.awayTeam,
        homeTeam: op.homeTeam,
        lastUpdateMs: op.lastUpdateMs,
        market,
      },
    );
  }
  let changed = false;
  for (const key of lane.keysByGame.get(gameKeyV2) ?? []) {
    if (!next.has(key)) {
      lane.byGameKey.delete(key);
      lane.trendByBucket.delete(key);
      lane.lastAnchorFair.delete(key);
      changed = true;
    }
  }
  lane.keysByGame.set(gameKeyV2, new Set(next.keys()));
  for (const [key, slice] of next) {
    const prev = lane.byGameKey.get(key);
    if (prev && opOddsSliceEqual(prev, slice)) {
      // Keep the PREV reference so the line index below stays
      // reference-stable across byte-equal re-deliveries.
      next.set(key, prev);
      continue;
    }
    // Trend arrows + anchor-withdrawal cache stay MONEYLINE vocabulary —
    // per-line trend state for ~50 lines/game is cost without a surface
    // (line cells render consensus + anchor only). Full-feed B: PERIOD
    // moneylines feed trend too, per their own bucket — the Game Lines
    // period rows are their surface.
    if (slice.market.marketType === 'moneyline') {
      feedOpTrend(lane, key, prev, slice);
      const anchor = selectOpAnchorBook(slice.market);
      if (anchor != null && anchor.awayFairTenths != null && anchor.homeFairTenths != null) {
        lane.lastAnchorFair.set(key, {
          bookmaker: anchor.bookmaker,
          awayFairTenths: anchor.awayFairTenths,
          homeFairTenths: anchor.homeFairTenths,
          drawFairTenths: anchor.drawFairTenths,
        });
      }
    }
    lane.byGameKey.set(key, slice);
    changed = true;
  }
  // Rebuild this game's line index from the (reference-stable) slices.
  // Full-game only — the producer's phase-1 scope.
  const lines: OpLineIndexEntry = { spreads: new Map(), totals: new Map() };
  for (const slice of next.values()) {
    const m = slice.market;
    if (m.periodTypeId !== 1 || typeof m.points !== 'number') continue;
    if (m.marketType === 'spread') lines.spreads.set(m.points, lane.byGameKey.get(opOddsCacheKey(gameKeyV2, 1, 'spread', m.points)) ?? slice);
    else if (m.marketType === 'total') lines.totals.set(m.points, lane.byGameKey.get(opOddsCacheKey(gameKeyV2, 1, 'total', m.points)) ?? slice);
  }
  if (lines.spreads.size > 0 || lines.totals.size > 0) lane.linesByGame.set(gameKeyV2, lines);
  else lane.linesByGame.delete(gameKeyV2);
  return changed;
}

function opBookEqual(a: OpOddsSlice['market']['books'][number], b: OpOddsSlice['market']['books'][number]): boolean {
  return (
    a.bookmaker === b.bookmaker &&
    a.awayDecimal === b.awayDecimal &&
    a.homeDecimal === b.homeDecimal &&
    a.drawDecimal === b.drawDecimal &&
    a.awayAmerican === b.awayAmerican &&
    a.homeAmerican === b.homeAmerican &&
    a.drawAmerican === b.drawAmerican &&
    a.awayFairTenths === b.awayFairTenths &&
    a.homeFairTenths === b.homeFairTenths &&
    a.drawFairTenths === b.drawFairTenths &&
    a.updatedAtMs === b.updatedAtMs
  );
}

// Phase-2 fields participate in the dedup: a frame that ONLY moves the
// consensus (or a book's fair tenths) must still tick, or the fair display
// freezes while raw prices hold still.
function opConsensusEqual(
  a: NonNullable<OpOddsSlice['market']['consensus']> | undefined,
  b: NonNullable<OpOddsSlice['market']['consensus']> | undefined,
): boolean {
  if (a == null || b == null) return a == null && b == null;
  return (
    a.awayFairProbCents === b.awayFairProbCents &&
    a.homeFairProbCents === b.homeFairProbCents &&
    a.drawFairProbCents === b.drawFairProbCents &&
    a.awayFairProbTenths === b.awayFairProbTenths &&
    a.homeFairProbTenths === b.homeFairProbTenths &&
    a.drawFairProbTenths === b.drawFairProbTenths &&
    a.contributingBooks === b.contributingBooks &&
    a.pinnacleAnchored === b.pinnacleAnchored &&
    a.lastUpdateMs === b.lastUpdateMs
  );
}

// Rule 12: a withdrawal change is a MUTATION on the wire (bumps rev) —
// the dedup must see it too, or a withdrawal-only frame ticks nothing
// and the strikethrough never appears/clears.
function opWithdrawnEqual(
  a: OpOddsSlice['market']['withdrawnBooks'],
  b: OpOddsSlice['market']['withdrawnBooks'],
): boolean {
  const al = a ?? [];
  const bl = b ?? [];
  if (al.length !== bl.length) return false;
  return al.every(
    (w, i) =>
      w.bookmaker === bl[i].bookmaker &&
      w.reason === bl[i].reason &&
      w.sinceMs === bl[i].sinceMs,
  );
}

function opOddsSliceEqual(a: OpOddsSlice, b: OpOddsSlice): boolean {
  return (
    a.awayTeam === b.awayTeam &&
    a.homeTeam === b.homeTeam &&
    a.lastUpdateMs === b.lastUpdateMs &&
    opConsensusEqual(a.market.consensus, b.market.consensus) &&
    opWithdrawnEqual(a.market.withdrawnBooks, b.market.withdrawnBooks) &&
    a.market.books.length === b.market.books.length &&
    a.market.books.every((book, i) => opBookEqual(book, b.market.books[i]))
  );
}

/** Delete every bucket belonging to one game (live tombstone — rule 1). */
function deleteOddsGame(lane: OddsLaneNs, gameKeyV2: string): boolean {
  const promoted = oddsGameKeyV2(gameKeyV2);
  let changed = false;
  for (const key of lane.keysByGame.get(promoted) ?? []) {
    lane.byGameKey.delete(key);
    lane.trendByBucket.delete(key);
    lane.lastAnchorFair.delete(key);
    changed = true;
  }
  lane.keysByGame.delete(promoted);
  if (lane.linesByGame.delete(promoted)) changed = true;
  return changed;
}

/** Atomic snapshot replace for ONE lane — the whole replaceOpOdds rebuild
 *  (reference-stable restore, line-index re-point, dropped-game trend/anchor
 *  cleanup), lane-parameterized. Returns whether anything effectively
 *  changed; the caller emits the feed-log + tick. */
function replaceOddsLane(lane: OddsLaneNs, ops: HydraOpOdds[]): boolean {
  const prev = new Map(lane.byGameKey);
  lane.byGameKey.clear();
  lane.keysByGame.clear(); // rebuilt by the writeOddsGame calls below
  lane.linesByGame.clear(); // games absent from the snapshot drop (rule 9)
  for (const op of ops) {
    if (op.markets.length > 0) writeOddsGame(lane, op);
  }
  // Restore prior references for byte-equal slices so `useOpOddsFor`
  // consumers stay reference-stable across a snapshot re-delivery of
  // unchanged state (zustand compares by reference).
  let changed = prev.size !== lane.byGameKey.size;
  for (const [key, slice] of lane.byGameKey) {
    const old = prev.get(key);
    if (old && opOddsSliceEqual(old, slice)) {
      lane.byGameKey.set(key, old);
    } else {
      changed = true;
    }
  }
  // Re-point the line index at the restored references (it was built
  // during writeOpOddsGame, before the restore pass above).
  for (const [gameKey, entry] of lane.linesByGame) {
    for (const [pts, slice] of entry.spreads) {
      const cur = lane.byGameKey.get(opOddsCacheKey(gameKey, 1, 'spread', pts));
      if (cur && cur !== slice) entry.spreads.set(pts, cur);
    }
    for (const [pts, slice] of entry.totals) {
      const cur = lane.byGameKey.get(opOddsCacheKey(gameKey, 1, 'total', pts));
      if (cur && cur !== slice) entry.totals.set(pts, cur);
    }
  }
  // Trend + last-anchor entries for games the snapshot dropped go
  // with them (rule 9).
  for (const key of lane.trendByBucket.keys()) {
    if (!lane.byGameKey.has(key)) lane.trendByBucket.delete(key);
  }
  for (const key of lane.lastAnchorFair.keys()) {
    if (!lane.byGameKey.has(key)) lane.lastAnchorFair.delete(key);
  }
  return changed;
}

// Sub-cent trend/recency state per consensus bucket (same key as
// `_consensusByGameKeyAndPeriod`). Fed on every ingested frame — including
// tenth-only frames whose integer fields match the cached value and
// sharpBookUsed flips with zero numeric change. Read via
// `getConsensusTrendFor` / `useConsensusGlow` for the number-brightness /
// trend-arrow / sharp-anchor treatments. Entries are replaced immutably
// (lib/consensusTrend returns a new object per material change) so Zustand
// selector consumers re-render exactly when a bucket updates.
const _consensusTrendByBucket = new Map<string, ConsensusTrendState>();

// Per-ticker last-receive timestamp (Date.now() at ingest). Drives the
// activity-fraction stale check in requestResnapshotIfStale — see
// ACTIVE_FRACTION_THRESHOLD below for why we look at the share of
// active tickers rather than the global lastMessageMs.
const _lastReceiveMs = new Map<string, number>();
// #847 — source-qualified age evidence, keyed by ticker. A G1-only sidecar:
// never written onto the Hydra market/book objects, never compared against
// the browser epoch. Retained per ticker so identical or reordered replay
// (worker greet, reconnect snapshot) cannot make evidence younger than the
// freshest observation already seen for the same source incarnation.
const _marketSourceAge = new Map<string, SourceAge>();
const _bookSourceAge = new Map<string, SourceAge>();
const _sourceAuthority = new FeedSourceAuthority();

// ── Held books (main 2026-10-03) → the lane's `_pendingBooks` ─────────────
// Main parks a book refused by the retired-feed guard (its row still names
// another feed) in `_heldBooks` and applies it when the row moves to that
// feed. streamrift already had exactly that mechanism — `_pendingBooks`
// (6e614417a, parked per ticker, replayed by ingestMarket when the parking
// feed writes the row, TTL + cap) — so there is ONE park/replay map, and
// every place main touched `_heldBooks` touches `_pendingBooks` instead.
// ── Displayed rows survive the floor→edge handover (2026-10-03) ─────────
// Traders on gbuff saw the Spread/Total line they were on jump to another
// line. DNL-006 retires a floor row the moment an edge's accepted ack lists
// it, and the edge's replacement row only lands in a later message — a
// render in between finds the selected line missing, and the board's
// selection (`seedLineKeys`) reseeds to the default for good. Every line a
// trader steps onto from the floor hits this within a second. A row a view
// holds a BOOK need for (what is on screen) is therefore HANDED OVER, not
// retired: it stays until the edge delivers it (its book — option 2,
// `_servingEdgeBookAuthority` — keeps its price live meanwhile), and a
// backstop retires it if the edge has not delivered it within
// HANDOVER_GRACE_MS. Every other floor row is retired at the ack exactly as
// DNL-006 requires.
export const HANDOVER_GRACE_MS = 10_000;

/** The connected core announcing `sport` — the floor the ingest gate admits
 *  for a row no edge is asking for (`feedShouldIngest`'s floorCore). */
function _floorCoreFor(sport: FeedSport | null): FeedClient | null {
  if (sport == null) return null;
  return _feedClients.find((f) => f.role === 'core' && !f.opOnly && f.client.connected
    && (_edgeLiveAnnounce.get(f.id)?.has(sport) ?? false)) ?? null;
}

/** Keep a row the floor serves instead of deleting it (2026-10-03, gbuff
 *  he2: alternate CFB strikes vanished from the line selector in the order
 *  the trader visited them). An edge that stops listing a row (a book need
 *  ended, a board re-pick moved) resets it out; the floor is eligible for
 *  it again but resends a row only when it changes, so a quiet strike never
 *  came back. The row stays — same identity, still in every line list —
 *  with every proof and book dropped, so it reads UNCONFIRMED (not
 *  tradable, no depth) until the floor's next frame or the edge re-lists
 *  it. Same retirement of evidence as `deleteMarketRecord`, minus the row. */
function demoteToFloor(ticker: string, floorId: string): void {
  _bookSourceFeed.delete(ticker);
  _bookPriceSourceFeed.delete(ticker);
  _withdrawnBookPrices.delete(ticker);
  _marketSourceFeed.set(ticker, floorId);
  _pendingHandover.delete(ticker);
  _pendingBooks.delete(ticker);
  _orderbooks.delete(ticker);
  removeDisplayBook(ticker);
  _lastOrderbookMs.delete(ticker);
  _bookSourceAge.delete(ticker);
  _marketSourceAge.delete(ticker);
  _priceSourceProof.delete(ticker);
  _bookSourceProof.delete(ticker);
  _tickerSourceProof.delete(ticker);
  _rowResyncSince.delete(ticker);
}
const _pendingHandover = new Map<string, { feedId: string; deadline: number }>();
let _handoverTimer: ReturnType<typeof setTimeout> | null = null;
function _armHandoverSweep(): void {
  if (_handoverTimer !== null || _pendingHandover.size === 0) return;
  let next = Infinity;
  for (const h of _pendingHandover.values()) next = Math.min(next, h.deadline);
  _handoverTimer = setTimeout(_sweepHandovers, Math.max(0, next - Date.now()) + 1);
}
function _sweepHandovers(): void {
  _handoverTimer = null;
  const now = Date.now();
  const coreIds = new Set(_feedClients.filter((f) => f.role === 'core').map((f) => f.id));
  let retired = 0;
  for (const [ticker, h] of [..._pendingHandover]) {
    if (h.deadline > now) continue;
    _pendingHandover.delete(ticker);
    const src = _marketSourceFeed.get(ticker);
    // Delivered by the edge (or anyone not the floor) since: handed over.
    if (src == null || !coreIds.has(src)) continue;
    // The edge dropped meanwhile: the floor is the rightful source again.
    if (!_feedClients.find((f) => f.id === h.feedId)?.client.connected) continue;
    // The request can end before delivery (line change / filter narrowing).
    // This old handover must not revoke a fresh, rightful floor replacement.
    const sport = rowFeedSport(_markets.get(ticker));
    if (sport == null || _edgeServing(sport, _markets.get(ticker)?.source)?.id !== h.feedId || !_feedRequests(h.feedId, sport, ticker)) continue;
    // Never delivered: the floor's last frame is not live (its frames are
    // the edge's to deliver now) — kept on screen, unconfirmed.
    demoteToFloor(ticker, src);
    retired++;
  }
  if (retired > 0) {
    clearDerivedRowCaches();
    invalidateGameWireKeys();
    clearDerivedRowRevStamps();
    touch('all');
  }
  _armHandoverSweep();
}

const ORDERBOOK_ROWS_SOURCE = 'orderbook-rows';
let _orderbookRowsKey = '';
/** Rows we hold books for must be delivered by the engine the books come
 *  from — declare them, ticker-scoped, so a line-scoped board's edge carries
 *  them too (a broad union is unaffected: it already carries them). */
function declareOrderbookRows(active: ReadonlySet<string>): void {
  const tickers = [...active].sort();
  const key = tickers.join(' ');
  if (key === _orderbookRowsKey) return;
  _orderbookRowsKey = key;
  declareFeedNeeds(ORDERBOOK_ROWS_SOURCE, tickers.length === 0 ? null : {
    tickers, sports: [], games: [], lineScope: 'any', cadenceMs: null,
  });
}
// Price scalars may have been written by a MARKET or a BOOK. Their proof must
// follow the last actual writer, not whichever channel most recently received data.
const _priceSourceProof = new Map<string, SourceProof>();
// Legacy feeds have no continuity proof, but still own their delivered books.
const _bookSourceFeed = new Map<string, string>();
const _bookPriceSourceFeed = new Map<string, string>();
const _withdrawnBookPrices = new Set<string>();
const _bookSourceProof = new Map<string, SourceProof>();
// Ticker-scalar authority (kalshi-line-availability-book-proof, A1): the proof
// of the last MARKET frame, captured only there — never from a book. It
// governs lastPrice / volume / openInterest presentation and nothing else;
// the executable bid/ask, freshness and line availability follow the price
// proof above, which is the book's whenever a usable book governs the row.
const _tickerSourceProof = new Map<string, SourceProof>();
// Per-row `pendingResync` display retention (B): the monotonic instant of the
// row's FIRST marked book frame under a session/generation. Repeated marked
// frames for the same row never move it; the clearing snapshot deletes it.
const _rowResyncSince = new Map<string, { session: object; sourceEpoch: unknown; sourceGen: unknown; sinceMonoMs: number }>();
export interface BookDisplaySnapshot {
  book: HydraOrderbook | null;
  retained: boolean;
  confirmedAtMs: number | null;
  /** Original monotonic expiry; consumers may use it to expire comparison
   * state after suspension, never to admit the retained book as current. */
  retentionExpiresAtMonoMs?: number;
  /** Identity metadata only; retained numeric book data is never actionable. */
  continuity?: SourceProof;
}
const _confirmedDisplayBooks = new Map<string, { book: HydraOrderbook; proof: SourceProof; sport: string | undefined; confirmedAtMs: number; viewedAtMonoMs: number }>();
let _displayLevelCount = 0;
function removeDisplayBook(ticker: string): void {
  const old = _confirmedDisplayBooks.get(ticker);
  if (old) _displayLevelCount -= old.book.yes.length + old.book.no.length;
  _confirmedDisplayBooks.delete(ticker);
}
let _displayExpiryTimer: ReturnType<typeof setTimeout> | null = null;
let _displayExpiryAt = Infinity;
function readBookDisplay(ticker: string): BookDisplaySnapshot {
  const current = _orderbooks.get(ticker) ?? null;
  const proof = _bookSourceProof.get(ticker);
  if (sourceBookUsable(ticker)) {
    const cached = _confirmedDisplayBooks.get(ticker);
    const now = performance.now();
    if (cached) {
      cached.viewedAtMonoMs = now;
      _confirmedDisplayBooks.delete(ticker);
      _confirmedDisplayBooks.set(ticker, cached);
    }
    if (current && proof && _markets.get(ticker)?.source === 'kalshi' && cached?.proof !== proof) {
      removeDisplayBook(ticker);
      const levels = current.yes.length + current.no.length;
      // At capacity, decline new retention rather than cycling hundreds of
      // active books through copies on every render. Evict only unviewed rows.
      while (_confirmedDisplayBooks.size > 0 && (_confirmedDisplayBooks.size >= 512 || _displayLevelCount + levels > 100_000)) {
        const oldest = _confirmedDisplayBooks.entries().next().value!;
        if (now - oldest[1].viewedAtMonoMs < 30_000) break;
        removeDisplayBook(oldest[0]);
      }
      if (_confirmedDisplayBooks.size < 512 && _displayLevelCount + levels <= 100_000) {
        _confirmedDisplayBooks.set(ticker, {
          book: { ...current, yes: current.yes.map(level => ({...level})), no: current.no.map(level => ({...level})) },
          proof, sport: _markets.get(ticker)?.sport, confirmedAtMs: Date.now(), viewedAtMonoMs: now,
        });
        _displayLevelCount += levels;
      }
    }
    return { book: current, retained: false, confirmedAtMs: null, continuity: proof };
  }
  const prior = _confirmedDisplayBooks.get(ticker);
  const deadline = prior && proof && _markets.get(ticker)?.source === 'kalshi'
    && prior.sport === _markets.get(ticker)?.sport
    && prior.proof.session === proof.session && prior.proof.row.sourceGen === proof.row.sourceGen
    && prior.proof.row.sourceEpoch === proof.row.sourceEpoch
    && _bookFeedServesRow(ticker, prior.proof.feed)
    ? _sourceAuthority.displayResyncDeadline(prior.proof) ?? rowResyncDeadline(ticker, prior.proof) : undefined;
  const remaining = deadline === undefined ? -1 : deadline - performance.now();
  if (!prior || remaining <= 0) return { book: null, retained: false, confirmedAtMs: null };
  if (deadline! < _displayExpiryAt) {
    if (_displayExpiryTimer !== null) clearTimeout(_displayExpiryTimer);
    _displayExpiryAt = deadline!;
    _displayExpiryTimer = setTimeout(() => {
      _displayExpiryTimer = null; _displayExpiryAt = Infinity;
      clearDerivedRowCaches(); _futureBoardsCache.clear(); touch('all');
    }, remaining + 1);
  }
  return { book: prior.book, retained: true, confirmedAtMs: prior.confirmedAtMs,
    retentionExpiresAtMonoMs: deadline, continuity: prior.proof };
}
/** B — per-row `pendingResync` display retention. A book frame marked
 * `pendingResync: true` at the confirmed book's exact session/generation,
 * whose lane control is still `valid`, retains that confirmed depth for 2 s
 * (the same display-only grace `displayResyncDeadline` gives a lane-wide
 * resync; `verdict()` stays unconfirmed for trading throughout). The
 * deadline is anchored to the row's FIRST mark — a re-published marked frame
 * (hydraedge W2) never restarts it. Genuine gaps, quarantine, reconnect and
 * generation changes get no grace: the confirmed proof must still verdict
 * continuous against its control. */
function rowResyncDeadline(ticker: string, confirmed: SourceProof): number | undefined {
  const current = _bookSourceProof.get(ticker);
  const anchor = _rowResyncSince.get(ticker);
  if (current?.row.pendingResync !== true || !anchor || anchor.session !== confirmed.session
    || anchor.sourceEpoch !== confirmed.row.sourceEpoch || anchor.sourceGen !== confirmed.row.sourceGen) return;
  const market = _markets.get(ticker);
  if (_sourceAuthority.verdict({ ...confirmed, row: { ...confirmed.row,
    source: market?.source, sport: market?.sport } }) !== 'continuous') return;
  return anchor.sinceMonoMs + 2_000;
}
function sourcePriceVerdict(ticker: string): SourceVerdict | undefined {
  if (_withdrawnBookPrices.has(ticker)) return 'unconfirmed';
  if (!_pairFeedServesRow(ticker, _marketSourceFeed.get(ticker))) return 'unconfirmed';
  return _sourceAuthority.verdict(_priceSourceProof.get(ticker))
    ?? (_sourceAuthority.required(_marketSourceFeed.get(ticker)) ? 'unconfirmed' : undefined);
}
/** Verdict of the ticker-scalar authority (`_tickerSourceProof`), with the
 * same "required feed, no proof ⇒ unconfirmed" fallback as the price verdict. */
function sourceTickerVerdict(ticker: string): SourceVerdict | undefined {
  if (!_pairFeedServesRow(ticker, _marketSourceFeed.get(ticker))) return 'unconfirmed';
  return _sourceAuthority.verdict(_tickerSourceProof.get(ticker))
    ?? (_sourceAuthority.required(_marketSourceFeed.get(ticker)) ? 'unconfirmed' : undefined);
}
/** A2 — the BOOK proof governs a Kalshi row's bid/ask whenever a usable book
 * exists: g1's quote scalars are book-derived (the edge derives the market
 * frame's yesBid/yesAsk from the same book state), so the executable price is
 * proven by the book, not by whichever channel last wrote `_priceSourceProof`. */
function bookGovernsPrice(ticker: string): boolean {
  return _markets.get(ticker)?.source === 'kalshi' && _bookSourceProof.has(ticker) && sourceBookUsable(ticker);
}
/** The verdict of the proof that governs the row's executable price: the
 * book's (`continuous` by definition of usable) when a usable Kalshi book
 * governs, otherwise the price proof's. Drives freshness, line availability
 * and the submit-time guard — one meaning, everywhere. */
function governingPriceVerdict(ticker: string): SourceVerdict | undefined {
  return bookGovernsPrice(ticker) ? 'continuous' : sourcePriceVerdict(ticker);
}
/** The `priorIsCurrent` predicate `FeedSourceAuthority.supersedes` computes,
 * re-derived through `capture` so the primitive stays untouched: the proof was
 * announced, its session is the feed's current one, and its own lane's control
 * still sits at its epoch/generation (a same-generation maintenance pause keeps
 * it current — authority is withdrawn, the observation is not). */
function proofIsCurrent(proof: SourceProof | undefined): boolean {
  if (!proof?.announced) return false;
  const now = _sourceAuthority.capture(proof.feed, proof.kind, proof.row);
  return now?.session === proof.session && now.announced;
}
function sourceBookUsable(ticker: string): boolean {
  const proof = _bookSourceProof.get(ticker);
  const market = _markets.get(ticker);
  if (proof) return _bookFeedServesRow(ticker, proof.feed)
    && _sourceAuthority.verdict({ ...proof, row: { ...proof.row,
      source: market?.source, sport: market?.sport,
    } }) === 'continuous';
  return _pairFeedServesRow(ticker, _marketSourceFeed.get(ticker)) && !_sourceAuthority.required(_marketSourceFeed.get(ticker));
}
// Last ACCEPTED delivery provenance per ticker — the feed id whose frame the
// gate admitted last (Danielle R1 DNL-002). Ownership is dynamic (an edge's
// sport falls back to the firehose while the edge is down), so a filter
// reset must retire rows by who actually delivered them, not by a feed's
// static claim scope. Absent for rows ingested without a source (tests,
// legacy callers) — those fall back to the claim scope.
const _marketSourceFeed = new Map<string, string>();
// Last ORDERBOOK-frame receive time per ticker, tracked separately from
// _lastReceiveMs (which market + trade frames also bump). The Kalshi
// orderbook-delivery gap freezes the book while market frames keep flowing,
// so a freshness indicator built on _lastReceiveMs would stay green through
// the freeze. This map is the honest "are orderbook frames still arriving"
// signal — read via getOrderbookAgeMs.
const _lastOrderbookMs = new Map<string, number>();
// Which feed delivered the latest orderbook frame per ticker — diagnostics
// only (the Cockpit's live books show it so "empty book" is self-explaining).
const _orderbookSourceFeed = new Map<string, string>();

// Stale-feed heuristic. The global `client.lastMessageMs` lies when even
// one ticker keeps ticking (which happens when hydra/upstream throttles
// almost everything but leaves a single feed alive). We instead measure
// "share of tickers that received any frame in the last N seconds":
//   - active = tickers in _markets with _lastReceiveMs > now - maxAgeMs
//   - if active / total < ACTIVE_FRACTION_THRESHOLD → resnapshot
// Threshold deliberately low (10%) so we only trigger when the feed is
// clearly mostly-dead — many markets are legitimately quiet (settled,
// pre-game) so a higher threshold would false-positive on healthy state.
// Min population guards against the early connect window where the
// fraction is meaningless on a few seed tickers.
const ACTIVE_FRACTION_THRESHOLD = 0.10;
const ACTIVE_FRACTION_MIN_POP = 20;
let _dirty = false;
/**
 * #730 — per-sport attribution of the pending publication.
 *
 * `version` is one global clock: every publication bumps it, and every
 * consumer keyed on it re-derives for sports it is not looking at. Each site
 * that marks the store dirty now also says WHICH sports the mutation can
 * touch; the flush bumps those sports' revisions in the same `set()` as
 * `version`, so a consumer can key its derivation on `revBySport[sport]` and
 * sit out publications about other sports. Rules:
 *
 *   - A mutation that can affect a sport's `getGames` rows names that sport
 *     (a market's bucket keys, which can be several — ITF rides atp AND wta).
 *   - A mutation the store cannot attribute names 'all' (fail closed). Rare
 *     plumbing events (feed status, directory, filter reset/ack, prune) also
 *     name 'all': they are cheap to over-publish and expensive to get wrong.
 *   - Consensus and OP-odds frames publish WITHOUT a sport bump: they are not
 *     inputs to `getGames` (rows read them through their own selectors,
 *     which run on every publication regardless), so bumping the selected
 *     sport for them would only rebuild rows the stabiliser then discards.
 *
 * Revisions are per sport, not per game; that finer split is the #695
 * GameDetail lane and is not started here.
 */
let _dirtySports: Set<Sport> | 'all' | null = null;
const ALL_SPORTS: readonly Sport[] = [...SPORT_TABS.map((t) => t.id), ...STANDALONE_SPORTS];
/**
 * A SYNCHRONOUS per-sport mutation counter (#754), beside the published one.
 *
 * `revBySport` carries the same attribution, but it is published at the
 * coalesced flush — so a selector cache stamped on it serves PRE-INGEST rows to
 * anything that reads in between, and "in between" is not hypothetical: a live
 * price frame followed by a synchronous read of the market it moved (the
 * PositionTracker line context does exactly that) would have kept showing the
 * old price until the next frame landed.
 *
 * This moves with the mutation itself, so a cache stamped on it cannot be
 * stale, and it keeps the attribution that makes a per-sport cache worth
 * having: one sport's frame invalidates one sport's rows.
 *
 * `'all'` gets its own counter rather than fanning out over every sport, so an
 * unattributed mutation is one increment and still invalidates everything.
 */
const _sportMutationSeq = new Map<Sport, number>();
let _allSportMutationSeq = 0;

/** The stamp a cached per-sport derivation was built at. */
function sportMutationStamp(sport: Sport): string {
  return `${_allSportMutationSeq}\u0000${_sportMutationSeq.get(sport) ?? 0}`;
}

function touch(sports: readonly Sport[] | 'all' | 'publish-only'): void {
  _dirty = true;
  if (sports === 'all' || (sports !== 'publish-only' && sports.length === 0)) {
    _dirtySports = 'all';
    _allSportMutationSeq += 1;
  } else if (sports !== 'publish-only') {
    // The mutation counter is bumped for every named sport even when the
    // dirty SET has already collapsed to 'all' below: the set is about which
    // revisions the next flush publishes, and this is about which cached rows
    // are now out of date — a sport that is genuinely dirty must be both.
    for (const sp of sports) _sportMutationSeq.set(sp, (_sportMutationSeq.get(sp) ?? 0) + 1);
    if (_dirtySports !== 'all') {
      _dirtySports ??= new Set();
      for (const sp of sports) _dirtySports.add(sp);
    }
  }
  scheduleTick();
}
function unionSportKeys(a: readonly Sport[] | undefined, b: readonly Sport[] | undefined): readonly Sport[] | 'all' {
  const set = new Set<Sport>([...(a ?? []), ...(b ?? [])]);
  return set.size === 0 ? 'all' : [...set];
}
function sportOfGameKey(gameKey: string): Sport | null {
  const i = gameKey.indexOf('|');
  return i > 0 ? (SPORT_NORMALIZE[gameKey.slice(0, i)] ?? null) : null;
}
let _rafId: number | null = null;
let _flushFn: (() => void) | null = null;

// Most recent message arrival across any source/ticker. Bumped on every
// ingest and surfaced through the store so React components can render
// "feed is stale" UI without polling HydraClient directly. Stays at 0
// until the first message arrives so consumers can distinguish "never
// connected" from "silent for a long time."
let _globalLastReceiveMs = 0;

// Most recent WS frame of ANY kind (pong, consensus, activeSports — not just
// market/orderbook data) on the FIREHOSE connection. Proves the transport is
// alive even when no market is changing; drives the feed dot's
// quiet-vs-stalled distinction (useFeedFreshness). Deliberately a module var,
// not store state: it can bump on every frame and nothing needs to re-render
// on it — readers poll it on their own tick via getLastKeepaliveMs.
let _lastKeepaliveMs = 0;

// Live feed clients — one per upstream (firehose + edge pipes), set by
// `useHydra` on mount, cleared on unmount. Views forward their orderbook-ticker
// needs through `setOrderbookNeeds`, which routes each ticker to the client that
// OWNS its sport (an NFL book request → the edge, an MLB one → the firehose) and
// proxies to that client's SharedWorker / direct-WS path.
/** What the store needs of a transport — the fleet's HydraClient and the
 *  feed gateway's GatewayClient both satisfy it. */
export interface FeedTransport {
  readonly connected: boolean;
  readonly lastMessageMs: number;
  /** `coalescible` ⊆ `tickers`: books only a BOARD draws (liquidity / resting /
   *  best price per cell), which a transport may serve at a cadence instead of
   *  per-delta. Everything else — the open ticket, a ladder, a tracker's own
   *  orders — stays per-delta. The fleet transport ignores it (its cadence is
   *  connection-wide); the gateway names the cadence on the wire. */
  setOrderbookNeeds(tickers: string[], coalescible?: string[]): void;
  requestResnapshot(): void;
}
export interface FeedClient {
  id: string;
  client: FeedTransport;
  /** True when this feed owns `sport` (core/edge: its sports; firehose:
   *  catch-all). Keyed by the FEED key — `cfbalt` and `cfb` are two
   *  assignments on two engines (main-alt-partition-he6-he7 D5.2). */
  accepts: (sport: FeedSport | null) => boolean;
  /** 'core' and 'edge' are both CLAIMANTS with identical arbitration
   *  semantics (preferred owner while announcing; firehose warm fallback) —
   *  every role check here is claimant-vs-firehose, not core-vs-edge. The
   *  role distinction exists for identity/display (Live Feed chip grouping). */
  role: 'firehose' | 'core' | 'edge';
  /** Engine display identity from feedConfig ('OG', env `label@` prefix).
   *  Absent → display surfaces fall back to positional C1/E1… from the id. */
  label?: string;
  /** Consumer WS URL (display/tooltip only — the client owns the connection). */
  url?: string;
  /** True on every OP-CAPABLE feed (the preferred source + the OG standby
   *  under failover). Display only — the binding itself lives in
   *  feedConfig/useHydra, DNL-003. Which one is ACTIVE right now comes via
   *  noteOpActiveFeed. */
  opAuthority?: boolean;
  /** Dedicated OP transport (`op:0`) — market-column UI ignores it. */
  opOnly?: boolean;
  /** A firehose-ROLE feed that nevertheless serves the FULL OddsPapi feed
   *  (lines, team totals, props) and no Unabated channel — the feed gateway,
   *  which wears the firehose role only for the source-evidence gate. Without
   *  this, "active OP feed is the firehose" reads as the OG moneyline-only
   *  standby: the Live Game badge said "GATEWAY fallback · moneyline only"
   *  and the Live Feed chip wore UNBTD on every gateway tab (2026-09-23). */
  opFullFeed?: boolean;
  /** The env-designated PREFERRED OP source (exactly one) — the OddsPapi
   *  column lists it first so the primary never reads as the backup. */
  opPreferred?: boolean;
  /** Position in the OP failover ladder (feedConfig `opRank`): 0 = designated
   *  authority, then each core in config order, OG last. Orders the OddsPapi
   *  column so the chips read as the preference order they actually are. */
  opRank?: number;
}
let _feedClients: FeedClient[] = [];

// The OP-capable feed currently feeding the tab's stager (useHydra's
// failover controller notes every switch here so FeedTape can show WHERE
// OP is coming from — OG vs the hydracore transport — live).
let _opActiveFeedId: string | null = null;

/** Display label of a feed for the tape's source column. Falls back to the
 *  stable id ('OG' for the firehose) when the feed didn't declare a label. */
function _feedLabelOf(feedId: string | null | undefined): string | undefined {
  if (feedId == null) return undefined;
  const fc = _feedClients.find((f) => f.id === feedId);
  if (!fc) return undefined;
  return fc.label ?? (fc.role === 'firehose' ? 'OG' : fc.id);
}

/** Snapshot of one feed's live sport ownership — see getFeedOwnership. */
export interface FeedOwnership {
  id: string;
  role: 'firehose' | 'core' | 'edge';
  connected: boolean;
  /** Engine display identity (see FeedClient.label). */
  label?: string;
  /** Upstream WS URL for tooltips; absent when the feed didn't declare it. */
  url?: string;
  /** True on every OP-capable feed (preferred + standby). */
  opAuthority?: boolean;
  /** True on the one capable feed ACTIVELY feeding the OP stager right now
   *  (follows failover switches). */
  opActive?: boolean;
  /** Dedicated OP transport — market columns skip this chip. */
  opOnly?: boolean;
  /** See FeedClient.opFullFeed — a firehose-role feed that is a full OP source. */
  opFullFeed?: boolean;
  /** The env-designated preferred OP source — listed first in the column. */
  opPreferred?: boolean;
  /** Position in the OP failover ladder (0 = designated authority, then each
   *  core in config order, OG last). Absent on feeds outside the ladder. */
  opRank?: number;
  /** Display-only, set by FeedTape's OP-column derivation: a capable feed
   *  currently on standby renders dimmed (healthy standby only — a downed
   *  chip stays full-opacity red). */
  opStandby?: boolean;
  /** FEED KEYS this feed currently owns (frames + book subscriptions route
   *  here) — a sport, or an ALT pseudo-sport shown RAW (`cfbalt` → the tape
   *  reads `he6 CFBALT`, D5.5). */
  sports: FeedSport[];
  /** Edge only: claimed-but-not-served sports that the FIREHOSE is covering —
   *  data still flows, just from OG. */
  idleClaims: FeedSport[];
  /** Sports NOBODY in the fleet is serving in this view — the claimant isn't
   *  delivering them and the firehose does not announce them either. Rendered
   *  honestly dark instead of being attributed to a feed that has no data for
   *  them. Listed on the claimant that wants the sport, or on the firehose
   *  entry when nothing claims it, so a dark sport is never invisible. */
  darkClaims: FeedSport[];
  /** D10 floor: sports this CORE is covering with the thin firehose because
   *  nobody else serves them — ownerless sports (their only source) or a
   *  dropped primary (degraded, thin-not-blank). Always [] on non-core
   *  feeds. Distinct from `sports` (real ownership) so the chip can render
   *  the honest degraded state. */
  floorSports: FeedSport[];
}

/** The firehose feed — primary for the global resnapshot gate and the catch-all
 *  for tickers whose sport can't be resolved. */
function _firehoseFeed(): FeedClient | null {
  return _feedClients.find((f) => f.role === 'firehose') ?? _feedClients[0] ?? null;
}

/** LIVE served set per edge feed — the ownership source of truth, distinct
 *  from `_activeSportsBySource` (the DISPLAY set, which deliberately holds the
 *  last non-empty announce through a feed's restart cold-cache so the sport
 *  chips don't blank). Ownership must never trust that continuity: an edge
 *  announcing `[]` ("cold cache, serving nothing") or dropping its socket must
 *  own nothing, or OG's frames for its claimed sports get deduped away while
 *  the edge delivers nothing — verified live 2026-07-09 (MLB went dark
 *  tab-wide after an edge restart). Honors empty announces; cleared on
 *  disconnect; repopulated only by a fresh announce. */
const _edgeLiveAnnounce = new Map<string, Set<FeedSport>>();
const _edgeLiveFeeds = new Map<string, Set<string>>();
const _directoryPairAssigned = new Map<string, Set<string>>();

/** The FEED key a stored row is owned under: the raw wire key kept by
 *  `stampFeedSport` on a folded ALT row (`nflalt`), else its sport. Every
 *  ownership / routing / provenance read of a row goes through this, never
 *  `normalizeSport(m.sport)` — the latter is the DISPLAY sport, which on an
 *  ALT row names the mainline edge's key, not the engine that serves it. */
function rowFeedSport(m: Pick<HydraMarket, 'sport' | 'feedSport'> | undefined): FeedSport | null {
  if (!m) return null;
  return normalizeFeedSport(m.feedSport ?? m.sport ?? '');
}

// ── Two-tier consumption (Dan, 2026-09-02) ──────────────────────────────
// What each connection is currently ASKING its engine for on the sports
// axis, read from ITS OWN installed filterAck (`effective.sports`; absent
// axis = every sport; no ack yet / legacy = every sport). The core floor is
// admitted for a sport whenever the edge that serves it is not asking for
// it — ownership by REQUEST, not only by announce — so the sports off
// screen ride the floor's thin 1 s tier while the edge carries the page,
// and the two never feed one sport at the same time (Dan, 2026-09-02: the
// warm feed must be invisible to the dash while the edge feeds — no
// latency jitter between tiers). The ack is the truth because the worker
// narrows only after its flicker hold and the engine's ack: until that ack
// lands the edge is still delivering, and the floor stays out.
function _feedRequests(feedId: string, sport: FeedSport, ticker?: string | null): boolean {
  const st = _feedFilterState.get(feedId);
  const eff = st?.effective;
  if (!eff) return true;
  // TICKERS-SCOPED (feed-filters-per-view, 2026-09-12): a connection whose
  // installed scope is an exact allow-list asks for THOSE ROWS, not for any
  // sport. It requests this frame only if the row is listed; every other row
  // of the sport stays the floor's. Without this branch an absent `sports`
  // axis read as "asks for everything it can own" — true for a broad legacy
  // scope, false for an allow-list — and a Position Tracker alone on its
  // browser would have shut the floor out of every edge-served sport and
  // retired every floor row of them at the ack (see noteFeedFilterAck).
  if (eff.tickers && eff.tickers.length > 0) {
    if (ticker == null) return false;
    st!.tickerSet ??= new Set(eff.tickers);
    return st!.tickerSet.has(ticker);
  }
  if (!eff.sports) return true;
  return eff.sports.some((code) => normalizeFeedSport(code) === sport);
}
// Ownership-transition listener (useHydra): re-negotiate the floor cadence
// when an edge appears/vanishes/changes its announce.
let _ownershipListener: (() => void) | null = null;

/** The healthy claimant (core or edge) that currently OWNS `sport`, or null.
 *  Dynamic ownership (claimant preferred / firehose fallback): a claimant owns
 *  a sport only while it (a) claims it (its static env scope), (b) has a live
 *  socket, and (c) lists it in its latest `activeSports` announcement ON THIS
 *  CONNECTION — the engine's own egress truth (a sport Blocked on the engine
 *  disappears from its announce; a cold-cache empty announce or a reconnect
 *  clears it). The moment any of those stop holding, ownership falls back to
 *  the firehose (OG hydra), which still receives every sport on its broad
 *  subscribe — a warm standby, not a resubscribe. For sports OG never carried
 *  (core shallow-tier: cricket/golf lane), that fallback naturally finds no
 *  frames — the boundary-rule-6 "single-source, dark on core outage" carve-out
 *  without special-casing. */
/** Core's DIRECTION: sport → feed ids core has assigned as primary.
 *
 *  Legacy sport assignments are a tie-break, not authority (Dan, 2026-08-15). They decide which of two
 *  sources serves a sport; it can never stop one that is genuinely serving.
 *  An assignment able to subtract ownership would let a development core
 *  darken a production edge, so every use of this map is a PREFERENCE pass
 *  layered over the unchanged one — never a filter on it.
 *
 *  Empty ⇒ core offers no direction ⇒ local config decides, which is level two
 *  of the rule and also exactly what happens when every core is unreachable
 *  and the cache is cold. */
const _directoryAssigned = new Map<FeedSport, Set<string>>();

/** Directory follow (`?directoryFollow`, default on; useHydra sets it at
 *  fleet construction). Off = an assigned pair inspects only its named
 *  primary and nothing asks for a directory re-fetch. */
let _directoryFollow = true;
export type DirectoryTriggerReason = 'announce' | 'connection';
// Directory re-fetch triggers (useHydra coalesces them into fetches).
let _directoryTriggerListener: ((reason: DirectoryTriggerReason) => void) | null = null;
// Pairs whose NAMED primary stopped serving them (stopped announcing, or its
// socket dropped) → that primary, held until the directory names someone
// else or the primary claims the pair again. hc1 flips its directory only
// when a transfer COMPLETEs, after the donor has gone silent, so a trigger's
// fetch can still read the donor; useHydra re-checks on a short ladder while
// a pair waits here. Each new abandonment bumps the epoch, which restarts
// that ladder.
const _directoryAwaiting = new Map<string, string>();
let _directoryAwaitEpoch = 0;

/** A claimant able to own (sport, venue) right now: connected and announcing
 *  the pair on this connection. `assumeConnected` reads one feed as still
 *  connected — the owner a drop is taking away (its client reports the drop
 *  before the store hears of it). */
function _pairEligible(fc: FeedClient, sport: FeedSport, venue: FeedVenue, assumeConnected?: string): boolean {
  return fc.role !== 'firehose' && fc.accepts(sport) &&
    (fc.client.connected || fc.id === assumeConnected) && _announcesPair(fc.id, sport, venue);
}

function _edgeServing(sport: FeedSport | null, venue?: FeedVenue | null, assumeConnected?: string): FeedClient | null {
  if (sport == null) return null;
  if (venue != null && _pairManaged(sport, venue)) {
    // Avoid building a fleet array on every quote/book read. The named
    // primary wins while it announces the pair; otherwise the sole announcing
    // claimant (a feedctl transfer's recipient while the tab still holds the
    // donor's assignment); two or more stop at ambiguity.
    return selectPairOwner(_feedClients, _directoryPairAssigned.get(feedPairKey(sport, venue)),
      fc => _pairEligible(fc, sport, venue, assumeConnected), !_directoryFollow);
  }
  // PASS 1 — core's preference, if it named anyone who is actually serving.
  // Eligibility is unchanged: connected AND announcing. Core says who is
  // preferred; the announce says who is really delivering.
  const preferred = _directoryAssigned.get(sport);
  if (preferred != null && preferred.size > 0) {
    for (const fc of _feedClients) {
      if (fc.role === 'firehose' || !preferred.has(fc.id)) continue;
      if (!fc.accepts(sport) || (!fc.client.connected && fc.id !== assumeConnected)) continue;
      if (_edgeLiveAnnounce.get(fc.id)?.has(sport)) return fc;
    }
  }
  // PASS 2 — unchanged. Reached when core named nobody, or named someone who
  // is not serving. This is the anti-subtraction property: an edge genuinely
  // serving a sport core did not assign still wins here.
  for (const fc of _feedClients) {
    if (fc.role === 'firehose' || !fc.accepts(sport)) continue;
    if (!fc.client.connected && fc.id !== assumeConnected) continue;
    if (_edgeLiveAnnounce.get(fc.id)?.has(sport)) return fc;
  }
  return null;
}

/** Venue of a market/ticker for ownership purposes: `FeedVenue`, imported
 *  from feedPairOwnership ('kalshi' | 'polymarket' | 'polymarket_us'). */
function _pairManaged(sport: FeedSport, venue: FeedVenue): boolean {
  return _directoryPairAssigned.has(feedPairKey(sport, venue)) ||
    _feedClients.some(f => f.role !== 'firehose' && f.accepts(sport) && _edgeLiveFeeds.has(f.id));
}

function _announcesPair(feedId: string, sport: FeedSport, venue: FeedVenue): boolean {
  if (!_edgeLiveAnnounce.get(feedId)?.has(sport)) return false;
  const pairs = _edgeLiveFeeds.get(feedId);
  return pairs == null || pairs.has(feedPairKey(sport, venue));
}

/** The pairs `feedId`'s live announce claims (a legacy announce without
 *  `activeFeeds` claims every venue of its sports). */
function _claimedPairs(feedId: string): Set<string> {
  const out = new Set<string>();
  for (const sport of _edgeLiveAnnounce.get(feedId) ?? []) {
    for (const venue of FEED_VENUES) if (_announcesPair(feedId, sport, venue)) out.add(feedPairKey(sport, venue));
  }
  return out;
}

function _splitPairKey(key: string): [FeedSport, FeedVenue] {
  const at = key.lastIndexOf('|');
  return [key.slice(0, at) as FeedSport, key.slice(at + 1) as FeedVenue];
}

/** Each pair's owner right now — read BEFORE an announce or a drop changes
 *  the claims, so `_resnapshotTakers` can tell a takeover from an owner that
 *  stayed put. A drop passes the dropping feed as `assumeConnected`. */
function _pairOwners(claims: Iterable<string>, assumeConnected?: string): Map<string, string | null> {
  const out = new Map<string, string | null>();
  for (const key of claims) {
    const [sport, venue] = _splitPairKey(key);
    out.set(key, _edgeServing(sport, venue, assumeConnected)?.id ?? null);
  }
  return out;
}

/** Snapshot the feed that just TOOK OVER a pair the claimant stopped serving
 *  (in `ownersBefore`, not in `stillClaimed`), once per feed, skipping feeds
 *  in `asked`. Its frames were refused while it was not the owner, so without
 *  this a quiet row keeps the previous owner's last reading until it ticks. An
 *  owner that did not change is never asked — a standby dropping a pair the
 *  primary holds costs nothing. */
function _resnapshotTakers(ownersBefore: ReadonlyMap<string, string | null>,
  stillClaimed: ReadonlySet<string>, asked: Set<string>): void {
  for (const [key, before] of ownersBefore) {
    if (stillClaimed.has(key)) continue;
    const [sport, venue] = _splitPairKey(key);
    const owner = _edgeServing(sport, venue);
    if (owner == null || owner.id === before || asked.has(owner.id)) continue;
    asked.add(owner.id);
    owner.client.requestResnapshot();
  }
}

/** The directory names `feedId` primary for some pair or sport. */
function _namedPrimary(feedId: string): boolean {
  for (const ids of _directoryPairAssigned.values()) if (ids.has(feedId)) return true;
  for (const ids of _directoryAssigned.values()) if (ids.has(feedId)) return true;
  return false;
}

/** No other claimant is eligible for at least one of `claims`. */
function _soleClaimant(feedId: string, claims: ReadonlySet<string>): boolean {
  for (const key of claims) {
    const [sport, venue] = _splitPairKey(key);
    if (!_feedClients.some(f => f.id !== feedId && _pairEligible(f, sport, venue))) return true;
  }
  return false;
}

/** `feedId` stopped serving `lost`: each pair the directory names it primary
 *  for waits for the directory to catch up (`_directoryAwaiting`). */
function _awaitDirectory(feedId: string, lost: Iterable<string>): void {
  let added = false;
  for (const key of lost) {
    if (!_directoryPairAssigned.get(key)?.has(feedId) || _directoryAwaiting.get(key) === feedId) continue;
    _directoryAwaiting.set(key, feedId);
    added = true;
  }
  if (added) _directoryAwaitEpoch += 1;
}

/** The sport-level announce can't express partial VENUE coverage — an edge
 *  serving MLB poly with Kalshi toggled off still announces "MLB", and
 *  trusting that blindly deduped OG's Kalshi MLB frames against a venue the
 *  edge wasn't sending (Kalshi went blank in g1, 2026-07-10). So within an
 *  announced sport, ownership is arbitrated per VENUE by observed delivery:
 *  the edge owns (sport, venue) only while it has actually delivered a market
 *  frame for that pair within the TTL. Delivery is recorded by useHydra's
 *  per-feed onMarket (noteFeedDelivery) BEFORE the ingest gate runs, so the
 *  edge's own frames establish ownership and their absence expires it. */
const EDGE_VENUE_TTL_MS = 30_000;
const _edgeVenueDeliveryMs = new Map<string, number>(); // `${feedId}|${sport}|${venue}`
const _venueOwnedByEdge = new Map<string, boolean>();   // `${sport}|${venue}` — transition detector

function _edgeDeliveringVenue(feedId: string, sport: FeedSport, venue: FeedVenue): boolean {
  const last = _edgeVenueDeliveryMs.get(`${feedId}|${sport}|${venue}`);
  if (last != null && Date.now() - last < EDGE_VENUE_TTL_MS) return true;
  // #1053 narrowed edge streams to the rows a view draws, so a quiet pregame
  // Kalshi row can go silent for minutes: frame recency alone sent Kalshi
  // books to the firehose (which has no MLB/CFB) whenever any routing pass
  // ran in a quiet spell — dan.node1 2026-10-02: Poly books in 2–5 s, Kalshi
  // in 7–18 s or never. The engine's own lane state is the venue's liveness;
  // an edge that stopped serving a venue reports no serving lane for it, so
  // the 2026-07-10 case (announce says MLB, Kalshi toggled off) still falls
  // back. Feeds without lanes keep the frame clock.
  return _laneServesVenue(feedId, sport, venue);
}

/** Lane states in which the engine is the venue's authority for the sport
 *  (serving, or recovering in place). `disconnected` / `unknown` are not. */
const SERVING_LANE_STATES: ReadonlySet<string> = new Set(['valid', 'resyncing', 'gap']);

/** The engine's own word on a (sport, venue): a lane of that venue for that
 *  sport in a serving state in the CURRENT session. Lanes are named per
 *  engine sport key (`kalshi-ticker:MLB`, `kalshi-orderbook:CFBALT`,
 *  `poly-book:MLB`) and compared on the folded FEED key, so an ALT lane
 *  stays distinct from its base sport. */
function _laneServesVenue(feedId: string, sport: FeedSport, venue: FeedVenue): boolean {
  for (const c of _sourceAuthority.lanes(feedId)) {
    if (!SERVING_LANE_STATES.has(c.state)) continue;
    const at = c.lane.indexOf(':');
    if (at < 0) continue;
    const kind = c.lane.slice(0, at);
    const laneVenue: FeedVenue | null = kind.startsWith('kalshi-') ? 'kalshi' : kind.startsWith('poly-') ? 'polymarket' : null;
    if (laneVenue !== venue) continue;
    if (normalizeFeedSport(c.lane.slice(at + 1)) === sport) return true;
  }
  return false;
}

/** The sport's serving edge, with its ORDERBOOK lane for the row's sport and
 *  venue serving in the current session (`kalshi-orderbook:<S>` /
 *  `poly-book:<S>`): its book is authoritative whatever feed the row
 *  currently arrives from. A retired / dropped / non-serving edge fails this,
 *  so the retired-feed guard still refuses its books. */
function _servingEdgeBookAuthority(feedId: string, market: HydraMarket): boolean {
  const sport = rowFeedSport(market);
  if (sport == null || _edgeServing(sport, market.source)?.id !== feedId) return false;
  const kind = market.source === 'kalshi' ? 'kalshi-orderbook' : market.source === 'polymarket' ? 'poly-book' : null;
  if (kind == null) return false;
  for (const c of _sourceAuthority.lanes(feedId)) {
    if (!SERVING_LANE_STATES.has(c.state)) continue;
    const at = c.lane.indexOf(':');
    if (at < 0 || c.lane.slice(0, at) !== kind) continue;
    if (normalizeFeedSport(c.lane.slice(at + 1)) === sport) return true;
  }
  return false;
}

/** The book from `feed` may stand for this row: the row's own feed, or the
 *  sport's serving edge with its book lane serving (above). Judged at READ
 *  time, so an edge that stops serving takes its unmoved rows' books with it. */
function _bookFeedServesRow(ticker: string, feed: string): boolean {
  if (!_pairFeedServesRow(ticker, feed, true)) return false;
  if (feed === _marketSourceFeed.get(ticker)) return true;
  const market = _markets.get(ticker);
  return market != null && _servingEdgeBookAuthority(feed, market);
}

/** Pair assignment can withdraw a still-connected source. Keep its last display
 * reading, but neither a late book nor a current lane makes it actionable. */
function _pairFeedServesRow(ticker: string, feed: string | undefined, book = false): boolean {
  const m = _markets.get(ticker);
  const sport = rowFeedSport(m);
  if (!m || !sport || !_pairManaged(sport, m.source)) return true;
  if (feed == null) return false;
  const owner = _edgeServing(sport, m.source);
  // Exact book subscriptions and the market-row filter are independent.
  // The current primary's proven book can arrive before its row handover.
  if (book && owner?.id === feed) return true;
  if (owner && _feedRequests(owner.id, sport, ticker)) return owner.id === feed;
  const floor = _feedClients.find(f => f.role === 'core' && !f.opOnly && f.client.connected &&
    _announcesPair(f.id, sport, m.source));
  return floor?.id === feed;
}

/** A change key for `feedId`'s serving lanes: book routing re-runs when it moves. */
function _servingLanesKey(feedId: string): string {
  return _sourceAuthority.lanes(feedId).filter((c) => SERVING_LANE_STATES.has(c.state)).map((c) => c.lane).sort().join(',');
}

// Per-view orderbook ticker needs. Keyed by a view-supplied id
// (e.g. `'value-dashboard'`, `'streamrift'`) so each view can declare
// its needs independently — the store unions across views before
// handing the set off to the client. Keeping this outside Zustand
// state avoids triggering re-renders on need changes.
const _viewOrderbookNeeds = new Map<string, Set<string>>();
/** Sorted-joined ticker list per view, so a view re-declaring the SAME set
 *  (sections re-run their effect on every row-identity change — 30 Hz on a
 *  live game) costs nothing: no re-route, no feedNeeds re-declare, no depth
 *  prune. Any change in membership still goes through in full. */
const _viewOrderbookNeedKeys = new Map<string, string>();

/** #1124: how many books each mounted view asks for, and the routed union as
 *  the total (shared tickers count once). With `groupOf`, views sharing a name
 *  are one entry counted by their union, so Game Detail's sections, which ask
 *  for many of the same books, don't double. O(views) ungrouped; grouped, only
 *  a group with several members walks its tickers (bounded by books needed). */
export function getOrderbookNeedsByView(
  groupOf?: (viewId: string) => string,
): { total: number; views: Array<{ viewId: string; books: number }> } {
  const views: Array<{ viewId: string; books: number }> = [];
  if (!groupOf) {
    for (const [viewId, needs] of _viewOrderbookNeeds) views.push({ viewId, books: needs.size });
    return { total: _activeOrderbookTickers.size, views };
  }
  const groups = new Map<string, Set<string>[]>();
  for (const [viewId, needs] of _viewOrderbookNeeds) {
    const name = groupOf(viewId);
    const members = groups.get(name);
    if (members) members.push(needs);
    else groups.set(name, [needs]);
  }
  for (const [name, members] of groups) {
    if (members.length === 1) { views.push({ viewId: name, books: members[0].size }); continue; }
    const union = new Set<string>();
    for (const m of members) for (const t of m) union.add(t);
    views.push({ viewId: name, books: union.size });
  }
  return { total: _activeOrderbookTickers.size, views };
}

/** #1124: the books one view group asked for, and the current source age of
 *  each that carries one. Read when Copy report is clicked; holds no state. */
export function getBookSourceAgesForGroup(
  groupOf: (viewId: string) => string,
  group: string,
): { books: number; agesMs: number[] } {
  const tickers = new Set<string>();
  for (const [viewId, needs] of _viewOrderbookNeeds) {
    if (groupOf(viewId) === group) for (const t of needs) tickers.add(t);
  }
  const agesMs: number[] = [];
  for (const t of tickers) {
    const age = sourceAgeNow(_bookSourceAge.get(t));
    if (age != null) agesMs.push(age);
  }
  return { books: tickers.size, agesMs };
}

/** #1124: markets delivered to this tab per sport bucket, largest first.
 *  O(sports). A market can sit in two buckets (series rows, ambiguous tennis),
 *  so the sizes can add up to more than the total. */
export function getMarketCountsBySport(): Array<{ sport: Sport; markets: number }> {
  const out: Array<{ sport: Sport; markets: number }> = [];
  for (const [sport, bucket] of _marketsBySport) if (bucket.size > 0) out.push({ sport, markets: bucket.size });
  return out.sort((a, b) => b.markets - a.markets);
}

function _unionOrderbookNeeds(): Set<string> {
  const union = new Set<string>();
  for (const needs of _viewOrderbookNeeds.values()) {
    for (const t of needs) union.add(t);
  }
  return union;
}

/** Views whose book needs are DRAWN AS CELLS (liquidity, resting, best price)
 *  — a cadence is enough there. Everything else (the order ticket, a ladder,
 *  the sniper, a position edit) reads the full per-delta book. Measured
 *  2026-09-23: an MLB moneyline board's 48 headline books were ~486 book
 *  frames/s per tab at per-delta. Fail per-delta: an id not listed here
 *  keeps the full rate. */
const BOARD_BOOK_VIEWS: ReadonlySet<string> = new Set(['value-dashboard', 'spreads-totals', 'golf-match-board', 'position-tracker', 'position-tracker-poly']);

/** A view that is not a board but draws SOME of its books as cells names
 *  those under a second id, `<viewId>|cells` (2026-10-09: Game Detail's
 *  spread/total rungs — every rung was a per-delta ladder need, 99–133 books
 *  and 3.8–5.9k frames/s on one game tab). The per-delta id keeps the ticket,
 *  the selected line and the open ladder; the `|cells` id carries the rest.
 *  The ladder-wins rule below still applies across the two ids.
 *  (`CELL_BOOK_VIEW_SUFFIX` lives in ./bookViewIds so mocked stores see it.) */
export function isCellBookView(viewId: string): boolean {
  return BOARD_BOOK_VIEWS.has(viewId) || viewId.endsWith(CELL_BOOK_VIEW_SUFFIX);
}

/** Tickers EVERY needing view draws as a cell — none of them is a ladder. */
function _coalescibleOrderbookNeeds(): Set<string> {
  const ladder = new Set<string>();
  const board = new Set<string>();
  for (const [viewId, needs] of _viewOrderbookNeeds) {
    const target = isCellBookView(viewId) ? board : ladder;
    for (const t of needs) target.add(t);
  }
  for (const t of ladder) board.delete(t);
  return board;
}

// ── Render-cadence throttle + terminal-market prune ──────────────
//
// Two coupled guards against the "bogs down 3×/day, stays slow all day, only a
// reload fixes it" failure (Jun 27 diagnosis):
//
//  1. THROTTLE. Under a tripled feed the inbound frame rate flushes on nearly
//     every animation frame (~60Hz), and each flush bumps `version`, which
//     re-runs the O(_markets) selectors across every sport — a re-render storm.
//     We cap the version-bump cadence to ~30Hz. The DATA layer
//     (_markets/_orderbooks) is still written synchronously on EVERY frame in
//     ingest*, so order placement and queue math always read live values; only
//     the React refresh is bounded, and 33ms is imperceptible for numbers.
//
//  2. PRUNE. `_markets` had no eviction path, so settled games' last frames
//     accumulated for the page lifetime — `_markets.size` (and every selector's
//     O(N) cost) ratcheted up all day until a hard reload. We evict markets
//     hydra has marked TERMINAL once they've gone quiet for 30 min — by then
//     hydra has long stopped sending them (it prunes ~5 min post-terminal), so
//     they're dead rows only g1 still holds. Quiet-but-open markets (pre-game,
//     low-volume) are NEVER evicted; only known-terminal + long-idle ones.
const MIN_FLUSH_INTERVAL_MS = 33; // ~30Hz cap on version bumps
const PRUNE_INTERVAL_MS = 60_000;
const PRUNE_TERMINAL_STALE_MS = 30 * 60_000;
let _lastFlushMs = 0;
let _lastPruneMs = 0;
let _flushTimer: ReturnType<typeof setTimeout> | null = null;

/** THE ONE `_markets.delete` path — the terminal prune and the feed-filter
 *  reset both go through here so every index stays in lockstep: without
 *  de-indexing, the _marketsBySport buckets (and _marketSportKeys) would
 *  retain a dangling reference to the evicted market and the list/game
 *  selectors would scan a dead row. Mirrors the removal half of
 *  indexMarketBySport. The conditionId/tokenId identity indexes are
 *  deliberately KEPT (as the prune always did): identity outlives the row,
 *  and the account plane resolves position cards through them. */
/** `keepBookOf`: a book (applied or held) that feed delivered survives the
 *  row — the floor→edge handoff retires the floor's row, and the taking
 *  edge's quiet book is not resent until it changes. */
function deleteMarketRecord(ticker: string, keepBookOf?: string): void {
  if (_golfMatches.delete(ticker)) _golfMatchList = null;
  _futureSelectionVersions.delete(ticker);
  if (_markets.delete(ticker)) {
    invalidateGameWireKeys();
    noteMarketIdentityRemoved(ticker);
  }
  _marketSourceFeed.delete(ticker);
  _kalshiExchangeIndexDiagnostics.delete(ticker);
  _pendingHandover.delete(ticker);
  if (keepBookOf == null || _pendingBooks.get(ticker)?.feed !== keepBookOf) _pendingBooks.delete(ticker);
  if (keepBookOf == null || _bookSourceFeed.get(ticker) !== keepBookOf) {
    _bookSourceFeed.delete(ticker);
    _orderbooks.delete(ticker);
    _orderbookSourceFeed.delete(ticker);
    removeDisplayBook(ticker);
    _lastOrderbookMs.delete(ticker);
    _bookSourceAge.delete(ticker);
    _bookSourceProof.delete(ticker);
    _rowResyncSince.delete(ticker);
  }
  _lastReceiveMs.delete(ticker);
  _marketSourceAge.delete(ticker);
  _priceSourceProof.delete(ticker);
  _bookPriceSourceFeed.delete(ticker);
  _withdrawnBookPrices.delete(ticker);
  _tickerSourceProof.delete(ticker);
  const indexed = _marketSportKeys.get(ticker);
  if (indexed) {
    for (const s of indexed) _marketsBySport.get(s)?.delete(ticker);
    deindexFuture(ticker, indexed);
    _marketSportKeys.delete(ticker);
  }
}

function isPruneEligible(ticker: string, m: HydraMarket, now: number): boolean {
  // Only known-terminal markets are eligible. A missing/empty status or any
  // non-terminal status keeps the row — a quiet open market must not be
  // dropped just for being quiet.
  if (!m.status || !TERMINAL_STATUSES.has(m.status)) return false;
  const lastMs = _lastReceiveMs.get(ticker) ?? 0;
  return now - lastMs >= PRUNE_TERMINAL_STALE_MS;
}

// ── Terminal-market prune scheduling (speed-demon Deploy C) ─────────────
// The scan is O(|_markets|) (~200k rows on a full slate). It used to run
// inline in the rAF flush, on whichever frame crossed the 60 s mark — the
// frame a keypress or a click-to-place could be sharing. It now runs on idle
// time: requestIdleCallback where the browser has it (setTimeout(0) fallback
// off the frame otherwise), and the pass yields to the idle deadline every
// PRUNE_CHUNK entries, resuming from the live Map iterator (deleting during
// Map iteration is well-defined; a filter reset's `_markets.clear()` simply
// ends the pass). The ~60 s cadence is stamped when the pass is SCHEDULED, so
// a long scan cannot bunch two passes together.
const PRUNE_IDLE_TIMEOUT_MS = 2_000;
const PRUNE_CHUNK = 256;
const PRUNE_FALLBACK_SLICE_MS = 8;
interface IdleDeadlineLike {
  didTimeout: boolean;
  timeRemaining: () => number;
}
let _pruneScheduled = false;
let _pruneCursor: Iterator<[string, HydraMarket]> | null = null;

function scheduleIdle(cb: (deadline: IdleDeadlineLike) => void): void {
  const ric = (globalThis as { requestIdleCallback?: (cb: (d: IdleDeadlineLike) => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof ric === 'function') {
    ric(cb, { timeout: PRUNE_IDLE_TIMEOUT_MS });
    return;
  }
  // No idle API (Safari, jsdom): a macrotask of its own, bounded to a short
  // wall-clock slice per turn so it still cannot pin the main thread.
  setTimeout(() => {
    const start = Date.now();
    cb({ didTimeout: false, timeRemaining: () => Math.max(0, PRUNE_FALLBACK_SLICE_MS - (Date.now() - start)) });
  }, 0);
}

function pruneStaleMarketsChunk(deadline: IdleDeadlineLike): void {
  const now = Date.now();
  if (!_pruneCursor) _pruneCursor = _markets.entries();
  let visited = 0;
  // Main's per-sport revision caches (#730) only recompute a sport whose
  // revision moved. Main pruned synchronously inside the flush and marked
  // every sport dirty; this prune runs off-frame, so it must publish what it
  // removed itself — otherwise a pruned terminal row stays on the board until
  // that sport next ticks, which for a finished slate is never.
  const prunedSports = new Set<Sport>();
  let prunedUnkeyed = false;
  const publish = () => {
    if (prunedUnkeyed) touch('all');
    else if (prunedSports.size > 0) touch([...prunedSports]);
  };
  for (;;) {
    const step = _pruneCursor.next();
    if (step.done) {
      _pruneCursor = null;
      _pruneScheduled = false;
      publish();
      return;
    }
    const [ticker, m] = step.value;
    if (isPruneEligible(ticker, m, now)) {
      const keys = _marketSportKeys.get(ticker);
      if (keys && keys.length > 0) for (const k of keys) prunedSports.add(k);
      else prunedUnkeyed = true;
      deleteMarketRecord(ticker);
    }
    // A callback the browser fired on `timeout` (busy tab) gets no idle
    // budget — finish the pass rather than crawl it one chunk per 2 s.
    if (++visited % PRUNE_CHUNK === 0 && !deadline.didTimeout && deadline.timeRemaining() <= 0) {
      publish();
      scheduleIdle(pruneStaleMarketsChunk);
      return;
    }
  }
}

function schedulePruneStaleMarkets(): void {
  if (_pruneScheduled) return;
  _pruneScheduled = true;
  scheduleIdle(pruneStaleMarketsChunk);
}

// ── Game-grain tick channel ─────────────────────────────────────────────
// The global `version` counter re-renders every subscriber on ANY market
// ticking anywhere (~30Hz on a busy slate). The single-game live view
// (/live/:gameKey) instead subscribes here at game grain via
// useSyncExternalStore: ingest marks the frame's game key(s) dirty, and the
// throttled flush notifies only the games that actually ticked — AFTER the
// version bump, so a listener's imperative getGames/getGameEvents derivation
// runs against a fresh `_getGamesCache` stamp. Only keys with a live listener
// are tracked (markGameDirty is a no-op for the thousands of games nobody
// watches), so the hot path stays O(1) per frame.
// A game is addressed by BOTH its legacy canonicalGameKey and its
// doubleheader-safe v2 key — ingest marks whichever it has; subscribe with
// the key(s) your surface resolves rows by (per-day GameRow.key = v2 key).
const _gameTickListeners = new Map<string, Set<() => void>>();
const _gameTickSeq = new Map<string, number>();
const _gameTickMs = new Map<string, number>();
const _gameTickBootMs = Date.now();
let _dirtyGameKeys: Set<string> | null = null;

const GAME_TICK_EXCLUDED_CLASSES: ReadonlySet<string> = new Set(DEFAULT_EXCLUDED_CLASSES);

/** Classes the game-grain boards never render: props (`marketType: 'prop'`)
 *  and every class the consumer feed filter excludes by default. Exported for
 *  the gate's test. */
export function isGameTickExcludedMarket(market: { marketType?: string; class?: string }): boolean {
  return market.marketType === 'prop' || (market.class != null && GAME_TICK_EXCLUDED_CLASSES.has(market.class));
}

function markGameDirty(...keys: Array<string | null | undefined>): void {
  for (const k of keys) {
    if (k && _gameTickListeners.has(k)) (_dirtyGameKeys ??= new Set()).add(k);
  }
}

/** Subscribe to flush-coalesced ticks for one game key. Returns unsubscribe. */
export function subscribeGameTick(gameKey: string, cb: () => void): () => void {
  let set = _gameTickListeners.get(gameKey);
  if (!set) {
    set = new Set();
    _gameTickListeners.set(gameKey, set);
  }
  set.add(cb);
  return () => {
    const s = _gameTickListeners.get(gameKey);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) _gameTickListeners.delete(gameKey);
  };
}

/** Monotonic per-game tick counter (0 = never ticked while listened). The
 *  useSyncExternalStore snapshot for subscribeGameTick. */
export function getGameTickSeq(gameKey: string): number {
  return _gameTickSeq.get(gameKey) ?? 0;
}

/** Wall-clock of the latest flush for this game. Before the first observed
 * tick, use the module boot time so a cache-warm initial paint starts fresh
 * rather than presenting an artificial epoch-old warning. */
export function getGameTickMs(gameKey: string): number {
  return _gameTickMs.get(gameKey) ?? _gameTickBootMs;
}

function notifyGameTicks(): void {
  // Feed→screen probe flush mark: every watched frame ingested since the
  // last flush is now "flushed"; the live view's derive + paint marks close
  // the batch. Runs before the early return so a batch never stalls on a
  // flush with no dirty listener (cheap: pending.size === 0 short-circuit).
  probeFeedFlush();
  if (!_dirtyGameKeys) return;
  const dirty = _dirtyGameKeys;
  _dirtyGameKeys = null;
  const now = Date.now();
  for (const k of dirty) {
    _gameTickSeq.set(k, (_gameTickSeq.get(k) ?? 0) + 1);
    _gameTickMs.set(k, now);
    const listeners = _gameTickListeners.get(k);
    if (listeners) for (const cb of [...listeners]) cb();
  }
}

// ── Consumer feed filters (Stage C) — per-feed ack state ─────────────
// The last filterAck each feed's transport reported (accepted OR rejected),
// for the health/verification surfaces: "is the filter installed, with what
// effective scope + cadence". Module-level (bumps `version` on change).
export interface FeedFilterState {
  /** Lazily built from `effective.tickers` (feed-filters-per-view): the
   *  per-frame arbitration below asks "is THIS row listed" on every core
   *  frame, and a list can be hundreds long. */
  tickerSet?: Set<string>;
  feedId: string;
  socketIncarnation: string;
  filterGeneration: number;
  effective: MarketFilterEffective | null;
  error: string | null;
  /** Wall-clock of the ack. */
  ackMs: number;
  /** Rows the LAST reset removed from this store (0 on a pure widening). */
  lastResetRemoved: number;
  /** Total rows removed by resets from this feed since the fleet mounted. */
  resetRemovedTotal: number;
  /** Rows this feed WITHDREW by `marketRemoved` since the fleet mounted
   *  (games-axis gate) — distinct from reset removals: a withdrawal is an
   *  identity change inside one generation, not a scope change. */
  engineRemovedTotal: number;
}
const _feedFilterState = new Map<string, FeedFilterState>();

function runFlush() {
  _rafId = null;
  _flushTimer = null;
  if (!_dirty || !_flushFn) return;
  _dirty = false;
  const now = Date.now();
  // The prune's CLOCK rides the (already throttled) flush — no standing timer
  // to leak in tests/SSR; at most once per PRUNE_INTERVAL_MS. During a fully
  // quiet period no flush runs, but neither does growth, so deferring is
  // harmless. The scan itself is handed to idle time, never this frame.
  if (now - _lastPruneMs >= PRUNE_INTERVAL_MS) {
    _lastPruneMs = now;
    schedulePruneStaleMarkets();
  }
  if (_lastFlushMs > 0) gatewayHealth.onFlush(now - _lastFlushMs);
  _lastFlushMs = now;
  _flushFn();
  // Game-grain notify AFTER the version bump: subscribers derive through
  // getGames et al., whose result cache is stamped by `version`.
  notifyGameTicks();
}

// Cached mirror of `_unionOrderbookNeeds()`. Rebuilt whenever a view's needs
// change (setOrderbookNeeds) or the feed clients (re)attach (setFeedClients),
// so ingestOrderbook can do an O(1) membership test per frame instead of
// re-unioning every view's needs on the hot path. ingestOrderbook gates its
// render-trigger + per-frame derived work on this set: orphaned orderbook
// frames a SharedWorker is still streaming during its 30s unsubscribe grace
// (after a view unmounts) get stored warm but no longer force a re-render.
// Feed-AGNOSTIC by design — a ticker is "active" if any view needs it,
// regardless of which feed (firehose / edge) delivers its frames; the routing
// in `_routeOrderbookNeeds` partitions the same union across feeds for the
// upstream subscription, an orthogonal concern.
let _activeOrderbookTickers: Set<string> = new Set();

// Partition the union of orderbook needs by owning feed and hand each client its
// complete subset (including empty → unsubscribe what it no longer needs). A
// ticker's sport comes from its ingested market frame; unknown ⇒ the firehose
// catch-all. Ownership is DYNAMIC (edge preferred / firehose fallback): a
// healthy, announcing edge owns its sports; otherwise the firehose does — so
// exactly one feed carries each book subscription at any moment, and an edge
// outage re-routes its books to the firehose on the next transition (see
// noteFeedStatus / ingestActiveSports).
/** The watched-game book lane's sink (services/gameBookLane). While a
 *  single-game page holds it, every book this tab needs is handed to the lane —
 *  grouped by the OWNING engine's URL, the same owner the fleet would have
 *  used — instead of to the fleet's clients, so each book has exactly one
 *  source: a socket that never carries a markets snapshot. */
/** Feed ids of the auxiliary book sockets that carry an OWNING engine's books
 *  on a socket of their own (services/gameBookLane.ts, services/liveGameLane.ts). */
function isAuxiliaryBookFeed(feedId: string): boolean {
  return feedId === 'book-lane' || feedId.startsWith('live-lane-');
}
/** Books the retired-feed gate refused ONLY because the delivering engine's
 *  market frame has not landed yet. On a fleet (re)connect an edge drains its
 *  ticker books first and owes the ~20k-row markets snapshot to a bounded
 *  continuation, so for a few seconds every book from the edge arrives while
 *  the row still names the firehose or the core floor as its source. The gate
 *  is right to refuse them THEN — but a quiet line's book is never re-sent, so
 *  dropping the frame left K LIQ $0 and an empty ladder until the next delta
 *  (node1, 2026-09-23, Milkshake's CFB spread/total board after a tab
 *  background/foreground). Park the frame per ticker and replay it the moment
 *  that same feed writes the row. Only a REQUIRED feed's book is parked: a
 *  firehose book refused for an edge-owned row stays refused (main's gate). */
const PENDING_BOOK_TTL_MS = 60_000;
const PENDING_BOOK_CAP = 10_000;
interface PendingBook { book: HydraOrderbook; feed: string; age?: SourceAge; observation?: SourceObservation; atMs: number }
const _pendingBooks = new Map<string, PendingBook>();
let _pendedBookFrames = 0;
/** Books parked because their engine's market frame had not landed yet (see
 *  `_pendingBooks`). Diagnostic only. */
export function getPendedBookFrameCount(): number {
  return _pendedBookFrames;
}
function pendBook(ticker: string, feed: string, book: HydraOrderbook, age: SourceAge | undefined, observation: SourceObservation | undefined): void {
  _pendedBookFrames++;
  _pendingBooks.delete(ticker); // re-insert so the map stays in age order for the cap eviction
  _pendingBooks.set(ticker, { book, feed, age, observation, atMs: Date.now() });
  if (_pendingBooks.size > PENDING_BOOK_CAP) {
    const oldest = _pendingBooks.keys().next().value;
    if (oldest !== undefined) _pendingBooks.delete(oldest);
  }
}
/** The parked book for `ticker` if `feed` is the one that parked it and it is
 *  still within the TTL; the entry is consumed either way. */
function takePendingBook(ticker: string, feed: string | undefined): PendingBook | null {
  const pend = _pendingBooks.get(ticker);
  if (!pend || feed == null || pend.feed !== feed) return null;
  _pendingBooks.delete(ticker);
  return Date.now() - pend.atMs <= PENDING_BOOK_TTL_MS ? pend : null;
}
export type BookLaneSink = (byUrl: Map<string, string[]>) => void;
let _bookLaneSink: BookLaneSink | null = null;

function _routeOrderbookNeeds(active: Set<string>): void {
  if (_feedClients.length === 0) return;
  const firehose = _firehoseFeed();
  const buckets = new Map<string, string[]>();
  const laneByUrl = new Map<string, string[]>();
  for (const fc of _feedClients) buckets.set(fc.id, []);
  for (const ticker of active) {
    const mkt = _markets.get(ticker);
    // The row's FEED key: an ALT row's book lives on the ALT edge (D5.2).
    const sport = rowFeedSport(mkt);
    const edge = _edgeServing(sport, mkt?.source);
    // Venue-aware: an edge only carries a ticker's book when it is actually
    // delivering that (sport, venue) — an announced sport with one venue
    // toggled off must not strand the other venue's books on the edge.
    const owner =
      edge != null && sport != null && mkt?.source != null && (_pairManaged(sport, mkt.source) || _edgeDeliveringVenue(edge.id, sport, mkt.source))
        ? edge
        : _bookFloor(sport, firehose, mkt?.source);
    if (!owner) continue;
    // EDGE-owned books only. The fallback (the firehose, or main's core floor
    // `_bookFloor`) owns a book merely until an edge has PROVEN it delivers
    // that venue — ownership flips to the edge a moment later — and the fleet
    // already holds a socket to it. Diverting those made the lane dial the
    // core, then drop it on the next re-route: a socket opened and closed per
    // flip (node1, 2026-09-21). So only the claimant path (`owner === edge`)
    // rides the lane; the floor fallback stays on the fleet's own socket.
    if (_bookLaneSink != null && owner === edge && owner.url && owner.role !== 'firehose') {
      const list = laneByUrl.get(owner.url);
      if (list) list.push(ticker); else laneByUrl.set(owner.url, [ticker]);
    } else {
      buckets.get(owner.id)!.push(ticker);
    }
  }
  const coalescible = _coalescibleOrderbookNeeds();
  for (const fc of _feedClients) {
    const list = buckets.get(fc.id) ?? [];
    fc.client.setOrderbookNeeds(list, list.filter((t) => coalescible.has(t)));
  }
  // After the fleet, so a ticker moving fleet → lane is never on neither.
  _bookLaneSink?.(laneByUrl);
}

/** Where a book goes when no edge is serving its (sport, venue): THE FLOOR,
 *  hc1 — never OG while any core is configured (Dan, 2026-10-03: "HC1 IS
 *  THE FLOOR"; g1 market data is hc + he only — OG carries nothing for the
 *  migrated sports, so a book sent there just went dark). A connected core
 *  announcing the sport first, then any connected core, then any configured
 *  core (its client replays the needs on reconnect). The firehose only in a
 *  fleet with no core at all (legacy / test fleets). */
function _bookFloor(sport: FeedSport | null, firehose: FeedClient | null, venue?: FeedVenue): FeedClient | null {
  // A dedicated OddsPapi socket is also labelled core, but its transport
  // deliberately has no markets or orderbooks. It is never a market floor.
  const cores = _feedClients.filter((f) => f.role === 'core' && f.opOnly !== true);
  if (cores.length === 0) return firehose?.opOnly === true ? null : firehose;
  return (sport != null ? cores.find((f) => f.client.connected &&
    (venue == null ? (_edgeLiveAnnounce.get(f.id)?.has(sport) ?? false) : _announcesPair(f.id, sport, venue))) : undefined)
    ?? cores.find((f) => f.client.connected)
    ?? cores[0];
}

/**
 * Test-only: publish the pending batch NOW, exactly as the RAF flush would
 * (same attribution, same single `set()`). Tests used to fake a flush with
 * `setState({ version: v + 1 })`; since #730 the dashboard keys on the
 * per-sport revisions that only the real flush computes, so a faked bump
 * publishes nothing a sport-keyed consumer can see. A frame the RAF chain
 * later runs finds nothing dirty and is a no-op.
 */
export function _flushMarketStoreNowForTests(): void {
  if (!_dirty || !_flushFn) return;
  _dirty = false;
  _lastFlushMs = Date.now();
  _flushFn();
}

function scheduleTick() {
  // Already scheduled (RAF armed or throttle timer pending) — coalesce.
  if (_rafId !== null || _flushTimer !== null) return;
  const sinceLast = Date.now() - _lastFlushMs;
  if (sinceLast >= MIN_FLUSH_INTERVAL_MS) {
    _rafId = requestAnimationFrame(runFlush);
  } else {
    // Too soon since the last flush — defer the remaining window, then align to
    // the next paint so the bump still lands on an animation frame.
    _flushTimer = setTimeout(() => {
      _flushTimer = null;
      _rafId = requestAnimationFrame(runFlush);
    }, MIN_FLUSH_INTERVAL_MS - sinceLast);
  }
}

// ── Store ────────────────────────────────────────────────────────

interface GameAccum {
  sport: Sport; date: string; awayCode: string; homeCode: string; title: string;
  kalshiAwayTicker: string | null; kalshiHomeTicker: string | null;
  polyTicker: string | null;
  /** Polymarket US (F1): the game's single PMUS- instrument, last-write-wins
   *  like polyTicker. */
  polyUsTicker: string | null;
  /** Scheduled event start epoch ms captured from `HydraMarket.eventStartTime`
   *  on any frame for this game. Sourced upstream from Polymarket Gamma or
   *  Kalshi milestones. First non-null value wins; later frames don't
   *  overwrite. */
  startTimeMs: number | null;
  /** Set when both outcome tickers in this accum are series-winner markets. */
  series?: { round: number; year: string };
  /** Latest seriesWins observed on any frame keyed to this row's
   *  canonicalSeriesKey: per-team wins keyed by canonical 3-char code. */
  seriesWins?: Record<string, number>;
  /** Latest seriesWinsToClinch observed on any frame for this series.
   *  4 best-of-7, 3 best-of-5, 2 best-of-3. Drives clinch-injection:
   *  inject only when some team's wins ≥ seriesWinsToClinch − 1
   *  (a team is on match point). */
  seriesWinsToClinch?: number;
  /** Set on per-day rows that borrowed series tickers because Kalshi
   *  dropped the per-day market for a possible clincher. Drives the UI
   *  badge / tooltip / trade-modal callout. */
  kalshiViaSeries?: { round: number; year: string };
  /** Tennis sibling of kalshiViaSeries: the row's Kalshi side is the two
   *  live legs of a tournament-winner event that has narrowed to a final
   *  (Kalshi lists no match market for a final). Drives the trade-modal
   *  callout. */
  kalshiViaTournament?: { eventTicker: string };
}

interface MarketState {
  version: number;       // Bumped on RAF tick — triggers re-render
  /** #730 — per-sport publication revisions, published in the SAME set() as
   *  `version`. `revBySport[sport]` moves only when a mutation attributed to
   *  that sport (or an unattributable one, which bumps every sport) was in
   *  the flushed batch. Absent = never bumped; read as 0. Consumers select a
   *  scalar (`s.revBySport[sport] ?? 0`) so an unrelated sport's frame leaves
   *  their selection unchanged. */
  revBySport: Readonly<Partial<Record<Sport, number>>>;
  connected: boolean;
  marketsCount: number;
  /** Membership/identity changes only; does not advance on price ticks. */
  gameIdentityVersion: number;
  orderbooksCount: number;
  /** Date.now() at the most recent ingest across any source/ticker. 0
   *  until the first message arrives. Drives the feed-freshness
   *  indicator and the click-triggered resnapshot heuristic. Updated
   *  on the same RAF flush as `version`, so consumers re-render at
   *  display refresh rate, not per-message. */
  lastMessageMs: number;
  /** Latest hydra backpressure level for the WS connection (0–3). At
   *  >= 2 hydra suspends orderbook frames entirely (market + consensus
   *  keep flowing, coalesced), so orderbook-driven views are rendering
   *  frozen books with no other error signal — surface a stale
   *  indicator. Cleared automatically when orderbook frames resume
   *  (the recovery signal) or the connection is rebuilt. */
  backpressureLevel: number;

  /** `sourceFeedId` (optional): the delivering feed's id, stamped onto the
   *  tape's source column — useHydra passes it from the ingesting client. */
  noteUsFeedStatus: (feed: string, connected: boolean) => void;
  noteUsTransportAlive: (feed: string, ageMs?: number) => void;
  withdrawUsBook: (feed: string, ticker: string) => void;
  withdrawBook: (feed: string, ticker: string) => void;
  getUsBoardQuotes: (sports: readonly UsGameSport[]) => ReadonlyMap<string, UsBrowserBoardQuote>;
  getUsBoardQuote: (usGameKey: string) => UsBrowserBoardQuote | null;
  getUsPositionQuote: (slug: string) => UsBrowserBoardQuote | UsBrowserLineQuote | null;
  /** #1231 — owned US full-game spread/total quotes for one US game, current or not. */
  getUsLineCandidates: (usGameKey: string) => { spreads: UsBrowserLineQuote[]; totals: UsBrowserLineQuote[] };
  /** #1231 — US game keys holding a line row. Slate games only (see UsLineQuotes.gamesWithLines). */
  getUsGamesWithLines: (sports: readonly UsGameSport[]) => ReadonlySet<string>;
  /** #1231/#1232 — the US game key for a full-game US slug; identity only (see gameKeyForUsSlug). */
  gameKeyForUsSlug: (slug: string) => string | null;
  /** #1231 — the board's US index read: slug → game identity for the slate's games. */
  setUsSlugIdentity: (sourceId: string, rows: readonly unknown[]) => void;
  clearUsSlugIdentity: (sourceId: string) => void;
  ingestMarket: (market: HydraMarket, sourceFeedId?: string, age?: SourceAge) => void;
  ingestOrderbook: (ticker: string, book: HydraOrderbook, sourceFeedId?: string, age?: SourceAge, observation?: SourceObservation) => void;
  requireSourceEvidence: (feedId: string) => void;
  noteSourceContinuity: (feedId: string, control: SourceContinuity | null, invalidLane?: SourceLane) => void;
  getSourceValidity: (ticker: string) => SourceVerdict | undefined;
  /** #958 — the ticker-scalar verdict (`lastPrice` / `volume` /
   *  `openInterest`) for readers of the raw `HydraMarket` that never build a
   *  `VenueBook`. Same value as `VenueBook.tickerSourceValidity`: Kalshi rows
   *  only, `undefined` for Polymarket and legacy feeds. */
  getTickerSourceValidity: (ticker: string) => SourceVerdict | undefined;
  /** Ingest one Unabated consensus update. Stored per
   *  (canonicalGameKey, periodTypeId) so in-period values (Q1 / inning /
   *  set) don't clobber the Full Game (periodTypeId=1) read consumers
   *  show by default. `serverTs` (the frame's broadcast time) dates the
   *  trend/recency state: on a snapshot-burst re-delivery it exceeds
   *  `lastUpdateMs` by the value's age, so stale snapshots seed dim
   *  instead of flashing bright. */
  ingestConsensus: (consensus: HydraConsensus, serverTs?: number, sourceFeedId?: string, age?: SourceAge) => void;
  /** #847 — current source-qualified age of the freshest producer evidence
   *  for a ticker, per kind: producer observation/change age plus local
   *  elapsed time, never a browser-epoch subtraction. Null per kind when no
   *  evidence arrived or its clock evidence is unknown/discontinuous; null
   *  overall when neither kind has ever been seen. Diagnostic read: market
   *  freshness classification still runs on `marketEvidenceAgeMs` until the
   *  producer contract for Core change stamps is settled (see that helper). */
  getSourceAge: (ticker: string) => { marketAgeMs: number | null; bookAgeMs: number | null } | null;
  /** Upsert one game's OddsPapi state (absolute per-game frame — rule 1;
   *  a `markets: []` tombstone deletes instead). Called ONLY from the
   *  firehose stager's accepted-change callbacks (DNL-003: one ingestion
   *  authority) — rev/epoch ordering is already settled upstream. */
  ingestOpOdds: (op: HydraOpOdds) => void;
  /** Delete one game's OP buckets (live no-coverage tombstone). */
  deleteOpOdds: (gameKeyV2: string) => void;
  /** Atomic snapshot commit (DNL-002): wholesale-replace the ENTIRE OP
   *  state with the committed set — a game absent from it is dropped from
   *  rendering (rule 9), never left showing its last cached value. */
  replaceOpOdds: (ops: HydraOpOdds[]) => void;
  /** Replace the active-sport set from hydra's `activeSports` meta frame
   *  (uppercase wire codes; normalized + filtered to g1's registry here). */
  ingestActiveSports: (sports: string[], sourceId?: string, filterAxes?: readonly string[], activeFeeds?: FeedPair[]) => void;
  /** The proposed filter axes a feed's engine advertised (g1xl-v2#1053
   *  Part 3) — `has('lineScope')` is what the board asks. Empty when the
   *  engine advertised nothing (every engine today). */
  getFeedFilterAxes: (feedId: string) => ReadonlySet<string>;
  setConnected: (connected: boolean) => void;
  setBackpressureLevel: (level: number) => void;
  /** A WS frame of any kind (incl. pong) arrived on the firehose — the
   *  transport is alive. Called by useHydra per frame; cheap (module-var
   *  write, no set()). */
  noteKeepalive: () => void;
  /** Imperative read of the last any-frame time (0 = never). Polled on a
   *  1s tick by useFeedFreshness, like getActivityCount. */
  getLastKeepaliveMs: () => number;
  /** Should a frame from `feedId` for `sport` be ingested? Dynamic ownership:
   *  an edge ingests the sports it claims; the firehose is the warm fallback —
   *  it ingests a claimed sport whenever no healthy edge is actually serving
   *  it (edge socket down, or the sport absent from the edge's live
   *  activeSports announce). With `venue` (pass the frame's `source`), the
   *  firehose additionally resumes a (sport, venue) pair the edge has stopped
   *  delivering — an announced sport with one venue toggled off on the edge
   *  must not blank that venue. Edge preferred; OG hydra is the safety net. */
  /** `ticker` lets a tickers-scoped edge (feed-filters-per-view) yield every
   *  UNLISTED row of its sports to the floor; omit only where no row is in
   *  hand (a scoped edge is then assumed not to ask for the row). */
  feedShouldIngest: (feedId: string, sport: FeedSport | null, venue?: FeedVenue, ticker?: string | null, previewOnly?: boolean) => boolean;
  /** Record that an EDGE feed delivered a market frame for (sport, venue).
   *  Called by useHydra's per-feed onMarket before the ingest gate — the
   *  edge's own frames establish per-venue ownership; their absence expires
   *  it (EDGE_VENUE_TTL_MS). */
  noteFeedDelivery: (feedId: string, sport: FeedSport | null, venue: FeedVenue) => void;
  /** Feed connection transition for EDGE pipes (the firehose drives
   *  setConnected). Re-routes orderbook needs to the new owner; an edge drop
   *  additionally asks the firehose for a backfill snapshot so quiet tickers
   *  don't sit on the dead edge's last values. */
  noteFeedStatus: (feedId: string, connected: boolean) => void;
  /** Consumer feed filters (Stage C): the ONE atomic reset for an installed
   *  filter generation on `feedId`'s connection. Drops every market row that
   *  feed would deliver (its claim scope; the firehose's catch-all) and that
   *  FAILS `scope` — evaluated with the server's own predicate semantics
   *  (rowPassesFilter). In-scope rows stay until the replacement snapshot
   *  overwrites them (the pre-existing resnapshot behaviour: never blank the
   *  board during the gap). Returns the number of rows removed. */
  applyFilterReset: (feedId: string, scope: FilterScope, pair?: { socketIncarnation: string; filterGeneration: number }) => number;
  /** Record a feed's latest filterAck (accepted or rejected) for the health
   *  surfaces. */
  noteFeedFilterAck: (feedId: string, ack: HydraFilterAckMsg) => void;
  /** `marketRemoved` — the engines WITHDRAW an already-delivered row whose
   *  identity change makes it fail the installed filter (games-axis release
   *  gate, hydra review DNL-003). Drops the ticker's row state outright.
   *  Ignored when the stamped pair is not the generation this feed currently
   *  holds (stale by construction), when the row is gone, or when another
   *  feed's delivery is authoritative for it (provenance, as DNL-002).
   *  Returns true when a row was actually removed. */
  applyMarketRemoved: (feedId: string, ticker: string, pair?: { socketIncarnation: string; filterGeneration: number }) => boolean;
  /** Games axis: the WIRE `canonicalGameKey` / `canonicalGameKeyV2` values that
   *  THIS game's own delivered rows carry — the only keys the engines will
   *  match (exact string, either key, no promotion). Prefers V2 when a row
   *  carries it and falls back to the legacy key only when it is absent, so a
   *  normal game narrows to one leg and only a genuinely ambiguous one accepts
   *  its doubleheader sibling. NEVER returns a synthesized key. Empty until the
   *  game's rows land — the caller declares sports only until then.
   *
   *  `gameKey` may be in either sport vocabulary: the sport segment is folded
   *  on both sides (`cfb` matches an `ncaaf` wire key), so a key composed from
   *  route params answers the same as a resolved `GameRow.key`. Nothing else
   *  about the key is folded. */
  getGameWireKeys: (gameKey: string) => string[];
  /** Per-feed filter state, fleet order. Empty when no feed has ack'd (flag
   *  off, legacy connections, or pre-ack). */
  getFeedFilterStates: () => FeedFilterState[];
  /** Part 0 (#1053): rows this store currently holds whose latest accepted
   *  delivery came from `feedId`, bucketed by the row's FEED key (raw —
   *  `cfbalt` stays distinct from `cfb`). Sorted by count, descending.
   *  Diagnostics only (walks every row; the health page polls it). */
  getFeedRowCounts: (feedId: string) => Array<{ sport: string; rows: number }>;
  /** Two-tier: sports in `viewed` (null = every sport an edge claims) that
   *  an edge CLAIMS but no edge currently SERVES — the floor is their only
   *  source, so it must run at the browser cadence, not the standby one. */
  floorSoleSourceSports: (viewed: ReadonlySet<FeedSport> | null) => FeedSport[];
  /** g1xl-v2#1106: every ALT feed key an edge CLAIMS, each with whether an
   *  edge currently SERVES it on both venues — the complement of
   *  `floorSoleSourceSports` over the ALT keys. Independent of any view, so
   *  every tab of a browser computes the same floor policy from it. */
  floorAltClaims: () => Array<{ sport: FeedSport; served: boolean }>;
  /** Two-tier: one listener for edge ownership transitions (connect/drop/
   *  announce change); useHydra re-negotiates the floor cadence from it. */
  setOwnershipListener: (fn: (() => void) | null) => void;
  /** Directory follow (default on). Off restores named-primary-only pair
   *  ownership and silences the re-fetch triggers (`?directoryFollow=off`). */
  setDirectoryFollow: (on: boolean) => void;
  /** useHydra's directory re-fetch: called when an edge adds or removes a
   *  pair claim ('announce') or a named primary's / sole claimant's
   *  connection moves ('connection'). */
  setDirectoryTriggerListener: (fn: ((reason: DirectoryTriggerReason) => void) | null) => void;
  /** Non-zero while a named primary has abandoned a pair (stopped announcing
   *  it, or dropped) and the directory still names it; a new abandonment
   *  changes the value. useHydra re-checks the directory sooner meanwhile. */
  getDirectoryAwaitEpoch: () => number;
  /** useHydra's OP failover controller notes which capable feed is ACTIVELY
   *  feeding the stager (initial designation + every failover switch) so
   *  the FeedTape OddsPapi column can show live provenance (OG vs core). */
  noteOpActiveFeed: (feedId: string) => void;
  /** Transport health of the ACTIVE OddsPapi lane, published by useHydra's
   *  failover controller (status transitions + its 5 s tick). A plain state
   *  field so the ladder's fair dot re-renders on the transition itself, not
   *  on the next market tick — when the feed is dead there may not be one. */
  opFeedHealth: OpFeedHealth;
  noteOpFeedHealth: (health: OpFeedHealth) => void;
  /** Record core's assigned primaries (from the feed directory) as an
   *  ownership PREFERENCE. Replaces the previous direction wholesale — core's
   *  latest word is its whole word — but never removes a feed or narrows a
   *  scope: an edge core did not name keeps whatever it is genuinely serving. */
  noteDirectoryAssignments: (assignments: Array<{ feedId: string; sports: FeedSport[]; feeds?: FeedPair[] }>, assignmentAuthority?: boolean) => void;
  /** Live per-feed sport ownership for status UI (FeedTape server chips).
   *  `sports` = sports this feed currently OWNS under dynamic arbitration
   *  (same rules as feedShouldIngest); `idleClaims` = sports an edge claims
   *  in its env scope but is not serving right now (disconnected, or absent
   *  from its live activeSports announce) — those sit on the firehose. With
   *  `venue`, ownership is narrowed to that venue's observed delivery, so the
   *  Kalshi and Polymarket columns can each show their own truth. */
  getFeedOwnership: (venue?: FeedVenue) => FeedOwnership[];
  /** Id of the feed that currently OWNS/ingests `sport` under the same
   *  arbitration feedShouldIngest/getFeedOwnership apply — serving claimant
   *  (edge/core) first, then the announcing + connected firehose, then a D10
   *  floor core. Null = nobody is serving the sport (honestly dark). Reads the
   *  existing arbitration state; no re-derivation. */
  ownerFeedForSport: (sport: Sport) => string | null;
  /** Wire the live feed clients (firehose + edge pipes). Called by `useHydra`
   *  on mount/unmount; [] clears them. */
  setFeedClients: (feeds: FeedClient[]) => void;
  /** Declare the set of tickers that currently-mounted views need orderbook
   *  data for. Forwards to the active HydraClient, which narrows the
   *  upstream orderbooks-channel subscription. Views that don't render
   *  depth data never need to call this. */
  setOrderbookNeeds: (viewId: string, tickers: string[]) => void;
  /** Install (or, with null, remove) the watched-game book lane's sink and
   *  re-route the current needs through it at once. See `BookLaneSink`. */
  setBookLaneSink: (sink: BookLaneSink | null) => void;
  /** Returns the count of tracked tickers that received any frame in
   *  the last `maxAgeMs`, alongside the total tracked count. Drives
   *  the freshness indicator's "is the system actually alive" check —
   *  a single chatty ticker keeping `lastMessageMs` warm shouldn't be
   *  enough to call the dot green. Read on a 1s tick from
   *  useFeedFreshness so it stays current even when nothing arrives. */
  /** Tickers with a frame in the last `maxAgeMs` vs total — optionally only
   *  those whose sport is in `sports` (the viewed sports; null = every
   *  sport). Over the whole 35k-row store the fraction is meaningless. */
  getActivityCount: (maxAgeMs: number, sports?: ReadonlySet<Sport> | null) => { active: number; total: number };
  /** Ask the active client for a fresh markets snapshot when the feed
   *  looks mostly-dead — i.e. fewer than ACTIVE_FRACTION_THRESHOLD of
   *  tracked tickers have received any frame in the last `maxAgeMs`.
   *  Catches the case the old "global lastMessageMs" gate missed: one
   *  chatty ticker (or one chatty venue) keeping the connection's
   *  global timestamp fresh while the rest of the cache rots. No-op on
   *  initial load (lastMessageMs === 0) and when the ticker population
   *  is too small for the fraction to be meaningful. */
  requestResnapshotIfStale: (maxAgeMs: number) => void;
  getGames: (
    sport: Sport,
    marketType: MarketType,
    /** `selectedDateIso` is the first day of the bounded view and
     *  `rangeEndIso` its last, both inclusive. Omitting `rangeEndIso` means a
     *  single day — the long-standing behavior, expressed as the degenerate
     *  range `[d, d]` rather than as a separate code path (#712). */
    filters?: {
      statusFilter?: StatusFilter;
      dateFilter?: DateFilter;
      selectedDateIso?: string;
      rangeEndIso?: string;
    }
  ) => GameRow[];
  /**
   * One row per canonical game with ALL spread + total line markets grouped
   * together. Same date/status filter semantics as `getGames`. The new
   * Spreads & Totals tab consumes this; future event-detail pages can read
   * the same shape. Series rows are excluded — they live in `getSeries`.
   */
  getGameEvents: (
    sport: Sport,
    filters?: {
      statusFilter?: StatusFilter;
      dateFilter?: DateFilter;
      selectedDateIso?: string;
      rangeEndIso?: string;
    },
    /** Scope of play. Default 'full' = full game (every existing caller is
     *  unchanged). Pass '1H' for NBA 1st-half or '1Q'–'4Q' for single-quarter
     *  spread/total lines (quarters are Kalshi-only). */
    period?: Period,
  ) => GameEventRow[];
  /** 3-way period moneyline (NBA/WNBA 1st half, soccer 1H/2H, MLB First 5
   *  Innings) for one game — Home / Away / Tie, with the two team rows paired
   *  to Polymarket's binary 1h-moneyline (Poly null for soccer in Phase 1 and
   *  for MLB F5, which is Kalshi-only) and the Tie Kalshi-only. Null when the
   *  game has no winner markets ingested for that period. `'full'` is accepted
   *  for soccer only — it sources the regulation 1X2 (full-game 3-way) so Game
   *  Lines can show it under "90 min" once the knockout "To Advance" market owns
   *  the top Moneyline slot. */
  getPeriodMoneyline: (gameKey: string, period: Period) => PeriodMoneyline | null;
  /** Soccer knockout "To Advance" 2-way (the two teams, no draw) for one game,
   *  as a binary GameRow — Kalshi's two per-team YES markets onto away/home,
   *  Polymarket's single binary split native/complement. Keyed `${gameKey}|advance`
   *  so it stays distinct from the 1X2 pair row that shares the canonicalGameKey.
   *  Null when no advance market is ingested (group stage, or both venues silent).
   *  Tolerates a one-sided venue ("—" on the missing side). See HYDRA_API.md
   *  "Soccer To Advance". */
  getAdvanceGame: (gameKey: string) => GameRow | null;
  /** Knockout "To Advance" markets as 2-outcome `MultiOutcomeEvent`s (the two
   *  teams, no draw), keyed identically to the regulation 1X2 event so the
   *  dashboard can swap a knockout game's 3-way for its advance 2-way in place.
   *  Each event carries `kind: 'advance'`; Polymarket's single binary is split
   *  native/complement onto the two outcomes. Empty for sports/slates with no
   *  advance markets. See HYDRA_API.md "Soccer To Advance". */
  getAdvanceEvents: (sport: Sport) => MultiOutcomeEvent[];
  /** The both-teams-to-score (BTTS) binary market for a game/period — a single
   *  yes/no per scope. `period` may be 'full' (BTTS has no top section, unlike
   *  spread/total/ML). Kalshi-only in Phase 1 (polymarket always null). Null
   *  when no BTTS market is ingested for that period. */
  getBtts: (gameKey: string, period: Period) => {
    kalshi: VenueBook | null;
    polymarket: VenueBook | null;
  } | null;
  /** MLB player-prop yes-ladders for one game, grouped stat → player →
   *  ascending threshold rungs (KXMLBHIT/HR/RBI/TB/SB/KS/OUTS/HRR). Classified
   *  by ticker prefix — the wire `marketType` is NOT authoritative for these
   *  (hydra sends none, the edge stamps 'prop'). Since poly-props-events
   *  (2026-07-16) the HR/KS stats join a Polymarket side client-side on
   *  (gameKey, stat, line, playerKey): playerKey = slugify(Kalshi
   *  yes_sub_title name) vs the Poly slug segment, threshold = Poly XptY line
   *  + 0.5. Unmatched names render UNPAIRED — never fuzzy-matched; an
   *  ambiguous slug (two Kalshi players slugifying identically) pairs
   *  neither. Stats with no ingested markets are omitted; empty array when
   *  the game has none (expected until lineups post). Never feeds the
   *  game-lines selectors — see classifyMlbExtendedTicker /
   *  classifyPolyExtendedTicker. */
  getMlbPlayerProps: (gameKey: string) => MlbPropStatGroup[];
  /** One-pass MLB prop snapshot for the live slate adapter (#569): every
   *  classified prop frame with its doubleheader-safe key, completed venue
   *  book, freshness, and read state. Plain records — no store maps escape.
   *  The day-wide surface cannot call getMlbPlayerProps per game: that walks
   *  the whole sport bucket on every call. */
  getMlbLivePlayerPropMarkets: () => LivePlayerPropMarket[];
  /** NFL player-prop ladders for one game (nfl-player-props, 2026-09-03),
   *  grouped stat → player → ascending threshold, in NFL_PROP_STATS order.
   *  Rows are selected by the WIRE class (`class: 'player-prop'` + `stat`);
   *  the ticker classifier is the identity source for the player segment
   *  and the fallback for frames from an engine that has not forwarded the
   *  class. Kalshi-only (no Polymarket NFL per-game props exist). The
   *  touchdown binaries (first-td / anytime-td / multi-td / dst-td) are
   *  single-rung ladders at the stat's fixed identity threshold; FIRSTTD's
   *  NONE leg is the "No Touchdown" ladder; the per-game D/ST touchdown is
   *  the "D/ST" ladder under dst-td. */
  getNflPlayerProps: (gameKey: string) => PlayerPropStatGroup[];
  /** Soccer goalscorer ladders for one fixture (soccer-goalscorer-props,
   *  2026-09-09): `goal` (per-player "1+" ladder) and `first-goal` (one rung
   *  per player at the fixed threshold, the NOGOAL leg last as "No Goal").
   *  Rows arrive under the SOCCERPROPS flow label and file under the
   *  fixture's canonical key; selected by the wire class like the NFL. */
  getSoccerPlayerProps: (gameKey: string) => PlayerPropStatGroup[];
  /** The one-pass live prop snapshot, per prop sport: 'mlb' is
   *  getMlbLivePlayerPropMarkets; 'nfl' walks the NFL bucket with the same
   *  identity / book / freshness / read-state authorities and no Poly branch. */
  getLivePlayerPropMarkets: (sport: PropSport) => LivePlayerPropMarket[];
  /** MLB per-game event markets for one game: the YRFI/NRFI binary
   *  (KXMLBRFI paired with Poly `-nrfi` — SAME polarity, no flip), the
   *  extra-innings binary (KXMLBEXTRAS paired with Poly `-extra-innings`),
   *  and per-team runs ladders (KXMLBTEAMTOTAL, Kalshi-only), away team's
   *  ladder first when the mapping is known. Null when nothing is ingested
   *  for the game. */
  getMlbEventMarkets: (gameKey: string) => MlbEventMarkets | null;
  /** CFB game-event props (cfb-game-props): the Kalshi-only per-game families
   *  classified by the wire `marketType` (lib/cfbWireMarkets), grouped for
   *  GameDetail's Events tab. Null when the game has none. */
  getCfbEventMarkets: (gameKey: string) => CfbEventMarkets | null;
  /** Kalshi UFC fight props (#848): total rounds, method of victory, method
   *  of finish, round of victory and goes-the-distance for one UFC game,
   *  admitted by the producer's canonical key AND the bout's exact shared
   *  Kalshi event code (lib/ufc/fightPropSelection). Null when the game has
   *  no KXUFCFIGHT bout or nothing names it. */
  getUfcFightProps: (gameKey: string) => UfcFightProps | null;
  /** Polymarket fight props for the fight whose Poly moneyline ticker is
   *  `polyTicker`: every live row on that slug stem the fight-prop grammar
   *  recognizes (lib/kalshiTicker/polyFightProps), ordered, each with its
   *  guarded Poly book. Read by STEM, so a Poly-only fight gets its props
   *  too; a prop whose producer key contradicts the fight's is left out.
   *  Empty until the feed carries them. */
  getPolyFightProps: (polyTicker: string) => PolyFightPropRow[];
  /** Which non-full periods have data for this game, by bet type — drives the
   *  Game Lines period tabs (enable only periods that actually have markets).
   *  Each list is ordered 1H, 2H, 1Q, 2Q, 3Q, 4Q. BTTS additionally includes
   *  'full' (it has no top section). One cheap pass over the game's frames (no
   *  event building). Moneyline is half-only (no quarter winner). */
  getAvailablePeriods: (gameKey: string) => {
    spread: Period[];
    total: Period[];
    moneyline: Period[];
    btts: Period[];
  };
  /** Championship/tournament futures boards for a sport. Team sports fold
   *  into one bare-year event (key `${sport}|${year}` — unchanged); tennis
   *  groups per tournament (key `${sport}|${year}|${seg}`, titled
   *  'WTA 2026 — WIM') so two open slams' legs never co-mingle: the same
   *  player holds legs in both at very different prices, and one flat
   *  click-to-trade list invites a misfire. */
  getSeriesEvents: (sport: Sport) => SeriesEvent[];
  /** Gameless Polymarket futures for a sport, grouped into boards (one
   *  tournament / award / season family per board).
   *
   *  DISJOINT from getSeriesEvents by construction: this reads ONLY frames
   *  hydraedge stamped `marketType: 'future'` (Polymarket season-futures
   *  branch), while getSeriesEvents reads locally-parsed Kalshi `series`
   *  tickers. No frame can satisfy both, so the two boards never duplicate a
   *  leg — and OG hydra, which stamps no marketType at all, contributes
   *  nothing here.
   *
   *  Grouping comes from the SLUG (lib/futures/parseFutureSlug), because the
   *  edge forwards no container identity on a future frame — `eventTicker` is
   *  the market's own conditionId. */
  getFutureBoards: (sport: Sport) => FutureBoard[];
  /** Sports currently holding at least one futures leg, in SPORT_TABS order
   *  with the tab-less futures-only sports (golf) appended. Drives the Futures
   *  view's tenant chips so a sport appears the moment its feed lands and
   *  disappears when its inventory settles out. */
  getFutureSports: () => Sport[];
  /** Exact-current future/series book; never resolves by a display name. */
  getFutureBook: (sport: Sport, venue: 'kalshi' | 'polymarket', ticker: string) => VenueBook | null;
  /** Session-local ticket identity; withdrawal/reclassification invalidates old drafts. */
  getFutureSelectionVersion: (ticker: string) => number | null;
  /** Explicit producer-identified dated matches; never inferred from futures. */
  getGolfMatches: () => readonly GolfMatchIdentity[];
  getGolfMatchBook: (venue: 'kalshi' | 'polymarket', ticker: string) => VenueBook | null;
  /** N-outcome events (soccer Home/Away/Draw today) grouped by event ticker.
   *  Filters mirror getGames semantics ('today' keeps events whose date ===
   *  selectedDateIso; 'active' drops finals; 'settled' keeps only finals) but
   *  DEFAULT TO UNFILTERED — Live discovery, GameDetail's resolver, and
   *  describePolyOrder all need every date. The dashboard passes its toolbar
   *  filters explicitly. */
  getMultiOutcomeEvents: (
    sport: Sport,
    filters?: { statusFilter?: StatusFilter; dateFilter?: DateFilter; selectedDateIso?: string },
  ) => MultiOutcomeEvent[];
  /** Set of sports that have at least one non-closed market frame ingested.
   *  Used by the dashboard toolbar to auto-hide chips for sports with no
   *  live activity. */
  getActiveSports: () => Set<Sport>;
  /** Last market frame for every ticker in the Hydra snapshot. This is an
   *  intentionally blunt escape hatch for fallback discovery surfaces that
   *  need to show raw markets even when the curated row parser cannot group
   *  them confidently. Returned as shallow copies so callers cannot mutate the
   *  store's live frame objects. */
  getMarketsSnapshot: () => HydraMarket[];
  getFeedLog: (source?: FeedLogSource) => FeedLogEntry[];
  getMarketPrice: (ticker: string) => number | null;
  /** Raw HydraMarket frame for a ticker (last update). Used by
   *  `describeMarket` to read Kalshi's title for the authoritative
   *  spread/total line. Returns null when the ticker isn't ingested. */
  getMarket: (ticker: string) => HydraMarket | null;
  /** The doubleheader-safe 5-seg game key from this market's CANONICAL keys,
   *  by the board's rule (gameKeyV2): hydra's v2 key, else its legacy key
   *  promoted to game 1 (the fallback HYDRA_API.md prescribes when v2 is
   *  absent) — but null when the frame is doubleheader-ambiguous (producer
   *  `dhAmbiguous`, or a witnessed gameNo ≥ 2 sibling) or carries no canonical
   *  key at all. Unlike the board, never falls back to parsing the ticker.
   *  Non-reactive, same contract as getMarket. */
  getCanonicalGameKey: (ticker: string) => string | null;
  /** What this market's fixture is currently CALLED, for sports whose display
   *  name rides the live feed rather than a code registry (soccer today).
   *
   *  One authority for every surface: the dashboard 3-way row, the knockout
   *  Advance rows, and — via `describeMarket` — every card, table and trade
   *  label. Resolves for ANY market family on the fixture (1X2, draw, spread,
   *  total, BTTS, To Advance), because hydra stamps them all with one
   *  canonical game key. Null for registry sports and for a fixture nothing
   *  has been ingested for; names are otherwise guaranteed non-empty, falling
   *  back to the raw code. O(1) — safe on the render path. */
  getSubtitleMatchup: (ticker: string) => SubtitleMatchupView | null;
  /** The feed's name for ONE outcome code on this market's fixture, or null.
   *
   *  The per-code companion to `getSubtitleMatchup`, for callers that already
   *  know which code sits on which side and only need the names — exactly how
   *  `getMultiOutcomeEvents` builds its row. It resolves during the window
   *  before hydra stamps a fixture, when the oriented lookup cannot: away/home
   *  codes arrive with the canonical game key, so a pre-canonical entry has
   *  names but no orientation. O(1). */
  getSubtitleName: (ticker: string, code: string) => string | null;
  /** Kalshi shard identity as observed across frames for one ticker. */
  getExchangeIndexDiagnostic: (ticker: string) => KalshiExchangeIndexDiagnostic;
  /** Slug ticker for a Polymarket conditionId (the key the relay's poly
   *  position/fills ledger uses). Null until the market's frame has been
   *  ingested (sport tab loaded / feed delivered it). */
  getTickerByConditionId: (conditionId: string) => string | null;
  /** Polymarket US (F1): the frame for a venue market slug, or null. The
   *  relay's US ledger addresses everything by slug; frames key by ticker. */
  getMarketByPmusSlug: (slug: string) => HydraMarket | null;
  /** Slug ticker for a Polymarket outcome tokenId (yes OR no token).
   *  Null until the market's frame has been ingested. */
  getTickerByTokenId: (tokenId: string) => string | null;
  /** Scheduled event start epoch ms resolved via the ticker's
   *  `canonicalGameKey` when available, or directly from the market frame
   *  for sports without a canonical key. Returns null when no upstream
   *  start signal has arrived yet. */
  getEventStartTimeMsByTicker: (ticker: string) => number | null;
  /** Doubleheader-safe game key for a ticker, or null when the frame is
   *  ambiguous (DNL-001) / carries no canonical key. Never promotes a legacy
   *  key on a doubleheader day. */
  getGameKeyV2ForTicker: (ticker: string) => string | null;
  /** Latest Full Game (periodTypeId=1) consensus snapshot for a canonical
   *  game key, or null if hydra hasn't broadcast one yet. The
   *  `useConsensusFor` hook wraps this for reactive component reads. */
  getConsensusFor: (canonicalGameKey: string | null | undefined) => HydraConsensus | null;
  /** Latest Full Game (periodTypeId=1) OddsPapi moneyline slice for a
   *  canonical game key, or null when the game has no OP coverage —
   *  missing/unmatched/tombstoned all read identically as null (the cell
   *  renders "—"). `useOpOddsFor` wraps this for reactive reads. */
  getOpOddsFor: (canonicalGameKey: string | null | undefined) => OpOddsSlice | null;
  /** The OP lane's state for one game — which ladder member is ACTIVE (and
   *  whether that is the moneyline-only OG standby), whether any line
   *  buckets are committed, and this game's full-game coverage + age. The
   *  GameDetail header badge reads it (Dan 2026-09-09: the page's odds
   *  status moves off the Unabated SSE). `useOpLaneStatus` wraps it. */
  getOpLaneStatus: (canonicalGameKey: string | null | undefined) => OpLaneStatus;
  /** Full-feed B: the latest slice for an ARBITRARY OP bucket — period-
   *  scoped (vendor `period` string verbatim) or full-game (periodKey 1) —
   *  requiring the exact declared strike on line families. Game Lines
   *  period rows and the specials surfaces read through this. Null = no
   *  coverage at that bucket (renders "—", rule 9). */
  getOpMarketFor: (
    canonicalGameKey: string | null | undefined,
    periodKey: number | string,
    marketType: string,
    points?: number | null,
    /** op-player-props: REQUIRED for `marketType === 'player-prop'` — the
     *  (stat, WIRE playerKey) of the ladder; a prop read without it
     *  resolves null. Ignored for every other family. */
    prop?: { stat: string; playerKey: string } | null,
  ) => OpOddsSlice | null;
  /** Full-feed B: every KNOWN-market slice for a game, stably sorted
   *  (marketType, then period, then points) — the widened-feed enumeration
   *  the team-totals/odd-even surface maps over. Builds a fresh array per
   *  call; consumers memoize against `version`. */
  getOpMarketsFor: (canonicalGameKey: string | null | undefined) => OpOddsSlice[];
  /** OP consensus AT A SPREAD LINE (hydracore §"Spread & total markets").
   *  `rowLine` is g1's signed away-perspective line (SpreadLine.line);
   *  orientation + sign conversion to the wire's provider-home-handicap
   *  `points` happens here (rule 4). Returns the slice plus the swap flag
   *  the renderer must apply to the away/home value slots. Null = no OP
   *  coverage at that exact line (missing / tombstoned) — renders
   *  "—", never an error (rule 9). */
  getOpSpreadLineFor: (
    canonicalGameKey: string | null | undefined,
    rowHomeCode: string | null | undefined,
    rowLine: number | null | undefined,
  ) => { slice: OpOddsSlice; swapped: boolean } | null;
  /** OP consensus AT A TOTAL LINE. Over rides the away slot, Under the
   *  home slot (sideIndex convention) — orientation never swaps. */
  getOpTotalLineFor: (
    canonicalGameKey: string | null | undefined,
    line: number | null | undefined,
  ) => OpOddsSlice | null;
  /** OP consensus AT A TEAM-TOTAL LINE for ONE team (full-feed B families
   *  `teamtotal-home` / `teamtotal-away`). The suffix is the PROVIDER's
   *  orientation, so the row's home/away is never used to pick a bucket:
   *  both buckets are scanned and the slice whose named team — per its own
   *  suffix (lib/opOdds.opTeamTotalTeamCode) — equals `teamCode` wins,
   *  at the exact declared line. `periodKey` is the
   *  bucket discriminator (1 = full game, else the vendor `period` string —
   *  OP_PERIOD_WIRE). Null = no coverage for that team at that line
   *  (renders "—", rule 9). Over rides the away slot (positional). */
  getOpTeamTotalLineFor: (
    canonicalGameKey: string | null | undefined,
    teamCode: string | null | undefined,
    line: number | null | undefined,
    periodKey?: number | string,
  ) => OpOddsSlice | null;
  /** True when ANY of the given row keys has per-line OP coverage — the
   *  SpreadsTotals surfaces' column auto-hide (mirrors hasOpOddsForAny;
   *  also the failover-asymmetry UX: OG serves moneyline only, so on OP
   *  failover the line columns hide/empty gracefully — rule 9 coverage
   *  semantics, not an error state). */
  hasOpLinesFor: (canonicalGameKeys: string[]) => boolean;
  /** True when ANY of the given row keys has OP coverage — the dashboard's
   *  slate-scoped column auto-hide (architecture §8: zero OP data on the
   *  current slate ⇒ the column hides and the UI is pixel-identical to
   *  pre-feature). */
  hasOpOddsForAny: (canonicalGameKeys: string[]) => boolean;
  /** Anchor-bookmaker slug of any cached OP entry (the book carrying
   *  hydra's `*FairTenths`, preferring `pinnacle`; falls back to the first
   *  book pre-phase-2) — names the provider + anchor in the column-header
   *  tooltip. Null when no OP data. */
  getAnyOpBookmaker: () => string | null;
  /** `contributingBooks` of any cached OP consensus — the header tooltip's
   *  book count (architecture §8: provider, book count, anchor). Null when
   *  no cached entry carries a consensus. */
  getAnyOpConsensusBooks: () => number | null;
  /** Trend/recency state for a game's OP consensus bucket (Full Game
   *  moneyline) — the OP/PNCL column's velocity arrows + recency
   *  brightness. Fed only by OP consensus tenths (never Unabated's);
   *  render via `useConsensusGlow`. `useOpTrendFor` wraps it for
   *  reactive reads. */
  getOpTrendFor: (canonicalGameKey: string | null | undefined) => ConsensusTrendState | null;
  /** Last-known anchor fair values for a game's OP bucket (provider
   *  orientation) — the number the phase-3 strikethrough is drawn over
   *  when the frame lists pinnacle in withdrawnBooks. Null when never
   *  seen this session. `useOpLastAnchorFairFor` wraps it. */
  getOpLastAnchorFairFor: (canonicalGameKey: string | null | undefined) => OpLastAnchorFair | null;
  /** Latest knockout "To Advance" 2-way consensus (period 1, marketType
   *  'advance') for a game, or null when hydra hasn't quoted one (group stage /
   *  pre-quote). Buckets apart from `getConsensusFor` — both share gameKey +
   *  periodTypeId. `useAdvanceConsensusFor` wraps it for reactive reads. */
  getAdvanceConsensusFor: (canonicalGameKey: string | null | undefined) => HydraConsensus | null;
  /** Sub-cent trend/recency state for the Full Game result consensus bucket
   *  (period 1, no marketType) — number-brightness recency, trailing-window
   *  tenths deltas, sharp-anchor flag. Render via `useConsensusGlow`.
   *  `useConsensusTrendFor` wraps this for reactive reads. */
  getConsensusTrendFor: (canonicalGameKey: string | null | undefined) => ConsensusTrendState | null;
  /** Trend/recency state for the knockout "To Advance" bucket (period 1,
   *  marketType 'advance') — the advance headline row's counterpart to
   *  `getConsensusTrendFor`. */
  getAdvanceConsensusTrendFor: (canonicalGameKey: string | null | undefined) => ConsensusTrendState | null;
  /** Seed the canonical-key → startTime cache from an external source
   *  (typically the Unabated gameStates poll). Fills in entries for
   *  games where Polymarket doesn't have a market — so spreads/totals
   *  on niche-sport games still get an event start for countdowns, GTS
   *  expiry, etc. First-write-wins: existing Poly-sourced entries
   *  aren't overwritten by the poll's seed. */
  seedEventStartTimeMsByKey: (gameKey: string, ms: number, statusId: 1 | 2 | 3) => void;
  /** Last trade price in YES-cents (invert for NO at the call site). */
  getLastTradeYesCents: (ticker: string) => number | null;
  /** Raw orderbook (yes / no level lists) by ticker, or null if not yet ingested. */
  getOrderbookRaw: (ticker: string) => HydraOrderbook | null;
  /** Human display only. Never pass retained data to trading or alerts. */
  getOrderbookDisplay: (ticker: string) => BookDisplaySnapshot;
  /** Ms since the last ORDERBOOK frame for this ticker (not market/trade
   *  frames), or null if no book has ever arrived. Drives per-book freshness
   *  indicators that must stay honest when deltas freeze but prices tick. */
  getOrderbookAgeMs: (ticker: string) => number | null;
  /** Diagnostics for one ticker's stored orderbook: raw level counts as held
   *  (before any display filter), age, the feed that delivered it, whether a
   *  view currently declares it as needed, and the raw top-of-book. Null when
   *  no orderbook frame was ever ingested for the ticker. */
  getOrderbookDiag: (ticker: string) => {
    yesLevels: number;
    noLevels: number;
    ageMs: number;
    sourceFeedId: string | null;
    needed: boolean;
    rawBestYesBid: number | null;
    rawBestNoBid: number | null;
    lastPrice: number | null;
  } | null;
  /** Ms since the freshest evidence for this ticker — the older of the browser
   *  observation stamp and the producer's own lastUpdateMs — or null if neither
   *  exists. See `marketEvidenceAgeMs` for why both arms are needed. */
  getMarketAgeMs: (ticker: string) => number | null;
}

const SERIES_TITLE: Record<string, string> = {
  nba: 'NBA Champion',
  wnba: 'WNBA Champion',
  nhl: 'Stanley Cup',
  mlb: 'World Series',
};
// Golf boards are per SERIES (one event each) — titles + leg names by ticker
// vocabulary (2026-09-09: Presidents Cup on Kalshi, USA / WORLD / TIE legs).
const GOLF_SERIES_TITLE: Record<string, string> = {
  KXPRESCUP: 'Presidents Cup',
  KXRYDERCUP: 'Ryder Cup',
  KXPGARYDER: 'Ryder Cup',
};
const GOLF_OUTCOME_NAMES: Record<string, string> = {
  USA: 'Team USA', WORLD: 'Team World', EUR: 'Team Europe', EUROPE: 'Team Europe', TIE: 'Tie',
};

// ── getGames result cache ─────────────────────────────────────────
//
// getGames re-derives a sport's whole game universe on every call — a
// ~560-line O(marketsForSport) sweep. The positions page fires it up to ~40×
// in a single mount commit (three held-sport sweeps: divergenceByTicker /
// upOddsByKey / polyBookNeeds, plus describeOrder's 14-sport asset-id sweep),
// every one with identical {statusFilter:'all', dateFilter:'all'} filters and
// the same store snapshot — so the rebuilds are pure duplication. Memoize the
// GameRow[] per (sport, marketType, filters) and serve repeats within one
// snapshot from here.
//
// Freshness — cached derivations carry three independent stamps:
//   (1) store `revBySport` — bumped on the coalesced RAF/throttled flush after an
//       ingest (the same signal the old `void get().version` read gated on).
//       Every ingest* sets `_dirty` + calls scheduleTick, so a static version
//       across a render commit means no flush has landed and the market data is
//       settled — the ~40× hot callers all run POST-flush and see a consistent
//       snapshot. (Caveat: `_markets`/`_orderbooks` mutate SYNCHRONOUSLY per
//       ingest while version bumps only on the flush, so an imperative getGames
//       landing mid-frame can serve rows one ingest stale until the next flush
//       — bounded by the flush cadence, same order as the wall-clock bound
//       below, and those callers read structural fields (tickers/tokens), not
//       sub-frame prices.)
//   (2) gameStatesStore `byGameKey` identity — setStates() installs a fresh Map
//       on every Unabated poll; getGames reads it in Pass 2 (final override +
//       winner checkmark), and that read is NOT gated by `version`, so it must
//       be part of the stamp or a poll update could be masked.
//   (3) clock second — time-derived state, freshness and date membership must
//       expire even without a payload for this sport. This is the existing
//       display-clock cadence, not a new market-publication throttle. The
//       Dashboard subscribes to that shared clock; imperative reads also
//       validate it. Crossing a second in either direction clears derivations.
// Sport revisions drop only that sport; game-state/clock changes drop all
// cached derivations lazily. No newer payload is held until the clock ticks.
//
// Bound: one Map, cleared wholesale on every generation change → at most one
// entry per (sport, marketType, filters) for the current snapshot; it cannot
// accumulate across a session.
//
// RETURN CONTRACT: getGames now hands the SAME GameRow[] (and the same row
// objects) to every caller within a snapshot — the return is SHARED and
// READ-ONLY. All current call sites only iterate / spread / filter it; a caller
// that sorts / pushes / splices the returned array, or writes a row field in
// place, would corrupt every sibling consumer. Copy first (`[...games]`) before
// mutating.
/** #730 — the per-sport revision each sport's cached rows were built at. */
const _getGamesCacheRevBySport = new Map<Sport, number>();
let _getGamesCacheStates: unknown = null;
let _getGamesCacheClockSecond: number | null = null;
/**
 * The bounded view's last day, normalized (#712).
 *
 * The window end is the ONE field callers can hand these selectors free-form,
 * so it is validated here rather than trusted: anything that isn't a bare
 * `YYYY-MM-DD` means "no end given" — a single day, the same reading as
 * omitting it entirely.
 *
 * That matters twice over. The filter reads the end with `??`, so an
 * unvalidated `''` would survive as the window end and exclude every row
 * (`rowDay > ''` is always true) — an empty board from a value that reads like
 * "unset". And the result cache keys on the same field, where `''` and
 * `undefined` would collapse to one entry with OPPOSITE filter semantics, so
 * one call could serve the other its rows. Normalizing at intake closes both
 * at once, and keeps the cache key honest about what was actually asked for.
 *
 * `selectedDateIso` is deliberately NOT validated the same way: today a
 * malformed start filters the board down to nothing, and treating it as absent
 * instead would skip the date gate entirely and show every date at once — a
 * worse answer than an empty board, and a change to shipped behavior.
 */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function normalizeRangeEndIso(value: string | undefined): string | undefined {
  return value !== undefined && ISO_DATE_ONLY.test(value) ? value : undefined;
}

const _getGamesCache = new Map<string, GameRow[]>();

// StreamRift F3 (2026-09-09): getPeriodMoneyline was an UNCACHED full-sport
// scan (a parseTicker per row) run per tick by the single-game live view. Same
// SHARED READ-ONLY return contract as getGameEvents. Stamp = (version,
// `_gameRowIngestGen`, gameStatesStore.byGameKey identity): `_gameRowIngestGen`
// is bumped by every ingestMarket / ingestOrderbook, because the row embeds
// VenueBook prices that a same-version frame re-stamps before any flush, and
// the sync-read contract (totalsPanelNav #96: "live context, not frozen at
// click") must hold there too. getGameEvents moved to main's #754 per-sport
// mutation stamp in the 09-24 merge; `clearDerivedRowCaches` drops this memo
// beside the other two at every identity move.
let _gameRowIngestGen = 0;
let _getPeriodMoneylineCacheVersion = -1;
let _getPeriodMoneylineCacheGen = -1;
let _getPeriodMoneylineCacheStates: unknown = null;
const _getPeriodMoneylineCache = new Map<string, PeriodMoneyline | null>();

// RETURN CONTRACT: getGameEvents hands the SAME GameEventRow[] (and the same
// row objects) to every caller within a snapshot, exactly as getGames does
// above — the return is SHARED and READ-ONLY. Every current call site only
// iterates / finds / filters / maps it; a caller that sorts or splices the
// returned array, or writes a row field in place, would corrupt every sibling
// consumer. Copy first (`[...events]`) before mutating.
/** #754 — the mutation stamp each sport's cached EVENT rows were built at. */
const _getGameEventsCache = new Map<string, GameEventRow[]>();
const _getGameEventsCacheStampBySport = new Map<Sport, string>();
let _getGameEventsCacheClockSecond: number | null = null;

/**
 * Drop every memoized row derivation at once.
 *
 * `getGames` and `getGameEvents` answer different questions off the SAME
 * market records, so anything that can re-seat one row can re-seat the other:
 * ingestion, withdrawal, a doubleheader identity moving, a source-authority
 * change. Clearing them together is what stops the next invalidation point
 * added here from remembering one cache and forgetting the other.
 *
 * The game-states map is the one input they do NOT share — moneyline rows read
 * it, event rows derive their state from market statuses — so that stamp stays
 * inside getGames rather than moving in here. It moves on every poll response,
 * and riding it would throw the event cache away at poll cadence to rebuild a
 * byte-identical answer.
 */
function clearDerivedRowCaches(): void {
  _getGamesCache.clear();
  _getGameEventsCache.clear();
  _getPeriodMoneylineCache.clear();
  _getPeriodMoneylineCacheVersion = -1;
}

/** The per-sport stamps beside those caches; cleared wherever both caches are
 *  dropped for a reason that is not attributable to one sport. */
function clearDerivedRowRevStamps(): void {
  _getGamesCacheRevBySport.clear();
  _getGameEventsCacheStampBySport.clear();
}

// ── Games axis: the WIRE keys a game's own rows carry ────────────────
// The engines match `filter.games` by exact string on either wire key, with
// no promotion, so the consumer may only ever declare a key the feed actually
// delivered. Anything g1 composes from a URL is a different shape
// (`composeHydraGameKey` is 3-seg `date|LO|HI`; the wire legacy key is
// `sport|date|AWAY|HOME`) and `GameRow.key` is PROMOTED — synthesized whenever
// the wire carried no V2. An allow-list of synthesized keys matches nothing
// and blanks the page, so this index is the only sanctioned source
// (hydra 2026-09-04: option 1, no engine change).
//
// Cached per game key. Only non-empty results are cached: a game whose rows
// have not landed yet must be able to answer again once they do.
//
// Invalidated ONLY through `invalidateGameWireKeys` — a membership/identity
// boundary, never a price one. `getGames` used to clear it alongside its own
// price-stamped result cache, which made a mounted GameDetail rescan every
// market at flush cadence for an unchanged answer (#808).
const _gameWireKeysCache = new Map<string, string[]>();
const _usGameWireKeysCache = new Map<string, string[]>();
let _gameIdentityVersion = 0;
function invalidateGameWireKeys(): void {
  _gameWireKeysCache.clear();
  _usGameWireKeysCache.clear();
  _mlbGameTickers = null;
  _gameIdentityVersion++;
}

// ── Futures index + board cache ──────────────────────────────────
// Futures move on the discovery SWEEP (~2 min), not on book ticks, so their
// boards get their own generation counter instead of riding `version` (which
// bumps at display refresh rate off every other sport). One golf sweep
// invalidates; 60 NBA book frames a second do not.
let _futureGeneration = 0;
let _futureBoardsGeneration = -1;
const _futureBoardsCache = new Map<Sport, FutureBoard[]>();
/** sport → future-leg tickers currently held. Maintained alongside
 *  `_marketsBySport` so getFutureSports is O(sports) instead of a full scan
 *  of every market on every render tick. */
const _futureTickersBySport = new Map<Sport, Set<string>>();
/** Board key for legs whose slug matches no known futures shape. */
const UNGROUPED_FUTURES_KEY = '__ungrouped__';

function realFuturePrice(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0 && value < 100 ? value : null;
}

/** Shared by the board and ticket gate; never round a sub-cent quote to zero. */
function futurePrice(market: HydraMarket): number | null {
  const bid = realFuturePrice(market.yesBid);
  const ask = realFuturePrice(market.yesAsk);
  return bid != null && ask != null ? midCents(bid, ask) : realFuturePrice(market.lastPrice) ?? bid;
}
let nextFutureSelectionVersion = 0;
const _golfMatches = new Map<string, GolfMatchIdentity>();
let _golfMatchList: readonly GolfMatchIdentity[] | null = null;
const _golfMatchConflicts = new Set<string>();
function golfMatchList(): readonly GolfMatchIdentity[] {
  if (_golfMatchList) return _golfMatchList;
  _golfMatchConflicts.clear();
  const groups = new Map<string, { identity: string; outcomes: Set<string> }>();
  for (const m of _golfMatches.values()) {
    const group = groups.get(m.key);
    if (!group) groups.set(m.key, { identity: m.groupIdentity, outcomes: new Set([m.outcome]) });
    else {
      if (group.identity !== m.groupIdentity || group.outcomes.has(m.outcome)) _golfMatchConflicts.add(m.key);
      group.outcomes.add(m.outcome);
    }
  }
  return _golfMatchList = [..._golfMatches.values()].sort((a, b) =>
    a.startTimeMs - b.startTimeMs || a.key.localeCompare(b.key) || a.ticker.localeCompare(b.ticker));
}
const _futureSelectionVersions = new Map<string, { identity: string; version: number }>();

function futureSelectionVersion(market: HydraMarket): number {
  const identity = JSON.stringify([market.source, market.sport, market.marketType, market.status,
    market.eventTicker, market.conditionId, market.yesTokenId, market.noTokenId,
    market.tickSize, market.negRisk, market.exchangeIndex,
    market.marketType === 'future' ? futurePrice(market) != null : null,
    market.sport?.toLowerCase() === 'golf' ? golfMatchIdentity(market)?.identity ?? null : null]);
  let entry = _futureSelectionVersions.get(market.ticker);
  if (!entry || entry.identity !== identity) {
    entry = { identity, version: ++nextFutureSelectionVersion };
    _futureSelectionVersions.set(market.ticker, entry);
  }
  return entry.version;
}

/** Keep the futures index in lockstep with a market's sport buckets. Called
 *  on every ingest (a re-ingest can reclassify) and mirrored by the terminal
 *  prune. Bumps the generation only when something actually changed, so a
 *  re-sweep that re-sends identical frames doesn't invalidate the cache. */
function indexMarketAsFuture(market: HydraMarket, sports: readonly Sport[]): void {
  const ticker = market.ticker;
  const isFuture = market.marketType === 'future' && market.source === 'polymarket';
  let changed = false;
  for (const sport of sports) {
    let bucket = _futureTickersBySport.get(sport);
    if (isFuture) {
      if (!bucket) { bucket = new Set(); _futureTickersBySport.set(sport, bucket); }
      if (!bucket.has(ticker)) { bucket.add(ticker); changed = true; }
    } else if (bucket?.delete(ticker)) {
      changed = true;
    }
  }
  // A future frame always refreshes the boards it belongs to — its PRICE may
  // have moved even when membership didn't.
  if (isFuture || changed) _futureGeneration++;
}

function deindexFuture(ticker: string, sports: readonly Sport[]): void {
  for (const sport of sports) {
    if (_futureTickersBySport.get(sport)?.delete(ticker)) _futureGeneration++;
  }
}

const _usBoardQuotes: UsBoardQuotes = new UsBoardQuotes((feed, sport, ticker): boolean =>
  useMarketStore.getState().feedShouldIngest(feed, normalizeFeedSport(sport), 'polymarket_us', ticker, true));
// #1231 — the board's US index read (slug → game identity), as main.
const _usSlugIdentity = new UsSlugIdentity();
// #1140 receiving: the gateway remains the only transport. Shared US screens
// read its held rows/books through one lazy projection, never main's inert cache.
// Mutation stamps move before a non-visible book's early return; published
// React versions alone are insufficient for synchronous tracking reads.
const NO_US_LINES: { spreads: UsBrowserLineQuote[]; totals: UsBrowserLineQuote[] } = Object.freeze({ spreads: [], totals: [] }) as never;
const _usObservation = new WeakMap<HydraMarket, { session: object | undefined }>();
const _usGatewaySessions = new Map<string, { connected: boolean; generation: object }>();
function observeUsGatewayRow(market: HydraMarket, feed: string | undefined): void {
  if (market.source === 'polymarket_us') _usObservation.set(market, { session: feed ? _usGatewaySessions.get(feed)?.generation : undefined });
}
let _gatewayUs: { stamp: string; index: UsGatewayQuoteIndex } | null = null;
function gatewayUsIndex(): UsGatewayQuoteIndex {
  const stamp = US_GAME_SPORTS.map(sp => sportMutationStamp(sp.toLowerCase() as Sport)).join('|');
  if (_gatewayUs?.stamp === stamp) return _gatewayUs.index;
  const inputs: UsGatewayQuoteInput[] = [];
  for (const ticker of _tickerByPmusSlug.values()) {
    const market = _markets.get(ticker);
    if (!market) continue;
    const book = _orderbooks.get(ticker);
    const token = _usObservation.get(market);
    const rowFeed = _marketSourceFeed.get(ticker), bookFeed = _bookSourceFeed.get(ticker);
    const bookProof = _bookSourceProof.get(ticker), priceProof = _priceSourceProof.get(ticker);
    const age = _bookSourceAge.get(ticker);
    const observationFeed = book ? bookFeed : rowFeed;
    // Preserve the lane's legacy receipt convention when no producer clock is
    // supplied. A supplied-but-invalid clock never becomes fresh by fallback.
    const observedAtMs = age ? age.sourceMs : _lastOrderbookMs.get(ticker) ?? null;
    const sport = baseSportOf((market.sport ?? '').toLowerCase() as Sport);
    const row = { ...market, canonicalTeams: market.canonicalAwayCode && market.canonicalHomeCode
      ? { away: market.canonicalAwayCode, home: market.canonicalHomeCode } : undefined };
    inputs.push({
      row,
      book: _withdrawnBookPrices.has(ticker) ? { kind: 'unavailable' }
        : book ? { kind: 'book', value: book, observedAtMs } : { kind: 'row' },
      currentAt: () => _markets.get(ticker) === market && _usObservation.get(market) === token
        && _orderbooks.get(ticker) === book && !_withdrawnBookPrices.has(ticker)
        && _marketSourceFeed.get(ticker) === rowFeed && _bookSourceFeed.get(ticker) === bookFeed
        && _bookSourceProof.get(ticker) === bookProof && _priceSourceProof.get(ticker) === priceProof
        && (!observationFeed || (_usGatewaySessions.get(observationFeed)?.connected !== false
          && _usGatewaySessions.get(observationFeed)?.generation === token?.session))
        && _pairFeedServesRow(ticker, rowFeed)
        && (!book || sourceBookUsable(ticker))
        && (!age || sourceAgeNow(age) !== null)
        && freshnessForMarket(market, sport) === 'fresh',
    });
  }
  const index = buildUsGatewayQuoteIndex(inputs);
  _gatewayUs = { stamp, index };
  return index;
}

export const useMarketStore = create<MarketState>((set, get) => {
  // Wire the flush function to bump version
  _flushFn = () => {
    const dirty = _dirtySports;
    _dirtySports = null;
    const prev = get().revBySport;
    let revBySport = prev;
    if (dirty != null) {
      const next: Partial<Record<Sport, number>> = { ...prev };
      for (const sp of dirty === 'all' ? ALL_SPORTS : [...dirty]) next[sp] = (prev[sp] ?? 0) + 1;
      revBySport = next;
    }
    set({
      version: get().version + 1,
      revBySport,
      marketsCount: _markets.size,
      gameIdentityVersion: _gameIdentityVersion,
      orderbooksCount: _orderbooks.size,
      lastMessageMs: _globalLastReceiveMs,
    });
  };

  return {
    version: 0,
    opFeedHealth: 'live',
    revBySport: {},
    connected: false,
    marketsCount: 0,
    gameIdentityVersion: 0,
    orderbooksCount: 0,
    lastMessageMs: 0,
    backpressureLevel: 0,

    noteUsFeedStatus: (feed, connected) => {
      const prior = _usGatewaySessions.get(feed);
      if (!prior || prior.connected !== connected) _usGatewaySessions.set(feed, { connected, generation: {} });
      _usBoardQuotes.status(feed, connected); touch('all');
    },
    noteUsTransportAlive: (feed, ageMs) => _usBoardQuotes.alive(feed, ageMs),
    withdrawUsBook: (feed, ticker) => { const sport = _usBoardQuotes.withdraw(feed, ticker); if (sport) touch([sport.toLowerCase() as Sport]); },
    withdrawBook: (feed, ticker) => {
      // No `PMUS-` early return (main routes US books to its browser-transport
      // US quote adapter): on streamrift the gateway's Polymarket US books live
      // in `_orderbooks` like any other venue's and must retire with them.
      if (_pendingBooks.get(ticker)?.feed === feed) _pendingBooks.delete(ticker);
      // A row and its book can have different owners during a handoff.
      if (_bookSourceFeed.get(ticker) !== feed) return;
      _orderbooks.delete(ticker);
      _bookSourceFeed.delete(ticker);
      removeDisplayBook(ticker);
      _lastOrderbookMs.delete(ticker);
      _bookSourceAge.delete(ticker);
      _bookSourceProof.delete(ticker);
      _rowResyncSince.delete(ticker);
      if (_bookPriceSourceFeed.get(ticker) === feed) {
        const market = _markets.get(ticker);
        if (market) {
          market.yesBid = market.yesAsk = market.noBid = market.noAsk = undefined;
        }
        _priceSourceProof.delete(ticker);
        _bookPriceSourceFeed.delete(ticker);
        _withdrawnBookPrices.add(ticker);
      }
      clearDerivedRowCaches();
      clearDerivedRowRevStamps();
      touch(_marketSportKeys.get(ticker) ?? 'all');
    },
    getUsBoardQuotes: (sports) => new Map([...gatewayUsIndex().boardByGame].filter(([, q]) => sports.includes(q.sport))),
    getUsBoardQuote: (usGameKey) => gatewayUsIndex().boardByGame.get(usGameKey) ?? null,
    getUsPositionQuote: (slug) => gatewayUsIndex().positionBySlug.get(slug) ?? null,
    getUsLineCandidates: (usGameKey) => gatewayUsIndex().linesByGame.get(usGameKey) ?? NO_US_LINES,
    getUsGamesWithLines: (sports) => {
      const games = new Set<string>();
      for (const [key, g] of gatewayUsIndex().linesByGame) {
        const q = g.spreads[0] ?? g.totals[0];
        if (q && sports.includes(q.sport)) games.add(key);
      }
      return games;
    },
    // Main's precedence: the board's identity read once published, else the
    // held rows (here the gateway's line rows; main's moneyline store is inert).
    gameKeyForUsSlug: (slug) => _usSlugIdentity.published()
      ? _usSlugIdentity.get(slug)
      : gatewayUsIndex().positionBySlug.get(slug)?.gameKey ?? null,
    setUsSlugIdentity: (sourceId, rows) => { _usSlugIdentity.set(sourceId, rows); touch('all'); },
    clearUsSlugIdentity: (sourceId) => { _usSlugIdentity.clear(sourceId); touch('all'); },
    requireSourceEvidence: (feedId) => {
      _sourceAuthority.require(feedId);
      clearDerivedRowCaches();
      touch('all');
    },
    noteSourceContinuity: (feedId, control, invalidLane) => {
      // Evidence is NEGOTIATED, not assumed: a feed becomes evidence-required
      // the moment it announces continuity (its first accepted control), not
      // when its client is built. An engine that never speaks the #847
      // protocol — the SuperHydra fork edges, 2026-09-23 — keeps legacy
      // age-based freshness instead of every one of its rows and books
      // reading `unconfirmed` forever (node1: STALE on every edge-owned
      // sport, books hidden, orders blocked, for every browser on the fleet
      // since the 09-21 gbuff/main merge). A disconnect / invalid control on
      // a feed that never announced changes nothing.
      if (!_sourceAuthority.required(feedId)) {
        if (control == null) return;
        _sourceAuthority.require(feedId);
      }
      const servingBefore = _servingLanesKey(feedId);
      _sourceAuthority.control(feedId, control, invalidLane);
      // A lane starting or stopping serving moves that venue's books now
      // (see _edgeDeliveringVenue) — not at the next unrelated routing pass.
      if (_servingLanesKey(feedId) !== servingBefore) _routeOrderbookNeeds(_activeOrderbookTickers);
      // Invalidate synchronous selectors now, before the coalesced React publish.
      clearDerivedRowCaches();
      _futureBoardsCache.clear();
      touch('all');
    },
    getSourceValidity: (ticker) => governingPriceVerdict(ticker),
    getTickerSourceValidity: (ticker) =>
      _markets.get(ticker)?.source === 'kalshi' ? sourceTickerVerdict(ticker) : undefined,
    ingestMarket: (market, sourceFeedId, age) => {
      // F0 venue fence: an unknown `source` never reaches any map, index,
      // log or cache. See feedVenueOf. Main's US guard (drop every `PMUS-`
      // ticker and any source but kalshi/polymarket here) is deliberately NOT
      // taken: on streamrift the feed gateway's Polymarket US lane
      // (`FEEDGW_PMUS_*`) pushes US rows through this ingest into the
      // `polyUsTicker` slot (buildPolyUsMoneylinePair). `feedVenueOf` admits
      // exactly kalshi / polymarket / polymarket_us, so an unknown source is
      // still fenced out.
      const venue = feedVenueOf(market.source);
      // ...and the ticker namespace must agree with the source for US rows
      // (main's guard against a `PMUS-` ticker cast as intl Polymarket, and
      // the reverse): a mislabelled frame never reaches either Poly slot.
      if (venue === null || (market.ticker.startsWith('PMUS-') !== (venue === 'polymarket_us'))) {
        _droppedUnknownSourceFrames++;
        return;
      }
      const proof = _sourceAuthority.capture(sourceFeedId, 'market', market);
      const priorPrice = _priceSourceProof.get(market.ticker);
      // Unchanged: a market frame whose proof sits at a RETIRED generation is
      // dropped whole — before any field or either proof changes — when a
      // current governing price proof is held. With none held it is admitted
      // for initial display and simply verdicts unconfirmed (#847).
      if (!_sourceAuthority.supersedes(priorPrice, proof)) return;
      // A1 — proof precedence. The price proof is the BOOK's whenever a usable
      // book governs this Kalshi row's bid/ask (the edge derives the frame's
      // yesBid/yesAsk from that same book state), so a market frame — with a
      // ticker proof or none — leaves it the book's; its own proof goes to the
      // ticker-scalar authority below. Judged on the incoming frame's identity
      // so a book that arrived before the row's first market frame counts.
      const heldBook = _bookSourceProof.get(market.ticker);
      const bookGoverns = market.source === 'kalshi' && heldBook != null && heldBook.feed === sourceFeedId
        && _sourceAuthority.verdict({ ...heldBook, row: { ...heldBook.row,
          source: market.source, sport: market.sport, feedSport: market.feedSport } }) === 'continuous';
      // Without a governing book: a proof-less frame (no row evidence at all —
      // `announced: false` and no lane to resolve a control from, which is
      // every market that has not quoted since an edge restart) never retires
      // a price proof that is current in this session (a withdrawn book waiting
      // on its control, or a bookless row's current ticker proof). Any other
      // frame's proof becomes the price proof exactly as before. The frame's
      // fields ingest regardless.
      const proofless = proof != null && !proof.announced && typeof proof.row.lane !== 'string';
      if (bookGoverns) _priceSourceProof.set(market.ticker, heldBook);
      else if (proofless && proofIsCurrent(priorPrice)) { /* keep the current price proof */ }
      else if (proof) _priceSourceProof.set(market.ticker, proof);
      else _priceSourceProof.delete(market.ticker);
      // The ticker-scalar authority follows every ADMITTED market frame — so a
      // proof-less frame truthfully reads unconfirmed, a current ticker frame
      // restores continuous, and a dropped retired replay (above) touches nothing.
      if (proof) _tickerSourceProof.set(market.ticker, proof);
      else _tickerSourceProof.delete(market.ticker);
      const now = Date.now();
      // Wire-shape guard before the wholesale store write: fold a
      // producer-violating canonicalTeams object into the tuple contract
      // (or strip it) so no downstream `.includes` can throw.
      coerceCanonicalTeams(market);
      if (market.source === 'kalshi') {
        _kalshiExchangeIndexDiagnostics.set(
          market.ticker,
          observeKalshiExchangeIndex(
            _kalshiExchangeIndexDiagnostics.get(market.ticker),
            market,
          ),
        );
      }
      // Wholesale replace — NOT a field-merge. This is what makes the DNL-002
      // transition safe by construction: an ambiguous re-stamp cannot leave a
      // stale `canonicalGameKeyV2` behind, and every numbered-key-derived
      // lookup re-derives from THIS record. (Contrast ingestOrderbook, which
      // deliberately merges.) Anything that starts merging market frames here
      // must clear the DH identity fields explicitly.
      const prevRecord = _markets.get(market.ticker);
      _withdrawnBookPrices.delete(market.ticker);
      const priceProof = _priceSourceProof.get(market.ticker);
      if (priceProof?.kind === 'orderbook') _bookPriceSourceFeed.set(market.ticker, priceProof.feed);
      else _bookPriceSourceFeed.delete(market.ticker);
      // #730 — the buckets this ticker sat in BEFORE this frame, so a frame
      // that moves a market between sports invalidates both.
      const prevSportKeys = _marketSportKeys.get(market.ticker);
      _markets.set(market.ticker, market);
      observeUsGatewayRow(market, sourceFeedId);
      // #1134: keep this account's display identity past the row's retirement.
      noteMarketIdentity(market);
      // Account orders can declare depth needs before the market frame arrives
      // (notably on route changes into props). Re-route on identity changes;
      // quote-only frames must never fan out subscription work.
      if (_activeOrderbookTickers.has(market.ticker) &&
          (!prevRecord || rowFeedSport(prevRecord) !== rowFeedSport(market) ||
           prevRecord.source !== market.source)) {
        _routeOrderbookNeeds(_activeOrderbookTickers);
      }

      // Only selected futures are tracked. Quote-only updates keep the draft;
      // an identity/status round trip between React paints cannot revive it.
      if (_futureSelectionVersions.has(market.ticker)) futureSelectionVersion(market);
      if (sourceFeedId != null) _marketSourceFeed.set(market.ticker, sourceFeedId);
      if (sourceFeedId != null && _pendingHandover.get(market.ticker)?.feedId === sourceFeedId) _pendingHandover.delete(market.ticker);
      // Before any key is derived: record gameNo ≥ 2 evidence for the base key.
      const newDhEvidence = witnessDhEvidence(market);
      // Drop memoized rows the moment a DH identity moves (see dhIdentityChanged)
      // so no consumer can read a row that still seats this ticker on its old
      // numbered game.
      //
      // `newDhEvidence` is here for `_getGamesCache`: getGames keys its rows
      // through gameKeyV2 → isDhAmbiguous, which READS `_dhBaseKeys`, so new
      // evidence can re-seat rows it has already built. The wire-key
      // invalidation beside it is incidental — `getGameWireKeys` promotes raw
      // and never consults `_dhBaseKeys` — so splitting these two clears would
      // be safe for that cache (#808).
      if (!prevRecord) invalidateGameWireKeys();
      // Ingest generation for the getPeriodMoneyline memo (F3):
      // a wholesale re-stamp changes row content (prices, #805 names) with no
      // identity move and no version bump until the flush, and a sync reader
      // in that window must still see it (totalsPanelNav #96). An integer
      // bump here; the memo compares it lazily on its next read.
      _gameRowIngestGen++;
      if (newDhEvidence || dhIdentityChanged(prevRecord, market)) {
        clearDerivedRowCaches();
        invalidateGameWireKeys();
        clearDerivedRowRevStamps();
      }
      if (market.conditionId) _tickerByConditionId.set(market.conditionId, market.ticker);
      if (market.yesTokenId) _tickerByTokenId.set(market.yesTokenId, market.ticker);
      if (market.noTokenId) _tickerByTokenId.set(market.noTokenId, market.ticker);
      if (market.pmusMarketSlug) _tickerByPmusSlug.set(market.pmusMarketSlug, market.ticker);
      indexMarketBySport(market);
      _lastReceiveMs.set(market.ticker, now);
      _globalLastReceiveMs = now;
      if (age) _marketSourceAge.set(market.ticker, retainSourceAge(_marketSourceAge.get(market.ticker), age));
      // Cache game key → start time. Polymarket Gamma and Kalshi milestone
      // frames can carry `eventStartTime`; siblings sharing the game read it
      // back via `getEventStartTimeMsByTicker`. Update policy is in
      // `updateStartTimeCache` — per-source slots resolved by fixed precedence.
      //
      // Keyed by the DOUBLEHEADER-SAFE v2 key, not the legacy key. Both games
      // of a doubleheader share ONE legacy key, so the legacy grain made the
      // within-source last-write-wins rule overwrite G1's start with G2's and
      // hand both tickers the nightcap time (live LAD/NYY, 2026-07-19). An
      // ordinary single game promotes to the same gameNo=1 key it would have
      // hashed to before, so this is grain-only — the per-source precedence
      // design is untouched. It also unifies the namespace with
      // `seedEventStartTimeMsByKey`, whose callers already pass v2 keys.
      //
      // An ambiguous frame (null key) writes NOTHING: it must not contribute a
      // start under a numbered key it may not belong to. Its own
      // `eventStartTime` is still readable off its record.
      const startKey = market.canonicalGameKey
        ? gameKeyV2(market, market.canonicalGameKey)
        : null;
      // Feed-named display names (#685). Same key ingestMarket already derived
      // for the start-time cache, so this costs a map hit on frames of a
      // subtitle-named sport and nothing at all on every other frame.
      indexSubtitleMatchup(market);
      if (startKey && market.eventStartTime) {
        const ms = Date.parse(market.eventStartTime);
        if (Number.isFinite(ms)) {
          updateStartTimeCache(startKey, ms, venue);
        }
      }
      pushFeedLog({
        ts: Date.now(),
        ticker: market.ticker,
        source: venue,
        sport: market.sport || '?',
        type: 'market',
        feedLabel: _feedLabelOf(sourceFeedId),
        yesBid: market.yesBid,
        yesAsk: market.yesAsk,
        volume: market.volume,
      });
      // Feed→screen probe ingest mark (watched tickers only — O(1) otherwise).
      probeFeedIngest(market.ticker, 'market', age?.publicationMs);
      // Game-grain ticks re-derive the live/sniper boards (moneyline, lines,
      // period books). A prop or other normally-excluded class (the Sniper
      // "More markets" sidecar, GameDetail's props tabs) is not on those
      // boards, so its frames must not re-derive them — a props torrent would
      // otherwise re-render the ladders it never touches. Those surfaces
      // re-render on the store `version` below, like every other reader.
      if (!isGameTickExcludedMarket(market)) markGameDirty(market.canonicalGameKey, startKey);
      touch(unionSportKeys(prevSportKeys, _marketSportKeys.get(market.ticker)));
      // The row is this feed's now: a book of theirs that arrived first goes
      // through the same gate it was refused by, and passes. (This is also
      // where main's `_heldBooks` replay lived — one park/replay map.)
      const pend = takePendingBook(market.ticker, sourceFeedId);
      if (pend) get().ingestOrderbook(market.ticker, pend.book, pend.feed, pend.age, pend.observation);
    },

    ingestOrderbook: (ticker, book, sourceFeedId, age, observation) => {
      // No `PMUS-` early return here (main's "dedicated US book adapter"
      // guard): the feed gateway's Polymarket US lane delivers US books
      // through this ingest on streamrift. See the venue fence in ingestMarket.
      // The envelope is authoritative; older book bodies omit its duplicate.
      if (book.ticker != null && book.ticker !== ticker) return;
      const market = _markets.get(ticker);
      // streamrift's auxiliary book sockets (the watched-game book lane, the
      // live/sniper lanes) dial the SAME engine that owns the market row and
      // carry that engine's books — they are the row's source, not a rival
      // feed. Fold them onto the owning feed BEFORE every main book gate
      // below (pair ownership, the retired-feed gate, source authority), or
      // every one of their frames is refused (node1, 2026-09-21: stale NFL
      // books on the game page the night of the merge).
      const rowFeed = _marketSourceFeed.get(ticker);
      if (rowFeed != null && sourceFeedId != null && isAuxiliaryBookFeed(sourceFeedId)) sourceFeedId = rowFeed;
      if (!_pairFeedServesRow(ticker, sourceFeedId, true)) return;
      // A retired feed's book must not replace prices selected from its successor.
      // Except the sport's SERVING edge with a serving orderbook lane: it is
      // the book's authority even before its row moves over from the floor
      // (main, dan.node1 2026-10-03). Inert on node1: fork edges announce no
      // #847 lanes and the gateway wears the firehose role.
      if ((_sourceAuthority.required(sourceFeedId) || _sourceAuthority.required(rowFeed))
        && market && sourceFeedId !== rowFeed
        && !(sourceFeedId != null && _servingEdgeBookAuthority(sourceFeedId, market))) {
        // ...but an engine's book that merely beat that engine's market frame
        // is parked, not lost — ingestMarket replays it when the row is theirs.
        if (sourceFeedId != null && _sourceAuthority.required(sourceFeedId)) pendBook(ticker, sourceFeedId, book, age, observation);
        return;
      }
      // Admitted from the row's own feed: nothing parked for it is newer.
      _pendingBooks.delete(ticker);
      // The row's RAW feed key rides with the capture (Danielle R1 DNL-001):
      // an ALT edge keys its book lane `kalshi-orderbook:NFLALT`, and the
      // stored row's `sport` is already folded to `NFL` for display — the
      // verdict must prove the lane against `feedSport`, or every ALT book
      // reads unconfirmed and is withheld from the venue book.
      const proof = _sourceAuthority.capture(sourceFeedId, 'orderbook', {
        ...observation, source: market?.source, sport: market?.sport, feedSport: market?.feedSport,
      });
      if (!_sourceAuthority.supersedes(_bookSourceProof.get(ticker), proof)
        || !_sourceAuthority.supersedes(_priceSourceProof.get(ticker), proof)) return;
      if (proof) _bookSourceProof.set(ticker, proof);
      else _bookSourceProof.delete(ticker);
      // B — per-row retention anchor: the FIRST marked frame under this
      // session/generation sets it; later marked frames leave it alone; the
      // clearing (unmarked) snapshot ends retention by removing it.
      if (proof?.row.pendingResync === true) {
        const anchor = _rowResyncSince.get(ticker);
        if (!anchor || anchor.session !== proof.session || anchor.sourceEpoch !== proof.row.sourceEpoch
          || anchor.sourceGen !== proof.row.sourceGen)
          _rowResyncSince.set(ticker, { session: proof.session, sourceEpoch: proof.row.sourceEpoch,
            sourceGen: proof.row.sourceGen, sinceMonoMs: performance.now() });
      } else _rowResyncSince.delete(ticker);
      const now = Date.now();
      // Captured before the overwrite so the stale-probe below can log
      // ms since the previous frame for this same ticker.
      const prevReceiveMs = _lastReceiveMs.get(ticker);
      _orderbooks.set(ticker, book);
      if (market) observeUsGatewayRow(market, sourceFeedId);
      _gameRowIngestGen++; // period-moneyline rows embed this book — see ingestMarket
      if (sourceFeedId != null) _bookSourceFeed.set(ticker, sourceFeedId);
      else _bookSourceFeed.delete(ticker);
      _lastReceiveMs.set(ticker, now);
      _lastOrderbookMs.set(ticker, now);
      if (sourceFeedId) _orderbookSourceFeed.set(ticker, sourceFeedId);
      _globalLastReceiveMs = now;
      if (age) _bookSourceAge.set(ticker, retainSourceAge(_bookSourceAge.get(ticker), age));

      // Derive price from orderbook (Hydra sends book updates far more often than market updates).
      // Kept unconditional (cheap, and keeps every market's bid/ask scalars warm
      // off the firehose) — the heavier per-frame work is gated below.
      const bestYesBid = book.yes?.length ? Math.max(...book.yes.map(l => l.price)) : null;
      const bestNoBid = book.no?.length ? Math.max(...book.no.map(l => l.price)) : null;
      const yesAsk = bestNoBid !== null ? mirrorCents(bestNoBid) : null;
      const existing = _markets.get(ticker);
      if (existing && (proof || bestYesBid !== null || yesAsk !== null)) {
        _withdrawnBookPrices.delete(ticker);
        if (sourceFeedId != null) _bookPriceSourceFeed.set(ticker, sourceFeedId);
        else _bookPriceSourceFeed.delete(ticker);
        if (proof) _priceSourceProof.set(ticker, proof);
        else _priceSourceProof.delete(ticker);
        existing.yesBid = bestYesBid ?? (proof ? undefined : existing.yesBid);
        existing.yesAsk = yesAsk ?? (proof ? undefined : existing.yesAsk);
        existing.noBid = bestNoBid ?? (proof ? undefined : existing.noBid);
        existing.noAsk = bestYesBid !== null ? mirrorCents(bestYesBid) : (proof ? undefined : existing.noAsk);
        if (!proof) existing.lastUpdateMs = Date.now(); // legacy-only receipt convention
      }

      // Gate render-trigger + per-frame derived work on whether a mounted view
      // actually needs this book. The SharedWorker keeps a ticker subscribed
      // for a 30s grace after the last view unmounts (SubscriptionAggregator
      // graceMs), so ~100 orphaned spread/total/F5 frames a second can keep
      // arriving for a game you've already left. The book is stored warm above
      // (instant on re-entry) and _lastReceiveMs/_lastOrderbookMs stay current
      // for the feed-health heuristics — but for a ticker no view is rendering
      // there's nothing to sample, log, or re-render. Bailing here is what stops
      // the back-from-game-detail render storm. (recordDepthSample is doubly
      // wasteful for these: setOrderbookNeeds already prunes depth-velocity
      // history to the active set, so a non-active sample has no live consumer.)
      // #754 — the book is ALREADY STORED above, so any cached derivation built
      // before it is now out of date, whether or not a view is rendering this
      // ticker. A synchronous reader can see that gap: `PositionTracker`'s
      // totals lookup reads `getGameEvents` across every sport, for markets no
      // board declared, and would otherwise be handed a row carrying the
      // previous book.
      //
      // Counter ONLY — no `touch()`, no `scheduleTick()` — so the bail below
      // keeps doing its job: nothing publishes, nothing re-renders, and the
      // back-from-game-detail storm it exists to stop is unaffected. The cost
      // is a lazy rebuild of that sport's rows on the next READ: measured at
      // one rebuild per sport per read over a 234-frame burst where 204 frames
      // were for non-active tickers, not one per frame.
      const bookSportKeys = _marketSportKeys.get(ticker);
      if (bookSportKeys && bookSportKeys.length > 0) {
        for (const sp of bookSportKeys) _sportMutationSeq.set(sp, (_sportMutationSeq.get(sp) ?? 0) + 1);
      } else {
        // No sport filed for this ticker yet: attribute it to nothing, which
        // the stamp reads as everything.
        _allSportMutationSeq += 1;
      }
      if (!_activeOrderbookTickers.has(ticker)) return;

      // Stale-probe diagnostic for the "ladder pinned, frames still
      // arriving" failure mode. Off by default — toggle in DevTools
      // with `localStorage.setItem('g1:staleProbe', '1')` then refresh
      // (production builds inline `import.meta.env.DEV = false`, so we
      // need a runtime switch to enable this on the deployed onions).
      // Logs per inbound frame: ticker, canonical game key (so we can
      // tell whether the row's lookup key matches the inbound), per-
      // side level counts AND best-bid prices (counts can churn while
      // top-of-book stays pegged — both signals are needed to tell
      // "data updating" from "data static, only mid-book churning"),
      // and ms since the previous frame for this ticker.
      if (typeof window !== 'undefined' && window.localStorage.getItem('g1:staleProbe') === '1') {
        const since = prevReceiveMs != null ? now - prevReceiveMs : null;
        console.log(
          `[stale-probe] ${ticker} canon=${existing?.canonicalGameKey ?? '?'} Y=${book.yes?.length ?? 0}(${bestYesBid ?? '-'}¢) N=${book.no?.length ?? 0}(${bestNoBid ?? '-'}¢) ${since != null ? `+${since}ms` : 'first'}`,
        );
      }

      // Per-side best-bid depth samples for bookDepthVelocity. Uses the
      // same sanity filter as ladder/helpers.bestBidDepthForSide so the
      // velocity Δ/min in the K Q / P Q columns reads consistently with
      // the displayed K Q depth. Stub bids far above last trade (Kalshi
      // sometimes leaves these dangling) are excluded so a stub being
      // pulled doesn't register as fake draining.
      //
      // CRITICAL: when bestRealBidLevel returns null (best bid drained
      // entirely or only stub bids remain), we still record `0` instead
      // of skipping. The drain-to-zero event is the most actionable
      // sweep signal — skipping the sample leaves the velocity stuck on
      // the last non-zero depth and the cell shows stale data right
      // when the trader needs the alert. Caught by GPT validation
      // 2026-05-25 post-paired-velocity push (a4bd4ff).
      const lastTradeYesCents = existing?.lastPrice ?? null;
      const lastTradeNoCents = lastTradeYesCents != null ? mirrorCents(lastTradeYesCents) : null;
      const yesBestReal = bestRealBidLevel(book.yes, lastTradeYesCents);
      const noBestReal = bestRealBidLevel(book.no, lastTradeNoCents);
      recordDepthSample(depthKey(ticker, 'yes'), yesBestReal?.size ?? 0);
      recordDepthSample(depthKey(ticker, 'no'), noBestReal?.size ?? 0);

      // Orderbook frames carry no `source`: the venue is the market record's,
      // else the ticker namespace (PMUS- before PM-). Unknown → no log row
      // rather than a mislabeled one.
      const bookVenue = feedVenueOf(existing?.source) ?? prefixVenue(ticker);
      if (bookVenue !== null) {
        pushFeedLog({
          ts: Date.now(),
          ticker,
          source: bookVenue,
          sport: existing?.sport || '?',
          type: 'orderbook',
          feedLabel: _feedLabelOf(sourceFeedId),
          yesBid: bestYesBid ?? undefined,
          yesAsk: yesAsk ?? undefined,
          bookLevels: (book.yes?.length ?? 0) + (book.no?.length ?? 0),
        });
      }
      // Feed→screen probe ingest mark. Behind the active-needs gate like the
      // dirty mark below; gated again inside on the live view's watched set,
      // so the fleet's other books cost a Map.size read. Wire reference:
      // the frame's publication stamp (main's SourceAge) when the client forwarded it, else the book's own
      // timestamp.
      probeFeedIngest(ticker, 'orderbook', age?.publicationMs ?? book.timestamp, age?.ageMs != null ? { ageMs: age.ageMs } : null);
      // Book frames dominate a live game's tick rate — mark the game so the
      // single-game view repaints its ladders. Behind the active-needs gate
      // above by design: the live view declares its tickers via
      // setOrderbookNeeds, and grace-period orphans shouldn't tick anyone.
      if (existing?.canonicalGameKey) {
        markGameDirty(
          existing.canonicalGameKey,
          gameKeyV2(existing, existing.canonicalGameKey),
        );
      }
      touch(_marketSportKeys.get(ticker) ?? 'all');
    },

    ingestConsensus: (consensus, serverTs, sourceFeedId, age) => {
      // Keyed by (gameKey, periodTypeId) so in-period values (Q1 / inning /
      // set) coexist with the Full Game read consumers show by default. Use the
      // doubleheader-safe v2 key (falling back to the legacy key promoted to the
      // gameNo=1 form) so a doubleheader's two games — which share one legacy
      // canonicalGameKey — cache separately instead of clobbering each other.
      const cacheGameKey = promoteToGameKeyV2(
        consensus.canonicalGameKeyV2 ?? consensus.canonicalGameKey,
      );
      // marketType segments the 'advance' 2-way apart from the 1X2 result frame
      // (both share gameKey + periodTypeId=1) — see consensusCacheKey.
      const key = consensusCacheKey(cacheGameKey, consensus.periodTypeId, consensus.marketType);

      // Trend/recency state folds in EVERY frame, before the value dedup:
      // tenth-only frames and sharpBookUsed flips are real updates even when
      // the integer fields below are unchanged. Date the frame by its
      // server-side age (serverTs − lastUpdateMs: 0 on a live broadcast,
      // minutes on a snapshot re-delivery of a quiet market) so recency
      // reflects when the value was computed, not when it was re-sent.
      const now = Date.now();
      // #847 — prefer the source-qualified age when the transport supplied
      // it: it carries the same server-side age PLUS any SharedWorker cache
      // residence and port delivery, so a greet replay of a quiet value seeds
      // as old as it really is instead of flashing as if just computed. The
      // same-source subtraction stays as the fallback (direct paths without
      // evidence, or evidence whose clock is unknown/discontinuous).
      const sourceStaleMs = age ? sourceAgeNow(age) : null;
      const stalenessMs = sourceStaleMs != null
        ? sourceStaleMs
        : serverTs != null ? Math.max(0, serverTs - consensus.lastUpdateMs) : 0;
      const prevTrend = _consensusTrendByBucket.get(key) ?? null;
      const nextTrend = ingestConsensusTrendFrame(
        prevTrend,
        trendFrameFromConsensus(consensus, now - stalenessMs),
        now,
      );
      const trendChanged = nextTrend !== prevTrend;
      if (trendChanged) _consensusTrendByBucket.set(key, nextTrend);

      // Value cache dedup — a redundant re-emit from the snapshot burst on
      // reconnect can still land. Tenths + sharpBookUsed participate: a frame
      // that only moved a tenth (or only flipped the anchor) must replace the
      // cached object so `useConsensusFor` consumers re-render.
      const prev = _consensusByGameKeyAndPeriod.get(key);
      if (
        prev
        && prev.awayFairProbCents === consensus.awayFairProbCents
        && prev.homeFairProbCents === consensus.homeFairProbCents
        && prev.drawFairProbCents === consensus.drawFairProbCents
        && prev.awayFairProbTenths === consensus.awayFairProbTenths
        && prev.homeFairProbTenths === consensus.homeFairProbTenths
        && prev.drawFairProbTenths === consensus.drawFairProbTenths
        && prev.sharpBookUsed === consensus.sharpBookUsed
        && prev.contributingBooks === consensus.contributingBooks
      ) {
        if (trendChanged) {
          markGameDirty(consensus.canonicalGameKey, cacheGameKey);
          touch('publish-only');
        }
        return;
      }
      _consensusByGameKeyAndPeriod.set(key, consensus);
      // Sport tag is sourced off the canonical key prefix (cheaper than
      // walking _markets — every canonical game key starts with sport).
      const sportFromKey = consensus.canonicalGameKey.split('|', 1)[0] ?? '?';
      pushFeedLog({
        ts: Date.now(),
        ticker: consensus.canonicalGameKey,
        source: 'unabated',
        sport: sportFromKey,
        type: 'consensus',
        feedLabel: _feedLabelOf(sourceFeedId),
        awayProbCents: consensus.awayFairProbCents,
        homeProbCents: consensus.homeFairProbCents,
        awayProbTenths: consensus.awayFairProbTenths,
        homeProbTenths: consensus.homeFairProbTenths,
        drawProbTenths: consensus.drawFairProbTenths ?? undefined,
        sharpBookUsed: consensus.sharpBookUsed,
        periodTypeId: consensus.periodTypeId,
      });
      markGameDirty(consensus.canonicalGameKey, cacheGameKey);
      touch('publish-only');
    },

    ingestOpOdds: (op) => {
      // Tombstone frames route through deleteOpOdds; tolerate one arriving
      // here (defensive — the stager splits the callbacks).
      const changed = op.markets.length === 0
        ? deleteOddsGame(_opLane, op.canonicalGameKeyV2 || op.canonicalGameKey)
        : writeOddsGame(_opLane, op);
      if (changed) {
        // Feed→screen probe: stamped only for a game a view is watching (one
        // Map.size read otherwise). Both key generations — the watcher may hold
        // either.
        probeFeedIngest(opProbeKey(op.canonicalGameKeyV2 || op.canonicalGameKey), 'opOdds', op.lastUpdateMs);
        if (op.canonicalGameKeyV2 && op.canonicalGameKey) {
          probeFeedIngest(opProbeKey(op.canonicalGameKey), 'opOdds', op.lastUpdateMs);
        }
        // OP tape (FeedTape "OddsPapi" column): full-game moneyline de-vig
        // consensus tenths when the frame carries them, plus book count.
        const fgMl = op.markets.find(
          (m) => m.marketType === 'moneyline' && m.periodTypeId === 1,
        );
        pushFeedLog({
          ts: Date.now(),
          ticker: op.canonicalGameKeyV2 || op.canonicalGameKey,
          source: 'oddspapi',
          sport: (op.sport ?? '').toLowerCase(),
          type: 'opOdds',
          feedLabel: _feedLabelOf(_opActiveFeedId),
          // Headline (full-game moneyline) books only — summing the full
          // spread/total ladders (~50 markets/game since 2026-08-11) turned
          // the tape's book count into noise.
          bookCount: fgMl?.books.length ?? 0,
          opTombstone: op.markets.length === 0 || undefined,
          awayProbCents: fgMl?.consensus?.awayFairProbCents,
          homeProbCents: fgMl?.consensus?.homeFairProbCents,
          awayProbTenths: fgMl?.consensus?.awayFairProbTenths,
          homeProbTenths: fgMl?.consensus?.homeFairProbTenths,
          drawProbTenths: fgMl?.consensus?.drawFairProbTenths ?? undefined,
        });
        markGameDirty(
          op.canonicalGameKey,
          op.canonicalGameKeyV2,
          promoteToGameKeyV2(op.canonicalGameKeyV2 || op.canonicalGameKey),
        );
        touch('publish-only');
      }
    },

    deleteOpOdds: (gameKeyV2) => {
      if (deleteOddsGame(_opLane, gameKeyV2)) {
        pushFeedLog({
          ts: Date.now(),
          ticker: gameKeyV2,
          source: 'oddspapi',
          sport: gameKeyV2.split('|')[0] ?? '',
          type: 'opOdds',
          feedLabel: _feedLabelOf(_opActiveFeedId),
          opTombstone: true,
        });
        touch('publish-only');
      }
    },

    replaceOpOdds: (ops) => {
      // Atomic replace: rebuild the whole map from the committed set. No
      // per-entry dedup needed — but skip the tick when the result is
      // byte-identical (snapshot re-delivery of unchanged state).
      if (replaceOddsLane(_opLane, ops)) {
        // One summary row per EFFECTIVE snapshot commit (byte-identical
        // re-deliveries skip it) — per-game rows here would wipe the ring.
        pushFeedLog({
          ts: Date.now(),
          ticker: 'snapshot',
          source: 'oddspapi',
          sport: '',
          type: 'opOdds',
          feedLabel: _feedLabelOf(_opActiveFeedId),
          opSnapshotGames: _opLane.byGameKey.size,
        });
        touch('publish-only');
      }
    },

    ingestActiveSports: (sports, sourceId = 'default', filterAxes, activeFeeds) => {
      const announcer = _feedClients.find((f) => f.id === sourceId);
      if (announcer?.opOnly) return;
      // Proposed-axes advertisement rides the same frame (g1xl-v2#1053
      // Part 3): recorded verbatim per feed, absent = nothing advertised.
      if (filterAxes != null) _filterAxesBySource.set(sourceId, new Set(filterAxes));
      else _filterAxesBySource.delete(sourceId);
      // Normalize hydra's uppercase wire codes to g1 Sport ids and filter to
      // the known registry — the frame can carry codes g1 doesn't render
      // (e.g. GOLF); filter, don't error. Each frame is the complete
      // replacement set for ITS feed, never a delta.
      // RAW feed keys (D5.2): `CFBALT` normalizes to `cfbalt`, never to
      // `cfb` — the ALT edge owns its own key. Display folds later.
      const next = new Set<FeedSport>();
      for (const code of sports) {
        const norm = normalizeFeedSport(code);
        if (norm) next.add(norm);
      }

      // OWNERSHIP first (edge sources only): the live served set takes every
      // announce at face value — an empty frame means "serving nothing right
      // now" and hands the edge's sports back to the firehose immediately.
      // The display continuity below must NOT apply here: trusting a cached
      // non-empty set through an edge cold restart deduped OG's MLB frames
      // against an edge serving nothing (2026-07-09, MLB dark tab-wide).
      // Dynamic ownership: a changed set moves per-sport ownership between
      // edge and firehose (e.g. the Feeds tuner flips MLB primary/blocked).
      // Re-route book subscriptions and snapshot the side that just took
      // sports over. Fresh connects (livePrev == null) skip the backfill —
      // the connect ops already snapshot.
      if (announcer != null && announcer.role !== 'firehose') {
        const livePrev = _edgeLiveAnnounce.get(sourceId) ?? null;
        const prevPairs = _edgeLiveFeeds.get(sourceId);
        const nextPairs = activeFeeds === undefined ? undefined : new Set(activeFeeds.map(p => feedPairKey(p.sport, p.venue)));
        const pairChanged = (prevPairs === undefined) !== (nextPairs === undefined) ||
          (nextPairs !== undefined && (prevPairs?.size !== nextPairs.size || [...nextPairs].some(p => !prevPairs?.has(p))));
        const liveChanged = pairChanged ||
          livePrev == null || livePrev.size !== next.size || ![...next].every((s) => livePrev.has(s));
        // Read before the change, and only for a real one: the identical
        // announce an engine re-sends on every subscribe op (each sport
        // toggle's filter change) costs nothing here.
        const claimsBefore = liveChanged ? _claimedPairs(sourceId) : new Set<string>();
        const ownersBefore = liveChanged && _directoryFollow ? _pairOwners(claimsBefore) : null;
        if (nextPairs === undefined) _edgeLiveFeeds.delete(sourceId);
        else _edgeLiveFeeds.set(sourceId, nextPairs);
        if (liveChanged) {
          _edgeLiveAnnounce.set(sourceId, next);
          const claimsAfter = _claimedPairs(sourceId);
          const lost = [...claimsBefore].filter((k) => !claimsAfter.has(k));
          for (const k of claimsAfter) if (_directoryAwaiting.get(k) === sourceId) _directoryAwaiting.delete(k);
          if (_directoryFollow && announcer.role === 'edge') _awaitDirectory(sourceId, lost);
          _usBoardQuotes.retireUnowned();
          clearDerivedRowCaches();
          clearDerivedRowRevStamps();
          _routeOrderbookNeeds(_activeOrderbookTickers);
          _ownershipListener?.();
          if (livePrev != null) {
            const asked = new Set<string>();
            if (pairChanged || [...next].some((s) => !livePrev.has(s))) {
              announcer.client.requestResnapshot();
              asked.add(announcer.id);
            }
            if (pairChanged || [...livePrev].some((s) => !next.has(s))) {
              const floor = _bookFloor(null, _firehoseFeed());
              floor?.client.requestResnapshot();
              if (floor) asked.add(floor.id);
            }
            if (ownersBefore) _resnapshotTakers(ownersBefore, claimsAfter, asked);
          }
          // Directory follow, triggers a + b: an edge added or removed a pair
          // claim (a named primary going silent on a pair is one of these).
          if (_directoryFollow && announcer.role === 'edge' &&
            (lost.length > 0 || [...claimsAfter].some((k) => !claimsBefore.has(k)))) {
            _directoryTriggerListener?.('announce');
          }
          touch('all');
        }
      }

      // DISPLAY (sport chips): an empty frame while this feed holds a
      // non-empty set is hydra's post-restart cold cache (~1-2 min window) —
      // keep the last non-empty set; the refill frame replaces it when
      // discovery completes. Keyed by sourceId so the firehose and edge pipes
      // union (getActiveSports) instead of clobbering. Flow labels collapse
      // onto their parent here (NFLPROPS → nfl): one chip per tab, while the
      // ownership set above keeps the raw label so arbitration stays per
      // label. Main's ALT pseudo-sports (D5.2) stay RAW here (`cfbalt` — the
      // FeedTape chip reads `he6 CFBALT`); getActiveSports folds those with
      // baseSportOf.
      const display = new Set<FeedSport>();
      for (const s of next) display.add(isAltFeedSport(s) ? s : displaySportOf(s));
      const prev = _activeSportsBySource.get(sourceId) ?? null;
      if (display.size === 0 && prev != null && prev.size > 0) return;
      if (prev != null && prev.size === display.size && [...display].every((s) => prev.has(s))) {
        return; // duplicate frame (re-sent on every subscribe op) — no tick
      }
      _activeSportsBySource.set(sourceId, display);

      touch('all');
    },

    getFeedFilterAxes: (feedId) => _filterAxesBySource.get(feedId) ?? NO_AXES,

    setConnected: (connected) => set({ connected }),

    setBackpressureLevel: (level) => set({ backpressureLevel: level }),

    noteKeepalive: () => {
      _lastKeepaliveMs = Date.now();
    },

    getLastKeepaliveMs: () => _lastKeepaliveMs,
    feedShouldIngest: (feedId, sport, venue, ticker, previewOnly = false) => {
      const fc = _feedClients.find((f) => f.id === feedId);
      if (fc?.opOnly) return false;
      const staticallyAccepts = fc?.accepts(sport) ?? false;
      // A claimant is any non-firehose feed — 'core' and 'edge' arbitrate
      // identically, and the pure truth table models both as 'edge'.
      const isFirehose = fc?.role === 'firehose';
      const isClaimant = fc != null && !isFirehose;
      // _edgeServing is consulted on the firehose's non-static path exactly as
      // before, and additionally for a claimant that accepts the sport — that
      // second call is what arbitrates between two claimants.
      const owner =
        fc == null ? null
          : isFirehose
            ? (staticallyAccepts ? null : _edgeServing(sport, venue))
            : (staticallyAccepts ? _edgeServing(sport, venue) : null);
      const edge = isFirehose ? owner : null;
      const hasSportAndVenue = sport != null && venue != null;
      const edgeDeliveringVenue =
        edge != null && sport != null && venue != null
          ? (_pairManaged(sport, venue) || _edgeDeliveringVenue(edge.id, sport, venue))
          : false;

      // ── D10 FLOOR eligibility (rule 6 as amended; Dan 2026-08-18) ──
      // A CORE frame OUTSIDE the core's claim scope is the thin firehose.
      // Eligible only when the sport has no serving owner anywhere: no edge
      // serving it and the firehose (OG) not announcing it. Recomputed per
      // frame, so an owner's return shuts the floor off on the next frame —
      // structurally unable to preempt. First ANNOUNCING core in fleet
      // order wins the sport so two floating cores never double-ingest.
      // Broad-and-INGESTED beats broad-and-discarded (the June sportFilter
      // ruling, extended): floor sports pre-populate so first click
      // renders, ownerless sports gain their only source, and a dropped
      // edge degrades to thin prices instead of blank.
      let coreFloorEligible = false;
      if (fc != null && fc.role === 'core' && !staticallyAccepts && sport != null) {
        const fh = _firehoseFeed();
        // Keyed by FEED key: OG announces base sports only, so an ALT key
        // (`cfbalt`) never matches here — OG carries no ALT rows, and the
        // floor is the ALT fallback by construction (spec D2.4, R7).
        const fhServes =
          !(venue != null && _pairManaged(sport, venue)) &&
          fh != null && fh.role === 'firehose' && fh.client.connected &&
          (_activeSportsBySource.get(fh.id)?.has(sport) ?? false);
        // Two-tier: an edge that serves the sport but is not currently
        // ASKING for it (the sport is off screen) yields the sport to the
        // floor's thin tier; the edge takes it back the moment it asks.
        const serving = _edgeServing(sport, venue);
        if (!fhServes && (serving == null || !_feedRequests(serving.id, sport, ticker))) {
          const floorCore = _feedClients.find(
            (f) =>
              f.role === 'core' && !f.opOnly && f.client.connected &&
              (venue == null ? (_edgeLiveAnnounce.get(f.id)?.has(sport) ?? false) : _announcesPair(f.id, sport, venue)),
          );
          coreFloorEligible = floorCore?.id === feedId;
        }
      }

      // Pair-aware claimants must prove their own announce and the selected owner.
      // Out-of-scope cores continue through the D10 floor path below.
      if (isClaimant && staticallyAccepts && sport != null && venue != null && _pairManaged(sport, venue)) {
        return owner?.id === feedId;
      }

      const decision = decideMarketFrameAdmission({
        feedRole: fc == null ? null : isFirehose ? 'firehose' : 'edge',
        staticallyAccepts,
        edgeServing: edge != null,
        hasSportAndVenue,
        edgeDeliveringVenue,
        ownedByOtherClaimant: isClaimant && owner != null && owner.id !== fc.id,
        coreFloorEligible,
      });

      // Venue-aware fallback: the edge announces the sport but has stopped
      // delivering this venue (e.g. Kalshi toggled off edge-side while poly
      // keeps flowing) — resume it on the firehose. On the ownership flip,
      // re-route books and snapshot the firehose so quiet tickers backfill.
      if (!previewOnly && decision.reason === 'firehose-venue-fallback' && sport != null && venue != null) {
        const key = `${sport}|${venue}`;
        if (_venueOwnedByEdge.get(key) === true) {
          _venueOwnedByEdge.set(key, false);
          _routeOrderbookNeeds(_activeOrderbookTickers);
          _bookFloor(sport, _firehoseFeed(), venue)?.client.requestResnapshot();
        }
      }

      return decision.admit;
    },

    noteFeedDelivery: (feedId, sport, venue) => {
      if (sport == null) return;
      const fc = _feedClients.find((f) => f.id === feedId);
      if (!fc || fc.role === 'firehose') return;
      const wasDelivering = _edgeDeliveringVenue(feedId, sport, venue);
      _edgeVenueDeliveryMs.set(`${feedId}|${sport}|${venue}`, Date.now());
      if (_edgeServing(sport, venue)?.id !== feedId) return;
      // First delivery for this (sport, venue) since the firehose held it —
      // the edge takes the pair over; move its book subscriptions across.
      const key = `${sport}|${venue}`;
      if (_venueOwnedByEdge.get(key) !== true) {
        _venueOwnedByEdge.set(key, true);
        _routeOrderbookNeeds(_activeOrderbookTickers);
      } else if (!wasDelivering) {
        // The venue had lapsed (no frame within the TTL, no serving lane): a
        // routing pass in between sent its books to the firehose, and the
        // flag above never re-fires — they stranded there until an unrelated
        // reroute. The first frame brings them back.
        _routeOrderbookNeeds(_activeOrderbookTickers);
      }
    },

    noteDirectoryAssignments: (assignments, assignmentAuthority) => {
      const previousPairs = new Map(_directoryPairAssigned);
      _directoryPairAssigned.clear();
      if (assignmentAuthority === true) {
        for (const a of assignments) for (const p of a.feeds ??
          a.sports.flatMap(sport => FEED_VENUES.map(venue => ({sport, venue})))) {
          const key = feedPairKey(p.sport, p.venue);
          const ids = _directoryPairAssigned.get(key) ?? new Set<string>();
          ids.add(a.feedId);
          _directoryPairAssigned.set(key, ids);
        }
      }
      // The directory caught up (it names someone else, or nobody): stop
      // waiting on it.
      for (const [key, feedId] of _directoryAwaiting) {
        if (!_directoryPairAssigned.get(key)?.has(feedId)) _directoryAwaiting.delete(key);
      }
      _directoryAssigned.clear();
      for (const { feedId, sports } of assignments) {
        for (const sport of sports) {
          const set = _directoryAssigned.get(sport) ?? new Set<string>();
          set.add(feedId);
          _directoryAssigned.set(sport, set);
        }
      }
      _usBoardQuotes.retireUnowned();
      clearDerivedRowCaches();
      clearDerivedRowRevStamps();
      const changedPairs = new Set([...previousPairs.keys(), ..._directoryPairAssigned.keys()]);
      const changedFeeds = new Set<string>();
      let pairChanged = false;
      for (const key of changedPairs) {
        const before = previousPairs.get(key);
        const after = _directoryPairAssigned.get(key);
        if (before?.size === after?.size && [...before ?? []].every(id => after?.has(id))) continue;
        pairChanged = true;
        for (const id of before ?? []) changedFeeds.add(id);
        for (const id of after ?? []) changedFeeds.add(id);
      }
      if (pairChanged) {
        const floor = _bookFloor(null, _firehoseFeed());
        floor?.client.requestResnapshot();
        for (const id of changedFeeds) {
          if (id !== floor?.id) _feedClients.find(f => f.id === id)?.client.requestResnapshot();
        }
      }
      _ownershipListener?.();
      // Ownership may have moved, so books follow and the firehose backfills
      // anything it is about to hand over or take back.
      _routeOrderbookNeeds(_activeOrderbookTickers);
      touch('all');
    },

    noteOpFeedHealth: (health) => {
      if (get().opFeedHealth === health) return;
      set({ opFeedHealth: health });
    },

    noteOpActiveFeed: (feedId) => {
      if (_opActiveFeedId === feedId) return;
      _opActiveFeedId = feedId;
      touch('all');
    },

    noteFeedStatus: (feedId, connected) => {
      const fc = _feedClients.find((f) => f.id === feedId);
      if (!fc || fc.role === 'firehose') return;
      // Dedicated OP transport: no sport claims, no book subscriptions —
      // its transitions concern only the OP failover controller (useHydra)
      // and the FeedTape chip, never the claimant re-route machinery. But
      // the chip DOES need a repaint (connected is read at snapshot time).
      if (fc.opOnly === true) {
        touch('all');
        return;
      }
      // A drop invalidates the live served set — ownership returns only when
      // the fresh connection's announce arrives (contractually before any
      // market frame). Restoring from a pre-drop cache here resurrected stale
      // ownership after an edge cold restart (the announce that followed was
      // empty and the old guard ignored it) — that's the MLB-dark bug.
      const claimsBefore = _claimedPairs(feedId);
      const ownersBefore = !connected && _directoryFollow ? _pairOwners(claimsBefore, feedId) : null;
      // Directory follow, trigger c: a named primary's or a sole claimant's
      // connection moved — judged on the claims it held before a drop.
      const followTrigger = _directoryFollow && fc.role === 'edge' &&
        (_namedPrimary(feedId) || _soleClaimant(feedId, claimsBefore));
      if (!connected && _directoryFollow && fc.role === 'edge') _awaitDirectory(feedId, claimsBefore);
      if (!connected) {
        _edgeLiveAnnounce.delete(feedId);
        // Retain pair-mode capability, but withdraw every live claim.
        if (_edgeLiveFeeds.has(feedId)) _edgeLiveFeeds.set(feedId, new Set());
        // Per-venue delivery is connection state too — a dead socket delivers
        // nothing, and a reconnect must re-prove delivery frame by frame.
        for (const key of [..._edgeVenueDeliveryMs.keys()]) {
          if (key.startsWith(`${feedId}|`)) _edgeVenueDeliveryMs.delete(key);
        }
        // Reset the transition detector too, or the reconnect's first
        // delivery sees a stale "already owned" flag and skips the book
        // re-route back to the edge.
        _venueOwnedByEdge.clear();
      }
      // Ownership just moved (edge ⇄ firehose) — re-route book subscriptions.
      _routeOrderbookNeeds(_activeOrderbookTickers);
      _ownershipListener?.();
      // Backfill the same market floor that receives the book needs. OG is
      // retained only for a legacy fleet without a market-capable Core.
      if (!connected) {
        const floor = _bookFloor(null, _firehoseFeed());
        floor?.client.requestResnapshot();
        // …and a sole claimant that took a pair over: snapshot that one too.
        if (ownersBefore) _resnapshotTakers(ownersBefore, new Set(), new Set(floor ? [floor.id] : []));
      }
      if (followTrigger) _directoryTriggerListener?.('connection');
      // Ownership-dependent UI (FeedTape server chips) keys off `version`.
      touch('all');
    },

    ownerFeedForSport: (sport) => {
      if (_feedClients.length === 0) return null;
      const edge = _edgeServing(sport);
      if (edge != null) return edge.id;
      const firehose = _firehoseFeed();
      if (
        firehose != null && firehose.client.connected &&
        (_activeSportsBySource.get(firehose.id)?.has(sport) ?? false)
      ) {
        return firehose.id;
      }
      // D10 floor: a connected core ANNOUNCING the sport outside its claim
      // scope covers it thin — same first-in-fleet-order pick as the gate.
      const floorCore = _feedClients.find(
        (f) =>
          f.role === 'core' && f.client.connected && !f.accepts(sport) &&
          (_edgeLiveAnnounce.get(f.id)?.has(sport) ?? false),
      );
      return floorCore?.id ?? null;
    },

    applyFilterReset: (feedId, scope, pair) => {
      _usBoardQuotes.reset(feedId, raw => rowPassesFilter(scope, raw as unknown as HydraMarket));
      const fc = _feedClients.find((f) => f.id === feedId);
      let removed = 0;
      let demoted = 0;
      for (const [ticker, m] of _markets) {
        // Only rows THIS connection delivered are its to retire (DNL-002):
        // ownership is dynamic — the firehose legitimately carries an edge's
        // sport while that edge is down — so provenance, not the static
        // claim scope, decides. A row whose latest accepted delivery came
        // from ANOTHER feed is that feed's to keep or retire. Rows with no
        // recorded provenance fall back to the claim scope. An unknown feed
        // id (fleet rewired underneath a late reset) retires nothing — the
        // replacement snapshot still repopulates whatever the live feed
        // serves.
        if (fc == null) continue;
        const src = _marketSourceFeed.get(ticker);
        if (src != null ? src !== feedId : !fc.accepts(rowFeedSport(m))) continue;
        if (rowPassesFilter(scope, m)) continue;
        // An EDGE narrowing: the floor serves this row again — keep it.
        const floor = fc.role === 'edge' ? _floorCoreFor(rowFeedSport(m)) : null;
        if (floor != null) {
          demoteToFloor(ticker, floor.id);
          demoted++;
          continue;
        }
        deleteMarketRecord(ticker);
        removed++;
      }
      if (removed > 0 || demoted > 0) {
        // Memoized game rows may seat a retired ticker — drop them so no
        // consumer reads a row the feed no longer serves.
        clearDerivedRowCaches();
        invalidateGameWireKeys();
        clearDerivedRowRevStamps();
      }
      // The reset precedes its ack on the wire (worker order), so the entry
      // may not exist yet — create it so the removal count survives the ack.
      const st = _feedFilterState.get(feedId) ?? {
        feedId, socketIncarnation: pair?.socketIncarnation ?? '', filterGeneration: pair?.filterGeneration ?? 0,
        effective: null, error: null, ackMs: 0, lastResetRemoved: 0, resetRemovedTotal: 0,
        engineRemovedTotal: 0,
      };
      st.lastResetRemoved = removed;
      st.resetRemovedTotal += removed;
      if (pair) { st.socketIncarnation = pair.socketIncarnation; st.filterGeneration = pair.filterGeneration; }
      _feedFilterState.set(feedId, st);
      touch('all');
      return removed;
    },

    getGameWireKeys: (gameKey) => {
      if (!gameKey) return [];
      const hit = _gameWireKeysCache.get(gameKey);
      const out = new Set<string>(hit ?? []);
      // Sport vocabulary is folded on BOTH sides (g1 id vs hydra spelling —
      // `cfb` vs `ncaaf`). Callers reach this from two directions: a resolved
      // `GameRow.key`, already in wire spelling, and a key composed from route
      // params, in g1 spelling. Comparing raw made the second one match nothing
      // on every aliased sport, which is the "allow-list assembled from route
      // text matches nothing" failure the games axis is supposed to avoid
      // (#797). `gameKeyMatchesUrl` has folded for this reason since he2's
      // canonical keys landed; this is the same policy on key-vs-key.
      const wanted = foldGameKeySport(gameKey);
      if (!hit) for (const m of _markets.values()) {
        // Rule 1/2 (hydra 2026-09-04): prefer V2 when the row carries it, fall
        // back to the legacy key only when it is absent, and never invent one.
        // A row carrying neither cannot be addressed on this axis at all.
        const wireKey = m.canonicalGameKeyV2 ?? m.canonicalGameKey ?? null;
        if (!wireKey) continue;
        // Group the row the way the selectors do, so "this game's rows" means
        // the same set the page renders. Only the sport segment folds — date,
        // gameNo and teams stay exact, so a fold can never merge two games.
        if (foldGameKeySport(promoteToGameKeyV2(wireKey)) !== wanted) continue;
        // The value is the WIRE key, never the folded one: the engines match on
        // the exact string they delivered.
        out.add(wireKey);
      }
      // Only a non-empty answer is cached: a game whose rows have not arrived
      // yet must be able to answer once they do.
      const baseKeys = hit ?? [...out].sort();
      if (!hit && baseKeys.length > 0) _gameWireKeysCache.set(gameKey, baseKeys);
      // US metadata has a separate replacement/owner lifecycle. Merge its
      // exact producer wire key after the K/P membership cache, without
      // invalidating or rescanning K/P for each US price update.
      const usSport = usGameSportForBoard(gameKey.split('|')[0]);
      if (usSport) {
        const normalized = normalizeUsGameKey(gameKey, usSport);
        if (normalized) {
          const usKeys = _usSlugIdentity.published() ? _usSlugIdentity.wireKeys(normalized) : [];
          for (const wireKey of usKeys) out.add(wireKey);
        }
      }
      if (out.size === baseKeys.length) { _usGameWireKeysCache.delete(gameKey); return baseKeys; }
      const keys = [...out].sort(), previous = _usGameWireKeysCache.get(gameKey);
      if (previous?.length === keys.length && previous.every((key, i) => key === keys[i])) return previous;
      _usGameWireKeysCache.set(gameKey, keys);
      return keys;
    },

    applyMarketRemoved: (feedId, ticker, pair) => {
      // Stale-pair removals are droppable by construction (hydra DNL-003):
      // a withdrawal describes the scope of ONE generation on ONE socket.
      // Once we hold a newer pair, an older removal is about a scope this
      // store no longer reflects, and acting on it would delete a row the
      // current generation legitimately re-delivered. No recorded ack yet
      // (the reset precedes it) means nothing to contradict — fall through.
      const st = _feedFilterState.get(feedId);
      if (st != null && st.ackMs > 0
        && (st.socketIncarnation !== pair?.socketIncarnation || st.filterGeneration !== pair?.filterGeneration)) {
        return false;
      }
      // streamrift: no `PMUS-` short-circuit into main's browser-transport US
      // quote adapter (c7723f4e0, not taken on this lane) — gateway US rows live
      // in `_markets` and are withdrawn by the provenance path below. Main's
      // #1092 book retirement runs for every venue here, US included.
      get().withdrawBook(feedId, ticker);
      const m = _markets.get(ticker);
      if (!m) return false;
      // Provenance, not the static claim scope (DNL-002): only the feed whose
      // delivery is authoritative for this row may withdraw it. The firehose
      // legitimately carries an edge's sport while that edge is down, and an
      // edge must not delete a row the floor owns.
      const fc = _feedClients.find((f) => f.id === feedId);
      if (fc == null) return false;
      const src = _marketSourceFeed.get(ticker);
      if (src != null ? src !== feedId : !fc.accepts(rowFeedSport(m))) return false;
      const removedSportKeys = _marketSportKeys.get(ticker);
      deleteMarketRecord(ticker, _bookSourceFeed.get(ticker));
      // Memoized game rows may seat the withdrawn ticker (same reason as a
      // reset) — drop them so no consumer reads a row the feed withdrew.
      clearDerivedRowCaches();
      invalidateGameWireKeys();
      clearDerivedRowRevStamps();
      if (st != null) st.engineRemovedTotal += 1;
      touch(removedSportKeys ?? 'all');
      return true;
    },

    noteFeedFilterAck: (feedId, ack) => {
      const prev = _feedFilterState.get(feedId);
      // Floor→edge authority handoff (Danielle R3 DNL-006): an ACCEPTED edge
      // generation that asks for a sport shuts the floor out of that sport
      // from this ack on — but stopping future floor frames does not revoke
      // floor-owned state already in the store, and a floor-only ticker the
      // edge's replacement snapshot never overwrites would stay resident
      // and renderable indefinitely. So, before any frame of this
      // generation can land (the ack precedes them in wire order), retire
      // every row a CORE floor delivered for a sport this edge now asks for
      // and can own. Rejected acks change nothing.
      const fc = _feedClients.find((f) => f.id === feedId);
      if (fc != null && fc.role === 'edge' && !ack.error && ack.effective) {
        const scope = ack.effective.sports;
        // A tickers-scoped generation (feed-filters-per-view) asks for exact
        // rows: only a floor-delivered row it LISTS is taken over (its
        // replacement snapshot carries it); the sport's other floor rows are
        // untouched — the edge never asked for them. See _feedRequests.
        const listed = ack.effective.tickers && ack.effective.tickers.length > 0 ? new Set(ack.effective.tickers) : null;
        const asksFor = (ticker: string, sport: FeedSport): boolean =>
          fc.accepts(sport) && (listed ? listed.has(ticker) : (!scope || scope.some((code) => normalizeFeedSport(code) === sport)));
        const coreIds = new Set(_feedClients.filter((f) => f.role === 'core').map((f) => f.id));
        let retired = 0;
        const deadline = Date.now() + HANDOVER_GRACE_MS;
        for (const [ticker, m] of _markets) {
          const src = _marketSourceFeed.get(ticker);
          if (src == null || !coreIds.has(src)) continue;
          const sport = rowFeedSport(m);
          if (sport == null || !asksFor(ticker, sport)) continue;
          if (_pairManaged(sport, m.source) && _edgeServing(sport, m.source)?.id !== feedId) continue;
          // On screen (a view holds its book): handed over, never blanked.
          if (_activeOrderbookTickers.has(ticker)) {
            if (_pendingHandover.get(ticker)?.feedId !== feedId) _pendingHandover.set(ticker, { feedId, deadline });
            continue;
          }
          deleteMarketRecord(ticker, feedId);
          retired++;
        }
        _armHandoverSweep();
        if (retired > 0) {
          clearDerivedRowCaches();
          invalidateGameWireKeys();
          clearDerivedRowRevStamps();
          if (prev) prev.resetRemovedTotal += retired;
        }
      }
      _feedFilterState.set(feedId, {
        feedId,
        socketIncarnation: ack.socketIncarnation,
        filterGeneration: ack.filterGeneration,
        effective: ack.error ? (prev?.effective ?? null) : (ack.effective ?? null),
        error: ack.error ?? null,
        ackMs: Date.now(),
        lastResetRemoved: prev?.lastResetRemoved ?? 0,
        resetRemovedTotal: prev?.resetRemovedTotal ?? 0,
        engineRemovedTotal: prev?.engineRemovedTotal ?? 0,
      });
      _ownershipListener?.(); // the installed scope decides who asks for what
      touch('all');
    },

    getFeedFilterStates: () =>
      _feedClients
        .map((fc) => _feedFilterState.get(fc.id))
        .filter((st): st is FeedFilterState => st != null),

    getFeedRowCounts: (feedId) => {
      const counts = new Map<string, number>();
      for (const [ticker, src] of _marketSourceFeed) {
        if (src !== feedId) continue;
        const m = _markets.get(ticker);
        if (!m) continue;
        const key = rowFeedSport(m) ?? '?';
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      return [...counts].map(([sport, rows]) => ({ sport, rows })).sort((a, b) => b.rows - a.rows || a.sport.localeCompare(b.sport));
    },

    floorSoleSourceSports: (viewed) => {
      const out: FeedSport[] = [];
      const claimants = _feedClients.filter((f) => f.role !== 'firehose' && f.opOnly !== true);
      // Every display sport AND its ALT key: a split sport's ALT edge going
      // down leaves the floor sole source for the ALT lines on screen.
      const universe = viewed ?? new Set<FeedSport>(
        Object.values(SPORT_NORMALIZE).flatMap((s) => [s, altTokenFor(s)]),
      );
      for (const sport of universe) {
        const claimed = claimants.some((f) => f.role === 'edge' && f.accepts(sport));
        // US book delivery still runs through the relay consumer. Do not
        // accelerate every browser floor sport for an unwired third venue.
        if (claimed && (['kalshi', 'polymarket'] as const).some(v => _edgeServing(sport, v) == null)) out.push(sport);
      }
      return out;
    },

    floorAltClaims: () => {
      const alts = [...new Set(Object.values(SPORT_NORMALIZE))].map((s) => altTokenFor(s));
      const sole = new Set(get().floorSoleSourceSports(new Set(alts)));
      return alts
        .filter((t) => _feedClients.some((f) => f.role === 'edge' && f.opOnly !== true && f.accepts(t)))
        .map((t) => ({ sport: t, served: !sole.has(t) }));
    },

    setOwnershipListener: (fn) => { _ownershipListener = fn; },

    setDirectoryFollow: (on) => {
      if (_directoryFollow === on) return;
      _directoryFollow = on;
      _directoryAwaiting.clear();
      _usBoardQuotes.retireUnowned();
      clearDerivedRowCaches();
      clearDerivedRowRevStamps();
      _routeOrderbookNeeds(_activeOrderbookTickers);
      _ownershipListener?.();
      touch('all');
    },

    setDirectoryTriggerListener: (fn) => { _directoryTriggerListener = fn; },

    getDirectoryAwaitEpoch: () => (_directoryAwaiting.size > 0 ? _directoryAwaitEpoch : 0),

    getFeedOwnership: (venue) => {
      if (_feedClients.length === 0) return [];
      if (venue == null && (_directoryPairAssigned.size > 0 || _edgeLiveFeeds.size > 0)) {
        const merged = new Map<string, FeedOwnership>();
        for (const v of FEED_VENUES) for (const row of get().getFeedOwnership(v)) {
          const old = merged.get(row.id);
          if (!old) merged.set(row.id, {...row});
          else for (const key of ['sports', 'idleClaims', 'darkClaims', 'floorSports'] as const) {
            old[key] = [...new Set([...(old[key] ?? []), ...(row[key] ?? [])])].sort();
          }
        }
        return [...merged.values()];
      }
      // Universe = every sport any feed has announced. OG's announce includes
      // edge-only sports it doesn't carry game data for (e.g. NFL), which is
      // what lets a downed edge's claims show up as idle instead of vanishing.
      // FEED keys, raw: an ALT edge's chip reads `he6 CFBALT` (D5.5).
      const universe = new Set<FeedSport>();
      for (const s of _activeSportsBySource.values()) for (const sport of s) universe.add(sport);
      const firehose = _firehoseFeed();
      const owned = new Map<string, FeedSport[]>(_feedClients.map((fc) => [fc.id, []]));
      // Can the firehose actually SERVE this sport? Its own announce is the
      // only honest answer. OG stopped carrying the migrated sports
      // (NFL/MLB/SOCCER), so the old code's assumption — "anything an edge
      // isn't serving falls back to OG" — now names OG as the owner of sports
      // it has no data for. The tape then reads "OG MLB" while nothing
      // arrives, which is worse than showing nothing: it sends someone
      // debugging a dead venue to the wrong engine.
      //
      // BOTH predicates, not just the announce (DNL-R12-P1): the display
      // cache in _activeSportsBySource deliberately survives disconnects
      // (the cold-cache rule), so a DOWN firehose still has cached sports —
      // and crediting them to OG masks a total outage as OG ownership, the
      // headline lie through a different path. Connection health gates
      // ownership here exactly as it does for edges in _edgeServing.
      const firehoseUp = firehose != null && firehose.client.connected;
      const firehoseAnnounced = firehose ? _activeSportsBySource.get(firehose.id) : undefined;
      const firehoseServes = (sport: FeedSport) => firehoseUp && (firehoseAnnounced?.has(sport) ?? false);

      // Sports NOBODY is serving in this view. Reported separately so the UI
      // can render them honestly dark rather than attributing them to a feed.
      const dark: FeedSport[] = [];
      // D10 floor coverage per core feed (announced, unclaimed, ownerless).
      const floor = new Map<string, FeedSport[]>();
      for (const sport of universe) {
        const edge = _edgeServing(sport, venue);
        // Venue view: the edge only shows as owner while actually delivering
        // that venue (same rule as the ingest gate / book routing).
        if (edge != null && (venue == null || _pairManaged(sport, venue) || _edgeDeliveringVenue(edge.id, sport, venue))) {
          owned.get(edge.id)!.push(sport);
          continue;
        }
        if (firehose && firehoseServes(sport) && !(venue != null && _pairManaged(sport, venue))) {
          owned.get(firehose.id)!.push(sport);
          continue;
        }
        // D10 floor before dark: a connected core ANNOUNCING the sport
        // outside its claim scope is covering it thin — honest degraded
        // state, not darkness. Same first-in-fleet-order pick as the
        // ingest gate, so the chip matches what actually ingests.
        const floorCore = _feedClients.find(
          (f) =>
            f.role === 'core' && !f.opOnly && f.client.connected && !f.accepts(sport) &&
            (venue == null ? (_edgeLiveAnnounce.get(f.id)?.has(sport) ?? false) : _announcesPair(f.id, sport, venue)),
        );
        if (floorCore) {
          (floor.get(floorCore.id) ?? floor.set(floorCore.id, []).get(floorCore.id))!.push(sport);
          continue;
        }
        dark.push(sport);
      }
      // ONE deterministic claimant per dark sport (DNL-R12-P2): listing it on
      // every accepting feed sent an operator toward multiple supposed owners
      // and broke the exactly-once invariant. Selection mirrors
      // _edgeServing's authority order — core's assignment first (a
      // preference, never an authority), then env-config order. A dark sport
      // nobody claims stays unmapped and falls to the firehose entry.
      const darkOwner = new Map<FeedSport, string>();
      for (const sport of dark) {
        const preferred = venue == null ? _directoryAssigned.get(sport) :
          _directoryPairAssigned.get(feedPairKey(sport, venue)) ?? _directoryAssigned.get(sport);
        let chosen =
          preferred != null && preferred.size > 0
            ? _feedClients.find((f) => f.role !== 'firehose' && preferred.has(f.id) && f.accepts(sport))
            : undefined;
        chosen ??= _feedClients.find((f) => f.role !== 'firehose' && f.accepts(sport));
        if (chosen) darkOwner.set(sport, chosen.id);
      }
      return _feedClients.map((fc) => {
        const sports = (owned.get(fc.id) ?? []).sort();
        return {
          id: fc.id,
          role: fc.role,
          connected: fc.client.connected,
          label: fc.label,
          url: fc.url,
          opAuthority: fc.opAuthority,
          opActive: fc.opAuthority === true ? fc.id === _opActiveFeedId : undefined,
          opOnly: fc.opOnly,
          opFullFeed: fc.opFullFeed,
          opPreferred: fc.opPreferred,
          opRank: fc.opRank,
          sports,
          // Claimed but not serving, AND the firehose covers it — data still
          // flows, just from OG. This is the benign case.
          idleClaims: fc.role !== 'firehose'
            ? [...universe]
                .filter((s) => fc.accepts(s) && !sports.includes(s) && !dark.includes(s))
                .sort()
            : [],
          // Nobody is serving these AT ALL. Attributed to exactly ONE
          // claimant (darkOwner — assignment preference, then config order),
          // so the tape points at a single engine that should be producing;
          // a dark sport no feed claims falls to the firehose entry rather
          // than vanishing from the display entirely. Exactly-once across
          // sports ∪ darkClaims is the invariant.
          darkClaims: fc.role !== 'firehose'
            ? dark.filter((s) => darkOwner.get(s) === fc.id).sort()
            : dark.filter((s) => !darkOwner.has(s)).sort(),
          floorSports: (floor.get(fc.id) ?? []).sort(),
        };
      });
    },

    setFeedClients: (feeds) => {
      _usBoardQuotes.retainFeeds(feeds.map(feed => feed.id));
      _sourceAuthority.retain(new Set(feeds.map(feed => feed.id)));
      _feedClients = feeds;
      _usBoardQuotes.retireUnowned();

      // Filter ack state belongs to a transport; a rewired fleet starts clean.
      for (const id of [..._feedFilterState.keys()]) {
        if (!feeds.some((f) => f.id === id)) _feedFilterState.delete(id);
      }
      // Live ownership state must not outlive its feed (unmount/remount
      // rewires clients; a stale entry would grant a fresh socket ownership
      // before its first announce).
      for (const id of [..._edgeLiveAnnounce.keys()]) {
        if (!feeds.some((f) => f.id === id)) _edgeLiveAnnounce.delete(id);
      }
      for (const id of [..._edgeLiveFeeds.keys()]) {
        if (!feeds.some(f => f.id === id)) _edgeLiveFeeds.delete(id);
      }
      for (const id of [..._filterAxesBySource.keys()]) {
        if (!feeds.some((f) => f.id === id)) _filterAxesBySource.delete(id);
      }
      // Replay any needs that views declared before the clients mounted,
      // routed to each feed's owned tickers. Refresh the render-gate mirror in
      // lockstep so orderbook frames arriving before the next setOrderbookNeeds
      // still tick the views that need them.
      _activeOrderbookTickers = _unionOrderbookNeeds();
      _routeOrderbookNeeds(_activeOrderbookTickers);
    },

    setBookLaneSink: (sink) => {
      _bookLaneSink = sink;
      _routeOrderbookNeeds(_activeOrderbookTickers);
    },

    setOrderbookNeeds: (viewId, tickers) => {
      const key = tickers.length === 0 ? '' : tickers.slice().sort().join('|');
      if ((_viewOrderbookNeedKeys.get(viewId) ?? '') === key) return;
      if (tickers.length === 0) {
        _viewOrderbookNeeds.delete(viewId);
        _viewOrderbookNeedKeys.delete(viewId);
      } else {
        _viewOrderbookNeeds.set(viewId, new Set(tickers));
        _viewOrderbookNeedKeys.set(viewId, key);
      }
      const active = _unionOrderbookNeeds();
      _activeOrderbookTickers = active;
      _routeOrderbookNeeds(active);
      declareOrderbookRows(active);
      // Prune bookDepthVelocity history for (ticker, side) keys no
      // longer in the active subscription set so long sessions don't
      // retain stale module-level Maps as the user sweeps across many
      // sport tabs / dates. Each ticker expands to yes+no keys.
      // GPT validation 2026-05-25 caught this as orphaned cleanup.
      const activeKeys = new Set<string>();
      for (const t of active) {
        activeKeys.add(depthKey(t, 'yes'));
        activeKeys.add(depthKey(t, 'no'));
      }
      pruneDepthVelocity(activeKeys);
    },

    requestResnapshotIfStale: (maxAgeMs: number) => {
      // Keep the legacy readiness order, but odds traffic cannot establish
      // whether a market transport has completed its initial connection.
      const fc = _feedClients.find(f => f.role === 'firehose' && f.opOnly !== true)
        ?? _feedClients.find(f => f.opOnly !== true);
      if (!fc) return;
      if (fc.client.lastMessageMs === 0) return; // initial load — let the connect snapshot carry it
      const total = _markets.size;
      if (total < ACTIVE_FRACTION_MIN_POP) return; // not enough population for the fraction to mean anything
      const cutoff = Date.now() - maxAgeMs;
      let active = 0;
      for (const ticker of _markets.keys()) {
        const ms = _lastReceiveMs.get(ticker);
        if (ms !== undefined && ms > cutoff) active++;
      }
      if (active / total < ACTIVE_FRACTION_THRESHOLD) {
        // Population fraction is global → refresh every market feed.
        for (const f of _feedClients) if (f.opOnly !== true) f.client.requestResnapshot();
      }
    },

    getActivityCount: (maxAgeMs: number, sports = null) => {
      const cutoff = Date.now() - maxAgeMs;
      let active = 0;
      let total = 0;
      for (const [ticker, m] of _markets) {
        if (sports != null) {
          const sp = normalizeSport(m.sport ?? '');
          if (sp == null || !sports.has(sp)) continue;
        }
        total++;
        const ms = _lastReceiveMs.get(ticker);
        if (ms !== undefined && ms > cutoff) active++;
      }
      return { active, total };
    },

    getGames: (sport: Sport, marketType: MarketType = 'moneyline', filters): GameRow[] => {
      // #730 — the result cache below is stamped by THIS sport's revision
      // (`revBySport`), not the global `version`; see the stamp check.

      // Multi-outcome sports (soccer 3-way) don't fit the binary away/home
      // GameRow — they're served by getMultiOutcomeEvents instead.
      if (MULTI_OUTCOME_SPORTS.has(sport) || STANDALONE_SPORTS.has(sport)) return [];

      const statusFilter = filters?.statusFilter ?? 'active';
      const dateFilter = filters?.dateFilter ?? 'today';
      const selectedDateIso = filters?.selectedDateIso;
      // Last day of the bounded view, inclusive. Absent — or malformed, see
      // normalizeRangeEndIso — means a single day (#712), expressed below as
      // the degenerate window [d, d] rather than as a second code path.
      const rangeEndIso = normalizeRangeEndIso(filters?.rangeEndIso);

      // Result cache (see _getGamesCache): identical {sport, marketType,
      // filters} sweeps within one store snapshot collapse to a single rebuild.
      // Payload stamp = (sport revision, gameStatesStore.byGameKey identity).
      // The separate clock-second stamp below covers time-derived fields.
      //
      // `_gameWireKeysCache` deliberately does NOT ride this stamp (#808). It
      // answers a MEMBERSHIP question — which wire keys a game's own rows carry
      // — and `version` bumps on every price flush, so clearing it here made a
      // mounted GameDetail rescan all of `_markets` (a regex parse per row) at
      // flush cadence to re-derive a byte-identical answer. Its own boundary is
      // `invalidateGameWireKeys`, which covers every input that can move the
      // result: insertion, exact-key/restamp/V2-promotion/ambiguity flips
      // (dhIdentityChanged compares both keys), new doubleheader evidence, and
      // every removal path (all funnel through deleteMarketRecord). The price
      // lane cannot reach it — a market frame is a wholesale `_markets.set`
      // replace, and the one in-place mutation of a resident record
      // (`ingestOrderbook`) writes price scalars only.
      //
      // Do not re-attach it here. That answer is CROSS-SPORT — it scans every
      // market, not one sport's bucket — so a per-sport/per-lane split of this
      // stamp (#730 AC-1, #695) would under-invalidate it.
      const statesMap = primaryGameStateEnabled() ? useCoreGameStatesStore.getState().records
        : useGameStatesStore.getState().byGameKey;
      // Payload revisions do not cover scheduled state, date membership or
      // freshness expiry. Bound those derived values to the existing 1s
      // display clock, independently of whether this sport receives a frame.
      // This never delays a payload invalidation; that still uses sportRev.
      const clockSecond = Math.floor(Date.now() / 1000);
      if (_getGamesCacheClockSecond !== clockSecond) {
        _getGamesCache.clear();
        _getGamesCacheClockSecond = clockSecond;
      }
      // #730 — the stamp is PER SPORT (AC-1): an unrelated sport's frame bumps
      // its own revision and leaves this sport's cached rows in place. A
      // game-state map change still drops every sport (it is not attributed).
      const sportRev = get().revBySport[sport] ?? 0;
      if (_getGamesCacheStates !== statesMap) {
        _getGamesCache.clear();
        _getGamesCacheRevBySport.clear();
        _getGamesCacheStates = statesMap;
      }
      if (_getGamesCacheRevBySport.get(sport) !== sportRev) {
        const prefix = `${sport}\u0000`;
        for (const k of [..._getGamesCache.keys()]) if (k.startsWith(prefix)) _getGamesCache.delete(k);
        _getGamesCacheRevBySport.set(sport, sportRev);
      }
      // U+0000 delimiter, written as an ESCAPE and not as a literal source byte
      // (#565). A raw NUL made grep/ripgrep classify this whole 5.5k-line file as
      // binary and return no matches SILENTLY; the escape is the identical runtime
      // character, so every cache key is exactly what it was.
      //
      // The delimiter is what stops two field tuples flattening into the same key.
      // It earns its keep here: the two trailing fields are UNVALIDATED strings
      // -- mdyToIso only splits on '/' and rejoins -- and they are now ADJACENT,
      // so without a separator a window ending on one date could key the same as
      // a differently-split pair. Keep every free-form field at the tail, behind
      // its own delimiter.
      const cacheKey = `${sport}\u0000${marketType}\u0000${statusFilter}\u0000${dateFilter}\u0000${selectedDateIso ?? ''}\u0000${rangeEndIso ?? ''}`;
      const cached = _getGamesCache.get(cacheKey);
      if (cached) return cached;

      // Pass 1: Group by game. Date filter applies at parse-time (cheap); status
      // filter defers to Pass 3 since we need to see all markets for the game
      // before deciding if any are still open.
      const games = new Map<string, GameAccum>();

      // Tennis tour routing fallback. Hydra now resolves ITF gender on its side
      // and emits sport "ATP"/"WTA" on the Polymarket tennis frame (only
      // "Tennis" when genuinely unknown), so the per-market block below routes
      // Poly tennis by m.sport. This map is the fallback for a frame hydra still
      // tags "Tennis": borrow the tour from the paired Kalshi side (which knows
      // it via m.sport) by canonicalGameKey. Built only for the tennis tabs.
      const tennisTourByKey = new Map<string, Sport>();
      if (sport === 'atp' || sport === 'wta') {
        // Both tour buckets hold every tennis market (marketSportKeys files
        // ambiguous tennis under both), so scanning this tab's bucket sees all
        // the Kalshi tennis sides we need to borrow tours from.
        for (const m of marketsForSport(sport)) {
          if (m.source !== 'kalshi' || !m.canonicalGameKey?.startsWith('tennis|') || !m.sport) continue;
          const tour = SPORT_NORMALIZE[m.sport];
          if (tour === 'atp' || tour === 'wta') tennisTourByKey.set(m.canonicalGameKey, tour);
        }
      }

      for (const market of marketsForSport(sport)) {
        // Series-keyed path. Hydra tags every series-related frame
        // (Kalshi KX*SERIES*, KX*SERIESSCORE*, KX*SERIESROADWIN*; Polymarket
        // *-playoffs-who-will-win-series-* slugs) with canonicalSeriesKey =
        // "{sport}|series|{YY}|TEAM_A-TEAM_B" (alpha-sorted, normalized).
        // We collapse cross-venue series tickers onto one row by that key,
        // matching the canonicalGameKey pattern used for per-day games.
        // Round number isn't in the key (Polymarket slug doesn't carry it);
        // we backfill from the Kalshi winner ticker's R{N} segment when
        // present. Only the SERIES winner family populates the row's pricing
        // pills today; SCORE / ROADWIN families share the key but their
        // book contributions are out of scope for this row.
        if (market.canonicalSeriesKey) {
          if (marketType !== 'moneyline') continue;
          const cskParts = market.canonicalSeriesKey.split('|');
          if (cskParts.length !== 4 || cskParts[1] !== 'series') continue;
          const cskSport = SPORT_NORMALIZE[cskParts[0]];
          if (cskSport !== sport) continue;
          const cskYear = cskParts[2];
          const cskTeams = cskParts[3].split('-');
          if (cskTeams.length !== 2) continue;
          const key = market.canonicalSeriesKey;
          if (!games.has(key)) {
            games.set(key, {
              sport, date: `9999-9-${cskYear}`,
              awayCode: cskTeams[0], homeCode: cskTeams[1],
              title: market.title || '',
              kalshiAwayTicker: null, kalshiHomeTicker: null, polyTicker: null, polyUsTicker: null,
              startTimeMs: null,
              series: { round: 0, year: cskYear },
            });
          }
          const g = games.get(key)!;
          // Track the latest seriesWins / seriesWinsToClinch across every
          // frame for this canonical series — including SCORE/ROADWIN/Poly
          // tickers, which share the key but don't drive the row's Y/N pills.
          if (market.seriesWins) {
            g.seriesWins = market.seriesWins;
          }
          if (market.seriesWinsToClinch != null) {
            g.seriesWinsToClinch = market.seriesWinsToClinch;
          }
          if (market.source === 'kalshi') {
            const scoreTicker = parseKalshiSeriesScoreTicker(market.ticker);
            if (scoreTicker) {
              const orderedTeams = orderedSeriesTeamsFromScoreTicker(
                scoreTicker.teamChunk,
                [cskTeams[0], cskTeams[1]],
              );
              if (orderedTeams && (g.awayCode !== orderedTeams[0] || g.homeCode !== orderedTeams[1])) {
                const oldAway = g.kalshiAwayTicker;
                const oldHome = g.kalshiHomeTicker;
                g.awayCode = orderedTeams[0];
                g.homeCode = orderedTeams[1];
                g.kalshiAwayTicker = oldHome;
                g.kalshiHomeTicker = oldAway;
              }
              if (g.series && g.series.round === 0 && scoreTicker.round != null) {
                g.series.round = scoreTicker.round;
                g.date = `9999-${scoreTicker.round}-${cskYear}`;
              }
              const yesTeam = market.canonicalYesTeam ?? scoreTicker.teamCode;
              const winsToClinch = market.seriesWinsToClinch ?? g.seriesWinsToClinch;
              const seriesWins = market.seriesWins ?? g.seriesWins;
              if (
                seriesScoreIsCurrentClincher(
                  scoreTicker,
                  yesTeam,
                  [cskTeams[0], cskTeams[1]],
                  seriesWins,
                  winsToClinch,
                )
              ) {
                if (yesTeam === g.awayCode) g.kalshiAwayTicker = market.ticker;
                else if (yesTeam === g.homeCode) g.kalshiHomeTicker = market.ticker;
              }
            } else {
              const parsed = parseTicker(market);
              if (isDirectKalshiTeamSeriesMarket(market)) {
                // Direct championship/conference futures (KXNBA-26-SAS,
                // KXNBAWEST-26-OKC, KXNHL-26-CAR, ...) do not carry a
                // round in the ticker. Newer Hydra builds canonicalize them
                // only when the event's active outcomes have narrowed to a
                // two-team matchup, so canonicalYesTeam is safe to project
                // directly onto the row.
                if (market.canonicalYesTeam === g.awayCode) g.kalshiAwayTicker = market.ticker;
                else if (market.canonicalYesTeam === g.homeCode) g.kalshiHomeTicker = market.ticker;
              } else if (parsed && parsed.seriesRound != null) {
                // canonicalSeriesKey is alpha-sorted; the Kalshi ticker
                // preserves the actual game's away→home order (team1 in
                // the ticker is the away team). Re-orient the row to the
                // Kalshi order when it differs from the alpha default,
                // swapping any tickers we've already accumulated so they
                // stay attached to the right team.
                if (g.awayCode !== parsed.awayCode || g.homeCode !== parsed.homeCode) {
                  const oldAway = g.kalshiAwayTicker;
                  const oldHome = g.kalshiHomeTicker;
                  g.awayCode = parsed.awayCode;
                  g.homeCode = parsed.homeCode;
                  g.kalshiAwayTicker = oldHome;
                  g.kalshiHomeTicker = oldAway;
                }
                if (parsed.outcomeCode === g.awayCode) g.kalshiAwayTicker = market.ticker;
                else if (parsed.outcomeCode === g.homeCode) g.kalshiHomeTicker = market.ticker;
                if (g.series && g.series.round === 0) {
                  g.series.round = parsed.seriesRound;
                  g.date = `9999-${parsed.seriesRound}-${cskYear}`;
                }
              }
            }
            // ROADWIN families share canonicalSeriesKey but parse either as
            // null or with seriesRound undefined; intentionally skipped here.
          } else if (market.source === 'polymarket') {
            g.polyTicker = market.ticker;
          } else if (market.source === 'polymarket_us') {
            g.polyUsTicker = market.ticker;
          }
          continue;
        }

        const parsed = parseTicker(market);
        if (!parsed || parsed.marketType !== marketType) continue;
        // Tour routing for Polymarket tennis. The PM-itf-* slug is gender-less
        // (parses to atp by default), so prefer hydra's resolved tour from
        // m.sport ("ATP"/"WTA"). When hydra couldn't resolve it (m.sport
        // "Tennis" → not in SPORT_NORMALIZE), fall back to borrowing the tour
        // from the paired Kalshi side via canonicalGameKey. Non-tennis tabs and
        // non-Poly markets fall through to the parsed sport untouched.
        let effSport = parsed.sport;
        if (market.source === 'polymarket' && (sport === 'atp' || sport === 'wta')) {
          const hydraTour = market.sport ? SPORT_NORMALIZE[market.sport] : undefined;
          if (hydraTour === 'atp' || hydraTour === 'wta') {
            effSport = hydraTour;
          } else if (market.canonicalGameKey) {
            const mapped = tennisTourByKey.get(market.canonicalGameKey);
            if (mapped) effSport = mapped;
          }
        }
        if (effSport !== sport) continue;
        // Full-game rows only. Period markets (NBA 1H) carry the full-game
        // canonicalGameKey and would otherwise collide on this accumulator
        // (g.polyTicker is last-write-wins below) — the 0cf7549 regression.
        // They surface via getPeriodMoneyline / getGameEvents(…, '1H') instead.
        // Trust BOTH the ticker-derived period and hydra's market.period so a
        // parser miss on a future period shape still can't leak into full game.
        if (resolveMarketPeriod(market, parsed) !== 'full') continue;
        const marketStartMs = parseStartTimeMs(market.eventStartTime);
        const eventDate = formatStartDateLocal(marketStartMs) ?? parsed.date;

        // NOTE: the selected-date filter is NOT applied here. It runs per ROW
        // at the top of pass 2 — a market's own date must never decide whether
        // it joins its row. See that gate for the rainout case that proves it.

        // Per-day games prefer the doubleheader-safe canonicalGameKeyV2 (splits
        // the two games of a doubleheader that share one canonicalGameKey),
        // falling back to canonicalGameKey (cross-venue alias collapse, e.g.
        // Kalshi CHW vs Polymarket CWS) promoted to the gameNo=1 form, then the
        // parsed shape. Series tickers without canonicalSeriesKey (older hydra)
        // fall through here too and key by parsed-shape — Kalshi-only fallback,
        // no Poly merge until canonicalSeriesKey lands.
        const key = parsed.seriesRound != null
          ? `${parsed.sport}|series|${parsed.seriesYear}|R${parsed.seriesRound}|${parsed.awayCode}|${parsed.homeCode}`
          : gameKeyV2(
            market,
            market.canonicalGameKey
              ?? `${parsed.sport}|${parsed.date}|${parsed.awayCode}|${parsed.homeCode}`,
          );
        // DNL-001 fail-closed: hydra proved this is a doubleheader day but
        // could not say which game — so this frame enters NEITHER row. It stays
        // in `_markets` (diagnostics/feed log can still see it); it just never
        // becomes tradeable surface under a game it may not belong to.
        if (key == null) continue;
        if (!games.has(key)) {
          games.set(key, {
            sport, date: eventDate,
            awayCode: parsed.awayCode, homeCode: parsed.homeCode,
            title: market.title || '',
            kalshiAwayTicker: null, kalshiHomeTicker: null, polyTicker: null, polyUsTicker: null,
            startTimeMs: null,
            series: parsed.seriesRound != null && parsed.seriesYear != null
              ? { round: parsed.seriesRound, year: parsed.seriesYear }
              : undefined,
          });
        }
        const g = games.get(key)!;

        // Upgrade accumulator codes off raw Polymarket slug aliases
        // (NYL→NY, POR→PDX, LAS→LV, GSV→GS for WNBA; same family for the
        // other sports) when hydra has canonicalized them. Kalshi-side
        // canonicalAwayCode/canonicalHomeCode is game-day-positional;
        // canonicalTeams is alpha-sorted only — prefer Kalshi when
        // present and fall back to canonicalTeams when the current codes
        // are still slug-raw. The final row-build path already prefers
        // kalshi canonical codes, so this keeps g.awayCode/g.homeCode
        // consistent for downstream consumers (e.g. buildPolyBooks's
        // canonicalYesTeam comparison).
        if (market.canonicalAwayCode && market.canonicalHomeCode) {
          g.awayCode = market.canonicalAwayCode;
          g.homeCode = market.canonicalHomeCode;
        } else if (
          market.canonicalTeams
          && !market.canonicalTeams.includes(g.awayCode)
        ) {
          [g.awayCode, g.homeCode] = market.canonicalTeams;
        }

        if (parsed.source === 'kalshi') {
          if (parsed.side === 'away') g.kalshiAwayTicker = market.ticker;
          else g.kalshiHomeTicker = market.ticker;
        } else if (parsed.source === 'polymarket') {
          g.polyTicker = market.ticker;
        } else if (parsed.source === 'polymarket_us') {
          g.polyUsTicker = market.ticker;
        }
        // Any other venue accumulates into NO slot (the F0 fence already
        // dropped it at ingest; this is belt-and-braces).

        // Event start time comes from hydra's `eventStartTime` (Polymarket
        // Gamma or Kalshi milestones). Accumulated first-non-null here as a
        // FALLBACK ONLY — the authoritative value is the per-source precedence
        // resolution applied to the row key just below.
        if (g.startTimeMs == null && marketStartMs != null) {
          g.startTimeMs = marketStartMs;
          g.date = formatStartDateLocal(marketStartMs) ?? g.date;
        }
      }

      // Pass 1.1: resolve each row's start through the per-source precedence
      // cache, keyed by the row's own (doubleheader-safe) key.
      //
      // Pre-fix the row took the FIRST non-null `eventStartTime` in market
      // iteration order and never consulted the cache, so the displayed start
      // depended on Kalshi/Poly frame arrival order — on the postponed
      // LAD/NYY opener (2026-07-19) a Poly frame landing first put the stale
      // 7/18-evening Gamma time on a tradeable card while the cache held the
      // correct Kalshi milestone. The cache resolves kalshi > polymarket
      // pre-start (and Unabated once the game has started), so routing the row
      // through it makes the displayed start order-independent AND venue-
      // correct. Series rows key by series key, miss the cache, and keep their
      // accumulated value untouched.
      for (const [rowKey, g] of games) {
        const resolved = _startTimeMsByGameKey.get(rowKey);
        if (resolved != null && resolved !== g.startTimeMs) {
          g.startTimeMs = resolved;
          g.date = formatStartDateLocal(resolved) ?? g.date;
        }
      }

      // Pass 1.5: clinch-game injection (additive — series rows untouched).
      // When Kalshi drops the per-day game ticker because the game is a
      // possible series clincher, the per-day row has no Kalshi data while
      // a series row exists for the same teams. Copy the series-winner
      // tickers onto the per-day row so the four pills render against the
      // live series book; `kalshiViaSeries` flags the row for the UI
      // badge + trade-modal callout that clarifies pill clicks settle on
      // the series outcome, not tonight's game. The series row itself
      // continues to render at the bottom of the moneyline list unchanged.
      //
      // Both row keys carry canonical (hydra-normalized) team codes:
      //   canonicalGameKey:   "{sport}|{YYYY-MM-DD}|{TEAM_A}|{TEAM_B}"
      //   canonicalSeriesKey: "{sport}|series|{YY}|{TEAM_A}-{TEAM_B}"
      // Joining on the row's accumulator awayCode/homeCode misses when the
      // per-day row was built from a Polymarket-only slug whose raw alias
      // differs from canonical (NHL: poly "mon" vs canonical "MTL"). Pull
      // the canonical pair from the key itself.
      function canonPairFromKey(rowKey: string): [string, string] | null {
        const parts = rowKey.split('|');
        // canonicalSeriesKey: sport|series|YY|TEAM_A-TEAM_B
        if (parts.length === 4 && parts[1] === 'series') {
          const teams = parts[3].split('-');
          return teams.length === 2 ? [teams[0], teams[1]] : null;
        }
        // v2 per-day game key: sport|date|gameNo|TEAM_LO|TEAM_HI
        if (parts.length === 5) {
          return [parts[3], parts[4]];
        }
        // Legacy 4-seg per-day key (defensive — v2 promotion makes these 5-seg).
        if (parts.length === 4) {
          return [parts[2], parts[3]];
        }
        return null;
      }
      const seriesByTeams = new Map<string, GameAccum>();
      for (const [key, g] of games) {
        if (!g.series) continue;
        const pair = canonPairFromKey(key);
        if (!pair) continue;
        const k = `${g.sport}|${[pair[0], pair[1]].sort().join('-')}`;
        seriesByTeams.set(k, g);
      }
      for (const [key, g] of games) {
        if (g.series) continue;
        if (g.kalshiAwayTicker || g.kalshiHomeTicker) continue;
        const pair = canonPairFromKey(key);
        if (!pair) continue;
        const k = `${g.sport}|${[pair[0], pair[1]].sort().join('-')}`;
        const series = seriesByTeams.get(k);
        if (!series || !series.series) continue;
        if (!series.kalshiAwayTicker && !series.kalshiHomeTicker) continue;
        // Inject only when some team is on match point (wins ≥ winsToClinch
        // − 1). Otherwise — series not started, or both teams trailing the
        // clinch line — a missing per-day Kalshi ticker is "Kalshi hasn't
        // listed yet" rather than "merged into the series winner". Covers
        // the Game-4-resets-to-3-2-or-2-2 case where a previous on-the-hill
        // game was lost and the next per-day ticker is just a regular game.
        // seriesWins undefined (pre-deploy / Polymarket-only) → skip.
        if (!series.seriesWins) continue;
        const winsToClinch = series.seriesWinsToClinch ?? 4;
        const onMatchPoint = Object.values(series.seriesWins).some(
          (w) => w >= winsToClinch - 1,
        );
        if (!onMatchPoint) continue;
        // Inherit Kalshi-canonical away/home order + tickers from the series
        // row. This both fixes the team alias display ("MON" → "MTL" →
        // "Montreal Canadiens") and lines up the per-team Y/N pills with
        // the right series ticker. Hydra's canonicalGameKey normalization
        // is the same registry the series parser uses, so codes agree.
        g.kalshiAwayTicker = series.kalshiAwayTicker;
        g.kalshiHomeTicker = series.kalshiHomeTicker;
        g.awayCode = series.awayCode;
        g.homeCode = series.homeCode;
        g.kalshiViaSeries = { round: series.series.round, year: series.series.year };
      }

      // Pass 1.6: tennis-final injection — tennis's "game 7". Kalshi lists no
      // KX*MATCH event for a slam final: the tournament futures event IS the
      // match (2026 Wimbledon women's = KXWTA-26WIM, down to two active legs).
      // Same move as the series-clincher injection above: copy the legs onto
      // the Poly-built match row as its Kalshi side. Guard rails:
      //  - only events with EXACTLY 2 live legs qualify (the decided-final
      //    state). A finalist usually also has an active leg in the NEXT
      //    slam's futures (Muchova: KXWTA-26USO-MUC, a 22-leg event) which
      //    must never pair here — the leg-count gate excludes it.
      //  - each leg's outcome code must prefix-match a DISTINCT row player
      //    (leg codes are 3-char, row codes full surnames from the Poly side).
      if (sport === 'atp' || sport === 'wta') {
        const legsByEvent = new Map<string, HydraMarket[]>();
        for (const m of marketsForSport(sport)) {
          if (m.source !== 'kalshi') continue;
          if (m.status !== 'open' && m.status !== 'active') continue;
          const p = parseTicker(m);
          if (!p || p.marketType !== 'series' || p.sport !== sport) continue;
          const ev = m.eventTicker || m.ticker;
          const list = legsByEvent.get(ev) ?? [];
          list.push(m);
          legsByEvent.set(ev, list);
        }
        const finals = Array.from(legsByEvent.entries()).filter(([, legs]) => legs.length === 2);
        if (finals.length > 0) {
          const legCode = (t: string): string => t.match(/-([A-Z0-9]+)$/)?.[1] ?? '';
          for (const [, g] of games) {
            if (g.series || g.kalshiAwayTicker || g.kalshiHomeTicker) continue;
            for (const [eventTicker, legs] of finals) {
              const awayLeg = legs.find((l) => g.awayCode.toUpperCase().startsWith(legCode(l.ticker)));
              const homeLeg = legs.find((l) => g.homeCode.toUpperCase().startsWith(legCode(l.ticker)));
              if (!awayLeg || !homeLeg || awayLeg === homeLeg) continue;
              g.kalshiAwayTicker = awayLeg.ticker;
              g.kalshiHomeTicker = homeLeg.ticker;
              g.kalshiViaTournament = { eventTicker };
              break;
            }
          }
        }
      }

      // Pass 2: Build rows
      const rows: GameRow[] = [];
      const nowMs = Date.now();
      // Unabated game states (fed by the value dashboard's 5s
      // gameStates poll). Empty Map when the poll isn't mounted —
      // lookups then return undefined and the price-collapse fallback
      // falls through to its schedule-signal guard. Snapshot once per
      // getGames call; keyed by the 3-seg hydra analytics key.
      const unabatedByKey = useGameStatesStore.getState().byGameKey;

      for (const [key, g] of games) {
        // Per-day games: filter to the selected date — at ROW level, off the
        // row's RESOLVED date (pass 1.1 above), never off each market's own
        // eventStartTime.
        //
        // Pre-fix this gate sat in pass 1 and tested every market separately,
        // so a row whose legs disagreed about the date was built from only the
        // agreeing legs. The 2026-07-21 New England storm rained out BAL@BOS
        // and PIT@NYY into doubleheaders on the 22nd: Polymarket carries a
        // postponed event over to the makeup game rather than relisting it, so
        // the makeup half (BAL/BOS G1, NYY/PIT G2) kept Poly's `…-2026-07-21`
        // slug — the one holding ALL the volume, 623K vs 1.4K on the fresh
        // 7/22 slug — while its Kalshi legs re-dated to the 22nd. The per-
        // market test dropped that Poly leg on the way IN, so a 7/22 board
        // rendered those games Kalshi-only with no Poly pills, while
        // dateFilter:'all' (what GameDetail's findGameRow uses) showed them
        // complete and correctly grouped. Deciding once per row keeps every
        // leg and still honors the selected day.
        // Bucket off the SCHEDULE start, not the resolved one: the resolved
        // value hands the wheel to Unabated at first pitch, and Unabated flaps.
        // g.date (resolved-derived, still what the row DISPLAYS) is the fallback
        // for rows the slot cache never saw.
        const scheduleMs = _scheduleStartMsByGameKey.get(key);
        const rowDay = (scheduleMs != null ? formatStartDateLocal(scheduleMs) : null) ?? g.date;
        if (dateFilter === 'today' && selectedDateIso && !g.series) {
          // Inclusive on both ends, and a pure string comparison: bare
          // YYYY-MM-DD dates order lexicographically, so a multi-day window
          // needs no clock and no offset and cannot be skewed by a DST
          // transition inside it. A single day tests as [d, d].
          const windowEndIso = rangeEndIso ?? selectedDateIso;
          if (rowDay < selectedDateIso || rowDay > windowEndIso) continue;
        }
        const kAwayMkt = (g.kalshiAwayTicker ? _markets.get(g.kalshiAwayTicker) : null) ?? null;
        const kHomeMkt = (g.kalshiHomeTicker ? _markets.get(g.kalshiHomeTicker) : null) ?? null;
        const kAwayBook = (g.kalshiAwayTicker ? _orderbooks.get(g.kalshiAwayTicker) : null) ?? null;
        const kHomeBook = (g.kalshiHomeTicker ? _orderbooks.get(g.kalshiHomeTicker) : null) ?? null;
        const polyMkt = (g.polyTicker ? _markets.get(g.polyTicker) : null) ?? null;
        const polyBook = (g.polyTicker ? _orderbooks.get(g.polyTicker) : null) ?? null;
        const polyUsMkt = (g.polyUsTicker ? _markets.get(g.polyUsTicker) : null) ?? null;
        const polyUsBook = (g.polyUsTicker ? _orderbooks.get(g.polyUsTicker) : null) ?? null;

        // ── Row display codes ────────────────────────────────────────────────
        // Prefer Kalshi's canonical codes (Poly leaves them undefined because
        // its slug order is YES-outcome, not game-day). Then, for roster-less
        // sports, re-express that positional pair in hydra's canonical code
        // space: the row already GROUPS on a full-surname key
        // (`ufc|2026-08-01|1|PREPOLEC|REBECKI`) while canonicalAwayCode still
        // carries the lossy ticker pack (`PRE`/`REB`), so leaving them split
        // meant the /game URL built from these codes re-imported the
        // three-letter collision the key space escaped — and archived rows,
        // which spell codes from the analytics key, linked the same match a
        // different way. Resolved BEFORE the Poly books so the YES-side stamp
        // (also full-surname) is compared against codes in its own space.
        let awayCode = kAwayMkt?.canonicalAwayCode ?? kHomeMkt?.canonicalAwayCode ?? g.awayCode;
        let homeCode = kAwayMkt?.canonicalHomeCode ?? kHomeMkt?.canonicalHomeCode ?? g.homeCode;
        if (ROSTERLESS_SPORTS.has(g.sport)) {
          const canonicalPair = rosterlessDisplayCodes(
            kAwayMkt?.canonicalTeams ?? kHomeMkt?.canonicalTeams ?? polyMkt?.canonicalTeams ?? polyUsMkt?.canonicalTeams,
            awayCode,
            homeCode,
          );
          if (canonicalPair) [awayCode, homeCode] = canonicalPair;
        }

        // Poly YES-side identity for this row, nullable: null = no stamp, or
        // a stamp matching neither/both sides. Resolved once against the
        // display codes and shared by the books, the winner attribution, and
        // the price-collapse veto below.
        const polyYesSide = polyMkt
          ? yesSideForRowOrNull(polyMkt.canonicalYesTeam, awayCode, homeCode)
          : null;

        // A Poly-only fight has no Kalshi subtitle to name its fighters, and
        // its codes are Gamma's slug codes (`BRE1`/`CHR20`) or bare surnames.
        // The Poly title carries both full names, first-named = YES, so they
        // follow the same YES side the book does; a title that disagrees with
        // either code keeps the codes (display only, [away, home]).
        const polyOnlyFightNames = ((): [string, string] | null => {
          if (!polyMkt || kAwayMkt || kHomeMkt || (g.sport !== 'ufc' && g.sport !== 'boxing')) return null;
          const yesSide = polyYesSide ?? 'away';
          const names = yesSide === 'away'
            ? polyFightSideNames(polyMkt.title, awayCode, homeCode)
            : polyFightSideNames(polyMkt.title, homeCode, awayCode);
          if (!names) return null;
          return yesSide === 'away' ? names : [names[1], names[0]];
        })();
        const polyIdentityFailClosed = !!(
          polyMkt
          && polyYesSide == null
          && (
            (polyMkt.canonicalTeams && (g.sport === 'atp' || g.sport === 'wta'))
            // #1090: a digit-coded Poly slug (ITF `scott1-reed1`) joined onto
            // a Kalshi-oriented row needs hydra's YES stamp. Hydra withholds it
            // when it cannot bind the YES name, and the slug-first=away default
            // would then guess against Kalshi's player order.
            || ((kAwayMkt || kHomeMkt) && POLY_DIGIT_COMPETITOR_RE.test(polyMkt.ticker))
          )
        );

        // Polymarket US identity (F1): the same fail-closed rule as intl —
        // a stamped tennis row whose YES side resolves to neither/both codes
        // gets no US book rather than a guessed orientation.
        const polyUsYesSide = polyUsMkt
          ? yesSideForRowOrNull(polyUsMkt.canonicalYesTeam, awayCode, homeCode)
          : null;
        const polyUsIdentityFailClosed = !!(
          polyUsMkt
          && polyUsYesSide == null
          && polyUsMkt.canonicalTeams
          && (g.sport === 'atp' || g.sport === 'wta')
        );

        function buildPolyUsBooks(): { away: VenueBook; home: VenueBook } | null {
          if (!polyUsMkt) return null;
          if (polyUsIdentityFailClosed) return null;
          return buildPolyUsMoneylinePair(
            polyUsMkt,
            polyUsBook,
            awayCode,
            homeCode,
            undefined,
            freshnessForMarket(polyUsMkt, g.sport, gameState),
          );
        }

        function buildPolyBooks(): { away: VenueBook; home: VenueBook } | null {
          if (!polyMkt) return null;
          // #92 fail-closed: an unresolvable identity on a stamped tennis row
          // must not guess — the legacy slug-first=away default can silently
          // attach an inverted book to a player after canonicalTeams has
          // replaced slug order with alpha order (the WTA Y/N inversion).
          // Omit the Poly book; any Kalshi side stays tradeable.
          // Scope: only frames the modern producer STAMPED (canonicalTeams
          // present) — there, a missing/non-member canonicalYesTeam is hydra
          // deliberately withholding an ambiguous identity, per its contract.
          // Wholly unstamped legacy frames keep the slug-order default (the
          // slug-derived fallback join is what paired them in the first place),
          // and fight sports keep their pinned legacy fallback. The one
          // exception is a digit-coded slug on a Kalshi-joined row (#1090):
          // its slug codes cannot line up with Kalshi's, so hydra's key joined
          // it and hydra's YES stamp must orient it.
          if (polyIdentityFailClosed) return null;
          // YES = the slug's YES competitor (canonicalYesTeam, else the legacy
          // slug-first=away default). The other side gets the inverted book.
          // Shared with getPeriodMoneyline via buildPolyMoneylinePair.
          return buildPolyMoneylinePair(
            polyMkt,
            polyBook,
            awayCode,
            homeCode,
            undefined,
            freshnessForMarket(polyMkt, g.sport, gameState),
          );
        }

        // Per-row state derived from the union of source statuses. `gameState`
        // drives both the active/settled filter (series rows use a broader
        // "any non-terminal = live" rule because their sub-tickers ship
        // 'active'/'initialized' between rounds) and the row sort + UI
        // treatment downstream. Computed once per row so callers don't
        // re-derive it per render.
        const statuses = [kAwayMkt?.status, kHomeMkt?.status, polyMkt?.status, polyUsMkt?.status].filter(Boolean) as string[];
        const derivedGameState = deriveGameStateWithAuthority(
          statuses,
          !!g.series,
          g.startTimeMs,
          nowMs,
          g.sport,
        );
        let gameState = derivedGameState.gameState;
        let gameStateAuthority = derivedGameState.authority;
        // Unabated game state for this row (statusId 1=scheduled, 2=live,
        // 3=final). Read once — gates both the price-collapse fallback and
        // the winner checkmark below. Null when the poll isn't mounted or
        // hydra hasn't seen the game.
        const hydraKey = toHydraGameKeyLive(key);
        const unabated = primaryGameStateEnabled() ? readPrimaryGameState(hydraKey)
          : hydraKey ? unabatedByKey.get(hydraKey) ?? null : null;
        // YES = the slug's YES competitor for Poly. Same resolution
        // buildPolyBooks uses, against the same display codes, so winner
        // attribution stays consistent with how prices render. The boolean
        // fallback is used only when Poly identity is usable; fail-closed Poly
        // frames are excluded from the consumers below.
        const yesIsAwayForPoly = (polyYesSide ?? 'away') === 'away';
        const yesIsAwayForPolyUs = (polyUsYesSide ?? 'away') === 'away';
        // Price-collapse fallback — LAST RESORT ONLY. A 99¢ price is not a
        // settlement signal: live blowouts pin 99/1 with innings left, and
        // top-of-book ticks across the 98↔99 boundary made rows flap
        // live↔final trade-by-trade (PHI/KC 2026-07-06). So the fallback
        // only applies when there is NO schedule/state signal at all —
        // startTimeMs unknown AND no Unabated game state (statusId 2 =
        // in-progress always wins; any known state is authoritative).
        // When it does apply, require the paired collapse shape: both
        // Kalshi sides collapsed (one ≥99, one ≤1), and Polymarket — a
        // single-YES market — corroborating the same side when it has a
        // print. A lone single-venue, single-side 99/1 print never fires.
        if (
          gameState === 'live' && !g.series &&
          g.startTimeMs == null && unabated == null
        ) {
          const isHigh = (p: number | null | undefined) => p != null && p >= 99;
          const isLow = (p: number | null | undefined) => p != null && p <= 1;
          const kaLast = kAwayMkt?.lastPrice;
          const khLast = kHomeMkt?.lastPrice;
          const pLast = polyIdentityFailClosed ? null : polyMkt?.lastPrice;
          const pUsLast = polyUsIdentityFailClosed ? null : polyUsMkt?.lastPrice;
          const kalshiPaired = (isHigh(kaLast) && isLow(khLast)) || (isLow(kaLast) && isHigh(khLast));
          if (kalshiPaired) {
            const kalshiWinnerIsAway = isHigh(kaLast);
            // Poly veto: when a Poly venue has a last print it must be
            // collapsed to the SAME side Kalshi collapsed to. No print →
            // Kalshi's paired collapse stands alone. Both Polymarket venues
            // are consulted at the same rank; neither can flip the state on
            // its own (the veto only exists inside a Kalshi paired collapse).
            const singleYesAgrees = (last: number | null | undefined, yesIsAway: boolean): boolean =>
              last == null
                ? true
                : isHigh(last)
                  ? yesIsAway === kalshiWinnerIsAway
                  : isLow(last)
                    ? yesIsAway !== kalshiWinnerIsAway
                    : false;
            const polyAgrees =
              singleYesAgrees(pLast, yesIsAwayForPoly) && singleYesAgrees(pUsLast, yesIsAwayForPolyUs);
            if (polyAgrees) {
              gameState = 'final';
              gameStateAuthority = 'heuristic';
            }
          }
        }

        const freshnessOf = (mkt: HydraMarket | null): MarketFreshness =>
          freshnessForMarket(mkt, g.sport, gameState);

        if (statusFilter !== 'all' && statuses.length > 0) {
          if (g.series) {
            // Series rows: any non-terminal status keeps the row live.
            const anyLive = statuses.some((s) => !TERMINAL_STATUSES.has(s));
            if (statusFilter === 'active' && !anyLive) continue;
            if (statusFilter === 'settled' && anyLive) continue;
          } else {
            if (statusFilter === 'active' && gameState === 'final') continue;
            if (statusFilter === 'settled' && gameState !== 'final') continue;
          }
        }

        // Winner checkmark requires an ACTUAL final signal — a venue's
        // terminal market status or Unabated statusId 3 — on top of the
        // row being final. deriveFinalWinnerSide reads the winner off a
        // 99¢/1¢ price, which during a live blowout is the same collapsed
        // price that isn't a result yet; ungated, it stamped checkmarks on
        // games with innings left. Time-coerced finals with no confirming
        // signal render final without a checkmark.
        const finalConfirmed =
          statuses.some((s) => TERMINAL_STATUSES.has(s)) || unabated?.statusId === 3;
        const finalWinnerSide = gameState === 'final' && finalConfirmed
          ? deriveFinalWinnerSide(
            kAwayMkt,
            kHomeMkt,
            polyIdentityFailClosed ? null : polyMkt,
            yesIsAwayForPoly,
            polyUsIdentityFailClosed ? null : polyUsMkt,
            yesIsAwayForPolyUs,
          )
          : null;

        rows.push({
          key, sport: g.sport, date: g.date, admissionDate: rowDay,
          timePt: formatStartTimeLocal(g.startTimeMs),
          startTimeMs: g.startTimeMs,
          gameState,
          gameStateAuthority,
          finalWinnerSide,
          awayCode, homeCode,
          // Registry name when the sport has one (NBA → "Boston Celtics");
          // else the COMPLETE athlete name Kalshi ships in each 1v1 market's
          // yes_sub_title (hydra forwards it as `subtitle`, e.g. "Adolfo Daniel
          // Vallejo"). A fight/match event lists one market per competitor, so
          // pairing the row's two Kalshi sides pairs the two subtitles — the
          // same model MLB props render from. The canonical full-surname code
          // is the fallback; ticker fragments are never used.
          awayName: polyOnlyFightNames?.[0] ?? competitorDisplayName(awayCode, g.sport, kAwayMkt),
          homeName: polyOnlyFightNames?.[1] ?? competitorDisplayName(homeCode, g.sport, kHomeMkt),
          series: g.series,
          kalshiViaSeries: g.kalshiViaSeries,
          kalshiViaTournament: g.kalshiViaTournament,
          kalshi: (kAwayMkt || kHomeMkt) ? {
            away: buildVenueBookFromMarket(kAwayMkt, kAwayBook, freshnessOf(kAwayMkt)),
            home: buildVenueBookFromMarket(kHomeMkt, kHomeBook, freshnessOf(kHomeMkt)),
          } : null,
          // Series and per-day rows both build a Poly venue book when a
          // matched Poly ticker is in the row. Series rows merge via
          // canonicalSeriesKey (set in Pass 1 above). Note: buildPolyBooks
          // assumes the slug's YES outcome is the row's away team — true
          // for per-day games (slug ordered away→home). For series, slug
          // YES-team orientation isn't guaranteed to match the alpha-sorted
          // canonicalSeriesKey teams; team mapping may render reversed on
          // some series rows. Follow-up: have hydra emit canonicalAwayCode
          // on Poly series slugs (= YES team) and conditionally flip here.
          polymarket: buildPolyBooks(),
          polymarketUs: buildPolyUsBooks(),
        });
      }

      // #1232: a US-only game is a real navigation row, never a fabricated
      // K/Intl market. The catalogue supplies oriented identity and schedule;
      // the separate owned US quote store supplies prices and freshness.
      const usSport = usGameSportForBoard(sport);
      if (marketType === 'moneyline' && usSport) {
        const identities = _usSlugIdentity.published() ? _usSlugIdentity.games([usSport])
          : gatewayUsIndex().games.filter(g => g.sport === usSport);
        const represented = new Set([...games.keys()].map(key => normalizeUsGameKey(key, usSport)));
        for (const identity of identities) {
          if (represented.has(identity.key)) continue;
          const day = formatStartDateLocal(identity.startTimeMs)!;
          if (dateFilter === 'today' && selectedDateIso
            && (day < selectedDateIso || day > (rangeEndIso ?? selectedDateIso))) continue;
          const state = deriveGameStateWithAuthority(['open'], false, identity.startTimeMs, nowMs, sport);
          if (statusFilter === 'active' && state.gameState === 'final' || statusFilter === 'settled' && state.gameState !== 'final') continue;
          represented.add(identity.key);
          rows.push({ key: identity.key, sport, date: day, admissionDate: day,
            timePt: formatStartTimeLocal(identity.startTimeMs), startTimeMs: identity.startTimeMs,
            gameState: state.gameState, gameStateAuthority: state.authority, finalWinnerSide: null,
            awayCode: identity.awayCode, homeCode: identity.homeCode,
            awayName: competitorDisplayName(identity.awayCode, sport, null),
            homeName: competitorDisplayName(identity.homeCode, sport, null), kalshi: null, polymarket: null });
        }
      }

      // Sort tiers: FINAL games sink to the bottom of the day's list;
      // within each gameState bucket, chronological by date then event
      // start time (unknown startTimeMs sentinels to +Infinity so it sorts
      // after any known time within the date), with awayCode as the alpha
      // tiebreak.
      rows.sort((a, b) => {
        const aFinal = a.gameState === 'final' ? 1 : 0;
        const bFinal = b.gameState === 'final' ? 1 : 0;
        if (aFinal !== bFinal) return aFinal - bFinal;
        const dateCmp = a.date.localeCompare(b.date);
        if (dateCmp !== 0) return dateCmp;
        const aMs = a.startTimeMs ?? Number.POSITIVE_INFINITY;
        const bMs = b.startTimeMs ?? Number.POSITIVE_INFINITY;
        if (aMs !== bMs) return aMs - bMs;
        return a.awayCode.localeCompare(b.awayCode);
      });
      _getGamesCache.set(cacheKey, rows);
      return rows;
    },

    getActiveSports: (): Set<Sport> => {
      void get().version;
      // Sourced from each feed's `activeSports` meta frame (ingestActiveSports),
      // NOT derived from ingested market frames. Unions across feed sources so
      // the firehose + edge pipes both contribute to the chip row. Returns a
      // copy: callers union in their own selections.
      // Fold FEED keys to display sports: an ALT edge's `cfbalt` announce
      // lights the CFB chip, never a chip of its own (D5.2 — display maps
      // to base).
      const union = new Set<Sport>();
      for (const set of _activeSportsBySource.values()) {
        for (const s of set) union.add(baseSportOf(s));
      }
      return union;
    },

    getMarketsSnapshot: (): HydraMarket[] => {
      void get().version;
      return Array.from(_markets.values(), (market) => ({ ...market }));
    },

    getFutureBoards: (sport: Sport): FutureBoard[] => {
      // Subscribes the caller to the render tick like every other selector,
      // but the WORK is gated on _futureGeneration below: futures ride the
      // shallow tier's sweep cadence (~2 min), while `version` bumps at
      // display refresh rate off every other sport's books. Rebuilding 2.1k
      // golf legs 60×/s to produce a byte-identical array is the trap this
      // cache exists to avoid.
      void get().version;
      if (_futureBoardsGeneration !== _futureGeneration) {
        _futureBoardsCache.clear();
        _futureBoardsGeneration = _futureGeneration;
      }
      const cached = _futureBoardsCache.get(sport);
      if (cached) return cached;

      interface Accum {
        containerKey: string;
        tour: string | null;
        titles: string[];
        legs: FutureLeg[];
      }
      const groups = new Map<string, Accum>();

      for (const market of marketsForSport(sport)) {
        // The wire discriminator is the whole gate. A frame without it is
        // either game-shaped (every Kalshi frame, every OG frame) or a dated
        // prop — neither belongs on a futures board, and guessing from the
        // ticker would drag both in.
        if (market.marketType !== 'future') continue;
        if (market.source !== 'polymarket') continue;

        const parsed = parseFutureSlug(market.ticker);
        // Ungrouped rather than guessed: a wrong container silently merges
        // unrelated markets on a click-to-trade surface.
        const containerKey = parsed?.containerKey ?? UNGROUPED_FUTURES_KEY;
        let group = groups.get(containerKey);
        if (!group) {
          group = { containerKey, tour: parsed?.tour ?? null, titles: [], legs: [] };
          groups.set(containerKey, group);
        }
        if (market.title) group.titles.push(market.title);

        const yesBid = realFuturePrice(market.yesBid);
        const yesAsk = realFuturePrice(market.yesAsk);
        // Mid when the feed sent both sides, else the last trade. NEVER a
        // fabricated 0: a sport in the cold window after being toggled onto an
        // edge arrives with every price field null until its first sweep, and
        // a 0¢ row would read as a real, tradeable price on that board.
        const priceCents = futurePrice(market);

        group.legs.push({
          ticker: market.ticker,
          name: parsed
            ? humanizeSlug(parsed.subjectSlug)
            // No parse ⇒ no subject to isolate; the prose title is the most
            // honest label available.
            : market.title ?? market.ticker,
          kindLabel: parsed?.kindLabel ?? null,
          yesBid,
          yesAsk,
          priceCents,
          lastPriceCents: market.lastPrice ?? null,
          volume: market.volume ?? null,
          status: market.status,
          source: 'polymarket',
        });
      }

      const boards: FutureBoard[] = [];
      for (const group of groups.values()) {
        if (group.legs.length === 0) continue;
        // Favorites first; unpriced legs sink below every priced one rather
        // than sorting as 0¢ longshots. Name breaks ties so the order is
        // stable across sweeps (an unpriced board is otherwise arbitrary).
        group.legs.sort((a, b) =>
          (b.priceCents ?? -1) - (a.priceCents ?? -1) ||
          a.name.localeCompare(b.name) ||
          (a.kindLabel ?? '').localeCompare(b.kindLabel ?? ''),
        );
        const volumes = group.legs.filter((l) => l.volume != null);
        boards.push({
          key: `${sport}|${group.containerKey}`,
          sport,
          title: group.containerKey === UNGROUPED_FUTURES_KEY
            ? 'Ungrouped'
            : containerTitle(group.titles, group.containerKey),
          tour: group.tour,
          legs: group.legs,
          pricedLegs: group.legs.filter((l) => l.priceCents != null).length,
          volume: volumes.length ? volumes.reduce((s, l) => s + (l.volume ?? 0), 0) : null,
          lastUpdateMs: group.legs.reduce(
            (ms, l) => Math.max(ms, _lastReceiveMs.get(l.ticker) ?? 0), 0,
          ),
        });
      }
      // Biggest board first (the flagship tournament outranks a 2-leg
      // sideshow), Ungrouped always last however big it is.
      boards.sort((a, b) => {
        const au = a.title === 'Ungrouped' ? 1 : 0;
        const bu = b.title === 'Ungrouped' ? 1 : 0;
        return au - bu || b.legs.length - a.legs.length || a.title.localeCompare(b.title);
      });
      _futureBoardsCache.set(sport, boards);
      return boards;
    },

    getFutureSports: (): Sport[] => {
      void get().version;
      const present = new Set<Sport>();
      for (const [sport, tickers] of _futureTickersBySport) {
        if (tickers.size > 0) present.add(sport);
      }
      // SPORT_TABS order for the tabbed sports, then the futures-only sports
      // that have no tab at all (golf) — otherwise they'd sort by nothing.
      const ordered = SPORT_TABS.map((t) => t.id).filter((s) => present.has(s));
      for (const s of present) if (!ordered.includes(s)) ordered.push(s);
      return ordered;
    },

    getFutureSelectionVersion: (ticker) => {
      const market = _markets.get(ticker);
      return market ? futureSelectionVersion(market) : null;
    },

    getGolfMatches: () => {
      // Identity-only cache: unrelated quote traffic does not rebuild/sort
      // the match inventory. Books are resolved independently at read time.
      return golfMatchList();
    },

    getGolfMatchBook: (venue, ticker) => {
      const market = _markets.get(ticker);
      if (!market || market.source !== venue || !golfMatchIdentity(market)) return null;
      golfMatchList();
      if (_golfMatchConflicts.has(_golfMatches.get(ticker)!.key)) return null;
      const age = marketEvidenceAgeMs(ticker, market, Date.now());
      // Dated match books are not the intentionally static futures feed.
      // No sport-duration heuristic: require recent evidence for this ticker.
      const freshness = Number.isFinite(market.lastUpdateMs) && market.lastUpdateMs > 0
        && age != null && age <= 30_000 ? 'fresh' : 'stale';
      return buildVenueBookFromMarket(market, _orderbooks.get(ticker) ?? null, freshness);
    },

    getFutureBook: (sport, venue, ticker) => {
      const market = _markets.get(ticker);
      if (!market || market.source !== venue || normalizeSport(market.sport ?? '') !== sport) return null;
      if (venue === 'polymarket') {
        if (market.marketType !== 'future') return null;
      } else if (!get().getSeriesEvents(sport).some((event) =>
        event.outcomes.some((outcome) => outcome.ticker === ticker))) return null;
      const book = buildVenueBookFromMarket(market, _orderbooks.get(ticker) ?? null,
        venue === 'polymarket' && !market.lastUpdateMs ? 'unknown' : freshnessForMarket(market, sport));
      return venue === 'polymarket' ? { ...book, priceCents: futurePrice(market) } : book;
    },

    getSeriesEvents: (sport: Sport): SeriesEvent[] => {
      void get().version;
      interface SeriesGroup {
        seg: string;
        year: string;
        seriesTicker: string;
        outcomes: SeriesOutcome[];
      }
      const groups = new Map<string, SeriesGroup>();

      const isTennis = sport === 'atp' || sport === 'wta';
      // Golf (2026-09-09): every Kalshi golf futures series is its own event
      // (Presidents Cup, later Ryder Cup / tournament winners) — the series
      // ticker is the segment, so two events never share one board.
      const isGolf = sport === 'golf';
      for (const market of marketsForSport(sport)) {
        const parsed = parseTicker(market);
        if (!parsed || parsed.sport !== sport || parsed.marketType !== 'series') continue;
        // Tennis: an open tournament event (KXWTA-26WIM while the final is
        // pending) keeps its dozens of already-finalized legs in hydra's
        // cache — drop them or the Futures list is mostly eliminated players.
        // Scoped to tennis so team-sport championship boards keep their
        // current behavior (a settled champion leg still shows).
        if (isTennis && market.status !== 'open' && market.status !== 'active') continue;
        const year = parsed.seriesYear ?? '';
        if (!year) continue;
        // Tennis tournaments board separately: two open slams list the SAME
        // player at very different prices (Muchova in the 2-leg KXWTA-26WIM
        // final and the 22-leg KXWTA-26USO field), so folding them into one
        // list shows duplicate identically-named rows with click-to-trade.
        // The EVENT ticker names the tournament and is authoritative — a
        // bare-year LEG can belong to a tournament event (KXWTA-26-NOS
        // lists under KXWTA-26WIM) — with the leg ticker's own glued token
        // as the fallback when hydra sends no event ticker. Team sports
        // keep the single bare-year board.
        // eventTicker is OPTIONAL on the wire — championship futures legs
        // may arrive without one (see parseTicker's fall-through above);
        // HydraClient's required typing is a lie tsc can't catch here.
        const seg = isTennis
          ? (market.eventTicker?.match(/^KX[A-Z]+-\d{2}([A-Z0-9]*)$/)?.[1] ??
             parsed.seriesEventSeg ?? '')
          : isGolf
            ? market.ticker.split('-')[0]
            : '';
        const key = seg ? `${sport}|${year}|${seg}` : `${sport}|${year}`;
        let group = groups.get(key);
        if (!group) {
          group = {
            seg,
            year,
            // Derive KX{SPORT} prefix from the ticker itself (everything before first dash)
            seriesTicker: market.ticker.split('-')[0],
            outcomes: [],
          };
          groups.set(key, group);
        }

        // Same authority the game cards read (#958 / #975): an unusable book
        // contributes no depth, and while the ticker verdict is unconfirmed
        // the held last price is not a fallback and the book carries the
        // verdict so `observedVolume` renders volume as unknown.
        const book = sourceBookUsable(market.ticker) ? _orderbooks.get(market.ticker) ?? null : null;
        const tickerSourceValidity = market.source === 'kalshi' ? sourceTickerVerdict(market.ticker) : undefined;
        const last = tickerSourceValidity === 'unconfirmed' ? null : market.lastPrice ?? null;
        // Retained bid/ask scalars can outlive the book that supplied them.
        // Reuse its governing authority before presenting their midpoint;
        // an independently confirmed last trade remains a valid fallback.
        const priceObserved = market.source !== 'kalshi' || governingPriceVerdict(market.ticker) !== 'unconfirmed';
        const price = priceObserved && market.yesBid != null && market.yesAsk != null
          ? midCents(market.yesBid, market.yesAsk) : last;
        const bids = book?.yes ?? [];
        const asks = book?.no ?? [];
        const bidLiq = bids.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
        const askLiq = asks.reduce((sum, l) => sum + (l.price * l.size) / 100, 0);
        const liq = bidLiq + askLiq;
        const bookDepth = bids.reduce((s, l) => s + l.size, 0) + asks.reduce((s, l) => s + l.size, 0);
        const vol = market.volume ?? bookDepth;

        group.outcomes.push({
          ticker: market.ticker,
          code: parsed.outcomeCode,
          // Tennis has no team-code registry (getTeamDisplayName echoes the
          // raw MUC/NOS code); Kalshi's subtitle carries the player's full
          // name, so prefer it there.
          name: isTennis && market.subtitle
            ? market.subtitle
            : isGolf
              ? (GOLF_OUTCOME_NAMES[parsed.outcomeCode] ?? market.subtitle ?? parsed.outcomeCode)
              : getTeamDisplayName(parsed.outcomeCode, sport),
          kalshi: {
            marketTicker: market.ticker,
            ...(tickerSourceValidity ? { tickerSourceValidity } : {}),
            priceCents: price,
            lastPriceCents: last,
            volume: vol, liquidity: Math.round(liq),
            yes: toBookLevels(bids), no: toBookLevels(asks),
          },
        });
      }

      const events: SeriesEvent[] = [];
      // Bare-year board first (the old single-event behavior), then
      // tournaments alphabetically; year is the outer order if several
      // are ever live at once.
      const ordered = Array.from(groups.entries()).sort(
        ([, a], [, b]) => a.year.localeCompare(b.year) || a.seg.localeCompare(b.seg),
      );
      for (const [key, group] of ordered) {
        if (group.outcomes.length === 0) continue;
        // Sort by priceCents desc (favorites first), unpriced last
        group.outcomes.sort((a, b) => {
          const pa = a.kalshi?.priceCents ?? -1;
          const pb = b.kalshi?.priceCents ?? -1;
          return pb - pa;
        });
        const baseTitle = `${SERIES_TITLE[sport] ?? sport.toUpperCase()} 20${group.year}`;
        events.push({
          key,
          sport,
          year: group.year,
          seriesTicker: group.seriesTicker,
          title: isGolf
            ? `${GOLF_SERIES_TITLE[group.seriesTicker] ?? group.seriesTicker.replace(/^KX/, '')} 20${group.year}`
            : group.seg ? `${baseTitle} — ${group.seg}` : baseTitle,
          outcomes: group.outcomes,
        });
      }
      return events;
    },

    getMultiOutcomeEvents: (sport, filters): MultiOutcomeEvent[] => {
      void get().version;
      const nowMs = Date.now();
      // Unfiltered by default — see the interface jsdoc.
      const statusFilter = filters?.statusFilter ?? 'all';
      const dateFilter = filters?.dateFilter ?? 'all';
      const selectedDateIso = filters?.selectedDateIso;

      interface Accum {
        date: string | null;
        startTimeMs: number | null;
        statuses: Set<string>;
        title: string;
        awayCode: string;
        homeCode: string;
        outcomes: Map<string, SeriesOutcome>;
        /** First Kalshi ticker seen — its series prefix names the competition
         *  (soccer league badge, 2026-09-09). */
        kalshiTicker: string | null;
      }
      const byEvent = new Map<string, Accum>();

      for (const market of marketsForSport(sport)) {
        // Player props (soccer-goalscorer-props, 2026-09-09): the SOCCERPROPS
        // rows file under the soccer bucket via the display alias and share
        // the fixture's canonical key. Their legs are players, never sides —
        // the wire class is authoritative (rule 8) and keeps them out of the
        // 3-way event before any yesOutcome / canonicalYesTeam is consulted.
        if (market.class === 'player-prop') continue;
        const parsed = parseTicker(market);
        const effSport = sportFromMarket(market, parsed);
        if (effSport !== sport) continue;
        // Knockout "To Advance" shares the 1X2's canonicalGameKey but is a
        // distinct market — exclude it so it never adds a phantom outcome to the
        // 3-way. The parsed guard below catches it once parseKalshiTicker tags
        // 'advance'; this hydra-field cross-check also covers a parse miss
        // (older build / parser drift) where parsed is null and the guard
        // wouldn't fire. See getAdvanceGame for where it surfaces instead.
        if (market.marketType === 'advance') continue;
        if (parsed && parsed.marketType !== 'moneyline') continue;
        // Full-game only. Ticker parse is authoritative (soccer KXWC1H/KXWC2H ⇒
        // '1H'/'2H'); hydra's market.period is the fallback. Either non-full
        // excludes the period moneyline so it never pollutes the full-game 3-way.
        const mktPeriod = resolveMarketPeriod(market, parsed);
        if (mktPeriod !== 'full') continue;

        const marketStartMs = parseStartTimeMs(market.eventStartTime);
        const keyParts = codesFromCanonicalGameKey(market.canonicalGameKey);
        const eventDate = formatStartDateLocal(marketStartMs) ?? keyParts?.date ?? parsed?.date ?? null;

        // Group by the doubleheader-safe v2 key when hydra provides a canonical
        // key (uniform 5-seg, gameNo-disambiguated); fall back to the event
        // ticker for pre-canonical Kalshi-only frames. Soccer has no
        // doubleheaders so gameNo is always 1, but keying uniformly keeps the
        // event key shape consistent with the binary getGames path.
        const eventKey = market.canonicalGameKeyV2 || market.canonicalGameKey
          ? gameKeyV2(market, market.canonicalGameKey ?? '')
          : market.eventTicker || market.ticker.replace(/-[^-]+$/, '');
        if (eventKey == null) continue; // DNL-001 fail-closed (defensive — soccer has no DH)
        let acc = byEvent.get(eventKey);
        if (!acc) {
          acc = {
            date: eventDate,
            startTimeMs: null,
            statuses: new Set(),
            title: market.eventTitle || market.title || '',
            awayCode: keyParts?.awayCode ?? parsed?.awayCode ?? '',
            homeCode: keyParts?.homeCode ?? parsed?.homeCode ?? '',
            outcomes: new Map(),
            kalshiTicker: null,
          };
          byEvent.set(eventKey, acc);
        }
        if (acc.kalshiTicker == null && market.source === 'kalshi') acc.kalshiTicker = market.ticker;
        if (market.status) acc.statuses.add(market.status);
        if (acc.startTimeMs == null && marketStartMs != null) {
          acc.startTimeMs = marketStartMs;
          acc.date = formatStartDateLocal(marketStartMs) ?? acc.date;
        }
        if (!acc.title && (market.eventTitle || market.title)) acc.title = market.eventTitle || market.title || '';
        if (market.canonicalAwayCode && market.canonicalHomeCode) {
          acc.awayCode = market.canonicalAwayCode.toUpperCase();
          acc.homeCode = market.canonicalHomeCode.toUpperCase();
        } else if ((!acc.awayCode || !acc.homeCode) && parsed?.awayCode && parsed?.homeCode) {
          acc.awayCode = parsed.awayCode;
          acc.homeCode = parsed.homeCode;
        }

        // Poly legs resolve through polyCanonicalOutcome, like the other three
        // soccer selectors (getPeriodMoneyline / getAdvanceGame /
        // getAdvanceEvents): a positional `HOME`/`AWAY` sentinel in yesOutcome
        // is not a team code, and read raw it fails the away/home guard below
        // on the team legs while the DRAW leg sails through — leaving a
        // draw-only event. The slug's own outcome segment is the fallback, NOT
        // parsed.outcomeCode, which for a Poly 1X2 slug is the first slug team
        // on every leg (draw included) and would misfile the other two.
        const code =
          market.source === 'polymarket'
            ? polyCanonicalOutcome(market) ??
              (effSport === 'soccer'
                ? soccerPolyOutcomeFromTicker(market.ticker)
                : normalizeOutcomeCode(parsed?.outcomeCode))
            : normalizeOutcomeCode(market.yesOutcome) ??
              normalizeOutcomeCode(market.canonicalYesTeam) ??
              normalizeOutcomeCode(parsed?.outcomeCode);
        if (!code) continue;
        const isDraw = isDrawOutcome(code);
        if (
          effSport === 'soccer' &&
          (
            !acc.awayCode ||
            !acc.homeCode ||
            (!isDraw && code !== acc.awayCode && code !== acc.homeCode)
          )
        ) {
          continue;
        }
        const book = _orderbooks.get(market.ticker) ?? null;
        const venueBook = buildVenueBookFromMarket(
          market,
          book,
          freshnessForMarket(market, effSport),
        );
        let outcome = acc.outcomes.get(code);
        if (!outcome) {
          const role: SeriesOutcome['role'] =
            isDraw ? 'draw'
              : code === acc.awayCode ? 'away'
                : code === acc.homeCode ? 'home'
                  : 'other';
          outcome = {
            ticker: market.ticker,
            code,
            name: isDraw
              ? 'Draw'
              : (subtitleNameHint(eventKey, code) ?? getTeamDisplayName(code, sport) ?? code),
            role,
            kalshi: null,
            polymarket: null,
          };
          acc.outcomes.set(code, outcome);
        }
        if (market.source === 'kalshi') {
          outcome.kalshi = venueBook;
          outcome.ticker = venueBook.marketTicker || outcome.ticker;
        } else if (market.source === 'polymarket') {
          outcome.polymarket = venueBook;
          if (!outcome.kalshi) outcome.ticker = venueBook.marketTicker || outcome.ticker;
        }
        if (!isDraw && (outcome.name === code || !outcome.name)) {
          outcome.name = subtitleNameHint(eventKey, code) ?? getTeamDisplayName(code, sport) ?? code;
        }
      }

      const out: MultiOutcomeEvent[] = [];
      for (const [key, acc] of byEvent) {
        if (acc.outcomes.size === 0) continue;
        const outcomes = Array.from(acc.outcomes.values());
        // A Draw only means something beside the teams it ties. When every
        // team leg was rejected by the guard above (or landed under another
        // key — keyless Poly legs each carry their own eventTicker) and only
        // the Draw survived, this is not a 3-way; emitting it put a standalone
        // "Draw" row on the board. The legs regroup once identity arrives.
        if (outcomes.every((o) => o.role === 'draw')) continue;
        // A team leg's role is a function of the event's FINAL orientation,
        // never of the orientation held when the leg was first seen. Legs
        // arrive in any order and not every leg carries away/home: a Poly 1X2
        // leg has no canonicalAwayCode/HomeCode, so an event seeded by one
        // holds the alpha-sorted key pair (BOL/CHA) until a Kalshi leg stamps
        // the real pair (CHA/BOL). Roles minted before that flip put the
        // Charlton book in the Bolton slot (EFL Championship on GBuff,
        // 2026-11-03): the pair row names its slots by code and fills them
        // by role, so the two must be read off the same pair.
        for (const o of outcomes) {
          if (o.role === 'draw') continue;
          o.role = o.code === acc.awayCode ? 'away' : o.code === acc.homeCode ? 'home' : 'other';
        }
        const roleRank = (o: SeriesOutcome): number =>
          o.role === 'home' ? 0
            : o.role === 'away' ? 1
              : o.role === 'draw' ? 2
                : 3;
        outcomes.sort((a, b) => {
          const rank = roleRank(a) - roleRank(b);
          if (rank !== 0) return rank;
          const pa = a.kalshi?.priceCents ?? a.polymarket?.priceCents ?? -1;
          const pb = b.kalshi?.priceCents ?? b.polymarket?.priceCents ?? -1;
          return pb - pa;
        });
        // Subtitle-first naming (contract settled 2026-08-22): registry-less
        // sports (soccer especially) carry the display name in the Kalshi
        // subtitle, so the header reads "New England @ New York City", not
        // "NE @ NYC". No soccer club-code→name table on purpose: the codes
        // moved 56/62 in one week (fcc→CIN, sdg→SD, …) — a static map is born
        // stale, the subtitle rides the feed.
        //
        // #685 moved that subtitle map out of this accumulator and into the
        // store-level index written at ingest, so the cards, the tables and
        // the knockout Advance rows read the SAME authority this row does.
        // Registry name then raw code remain the fallbacks beneath it.
        const awayName = acc.awayCode
          ? (subtitleNameHint(key, acc.awayCode) ?? getTeamDisplayName(acc.awayCode, sport))
          : '';
        const homeName = acc.homeCode
          ? (subtitleNameHint(key, acc.homeCode) ?? getTeamDisplayName(acc.homeCode, sport))
          : '';
        const title =
          acc.awayCode && acc.homeCode
            ? `${awayName || acc.awayCode} @ ${homeName || acc.homeCode}`
            : acc.title;
        const derivedGameState = deriveGameStateWithAuthority(
          [...acc.statuses],
          false,
          acc.startTimeMs,
          nowMs,
          sport,
        );
        out.push({
          key,
          sport,
          category: `${sport}-game`,
          date: acc.date,
          timePt: formatStartTimeLocal(acc.startTimeMs),
          startTimeMs: acc.startTimeMs,
          gameState: derivedGameState.gameState,
          gameStateAuthority: derivedGameState.authority,
          title,
          awayCode: acc.awayCode,
          homeCode: acc.homeCode,
          awayName: awayName || acc.awayCode,
          homeName: homeName || acc.homeCode,
          outcomes,
          // Competition badge (2026-09-09): with Serie A / Bundesliga / La Liga
          // on the same tab as EPL / UCL / MLS, rows need their league.
          ...(sport === 'soccer' && acc.kalshiTicker
            ? (() => { const l = soccerLeagueLabel(acc.kalshiTicker); return l ? { league: l } : {}; })()
            : {}),
        });
      }

      // Toolbar filters — same semantics as getGames. Applied to the
      // assembled events (the list is small) rather than per-market, since
      // an event's date refines as its markets accumulate.
      const filtered = out.filter((e) => {
        if (dateFilter === 'today' && selectedDateIso && e.date !== selectedDateIso) return false;
        if (statusFilter === 'active' && e.gameState === 'final') return false;
        if (statusFilter === 'settled' && e.gameState !== 'final') return false;
        return true;
      });

      const stateRank = { live: 0, pre: 1, final: 2 } as const;
      filtered.sort((a, b) => {
        if (stateRank[a.gameState] !== stateRank[b.gameState]) return stateRank[a.gameState] - stateRank[b.gameState];
        return (a.startTimeMs ?? Infinity) - (b.startTimeMs ?? Infinity);
      });
      return filtered;
    },

    getGameEvents: (sport, filters, period = 'full'): GameEventRow[] => {
      void get().version;

      const statusFilter = filters?.statusFilter ?? 'active';
      const dateFilter = filters?.dateFilter ?? 'today';
      const selectedDateIso = filters?.selectedDateIso;
      // Last day of the bounded view, inclusive. Absent — or malformed, see
      // normalizeRangeEndIso — means a single day (#712), expressed below as
      // the degenerate window [d, d] rather than as a second code path.
      const rangeEndIso = normalizeRangeEndIso(filters?.rangeEndIso);

      // Result cache (#754), the shape `_getGamesCache` already holds: identical
      // {sport, period, filters} reads within one store snapshot collapse to a
      // single rebuild. This selector re-parses every ticker in the sport on
      // every call (~35ms on an in-season CFB catalog), and discovery asks it
      // once per sport per publication — without this, that is exactly the
      // unbounded render fan-out the feature is not allowed to introduce.
      //
      // Stamp = (this sport's mutation counter, clock second). The clock
      // second bounds the time-derived fields — freshness, live/pre crossing
      // startTimeMs — to the same 1s display clock the moneyline rows use,
      // independently of whether this sport receives a frame.
      //
      // The mutation counter rather than the published `revBySport` getGames
      // stamps on: see `sportMutationStamp`. The published revision arrives at
      // the coalesced flush, so a cache stamped on it hands pre-ingest rows to
      // anything that reads between a frame and that flush — a live price and
      // a synchronous read of the market it moved is precisely that sequence.
      // The attribution is identical, so an unrelated sport's frame still
      // leaves this sport's cached rows in place (#730 AC-1).
      //
      // `useGameStatesStore.byGameKey` is deliberately NOT in the stamp, unlike
      // getGames: these rows derive gameState from market statuses alone and
      // never read that map, which moves on every poll response — riding it
      // would throw the cache away at poll cadence to rebuild an identical
      // answer. Every input these two DO share is covered by
      // `clearDerivedRowCaches`.
      const clockSecond = Math.floor(Date.now() / 1000);
      if (_getGameEventsCacheClockSecond !== clockSecond) {
        _getGameEventsCache.clear();
        _getGameEventsCacheClockSecond = clockSecond;
      }
      const mutationStamp = sportMutationStamp(sport);
      if (_getGameEventsCacheStampBySport.get(sport) !== mutationStamp) {
        const prefix = `${sport}\u0000`;
        for (const k of [..._getGameEventsCache.keys()]) {
          if (k.startsWith(prefix)) _getGameEventsCache.delete(k);
        }
        _getGameEventsCacheStampBySport.set(sport, mutationStamp);
      }
      // U+0000 delimiter, written as an ESCAPE and never as a literal source
      // byte (#565), and every free-form field kept at the tail behind its own
      // separator — the same discipline, for the same reason, as the getGames
      // key above: the two date fields are unvalidated strings, and without a
      // separator two different windows could flatten into one key.
      const eventsCacheKey = `${sport}\u0000${period}\u0000${statusFilter}\u0000${dateFilter}\u0000${selectedDateIso ?? ''}\u0000${rangeEndIso ?? ''}`;
      const cachedEvents = _getGameEventsCache.get(eventsCacheKey);
      if (cachedEvents) return cachedEvents;

      // Pass 1 — group spread/total tickers by canonical event key.
      // Key: hydra's canonicalGameKey when present (preferred — collapses
      // cross-venue slug aliases like CHW/CWS), else "sport|date|A|B" with
      // alpha-sorted teams to match hydra's convention.
      interface EventAccum {
        sport: Sport;
        date: string;
        /** The reader-local SCHEDULE day this event is admitted to, which is
         *  not always the day it displays — see the admission block below. */
        admissionDate: string;
        /** Display codes — preferred from hydra's canonical (e.g. "ARI"
         *  when Kalshi raw says "AZ"); falls back to raw when canonical
         *  isn't provided. Used for getTeamDisplayName + chip/panel UI. */
        awayCode: string;
        homeCode: string;
        /** Raw codes exactly as they appear in Kalshi ticker outcomes —
         *  what `sp.teamCode` is compared against to determine favored.
         *  Must NOT be canonicalised or the AZ-vs-ARI mismatch flips
         *  every underdog spread bucket to "home". */
        rawAwayCode: string;
        rawHomeCode: string;
        /** True once a frame stamped hydra's canonical away/home on this
         *  event. Only then can an outcome's team code be matched against the
         *  display pair; before that the raw positional codes are the only
         *  orientation there is. */
        hasCanonical: boolean;
        /** Scheduled event start epoch ms — first non-null eventStartTime
         *  seen on any sub-ticker for this canonical event. Wins over later
         *  frames so we don't churn it on every update. Null when unknown. */
        startTimeMs: number | null;
        /** All statuses seen across this event's sub-tickers (Kalshi spreads,
         *  totals, Poly spreads, totals). Drives gameState derivation in
         *  Pass 2 — same rule as moneyline rows. */
        statuses: Set<string>;
        kalshiSpreads: Map<string, { ticker: string; teamCode: string; bucket: number }>;
        kalshiTotals: Map<string, { ticker: string; strike: number }>;
        /** `side` is the slot the Poly slug implies by position (fallback);
         *  `teamCode` is the slug team the side names, resolved against the
         *  canonical pair in pass 2 (#1445). */
        polySpreads: Array<{ ticker: string; line: number; side: 'home' | 'away'; teamCode?: string }>;
        polyTotals: Array<{ ticker: string; line: number }>;
      }
      const events = new Map<string, EventAccum>();

      // Cross-key index: eventStartTime + statuses pulled off ALL markets
      // sharing a canonicalGameKey, regardless of marketType. Hydra's
      // eventStartTime often lives on the Gamma per-game
      // record, which most reliably attaches to the per-day moneyline
      // slug (`PM-{sport}-{away}-{home}-{date}`) — not necessarily the
      // per-line spread/total slugs. Without this, getGameEvents misses
      // start time + the GAME ticker's status flips, so spread/total
      // events never light up live or sort by start time. We walk
      // _markets once here and have Pass 2 fall back to the index when
      // the spread/total accumulator is empty.
      const startTimeByKey = new Map<string, number>();
      const startDateByKey = new Map<string, string>();
      const statusesByKey = new Map<string, Set<string>>();
      // #805 — the full-game moneyline frame that NAMES each side, by outcome
      // code, so a spread/total row can read its matchup the way the moneyline
      // board does. A spread ticker's own subtitle describes the BET ("Georgia
      // Tech wins by over 3.5 points"), so the name has to come off the game
      // market that shares this canonical key; that market is in this same
      // sport bucket, exactly like the start time above.
      const namingFramesByKey = new Map<string, Map<string, HydraMarket>>();
      // Sport-scoped: every market sharing a game's canonicalGameKey is the same
      // sport (the key is `{sport}|…`) and so is filed in this bucket, so the
      // moneyline slug that carries the start time is here alongside the
      // spread/total lines the main loop consults the index for.
      for (const m of marketsForSport(sport)) {
        // Index by the doubleheader-safe v2 key so the two games of a
        // doubleheader don't share one start-time/status entry.
        const ck = (m.canonicalGameKeyV2 || m.canonicalGameKey)
          ? gameKeyV2(m, m.canonicalGameKey ?? '')
          : null;
        if (!ck) continue; // ambiguous frames contribute nothing (DNL-001)
        // Prefer the store's per-source precedence resolution for this key
        // (pre-start kalshi > unabated > polymarket; unabated once started).
        // Pre-fix this index was FIRST-FRAME-WINS, so a stale Poly Gamma time
        // beat the game's corrected Kalshi milestone purely by arrival order.
        // The resolved value is the same on every iteration, so the index is
        // now independent of ingest order; the frame's own value is used only
        // on a cache miss.
        const resolved = _startTimeMsByGameKey.get(ck);
        const ms = resolved ?? parseStartTimeMs(m.eventStartTime);
        if (ms != null && (resolved != null || !startTimeByKey.has(ck))) {
          startTimeByKey.set(ck, ms);
          const localDate = formatStartDateLocal(ms);
          if (localDate) startDateByKey.set(ck, localDate);
        }
        if (m.status) {
          let set = statusesByKey.get(ck);
          if (!set) { set = new Set(); statusesByKey.set(ck, set); }
          set.add(m.status);
        }
        // Parse only the frames that could possibly name a club — this walk
        // covers every market of the sport, props included, and the main loop
        // below already parses each of them once.
        if (!mayNameAClub(m)) continue;
        const namingParse = parseTicker(m);
        if (!isClubNamingFrame(m, namingParse)) continue;
        // Same code chain the multi-outcome board uses. `yesOutcome` is absent
        // on the REST rows the CFB edge serves today (hydraedge
        // yes-team-rest-b2prime), and a moneyline leg's ticker suffix IS its
        // team code — so without the parse fallback exactly the sport this
        // issue is about would keep its codes.
        const namedCode =
          normalizeOutcomeCode(m.yesOutcome) ??
          normalizeOutcomeCode(m.canonicalYesTeam) ??
          normalizeOutcomeCode(namingParse?.outcomeCode);
        if (!namedCode || isDrawOutcome(namedCode)) continue;
        let byCode = namingFramesByKey.get(ck);
        if (!byCode) { byCode = new Map(); namingFramesByKey.set(ck, byCode); }
        // Wholesale replace, the same discipline the subtitle index follows:
        // the latest naming frame for a code wins, including when it stops
        // carrying a usable subtitle. A producer that stops naming a team must
        // surface as the code floor, not as a value we happen to still hold.
        byCode.set(namedCode, m);
      }

      /** The board's name for one side of an event row — the moneyline board's
       *  own resolution (registry, then the team's full-game Kalshi subtitle,
       *  then the code), reached through the naming frame indexed above.
       *
       *  Two codes because they can disagree: `displayCode` is the canonical
       *  code the row renders and the registry is keyed by (ARI), `rawCode` is
       *  the Kalshi outcome the naming frame is filed under (AZ). Looking up
       *  only one of them silently loses the name wherever hydra canonicalises. */
      const sideName = (
        eventKey: string,
        eventSport: Sport,
        displayCode: string,
        rawCode: string,
      ): string => {
        const byCode = namingFramesByKey.get(eventKey);
        const frame =
          byCode?.get(normalizeOutcomeCode(displayCode) ?? '') ??
          byCode?.get(normalizeOutcomeCode(rawCode) ?? '') ??
          null;
        return competitorDisplayName(displayCode, eventSport, frame);
      };

      for (const market of marketsForSport(sport)) {
        const parsed = parseTicker(market);
        if (!parsed) continue;
        if (parsed.sport !== sport) continue;
        // Scope to the requested period. Default 'full' excludes NBA period
        // lines (1H + quarters, which share the full-game canonicalGameKey)
        // from the full-game event rows — the 0cf7549 invariant. A non-full
        // period returns only its own lines. The ticker parse is authoritative
        // for the period token (KXNBA1QTOTAL ⇒ '1Q'); hydra's market.period is
        // the fallback signal. Whichever is non-full wins, so a period market
        // can never be misclassified 'full' and leak into the full-game rows.
        const mktPeriod = resolveMarketPeriod(market, parsed);
        if (mktPeriod !== period) continue;
        if (parsed.marketType !== 'spread' && parsed.marketType !== 'total') continue;
        // Doubleheader-safe v2 grouping key (uniform 5-seg). Null when the
        // frame carries no canonical key at all — then we key off the parsed
        // shape (also promoted to the gameNo=1 form for a consistent key shape)
        // — OR when the frame is an ambiguous DH frame, which must be DROPPED
        // rather than fall through to that fallback: the fallback promotes to
        // gameNo=1 and would seat the line on GAME 1's event row (DNL-001).
        const hasCanonicalKey = !!(market.canonicalGameKeyV2 || market.canonicalGameKey);
        const ckV2 = hasCanonicalKey
          ? gameKeyV2(market, market.canonicalGameKey ?? '')
          : null;
        if (hasCanonicalKey && ckV2 == null) continue;
        // Index first: it carries the per-source precedence resolution for the
        // whole game. The line's own eventStartTime is the fallback, not the
        // preference — a stale Poly Gamma time stamped on this very spread must
        // lose to the game's corrected Kalshi milestone.
        const indexedStart = ckV2 ? startTimeByKey.get(ckV2) : undefined;
        const marketStartMs = indexedStart ?? parseStartTimeMs(market.eventStartTime) ?? null;
        const eventDate = formatStartDateLocal(marketStartMs) ?? (
          ckV2 ? startDateByKey.get(ckV2) : undefined
        ) ?? parsed.date;
        // ONE admission day, shared with getGames (#754).
        //
        // `eventDate` above is RESOLVED-derived, and the resolved start hands
        // the wheel to Unabated at first pitch — a value that flaps. getGames
        // has always bucketed off the SCHEDULE start for exactly that reason,
        // so a postponed game whose resolved start drifted across local
        // midnight was admitted to one day on the moneyline board and the other
        // day here: present on one board's Today and absent from the other's,
        // with nothing on screen explaining the absence. The schedule map is
        // keyed by the same game key, so both boards can read the same answer.
        //
        // The row's DISPLAYED date and start time are unchanged; this decides
        // membership only. Events with no canonical key — and so no entry in
        // the schedule map — keep the prior behaviour.
        const scheduleMs = ckV2 ? _scheduleStartMsByGameKey.get(ckV2) : undefined;
        const scheduleDay = scheduleMs != null ? formatStartDateLocal(scheduleMs) : null;
        const admissionDay = scheduleDay ?? eventDate;
        // Inclusive range, same lexicographic reasoning as getGames (#712);
        // a single day is the degenerate window [d, d].
        if (
          dateFilter === 'today' &&
          selectedDateIso &&
          (admissionDay < selectedDateIso || admissionDay > (rangeEndIso ?? selectedDateIso))
        ) continue;

        const sortedTeams = [parsed.awayCode, parsed.homeCode].sort();
        const fallbackKey = promoteToGameKeyV2(`${parsed.sport}|${eventDate}|${sortedTeams[0]}|${sortedTeams[1]}`);
        const key = ckV2 ?? fallbackKey;

        let e = events.get(key);
        if (!e) {
          e = {
            sport: parsed.sport,
            date: eventDate,
            admissionDate: admissionDay,
            awayCode: parsed.awayCode,
            homeCode: parsed.homeCode,
            rawAwayCode: parsed.awayCode,
            rawHomeCode: parsed.homeCode,
            hasCanonical: false,
            startTimeMs: null,
            statuses: new Set(),
            kalshiSpreads: new Map(),
            kalshiTotals: new Map(),
            polySpreads: [],
            polyTotals: [],
          };
          events.set(key, e);
        }

        // Display codes upgrade to hydra's canonical (e.g. "AZ" → "ARI")
        // when provided; raw codes stay pinned to the Kalshi ticker
        // outcome so the favored comparison below still matches.
        if (market.canonicalAwayCode && market.canonicalHomeCode) {
          e.awayCode = market.canonicalAwayCode;
          e.homeCode = market.canonicalHomeCode;
          e.hasCanonical = true;
        }
        // If this event was first seeded by a Polymarket slug alias
        // (e.g. OAK) and a Kalshi frame arrives later with a different raw
        // code (e.g. SAC), refresh the raw comparison codes from Kalshi.
        // `favored` below compares Kalshi spread outcome prefixes against
        // these fields; using Poly aliases there flips the side.
        //
        // CRITICAL: only trust moneyline / spread tickers for this. Totals
        // tickers don't encode a team in their outcome ("…-2") so the
        // parser falls back to a naive mid-split of the matchup string,
        // which silently breaks for variable-length codes (e.g. TBNYY
        // mid-splits to TBN/YY). Letting a total overwrite the rawAway/
        // HomeCode then poisons the spread-favored comparison so TB
        // tickers get classified as home-favored and collide with NYY
        // entries under my dedupe. Gating the write to moneyline/spread
        // — both of which anchor on a real teamCandidate from the
        // outcome — keeps the raw codes pinned to truth.
        // (This loop already filters to spread/total markets — moneyline
        // tickers never reach here. So gating on marketType === 'spread'
        // alone is sufficient to keep totals' bad mid-split codes out.)
        if (parsed.source === 'kalshi' && parsed.marketType === 'spread') {
          e.rawAwayCode = parsed.awayCode;
          e.rawHomeCode = parsed.homeCode;
        }

        // Capture event start time + per-ticker status for live/final
        // derivation. Mirrors moneyline GameAccum.startTimeMs — first
        // non-null wins; later frames don't overwrite. Status flows from
        // every sub-ticker so gameState reflects the union.
        // Every market of this event reads the same schedule entry, so this
        // converges on the schedule day as soon as any frame carries the
        // canonical key; the accumulator may have been seeded by one that
        // did not.
        if (scheduleDay) e.admissionDate = scheduleDay;
        if (e.startTimeMs == null && marketStartMs != null) {
          e.startTimeMs = marketStartMs;
          e.date = formatStartDateLocal(marketStartMs) ?? e.date;
        }
        if (market.status) e.statuses.add(market.status);

        if (parsed.source === 'kalshi') {
          if (
            parsed.marketType === 'spread' &&
            parsed.spreadTeamCode !== undefined &&
            parsed.spreadBucket !== undefined
          ) {
            e.kalshiSpreads.set(market.ticker, {
              ticker: market.ticker,
              teamCode: parsed.spreadTeamCode,
              bucket: parsed.spreadBucket,
            });
          } else if (
            parsed.marketType === 'total' &&
            parsed.totalStrike !== undefined
          ) {
            e.kalshiTotals.set(market.ticker, {
              ticker: market.ticker,
              strike: parsed.totalStrike,
            });
          }
        } else if (parsed.source === 'polymarket') {
          if (
            parsed.marketType === 'spread' &&
            parsed.polyLine !== undefined &&
            parsed.polySpreadSide
          ) {
            e.polySpreads.push({
              ticker: market.ticker,
              line: parsed.polyLine,
              side: parsed.polySpreadSide,
              teamCode: parsed.polySpreadTeamCode,
            });
          } else if (parsed.marketType === 'total' && parsed.polyLine !== undefined) {
            e.polyTotals.push({ ticker: market.ticker, line: parsed.polyLine });
          }
        }
      }

      // Pass 2 — build SpreadLine[] / TotalLine[] per event.
      // Kalshi sports spread/total markets are observed in both 'open' and
      // 'active' statuses (verified live 2026-05-12: WNBA spread/total all
      // ship as 'active', NBA moneyline ships as 'open'). Treat any
      // non-terminal status as live — same rule the series-row path in
      // `getGames` already uses.
      const isMarketActive = (ticker: string): boolean | null => {
        const mkt = _markets.get(ticker);
        if (!mkt?.status) return null;
        return !TERMINAL_STATUSES.has(mkt.status);
      };
      const passesStatus = (ticker: string): boolean => {
        if (statusFilter === 'all') return true;
        const live = isMarketActive(ticker);
        if (live == null) return statusFilter === 'active'; // unknown → keep active
        return statusFilter === 'active' ? live : !live;
      };

      // Which slot a team code sits on, read off the event's FINAL canonical
      // pair (#1445). Null when the event has no canonical pair or the code
      // matches neither side exactly (an alias such as Kalshi "AZ" against
      // canonical "ARI"), in which case callers fall back to ticker position —
      // the only orientation a keyless frame has. Matching by code rather than
      // by position is what keeps spreads right whichever way the producer
      // orients soccer (Kalshi lists the home team first there; hydracore's
      // pair-order stamp is being corrected to say so), because the label and
      // the OddsPapi lookup already read the canonical pair.
      const slotForCode = (code: string | undefined, e: EventAccum): 'away' | 'home' | null => {
        if (!code || !e.hasCanonical) return null;
        const c = code.toUpperCase();
        const isAway = c === e.awayCode.toUpperCase();
        const isHome = c === e.homeCode.toUpperCase();
        if (isAway === isHome) return null;
        return isAway ? 'away' : 'home';
      };

      const rows: GameEventRow[] = [];
      const nowMs = Date.now();
      for (const [key, e] of events) {
        // Poly spread sides resolved against the final pair; positional fallback.
        const polySpreads = e.polySpreads.map((p) => ({ ...p, side: slotForCode(p.teamCode, e) ?? p.side }));
        const spreads: SpreadLine[] = [];
        for (const sp of e.kalshiSpreads.values()) {
          if (!passesStatus(sp.ticker)) continue;
          const mkt = _markets.get(sp.ticker) ?? null;
          const book = _orderbooks.get(sp.ticker) ?? null;
          // `floorStrike` is the STRUCTURED line (hydraedge 2026-07-31) and
          // wins outright — Kalshi's spread prose is not stable across series
          // ("Goal Diff Reg Time: England wins by more than 5.5 goals" vs
          // "New York City wins by more than 2.5 goals"), so parsing it was
          // always the weak link. Absent (OG hydra frames, and every
          // moneyline) falls through to the subtitle, then `title` for
          // pre-subtitle frames, then `bucket − 0.5`. Per-sport bucket math
          // intentionally avoided: MLB names buckets by min margin, NBA/NHL by
          // line floor. NOTE `??` not `||` — a 0 line must not fall through.
          const lineMag =
            mkt?.floorStrike ??
            parseLineFromSubtitle(mkt?.subtitle) ??
            parseLineFromSubtitle(mkt?.title) ??
            sp.bucket - 0.5;
          // Moneyline-equivalent buckets (lineMag < 1) live in the game
          // market — Kalshi UI hides them from the spread tab. Drop them.
          if (lineMag < 1) continue;
          // The favored slot is the slot of the OUTCOME TEAM on the canonical
          // pair (#1445); only when the code matches neither side (Kalshi "AZ"
          // against canonical "ARI") does ticker position decide, compared
          // against the raw codes that share the outcome's spelling.
          const favored: 'away' | 'home' =
            slotForCode(sp.teamCode, e) ?? (sp.teamCode === e.rawAwayCode ? 'away' : 'home');
          const signedLine = favored === 'away' ? -lineMag : +lineMag;
          const kalshi = buildVenueBookFromMarket(mkt, book, freshnessForMarket(mkt, e.sport));
          // Side-aware Poly match: poly emits one slug per side per line
          // (`spread-home-5pt5` vs `spread-away-5pt5`). Match must align on
          // BOTH magnitude AND side — picking the wrong-side slug would
          // attach a poly book that represents the OPPOSITE bet, and the
          // modal would silently route trades to the wrong outcome token.
          const polyMatch = matchPolySpread(polySpreads, lineMag, favored);
          const polyMkt = polyMatch ? _markets.get(polyMatch.ticker) ?? null : null;
          const polyBook = polyMatch ? _orderbooks.get(polyMatch.ticker) ?? null : null;
          const polymarket = polyMkt
            // #154 (D3): a poly spread/total is ONE binary market — Over/Under
            // (or team+/team-) are its two sides referencing the same book, so
            // its volume/liquidity is a shared binary-book aggregate. Flag it so
            // every surface that renders it cues it as shared. NOTE: it is
            // rendered ONCE per row on all of them — SpreadsTotalsTable emits
            // one <tr> per line, and the GameDetail synth rows resolve to a
            // single `singleSide`. The flag means "whole-book total", not
            // "shown twice"; only the moneyline pair actually duplicates.
            ? { ...buildVenueBookFromMarket(polyMkt, polyBook, freshnessForMarket(polyMkt, e.sport)), isSharedBinaryBook: true }
            : undefined;
          spreads.push({ line: signedLine, favored, kalshi, polymarket });
        }

        // Surface Polymarket-only spreads (lines Kalshi doesn't list) as their
        // own rows — INCLUDING full-game. The old `period !== 'full'` guard
        // dropped a Poly-only full-game spread (e.g. a WNBA 6.5 that Kalshi
        // never posts), so the selector looked limited to Kalshi's offerings —
        // the issue #75 gap. Mirrors the totals poly-only block below, which had
        // the same guard removed for the same reason. The side-aware `favored`
        // (from the Poly slug) keeps the trade routed to the correct outcome
        // token, and `alreadySeeded` still prevents duplicating a line already
        // anchored to a Kalshi spread.
        for (const poly of polySpreads) {
          if (!passesStatus(poly.ticker)) continue;
          const favored = poly.side;
          const signedLine = favored === 'away' ? -poly.line : +poly.line;
          // Seeded = the SAME executable contract (exact line, same covering
          // side) already sits on a Kalshi-anchored row above. A neighboring
          // quarter or whole line is a different contract and gets its own
          // row; the old ±0.25 check silently dropped it.
          const alreadySeeded = spreads.some(
            (sp) => Math.abs(sp.line) === poly.line && sp.favored === favored,
          );
          if (alreadySeeded) continue;
          const polyMkt = _markets.get(poly.ticker) ?? null;
          const polyBook = _orderbooks.get(poly.ticker) ?? null;
          const polymarket = polyMkt
            // #154 (D3): a poly spread/total is ONE binary market — Over/Under
            // (or team+/team-) are its two sides referencing the same book, so
            // its volume/liquidity is a shared binary-book aggregate. Flag it so
            // every surface that renders it cues it as shared. NOTE: it is
            // rendered ONCE per row on all of them — SpreadsTotalsTable emits
            // one <tr> per line, and the GameDetail synth rows resolve to a
            // single `singleSide`. The flag means "whole-book total", not
            // "shown twice"; only the moneyline pair actually duplicates.
            ? { ...buildVenueBookFromMarket(polyMkt, polyBook, freshnessForMarket(polyMkt, e.sport)), isSharedBinaryBook: true }
            : undefined;
          if (polymarket) spreads.push({ line: signedLine, favored, polymarket });
        }

        // Collision guard: same (magnitude, favored) shouldn't appear
        // twice — that's the same conceptual bet. If two distinct
        // Kalshi tickers end up with the same parsed mag (parser miss
        // + bucket fallback colliding, or stray subtitle drift),
        // collapse them and keep the entry whose Kalshi book is
        // actually live (non-null priceCents). Without this, the chip
        // strip surfaces two visually-identical "+1.5" chips whose
        // underlying markets differ in price, and the trader has no
        // way to tell which one they're picking.
        const byMagFav = new Map<string, SpreadLine>();
        for (const sp of spreads) {
          const key = `${Math.abs(sp.line)}|${sp.favored}`;
          const existing = byMagFav.get(key);
          if (!existing) {
            byMagFav.set(key, sp);
            continue;
          }
          // Prefer the one with a live price; otherwise keep the first.
          const existingLive = (existing.kalshi?.priceCents ?? existing.polymarket?.priceCents ?? null) != null;
          const candidateLive = (sp.kalshi?.priceCents ?? sp.polymarket?.priceCents ?? null) != null;
          if (candidateLive && !existingLive) {
            byMagFav.set(key, sp);
          }
        }
        const deduped = Array.from(byMagFav.values());
        deduped.sort((a, b) => a.line - b.line);
        spreads.length = 0;
        spreads.push(...deduped);

        const totals: TotalLine[] = [];
        for (const t of e.kalshiTotals.values()) {
          if (!passesStatus(t.ticker)) continue;
          const mkt = _markets.get(t.ticker) ?? null;
          const book = _orderbooks.get(t.ticker) ?? null;
          // Hydra forwards Kalshi's per-market subtitle (`yes_sub_title`)
          // for totals — e.g. "Over 203.5 points scored" — which is the
          // canonical line. Per-sport strike math kept as a fallback for
          // pre-subtitle hydra frames only: MLB + WNBA name by minimum
          // total (strike − 0.5); NBA/NHL by line floor (strike + 0.5).
          // `floorStrike` (hydraedge 2026-07-31) is the structured line and
          // wins; the subtitle parse is the fallback for OG frames. `??` so a
          // 0 line survives.
          const subtitleLine = mkt?.floorStrike ?? parseLineFromSubtitle(mkt?.subtitle);
          // Min-margin naming: the Kalshi strike is the minimum total for YES,
          // so the binary line is strike − 0.5 (MLB runs, WNBA points, soccer
          // goals — "1 or more goals" ⇒ Over 0.5). NBA/NHL name by line floor
          // (strike + 0.5). Subtitle is authoritative when present; this is the
          // pre-subtitle fallback only.
          const line = subtitleLine ?? kalshiTotalLineFromStrike(t.ticker, t.strike);
          const kalshi = buildVenueBookFromMarket(mkt, book, freshnessForMarket(mkt, e.sport));
          // Exact strike identity, after each venue's own settlement
          // conversion: a Poly total a quarter or a half point away is a
          // different contract, never a stand-in (spreadMatch.ts has the
          // same rule for spreads). An unmatched Poly line gets its own row
          // in the pass below instead.
          const polyMatch = Number.isFinite(line) ? e.polyTotals.find((p) => p.line === line) : undefined;
          const polyMkt = polyMatch ? _markets.get(polyMatch.ticker) ?? null : null;
          const polyBook = polyMatch ? _orderbooks.get(polyMatch.ticker) ?? null : null;
          const polymarket = polyMkt
            // #154 (D3): a poly spread/total is ONE binary market — Over/Under
            // (or team+/team-) are its two sides referencing the same book, so
            // its volume/liquidity is a shared binary-book aggregate. Flag it so
            // every surface that renders it cues it as shared. NOTE: it is
            // rendered ONCE per row on all of them — SpreadsTotalsTable emits
            // one <tr> per line, and the GameDetail synth rows resolve to a
            // single `singleSide`. The flag means "whole-book total", not
            // "shown twice"; only the moneyline pair actually duplicates.
            ? { ...buildVenueBookFromMarket(polyMkt, polyBook, freshnessForMarket(polyMkt, e.sport)), isSharedBinaryBook: true }
            : undefined;
          totals.push({ line, kalshi, polymarket });
        }

        // Surface Polymarket totals that DON'T line up with any Kalshi strike as
        // their own rows — INCLUDING full-game. The old `period !== 'full'` guard
        // dropped these, so a game whose Poly total sat at a line Kalshi does not
        // list (the two venues post slightly different totals) showed a BLANK
        // Polymarket column instead of the real Poly market — the MLB-totals issue
        // a trader reported. Seeded = the SAME line already sits on a
        // Kalshi-anchored row (exact strike, as in the pairing above); a quarter
        // or whole-number neighbor is its own contract and its own row.
        for (const poly of e.polyTotals) {
          if (!passesStatus(poly.ticker)) continue;
          const alreadySeeded = totals.some((t) => t.line === poly.line);
          if (alreadySeeded) continue;
          const polyMkt = _markets.get(poly.ticker) ?? null;
          const polyBook = _orderbooks.get(poly.ticker) ?? null;
          const polymarket = polyMkt
            // #154 (D3): a poly spread/total is ONE binary market — Over/Under
            // (or team+/team-) are its two sides referencing the same book, so
            // its volume/liquidity is a shared binary-book aggregate. Flag it so
            // every surface that renders it cues it as shared. NOTE: it is
            // rendered ONCE per row on all of them — SpreadsTotalsTable emits
            // one <tr> per line, and the GameDetail synth rows resolve to a
            // single `singleSide`. The flag means "whole-book total", not
            // "shown twice"; only the moneyline pair actually duplicates.
            ? { ...buildVenueBookFromMarket(polyMkt, polyBook, freshnessForMarket(polyMkt, e.sport)), isSharedBinaryBook: true }
            : undefined;
          if (polymarket) totals.push({ line: poly.line, polymarket });
        }
        totals.sort((a, b) => a.line - b.line);

        if (spreads.length === 0 && totals.length === 0) continue;

        // Cross-key fallback: prefer eventStartTime / status from any
        // market sharing this canonicalGameKey when the spread/total
        // accumulator is missing data. The schedule signal often arrives
        // on a per-game moneyline frame rather than per-line slugs, and
        // the GAME ticker's status is the cleanest live/final signal —
        // both are captured by the
        // startTimeByKey / statusesByKey indices above.
        const startTimeMs = e.startTimeMs ?? startTimeByKey.get(key) ?? null;
        const unionStatuses = new Set(e.statuses);
        const xStatuses = statusesByKey.get(key);
        if (xStatuses) for (const s of xStatuses) unionStatuses.add(s);
        const derivedGameState = deriveGameStateWithAuthority(
          Array.from(unionStatuses),
          false, // spread/total events aren't series rows — same strict 'open'+startTime live rule
          startTimeMs,
          nowMs,
          e.sport,
        );

        rows.push({
          key,
          sport: e.sport,
          date: e.date,
          admissionDate: e.admissionDate,
          timePt: formatStartTimeLocal(startTimeMs),
          startTimeMs,
          gameState: derivedGameState.gameState,
          gameStateAuthority: derivedGameState.authority,
          awayCode: e.awayCode,
          homeCode: e.homeCode,
          awayName: sideName(key, e.sport, e.awayCode, e.rawAwayCode),
          homeName: sideName(key, e.sport, e.homeCode, e.rawHomeCode),
          spreads,
          totals,
          // #1030 — the period scope these lines were admitted under (period
          // events share the full-game key); venue joins read it, never a ticker.
          period,
        });
      }

      // #1262: native US-only lines join the same event collection. Their
      // typed metadata creates rows; only owned feed quotes supply prices.
      const usSport = period === 'full' ? usGameSportForBoard(sport) : null;
      if (usSport) {
        const inventory = _usSlugIdentity.published() ? _usSlugIdentity.lines(usSport) : gatewayUsIndex().lines.filter(line => line.game.sport === usSport);
        if (inventory.length > 0) mergeUsStandaloneLines(rows, inventory,
          get().getGames(sport, 'moneyline', filters), key => get().getUsLineCandidates(key));
      }

      // Same sort tier as moneyline rows: FINAL events sink to the bottom of
      // the day's list, then chronological by date + event start time, with
      // alpha tiebreak. Unknown startTimeMs sentinels to +Infinity within
      // its date bucket.
      rows.sort((a, b) => {
        const aFinal = a.gameState === 'final' ? 1 : 0;
        const bFinal = b.gameState === 'final' ? 1 : 0;
        if (aFinal !== bFinal) return aFinal - bFinal;
        const dateCmp = a.date.localeCompare(b.date);
        if (dateCmp !== 0) return dateCmp;
        const aMs = a.startTimeMs ?? Number.POSITIVE_INFINITY;
        const bMs = b.startTimeMs ?? Number.POSITIVE_INFINITY;
        if (aMs !== bMs) return aMs - bMs;
        return a.awayCode.localeCompare(b.awayCode);
      });

      _getGameEventsCache.set(eventsCacheKey, rows);
      return rows;
    },

    getPeriodMoneyline: (gameKey, period): PeriodMoneyline | null => {
      const version = get().version;
      // Result cache — the _getGamesCache pattern. A null answer is cached
      // too (the scan that produced it is the same full-sport walk).
      const statesMap = useGameStatesStore.getState().byGameKey;
      if (
        _getPeriodMoneylineCacheVersion !== version
        || _getPeriodMoneylineCacheGen !== _gameRowIngestGen
        || _getPeriodMoneylineCacheStates !== statesMap
      ) {
        _getPeriodMoneylineCache.clear();
        _getPeriodMoneylineCacheVersion = version;
        _getPeriodMoneylineCacheGen = _gameRowIngestGen;
        _getPeriodMoneylineCacheStates = statesMap;
      }
      const cacheKey = `${gameKey}\u0000${period}`;
      if (_getPeriodMoneylineCache.has(cacheKey)) return _getPeriodMoneylineCache.get(cacheKey) ?? null;
      // Collect the (up to 3) Kalshi winner outcomes + the Poly book(s) for this
      // game's period. Kalshi: KXNBA1HWINNER / KXWC1H / KXWC2H-…-{TEAM|TIE} →
      // away / home / tie books. Polymarket has two shapes:
      //   • NBA/WNBA: ONE binary `-1h-moneyline` → paired native/inverted onto
      //     the two team rows (no tie).
      //   • Soccer: THREE separate markets `-halftime-result-{away|home|draw}`
      //     (parsed to a team code + isTie) → placed individually, incl. a draw.
      // All share the full-game canonicalGameKey.
      let awayKalshi: VenueBook | null = null;
      let homeKalshi: VenueBook | null = null;
      let tieKalshi: VenueBook | null = null;
      let awayCode = '';
      let homeCode = '';
      let nbaPolyMkt: HydraMarket | null = null; // NBA single-binary to pair
      // Soccer: per-outcome Poly books, matched by team code below.
      let awayPolySoccer: VenueBook | null = null;
      let homePolySoccer: VenueBook | null = null;
      let tiePolySoccer: VenueBook | null = null;
      const polySoccer: Array<{ parsed: ParsedTicker; mkt: HydraMarket }> = [];
      for (const m of marketsForGameKey(gameKey)) {
        // gameKey is the doubleheader-safe v2 row key — match each market by its
        // own v2 key so a doubleheader's two games keep separate period markets.
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        const parsed = parseTicker(m);
        if (!parsed || resolveMarketPeriod(m, parsed) !== period || parsed.marketType !== 'moneyline') continue;
        if (m.source === 'kalshi') {
          awayCode = parsed.awayCode;
          homeCode = parsed.homeCode;
          const book = buildVenueBookFromMarket(
            m,
            _orderbooks.get(m.ticker) ?? null,
            freshnessForMarket(m, parsed.sport),
          );
          // `parsed.isTie` is only stamped for HALF periods (1H/2H/F5); the
          // full-game 1X2 draw (KXWCGAME-…-TIE, period 'full') isn't, so detect
          // it from the outcome too (hydra yesOutcome='DRAW' / the raw 'TIE'
          // suffix). Without this the Draw drops off the "90 min" line.
          const isTie =
            parsed.isTie ||
            normalizeOutcomeCode(m.yesOutcome) === 'DRAW' ||
            isDrawOutcome(parsed.outcomeCode);
          if (isTie) tieKalshi = book;
          else if (parsed.outcomeCode === parsed.awayCode) awayKalshi = book;
          else if (parsed.outcomeCode === parsed.homeCode) homeKalshi = book;
        } else if (m.source === 'polymarket') {
          if (MULTI_OUTCOME_SPORTS.has(parsed.sport)) {
            // Soccer 3-way — defer placement until codes are known.
            polySoccer.push({ parsed, mkt: m });
            // Poly-only game (Kalshi not open yet): seed the away/home identity
            // from hydra's reconciled canonical team by slot, so the slug's
            // alias spelling (`kr`) doesn't block the match below.
            const canon = polyCanonicalOutcome(m);
            if (canon && canon !== 'DRAW' && !parsed.isTie) {
              if (parsed.side === 'away' && !awayCode) awayCode = canon;
              else if (parsed.side === 'home' && !homeCode) homeCode = canon;
            }
          } else {
            nbaPolyMkt = m; // NBA binary
          }
          // Poly slug lacks team labels until paired; capture codes if Kalshi
          // hasn't (poly-only game, before the Kalshi side opens).
          if (!awayCode) awayCode = parsed.awayCode;
          if (!homeCode) homeCode = parsed.homeCode;
        }
      }
      if (!awayKalshi && !homeKalshi && !tieKalshi && !nbaPolyMkt && polySoccer.length === 0) {
        _getPeriodMoneylineCache.set(cacheKey, null);
        return null;
      }

      // Soccer: place each Poly outcome by its team code (draw → tie). Prefer
      // hydra's reconciled canonical team (`yesOutcome` = KOR/MEX) over the
      // slug-derived parse so alias countries pair — Poly spells Korea `kr`,
      // which the slug uppercases to `KR` ≠ Kalshi `KOR`. Falls back to the slug
      // parse for frames hydra hasn't reconciled yet. Matching by code (not slug
      // position) stays correct when Poly's away/home order differs from Kalshi's.
      for (const { parsed, mkt } of polySoccer) {
        const book = buildVenueBookFromMarket(
          mkt,
          _orderbooks.get(mkt.ticker) ?? null,
          freshnessForMarket(mkt, parsed.sport),
        );
        const canon = polyCanonicalOutcome(mkt);
        // Full-game Poly draw (`-draw`) carries yesOutcome='DRAW' but no
        // parsed.isTie (that's half-only) — detect the draw from the canonical
        // outcome so the "90 min" Draw line pairs its Poly book too.
        if (parsed.isTie || canon === 'DRAW') { tiePolySoccer = book; continue; }
        const code = canon ?? parsed.outcomeCode;
        if (code === homeCode) homePolySoccer = book;
        else if (code === awayCode) awayPolySoccer = book;
      }

      // NBA: pair the single binary native/inverted onto the two team rows.
      let awayPoly: VenueBook | null = awayPolySoccer;
      let homePoly: VenueBook | null = homePolySoccer;
      if (nbaPolyMkt) {
        const pair = buildPolyMoneylinePair(
          nbaPolyMkt,
          _orderbooks.get(nbaPolyMkt.ticker) ?? null,
          awayCode,
          homeCode,
          undefined,
          freshnessForMarket(
            nbaPolyMkt,
            sportFromMarket(nbaPolyMkt, parseTicker(nbaPolyMkt)),
          ),
        );
        awayPoly = pair.away;
        homePoly = pair.home;
      }
      const row: PeriodMoneyline = {
        gameKey, period, awayCode, homeCode,
        away: { kalshi: awayKalshi, polymarket: awayPoly },
        home: { kalshi: homeKalshi, polymarket: homePoly },
        tie: { kalshi: tieKalshi, polymarket: tiePolySoccer },
      };
      _getPeriodMoneylineCache.set(cacheKey, row);
      return row;
    },

    getAdvanceGame: (gameKey): GameRow | null => {
      void get().version;
      const nowMs = Date.now();
      // Collect the knockout "To Advance" markets for this game and collapse them
      // to a normal binary GameRow — structurally identical to any US-sports
      // moneyline: Kalshi lists two complementary per-team YES markets, Poly lists
      // one binary whose YES = the first slug team and whose NO is the other team.
      let awayCode = '';
      let homeCode = '';
      let date: string | null = null;
      let startTimeMs: number | null = null;
      const statuses = new Set<string>();
      let awayKalshi: VenueBook | null = null;
      let homeKalshi: VenueBook | null = null;
      let polyMkt: HydraMarket | null = null;
      let polyYesCode: string | null = null;
      // Promotion evidence, 1X2 side: does Kalshi quote the regulation result
      // this advance row would displace? Read from the raw frames here, once.
      let resultKalshiQuoted = false;

      for (const m of marketsForGameKey(gameKey)) {
        // gameKey is the doubleheader-safe v2 row key — match each market by its
        // own v2 key (soccer has no doubleheaders, but keep the shape uniform).
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        const parsed = parseTicker(m);
        // Hydra's marketType is authoritative; the parsed type is the cross-check
        // (and the fallback when an older build doesn't stamp the field).
        if (m.marketType !== 'advance' && parsed?.marketType !== 'advance') {
          if (isQuotedKalshiFullMoneyline(m, parsed)) resultKalshiQuoted = true;
          continue;
        }

        if (m.status) statuses.add(m.status);
        const sMs = parseStartTimeMs(m.eventStartTime);
        if (startTimeMs == null && sMs != null) startTimeMs = sMs;
        // Away/home: Kalshi carries position-derived canonicalAwayCode/HomeCode;
        // Poly carries only canonicalTeams, so fall back to the alpha-sorted
        // canonicalGameKey pair when no Kalshi frame has set them yet.
        if (m.canonicalAwayCode && m.canonicalHomeCode) {
          awayCode = m.canonicalAwayCode.toUpperCase();
          homeCode = m.canonicalHomeCode.toUpperCase();
        }
        const keyParts = codesFromCanonicalGameKey(m.canonicalGameKey);
        if (!date) date = keyParts?.date ?? parsed?.date ?? null;
        if (!awayCode && keyParts) awayCode = keyParts.awayCode;
        if (!homeCode && keyParts) homeCode = keyParts.homeCode;

        const code =
          normalizeOutcomeCode(m.yesOutcome) ?? normalizeOutcomeCode(parsed?.outcomeCode);
        const book = buildVenueBookFromMarket(
          m,
          _orderbooks.get(m.ticker) ?? null,
          freshnessForMarket(m, 'soccer'),
        );
        if (m.source === 'kalshi') {
          if (code && code === homeCode) homeKalshi = book;
          else if (code && code === awayCode) awayKalshi = book;
          // Pre-canonical fallback (Kalshi codes not yet reconciled): use the raw
          // ticker position so the two YES markets still land on the right side.
          else if (code && code === normalizeOutcomeCode(parsed?.homeCode)) homeKalshi = book;
          else if (code && code === normalizeOutcomeCode(parsed?.awayCode)) awayKalshi = book;
        } else if (m.source === 'polymarket') {
          polyMkt = m;
          polyYesCode = polyCanonicalOutcome(m) ?? normalizeOutcomeCode(parsed?.outcomeCode);
        }
      }

      if (!awayKalshi && !homeKalshi && !polyMkt) return null;
      if (!awayCode || !homeCode) return null;

      // Poly's single binary → away/home via the YES team (yesOutcome). The
      // complement side is the same market inverted (price 100−yes, ladders +
      // tokens swapped) — exactly the US-sports moneyline projection.
      let awayPoly: VenueBook | null = null;
      let homePoly: VenueBook | null = null;
      if (polyMkt) {
        const pair = buildPolyMoneylinePair(
          polyMkt,
          _orderbooks.get(polyMkt.ticker) ?? null,
          awayCode,
          homeCode,
          polyYesCode,
          freshnessForMarket(polyMkt, 'soccer'),
        );
        awayPoly = pair.away;
        homePoly = pair.home;
      }
      // Listing, not market — no venue quotes a price. See getAdvanceEvents
      // for the 2026-09-10 league-phase evidence behind this gate. The
      // evidence object rides the row so GameDetail's headline and the "90
      // min" demotion decide on it, never on the renderable Kalshi pair.
      const advancePromotion = advancePromotionEvidence(
        { awayKalshi, homeKalshi, awayPoly, homePoly },
        resultKalshiQuoted,
      );
      if (!advancePromotion.advanceQuoted) return null;

      const gameState = deriveGameState([...statuses], false, startTimeMs, nowMs, 'soccer');
      // Clean team names — like every other moneyline row, and now from the
      // same authority (#685). The advance frames' OWN subtitles read
      // "England advances", a sentence rather than a name, so they are
      // excluded from the index; the fixture's 1X2 legs supply the names and
      // this row inherits them through the shared canonical game key. Before
      // #685 this fell through getTeamDisplayName, which for registry-less
      // soccer echoes the code — so a knockout game rendered "ENG @ COD" one
      // row below its own 1X2 reading "England @ Congo". The order still
      // routes to the KXWCADVANCE ticker / advance token.
      return {
        key: `${gameKey}|advance`,
        sport: 'soccer',
        date: date ?? '',
        timePt: formatStartTimeLocal(startTimeMs),
        startTimeMs,
        gameState,
        finalWinnerSide: null,
        awayCode,
        homeCode,
        awayName: subtitleNameForCode(gameKey, awayCode),
        homeName: subtitleNameForCode(gameKey, homeCode),
        // Tolerate a one-sided venue: render a column only when it carries both
        // teams (Poly always does via its complement; Kalshi needs both YES
        // markets listed — until then the K column shows "—").
        kalshi: awayKalshi && homeKalshi ? { away: awayKalshi, home: homeKalshi } : null,
        polymarket: awayPoly && homePoly ? { away: awayPoly, home: homePoly } : null,
        advancePromotion,
      };
    },

    getAdvanceEvents: (sport): MultiOutcomeEvent[] => {
      void get().version;
      const nowMs = Date.now();
      // The 2-outcome (no-draw) sibling of getMultiOutcomeEvents: group the
      // knockout "To Advance" markets by the SAME event key as the 1X2 so the
      // dashboard can swap a knockout game's 3-way for its advance 2-way in place.
      interface Accum {
        date: string | null;
        startTimeMs: number | null;
        statuses: Set<string>;
        awayCode: string;
        homeCode: string;
        awayKalshi: VenueBook | null;
        homeKalshi: VenueBook | null;
        polyMkt: HydraMarket | null;
        polyYesCode: string | null;
      }
      const byEvent = new Map<string, Accum>();
      // Promotion evidence, 1X2 side, per event key — see getAdvanceGame.
      const resultKalshiQuotedKeys = new Set<string>();

      for (const market of marketsForSport(sport)) {
        const parsed = parseTicker(market);
        const effSport = sportFromMarket(market, parsed);
        if (effSport !== sport) continue;
        const isAdvance = market.marketType === 'advance' || parsed?.marketType === 'advance';
        if (!isAdvance && !isQuotedKalshiFullMoneyline(market, parsed)) continue;

        const eventKey = market.canonicalGameKeyV2 || market.canonicalGameKey
          ? gameKeyV2(market, market.canonicalGameKey ?? '')
          : market.eventTicker || market.ticker.replace(/-[^-]+$/, '');
        if (eventKey == null) continue; // DNL-001 fail-closed (defensive — soccer has no DH)
        if (!isAdvance) {
          resultKalshiQuotedKeys.add(eventKey);
          continue;
        }
        let acc = byEvent.get(eventKey);
        if (!acc) {
          acc = {
            date: null, startTimeMs: null, statuses: new Set(),
            awayCode: '', homeCode: '', awayKalshi: null, homeKalshi: null,
            polyMkt: null, polyYesCode: null,
          };
          byEvent.set(eventKey, acc);
        }
        if (market.status) acc.statuses.add(market.status);
        const sMs = parseStartTimeMs(market.eventStartTime);
        if (acc.startTimeMs == null && sMs != null) acc.startTimeMs = sMs;
        if (market.canonicalAwayCode && market.canonicalHomeCode) {
          acc.awayCode = market.canonicalAwayCode.toUpperCase();
          acc.homeCode = market.canonicalHomeCode.toUpperCase();
        }
        const keyParts = codesFromCanonicalGameKey(market.canonicalGameKey);
        if (!acc.date) acc.date = keyParts?.date ?? parsed?.date ?? null;
        if (!acc.awayCode && keyParts) acc.awayCode = keyParts.awayCode;
        if (!acc.homeCode && keyParts) acc.homeCode = keyParts.homeCode;

        const code =
          normalizeOutcomeCode(market.yesOutcome) ?? normalizeOutcomeCode(parsed?.outcomeCode);
        const book = buildVenueBookFromMarket(
          market,
          _orderbooks.get(market.ticker) ?? null,
          freshnessForMarket(market, sport),
        );
        if (market.source === 'kalshi') {
          if (code && code === acc.homeCode) acc.homeKalshi = book;
          else if (code && code === acc.awayCode) acc.awayKalshi = book;
          else if (code && code === normalizeOutcomeCode(parsed?.homeCode)) acc.homeKalshi = book;
          else if (code && code === normalizeOutcomeCode(parsed?.awayCode)) acc.awayKalshi = book;
        } else if (market.source === 'polymarket') {
          acc.polyMkt = market;
          acc.polyYesCode = polyCanonicalOutcome(market) ?? normalizeOutcomeCode(parsed?.outcomeCode);
        }
      }

      const out: MultiOutcomeEvent[] = [];
      for (const [key, acc] of byEvent) {
        if (!acc.awayCode || !acc.homeCode) continue;
        if (!acc.awayKalshi && !acc.homeKalshi && !acc.polyMkt) continue;

        // Poly's single binary → away/home via the YES team (yesOutcome), the
        // other side its NO complement (native/inverted split).
        let awayPoly: VenueBook | null = null;
        let homePoly: VenueBook | null = null;
        if (acc.polyMkt) {
          const pair = buildPolyMoneylinePair(
            acc.polyMkt,
            _orderbooks.get(acc.polyMkt.ticker) ?? null,
            acc.awayCode,
            acc.homeCode,
            acc.polyYesCode,
            freshnessForMarket(acc.polyMkt, sport),
          );
          awayPoly = pair.away;
          homePoly = pair.home;
        }

        // Bookless advance frames are listings, not markets. 2026-09-10 (UCL
        // league phase, he3): Poly listed `-team-to-advance` for EVERY fixture
        // with no bid, ask or trade, Kalshi listed no ADVANCE at all, and the
        // two frames core could key replaced their fixture's 1X2 on the board
        // — Kalshi prices, Poly prices and the Draw row gone, two empty rows
        // left. No venue quoting a price ⇒ exactly as if no advance frame
        // existed. Same gate in getAdvanceGame; the promotion itself also
        // requires a Kalshi counterpart — evidence built ONCE here and carried
        // on the event, decided by advancePromotes at every consumer.
        const advancePromotion = advancePromotionEvidence(
          { awayKalshi: acc.awayKalshi, homeKalshi: acc.homeKalshi, awayPoly, homePoly },
          resultKalshiQuotedKeys.has(key),
        );
        if (!advancePromotion.advanceQuoted) continue;

        // Same shared authority as the 1X2 row this one replaces in place
        // (#685) — see getAdvanceGame for why the advance frames' own
        // subtitles ("Switzerland advances") are not the name source.
        const awayName = subtitleNameForCode(key, acc.awayCode);
        const homeName = subtitleNameForCode(key, acc.homeCode);
        const mkOutcome = (
          code: string, name: string, role: 'away' | 'home',
          k: VenueBook | null, p: VenueBook | null,
        ): SeriesOutcome => ({
          ticker: k?.marketTicker || p?.marketTicker || '',
          code, name, role, kalshi: k, polymarket: p,
        });
        const derivedGameState = deriveGameStateWithAuthority(
          [...acc.statuses],
          false,
          acc.startTimeMs,
          nowMs,
          sport,
        );
        out.push({
          key,
          sport,
          category: `${sport}-game`,
          date: acc.date,
          timePt: formatStartTimeLocal(acc.startTimeMs),
          startTimeMs: acc.startTimeMs,
          gameState: derivedGameState.gameState,
          gameStateAuthority: derivedGameState.authority,
          title: `${awayName} @ ${homeName}`,
          awayCode: acc.awayCode,
          homeCode: acc.homeCode,
          awayName,
          homeName,
          // away first so multiOutcomePairRow's away slot is the away team; the
          // table re-sorts by price within the pair anyway.
          outcomes: [
            mkOutcome(acc.awayCode, awayName, 'away', acc.awayKalshi, awayPoly),
            mkOutcome(acc.homeCode, homeName, 'home', acc.homeKalshi, homePoly),
          ],
          kind: 'advance',
          advancePromotion,
        });
      }

      const stateRank = { live: 0, pre: 1, final: 2 } as const;
      out.sort((a, b) => {
        if (stateRank[a.gameState] !== stateRank[b.gameState]) return stateRank[a.gameState] - stateRank[b.gameState];
        return (a.startTimeMs ?? Infinity) - (b.startTimeMs ?? Infinity);
      });
      return out;
    },

    getBtts: (gameKey, period) => {
      void get().version;
      // Both-teams-to-score is a single binary per game/period (Kalshi
      // KX*{period}BTTS-…-BTTS). One Kalshi book (+ Poly in a later phase),
      // collapsed onto the full-game canonicalGameKey like every period market.
      let kalshi: VenueBook | null = null;
      let polymarket: VenueBook | null = null;
      for (const m of marketsForGameKey(gameKey)) {
        // gameKey is the doubleheader-safe v2 row key — match each market by its
        // own v2 key so a doubleheader's two games keep separate period markets.
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        const parsed = parseTicker(m);
        if (!parsed || parsed.marketType !== 'btts' || resolveMarketPeriod(m, parsed) !== period) continue;
        const book = buildVenueBookFromMarket(
          m,
          _orderbooks.get(m.ticker) ?? null,
          freshnessForMarket(m, parsed.sport),
        );
        if (m.source === 'kalshi') kalshi = book;
        else if (m.source === 'polymarket') polymarket = book;
      }
      if (!kalshi && !polymarket) return null;
      return { kalshi, polymarket };
    },

    getMlbPlayerProps: (gameKey): MlbPropStatGroup[] => {
      void get().version;
      // stat → playerKey → ladder. Classification is by ticker prefix (exact
      // series segment — classifyMlbExtendedTicker) because the wire marketType
      // is not authoritative for these series; the same classifier returning
      // null for every other MLB series is what keeps props OUT of the
      // game-lines selectors (whose internal parser drops these series in turn).
      // Poly prop frames (PM `-hr-`/`-k-` slugs — classifyPolyExtendedTicker)
      // are collected in the same pass and joined AFTER the Kalshi ladders
      // exist, on (stat, playerKey slug, threshold).
      const byStat = new Map<MlbPropStat, Map<string, PlayerPropLadder>>();
      const polyProps: Array<{ stat: MlbPropStat; playerSlug: string; threshold: number; m: HydraMarket }> = [];
      for (const m of mlbMarketsForGameKey(gameKey)) {
        // gameKey is the doubleheader-safe v2 row key — match each market by
        // its own v2 key so a doubleheader's two games keep separate ladders.
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        if (m.source === 'polymarket') {
          // Doubleheader FAIL-CLOSED (DNL-G1): the v2-key gate above promotes
          // a legacy 3-seg key to the gameNo=1 form, so a Poly frame missing
          // the V2 stamp on a DH day would attach game 2's books to game 1 —
          // a cross-venue wrong-game pair. The engines stamp
          // canonicalGameKeyV2 on every poly-props-events frame (contract);
          // a frame without it renders unpaired instead of wrongly paired.
          if (!m.canonicalGameKeyV2) continue;
          const pCls = classifyPolyExtendedTicker(m.ticker);
          if (pCls?.kind === 'prop') {
            polyProps.push({ stat: pCls.stat, playerSlug: pCls.playerSlug, threshold: pCls.threshold, m });
          }
          continue;
        }
        if (m.source !== 'kalshi') continue;
        if (!isMlbExtendedSeriesTicker(m.ticker)) continue;
        const cls = classifyMlbExtended(m.ticker);
        if (cls?.kind !== 'prop') continue;
        let statMap = byStat.get(cls.stat);
        if (!statMap) { statMap = new Map(); byStat.set(cls.stat, statMap); }
        // Identity: the wire `playerKey` when core stamped it (spec DNL-002
        // atomic migration — same string as the OP bucket), else the
        // ticker's TEAM+PLAYERCODE exactly as before the field existed.
        const wireKey = wirePlayerKey(m);
        const playerKey = wireKey ?? `${cls.teamCode}${cls.playerId}`;
        let ladder = statMap.get(playerKey);
        if (!ladder) {
          ladder = {
            playerKey,
            ...(wireKey != null ? { opPlayerKey: wireKey } : {}),
            playerName: formatPlayerName(cls.playerId, cls.jerseyNumber),
            teamCode: cls.teamCode,
            rungs: [],
          };
          statMap.set(playerKey, ladder);
        }
        // Kalshi's yes_sub_title ("Fernando Tatis Jr.: 2+") names the player
        // authoritatively — upgrade the ticker-derived fallback when present.
        const subName = playerNameFromSubtitle(m.subtitle);
        if (subName) ladder.playerName = subName;
        ladder.rungs.push({
          threshold: cls.threshold,
          kalshi: buildVenueBookFromMarket(
            m,
            _orderbooks.get(m.ticker) ?? null,
            freshnessForMarket(m, 'mlb'),
          ),
          polymarket: null,
        });
      }
      // ── Poly join (client-side pairing, poly-props-events) ─────────────
      // playerKey = slugify(Kalshi yes_sub_title name) vs Poly's slug segment.
      // NEVER fuzzy: an unmatched slug renders as its own unpaired ladder, and
      // an AMBIGUOUS slug (two Kalshi players slugifying identically — the
      // Poly side carries no team code to tie-break with) pairs neither.
      // Ticker-derived fallback names ("F. Tatis #23") slugify to shapes a
      // Poly slug can't take, so they simply never match — no special-casing.
      if (polyProps.length > 0) {
        const AMBIGUOUS = Symbol('ambiguous-slug');
        const slugIndex = new Map<MlbPropStat, Map<string, PlayerPropLadder | typeof AMBIGUOUS>>();
        for (const { stat } of polyProps) {
          if (slugIndex.has(stat)) continue;
          const idx = new Map<string, PlayerPropLadder | typeof AMBIGUOUS>();
          for (const ladder of byStat.get(stat)?.values() ?? []) {
            const slug = slugifyPlayerName(ladder.playerName);
            if (!slug) continue;
            idx.set(slug, idx.has(slug) ? AMBIGUOUS : ladder);
          }
          slugIndex.set(stat, idx);
        }
        for (const { stat, playerSlug, threshold, m } of polyProps) {
          // No orderbook exists for Poly prop tickers (static Gamma snapshot,
          // never WS-subscribed) — the book is market-frame-only by design,
          // and sentinel-guarded (DNL-G2): one-sided is its steady state.
          const book = buildGuardedPolyBook(
            m,
            _orderbooks.get(m.ticker) ?? null,
            freshnessForMarket(m, 'mlb'),
          );
          const hit = slugIndex.get(stat)?.get(playerSlug);
          if (hit && hit !== AMBIGUOUS) {
            const rung = hit.rungs.find((r) => r.threshold === threshold);
            if (rung) rung.polymarket = book;
            else hit.rungs.push({ threshold, kalshi: null, polymarket: book });
            continue;
          }
          // Unpaired (no Kalshi ladder, or ambiguous slug): render as a
          // Poly-only ladder keyed by the slug. Poly gives no team context.
          let statMap = byStat.get(stat);
          if (!statMap) { statMap = new Map(); byStat.set(stat, statMap); }
          let ladder = statMap.get(playerSlug);
          if (!ladder) {
            ladder = {
              playerKey: playerSlug,
              playerName: titleCasePolySlug(playerSlug),
              teamCode: '',
              rungs: [],
            };
            statMap.set(playerSlug, ladder);
          }
          const rung = ladder.rungs.find((r) => r.threshold === threshold);
          if (rung) rung.polymarket = book;
          else ladder.rungs.push({ threshold, kalshi: null, polymarket: book });
        }
      }
      const groups: MlbPropStatGroup[] = [];
      for (const stat of MLB_PROP_STATS) {
        const statMap = byStat.get(stat);
        if (!statMap) continue;
        const players = [...statMap.values()];
        for (const p of players) p.rungs.sort((a, b) => a.threshold - b.threshold);
        // Team-less (Poly-only) ladders sort AFTER every teamed ladder
        // instead of '' floating them to the top.
        players.sort(
          (a, b) =>
            Number(a.teamCode === '') - Number(b.teamCode === '')
            || a.teamCode.localeCompare(b.teamCode)
            || a.playerName.localeCompare(b.playerName),
        );
        groups.push({ stat, players });
      }
      return groups;
    },

    getMlbLivePlayerPropMarkets: (): LivePlayerPropMarket[] => {
      void get().version;
      // One pass over the sport bucket for the WHOLE day. The per-game
      // selector above re-walks that bucket on every call, so calling it once
      // per game is what this exists to replace. Same classification, identity,
      // book, and freshness authorities as that selector — only the grouping
      // moves out, into the pure adapter.
      const records: LivePlayerPropMarket[] = [];
      for (const m of marketsForSport('mlb')) {
        // Same doubleheader-safe identity as every per-game selector: null
        // (dhAmbiguous / witnessed-DH legacy key) is excluded, never promoted.
        const gameKey = gameKeyV2(m, m.canonicalGameKey ?? '');
        if (!gameKey) continue;
        if (m.source === 'polymarket') {
          // DNL-G1 fail-closed: a Poly frame without the v2 stamp on a DH day
          // would seat game 2's book on game 1 — drop entirely.
          if (!m.canonicalGameKeyV2) continue;
          const pCls = classifyPolyExtendedTicker(m.ticker);
          if (pCls?.kind !== 'prop') continue;
          records.push({
            gameKey,
            awayCode: m.canonicalAwayCode ? m.canonicalAwayCode.toUpperCase() : null,
            homeCode: m.canonicalHomeCode ? m.canonicalHomeCode.toUpperCase() : null,
            ticker: m.ticker,
            classification: {
              venue: 'polymarket', stat: pCls.stat, playerSlug: pCls.playerSlug, threshold: pCls.threshold,
            },
            book: buildGuardedPolyBook(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, 'mlb')),
            scheduledStartMs: parseStartTimeMs(m.eventStartTime),
            readState: readStateForMarket(m),
          });
          continue;
        }
        if (m.source !== 'kalshi') continue;
        if (!isMlbExtendedSeriesTicker(m.ticker)) continue;
        const cls = classifyMlbExtended(m.ticker);
        if (cls?.kind !== 'prop') continue;
        records.push({
          gameKey,
          awayCode: m.canonicalAwayCode ? m.canonicalAwayCode.toUpperCase() : null,
          homeCode: m.canonicalHomeCode ? m.canonicalHomeCode.toUpperCase() : null,
          ticker: m.ticker,
          classification: {
            venue: 'kalshi',
            stat: cls.stat,
            teamCode: cls.teamCode,
            playerId: cls.playerId,
            jerseyNumber: cls.jerseyNumber,
            // Same atomic identity migration as getMlbPlayerProps: the
            // day-wide slate and the per-game pane must key one player
            // identically (dailySlateModel dedups on it).
            ...(wirePlayerKey(m) != null ? { playerKey: wirePlayerKey(m)! } : {}),
            threshold: cls.threshold,
            subtitleName: playerNameFromSubtitle(m.subtitle),
          },
          book: buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, 'mlb')),
          scheduledStartMs: parseStartTimeMs(m.eventStartTime),
          readState: readStateForMarket(m),
        });
      }
      return records;
    },

    getNflPlayerProps: (gameKey): PlayerPropStatGroup[] => {
      void get().version;
      const byStat = new Map<NflPropStat, Map<string, PlayerPropLadder>>();
      for (const m of marketsForGameKey(gameKey)) {
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        const f = classifyNflPropFrame(m);
        if (!f) continue;
        let statMap = byStat.get(f.stat);
        if (!statMap) { statMap = new Map(); byStat.set(f.stat, statMap); }
        const playerKey = f.playerKey;
        let ladder = statMap.get(playerKey);
        if (!ladder) {
          ladder = {
            playerKey,
            ...(f.opPlayerKey != null ? { opPlayerKey: f.opPlayerKey } : {}),
            playerName: f.playerName ?? formatPlayerName(f.playerId, f.jerseyNumber),
            teamCode: f.teamCode,
            rungs: [],
          };
          statMap.set(playerKey, ladder);
        }
        if (f.playerName) ladder.playerName = f.playerName;
        ladder.rungs.push({
          threshold: f.threshold,
          kalshi: buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, 'nfl')),
          polymarket: null,
        });
      }
      const groups: PlayerPropStatGroup[] = [];
      for (const stat of NFL_PROP_STATS) {
        const statMap = byStat.get(stat);
        if (!statMap) continue;
        const players = [...statMap.values()];
        for (const p of players) p.rungs.sort((a, b) => a.threshold - b.threshold);
        // Team-less ladders (the NONE / D/ST legs) sort AFTER every teamed
        // ladder — "No Touchdown" closes the 1st Touchdown list.
        players.sort(
          (a, b) =>
            Number(a.teamCode === '') - Number(b.teamCode === '')
            || a.teamCode.localeCompare(b.teamCode)
            || a.playerName.localeCompare(b.playerName),
        );
        groups.push({ stat, players });
      }
      return groups;
    },

    getSoccerPlayerProps: (gameKey): PlayerPropStatGroup[] => {
      void get().version;
      const byStat = new Map<SoccerPropStat, Map<string, PlayerPropLadder>>();
      for (const m of marketsForGameKey(gameKey)) {
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        const f = classifySoccerPropFrame(m);
        if (!f) continue;
        let statMap = byStat.get(f.stat);
        if (!statMap) { statMap = new Map(); byStat.set(f.stat, statMap); }
        let ladder = statMap.get(f.playerKey);
        if (!ladder) {
          ladder = {
            playerKey: f.playerKey,
            playerName: f.playerName ?? formatPlayerName(f.playerId, f.jerseyNumber),
            teamCode: f.teamCode,
            rungs: [],
          };
          statMap.set(f.playerKey, ladder);
        }
        if (f.playerName) ladder.playerName = f.playerName;
        ladder.rungs.push({
          threshold: f.threshold,
          kalshi: buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, 'soccer')),
          polymarket: null,
        });
      }
      const groups: PlayerPropStatGroup[] = [];
      for (const stat of SOCCER_PROP_STATS) {
        const statMap = byStat.get(stat);
        if (!statMap) continue;
        const players = [...statMap.values()];
        for (const p of players) p.rungs.sort((a, b) => a.threshold - b.threshold);
        players.sort(
          (a, b) =>
            Number(a.teamCode === '') - Number(b.teamCode === '')
            || a.teamCode.localeCompare(b.teamCode)
            || a.playerName.localeCompare(b.playerName),
        );
        groups.push({ stat, players });
      }
      return groups;
    },

    getLivePlayerPropMarkets: (sport): LivePlayerPropMarket[] => {
      if (sport === 'mlb') return get().getMlbLivePlayerPropMarkets();
      if (sport === 'soccer') {
        void get().version;
        const records: LivePlayerPropMarket[] = [];
        for (const m of marketsForSport('soccer')) {
          const gameKey = gameKeyV2(m, m.canonicalGameKey ?? '');
          if (!gameKey) continue;
          const f = classifySoccerPropFrame(m);
          if (!f) continue;
          records.push({
            gameKey,
            awayCode: m.canonicalAwayCode ? m.canonicalAwayCode.toUpperCase() : null,
            homeCode: m.canonicalHomeCode ? m.canonicalHomeCode.toUpperCase() : null,
            ticker: m.ticker,
            classification: {
              venue: 'kalshi',
              stat: f.stat,
              teamCode: f.teamCode,
              playerId: f.playerId,
              jerseyNumber: f.jerseyNumber,
              playerKey: f.playerKey,
              threshold: f.threshold,
              subtitleName: f.playerName,
            },
            book: buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, 'soccer')),
            scheduledStartMs: parseStartTimeMs(m.eventStartTime),
            readState: readStateForMarket(m),
          });
        }
        return records;
      }
      void get().version;
      const records: LivePlayerPropMarket[] = [];
      for (const m of marketsForSport('nfl')) {
        const gameKey = gameKeyV2(m, m.canonicalGameKey ?? '');
        if (!gameKey) continue;
        const f = classifyNflPropFrame(m);
        if (!f) continue;
        records.push({
          gameKey,
          awayCode: m.canonicalAwayCode ? m.canonicalAwayCode.toUpperCase() : null,
          homeCode: m.canonicalHomeCode ? m.canonicalHomeCode.toUpperCase() : null,
          ticker: m.ticker,
          classification: {
            venue: 'kalshi',
            stat: f.stat,
            teamCode: f.teamCode,
            playerId: f.playerId,
            jerseyNumber: f.jerseyNumber,
            playerKey: f.playerKey,
            threshold: f.threshold,
            subtitleName: f.playerName,
          },
          book: buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, 'nfl')),
          scheduledStartMs: parseStartTimeMs(m.eventStartTime),
          readState: readStateForMarket(m),
        });
      }
      return records;
    },

    getMlbEventMarkets: (gameKey): MlbEventMarkets | null => {
      void get().version;
      let rfiKalshi: VenueBook | null = null;
      let rfiPoly: VenueBook | null = null;
      let extrasKalshi: VenueBook | null = null;
      let extrasPoly: VenueBook | null = null;
      const totalsByTeam = new Map<string, TeamTotalLadder>();
      let awayCode = '';
      let homeCode = '';
      for (const m of marketsForGameKey(gameKey)) {
        // Same doubleheader-safe per-market v2 key match as getMlbPlayerProps.
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        if (m.source === 'polymarket') {
          // Same doubleheader FAIL-CLOSED rule as getMlbPlayerProps (DNL-G1):
          // no V2 stamp → unpaired beats wrongly paired.
          if (!m.canonicalGameKeyV2) continue;
          // Poly `-nrfi` / `-extra-innings` binaries (poly-props-events).
          // POLARITY IS THE SAME on both — despite the slug name, the -nrfi
          // question is "Will there be a run scored in the first inning?"
          // (Poly YES = run scores ≡ Kalshi KXMLBRFI YES, live-verified
          // 2026-07-16) — so the book attaches DIRECTLY, never inverted.
          // Unlike Poly props these carry live WS books, but the same
          // sentinel guard applies while a side is empty (DNL-G2).
          const pCls = classifyPolyExtendedTicker(m.ticker);
          if (pCls?.kind === 'rfi') {
            rfiPoly = buildGuardedPolyBook(
              m,
              _orderbooks.get(m.ticker) ?? null,
              freshnessForMarket(m, 'mlb'),
            );
          } else if (pCls?.kind === 'extras') {
            extrasPoly = buildGuardedPolyBook(
              m,
              _orderbooks.get(m.ticker) ?? null,
              freshnessForMarket(m, 'mlb'),
            );
          }
          continue;
        }
        if (m.source !== 'kalshi') continue;
        if (!isMlbExtendedSeriesTicker(m.ticker)) continue;
        const cls = classifyMlbExtended(m.ticker);
        if (!cls || cls.kind === 'prop') continue;
        // Kalshi frames carry the position-derived away/home pair — captured to
        // order the team-total ladders away-first below.
        if (m.canonicalAwayCode && m.canonicalHomeCode) {
          awayCode = m.canonicalAwayCode.toUpperCase();
          homeCode = m.canonicalHomeCode.toUpperCase();
        }
        const book = buildVenueBookFromMarket(
          m,
          _orderbooks.get(m.ticker) ?? null,
          freshnessForMarket(m, 'mlb'),
        );
        if (cls.kind === 'rfi') {
          rfiKalshi = book;
        } else if (cls.kind === 'extras') {
          extrasKalshi = book;
        } else {
          let ladder = totalsByTeam.get(cls.teamCode);
          if (!ladder) {
            ladder = {
              teamCode: cls.teamCode,
              teamName: getTeamDisplayName(cls.teamCode, 'mlb') || cls.teamCode,
              rungs: [],
            };
            totalsByTeam.set(cls.teamCode, ladder);
          }
          // Team totals are Kalshi-only (Poly `-team-total-` not ingested).
          ladder.rungs.push({ threshold: cls.threshold, kalshi: book, polymarket: null });
        }
      }
      if (!rfiKalshi && !rfiPoly && !extrasKalshi && !extrasPoly && totalsByTeam.size === 0) return null;
      const teamRank = (code: string) => (code === awayCode ? 0 : code === homeCode ? 1 : 2);
      const teamTotals = [...totalsByTeam.values()].sort(
        (a, b) => teamRank(a.teamCode) - teamRank(b.teamCode) || a.teamCode.localeCompare(b.teamCode),
      );
      for (const t of teamTotals) t.rungs.sort((a, b) => a.threshold - b.threshold);
      return {
        rfi: rfiKalshi || rfiPoly ? { kalshi: rfiKalshi, polymarket: rfiPoly } : null,
        extras: extrasKalshi || extrasPoly ? { kalshi: extrasKalshi, polymarket: extrasPoly } : null,
        teamTotals,
      };
    },

    getCfbEventMarkets: (gameKey): CfbEventMarkets | null => {
      void get().version;
      // Classification is the WIRE class (marketType + stat + canonicalYesTeam
      // + subtitle) — deliberately not the ticker: CFB's variable-width codes
      // and packed combo suffixes (GTGT) are exactly what a client parser
      // gets wrong (hydracore cfb-game-props brief §2). Unknown classes are
      // ignored (rule 8), so today's `marketType: 'game'` rows simply never
      // land here. Kalshi-only by construction (no Poly counterpart, §5).
      // NFL rides the same selector (nfl-game-lines-props): the classifier
      // admits both football sports; freshness follows the game's own sport.
      const eventSport: Sport = SPORT_NORMALIZE[gameKey.split('|', 1)[0] ?? ''] ?? 'cfb';
      const enumsByCls = new Map<string, CfbEventOutcome[]>();
      const binaries: CfbEventMarkets['binaries'] = [];
      const teamStatByKey = new Map<string, CfbTeamStatLadder>();
      const totalsByCls = new Map<string, CfbOuLine[]>();
      const racesByPoints = new Map<number, RaceToOutcome[]>();
      const bothTeamsPoints: CfbOuLine[] = [];
      const tickers = new Set<string>();
      let awayCode = '';
      let homeCode = '';
      for (const m of marketsForGameKey(gameKey)) {
        // Same per-market v2 key match as the MLB event selectors.
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        const cls = classifyCfbWireMarket(m);
        if (!cls) continue;
        if (m.canonicalAwayCode && m.canonicalHomeCode) {
          awayCode = m.canonicalAwayCode.toUpperCase();
          homeCode = m.canonicalHomeCode.toUpperCase();
        }
        const kalshi = buildVenueBookFromMarket(
          m,
          _orderbooks.get(m.ticker) ?? null,
          freshnessForMarket(m, eventSport),
        );
        tickers.add(m.ticker);
        if (cls.kind === 'enum') {
          const list = enumsByCls.get(cls.cls) ?? [];
          list.push({ ticker: m.ticker, label: cls.label, teamSide: null, teamCode: cls.teamCode, kalshi });
          enumsByCls.set(cls.cls, list);
        } else if (cls.kind === 'binary') {
          binaries.push({ cls: cls.cls, ticker: m.ticker, label: cls.label, teamSide: null, teamCode: cls.teamCode, kalshi });
        } else if (cls.kind === 'team-stat') {
          const key = `${cls.stat}|${cls.teamCode}|${cls.period}`;
          let ladder = teamStatByKey.get(key);
          if (!ladder) {
            ladder = { stat: cls.stat, statLabel: cfbTeamStatLabel(cls.stat), teamSide: 'away', teamCode: cls.teamCode, period: cls.period, lines: [] };
            teamStatByKey.set(key, ladder);
          }
          ladder.lines.push({ line: cls.line, ticker: m.ticker, kalshi });
        } else if (cls.kind === 'race') {
          const list = racesByPoints.get(cls.points) ?? [];
          list.push({ ticker: m.ticker, label: cls.label, teamSide: null, teamCode: cls.teamCode, kalshi });
          racesByPoints.set(cls.points, list);
        } else if (cls.kind === 'both-points') {
          bothTeamsPoints.push({ line: cls.line, ticker: m.ticker, kalshi });
        } else {
          const list = totalsByCls.get(cls.cls) ?? [];
          list.push({ line: cls.line, ticker: m.ticker, kalshi });
          totalsByCls.set(cls.cls, list);
        }
      }
      if (tickers.size === 0) return null;
      const sideOf = (code: string | null): 'away' | 'home' | null =>
        code && code === awayCode ? 'away' : code && code === homeCode ? 'home' : null;
      const sideRank = (side: 'away' | 'home' | null) => (side === 'away' ? 0 : side === 'home' ? 1 : 2);
      const enums: CfbEventEnumGroup[] = [];
      for (const g of CFB_ENUM_GROUPS) {
        const outcomes = enumsByCls.get(g.cls);
        if (!outcomes) continue;
        for (const o of outcomes) o.teamSide = sideOf(o.teamCode);
        outcomes.sort(
          (a, b) => sideRank(a.teamSide) - sideRank(b.teamSide) || a.label.localeCompare(b.label),
        );
        enums.push({ cls: g.cls, label: g.label, outcomes });
      }
      // Binaries: overtime, 2-point, every-quarter BTTS, then the D/ST rows
      // (away before home). A D/ST row whose team is not one of the row's
      // two codes is dropped — fail closed at the row level too (DNL-002).
      for (const b of binaries) b.teamSide = sideOf(b.teamCode);
      const sidedBinaries = binaries.filter((b) => b.cls !== 'player-prop' || b.teamSide != null);
      const binRank = (c: string) => (c === 'overtime' ? 0 : c === 'two-point' ? 1 : c === 'every-quarter-btts' ? 2 : 3);
      sidedBinaries.sort(
        (a, b) => binRank(a.cls) - binRank(b.cls) || sideRank(a.teamSide) - sideRank(b.teamSide) || a.label.localeCompare(b.label),
      );
      // Team stats: a ladder whose team is not one of the row's two codes is
      // dropped here (fail closed — the classifier already required the stamp
      // to be inside the frame's own pair; this is the row-level check).
      const teamStats: CfbTeamStatLadder[] = [];
      for (const ladder of teamStatByKey.values()) {
        const side = sideOf(ladder.teamCode);
        if (!side) continue;
        ladder.teamSide = side;
        ladder.lines.sort((a, b) => a.line - b.line);
        teamStats.push(ladder);
      }
      const PERIOD_RANK: Record<Period, number> = { full: 0, '1H': 1, '2H': 2, F5: 3, '1Q': 4, '2Q': 5, '3Q': 6, '4Q': 7 };
      teamStats.sort(
        (a, b) =>
          cfbTeamStatRank(a.stat) - cfbTeamStatRank(b.stat)
          || PERIOD_RANK[a.period] - PERIOD_RANK[b.period]
          || sideRank(a.teamSide) - sideRank(b.teamSide),
      );
      // Race-to (NFL): points lines ascending; legs away → home → NONE.
      const races: RaceToLine[] = [...racesByPoints.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([points, outcomes]) => {
          for (const o of outcomes) o.teamSide = sideOf(o.teamCode);
          outcomes.sort((a, b) => sideRank(a.teamSide) - sideRank(b.teamSide) || a.label.localeCompare(b.label));
          return { points, outcomes };
        });
      bothTeamsPoints.sort((a, b) => a.line - b.line);
      const totals: CfbTotalLadder[] = [];
      for (const cls of ['total-touchdowns', 'total-fieldgoals'] as const) {
        const lines = totalsByCls.get(cls);
        if (!lines) continue;
        lines.sort((a, b) => a.line - b.line);
        totals.push({ cls, label: CFB_TOTAL_LABELS[cls], lines });
      }
      if (enums.length === 0 && sidedBinaries.length === 0 && teamStats.length === 0 && totals.length === 0
        && races.length === 0 && bothTeamsPoints.length === 0) return null;
      return { enums, binaries: sidedBinaries, teamStats, totals, races, bothTeamsPoints, tickers };
    },

    getUfcFightProps: (gameKey): UfcFightProps | null => {
      void get().version;
      if (SPORT_NORMALIZE[gameKey.split('|', 1)[0] ?? ''] !== 'ufc') return null;
      return selectUfcFightProps({
        gameKey,
        markets: marketsForSport('ufc'),
        // The KXUFCFIGHT leg's key is the one getGames files the board row
        // under (canonical v2 key, else the parsed fallback) — so a prop row
        // can only ever join the row the trader is looking at.
        fightLegOf: (m) => {
          const parsed = parseTicker(m);
          if (!parsed || parsed.sport !== 'ufc' || parsed.marketType !== 'moneyline') return null;
          if (parsed.side !== 'away' && parsed.side !== 'home') return null;
          if (!parsed.outcomeCode) return null;
          const key = gameKeyV2(
            m,
            m.canonicalGameKey ?? `${parsed.sport}|${parsed.date}|${parsed.awayCode}|${parsed.homeCode}`,
          );
          return { key, code: parsed.outcomeCode, side: parsed.side };
        },
        propKeyOf: (m) =>
          m.canonicalGameKeyV2 || m.canonicalGameKey
            ? gameKeyV2(m, m.canonicalGameKey ?? '')
            : undefined,
        bookOf: (m) => buildVenueBookFromMarket(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, 'ufc')),
      });
    },

    getPolyFightProps: (polyTicker): PolyFightPropRow[] => {
      void get().version;
      const sport = polyTicker.startsWith('PM-boxing-') ? 'boxing' : polyTicker.startsWith('PM-ufc-') ? 'ufc' : null;
      if (!sport) return [];
      const fight = _markets.get(polyTicker);
      const fightKey = fight?.canonicalGameKeyV2 || fight?.canonicalGameKey
        ? gameKeyV2(fight, fight.canonicalGameKey ?? '')
        : null;
      const prefix = `${polyTicker}-`;
      const out: Array<PolyFightPropRow & { order: number }> = [];
      for (const m of marketsForSport(sport)) {
        if (!m.ticker.startsWith(prefix)) continue;
        if (m.status && TERMINAL_STATUSES.has(m.status)) continue;
        const cls = classifyPolyFightProp(m.ticker);
        if (!cls || cls.stem !== polyTicker) continue;
        // The stem is the venue's own event identity; a producer key on the
        // prop that names a different fight than the moneyline's is a
        // contradiction, so the prop stays off the fight.
        const propKey = m.canonicalGameKeyV2 || m.canonicalGameKey ? gameKeyV2(m, m.canonicalGameKey ?? '') : null;
        if (fightKey && propKey && propKey !== fightKey) continue;
        out.push({
          ticker: m.ticker,
          title: m.title || m.ticker,
          group: cls.group,
          ...(cls.line !== undefined ? { line: cls.line } : {}),
          polymarket: buildGuardedPolyBook(m, _orderbooks.get(m.ticker) ?? null, freshnessForMarket(m, sport)),
          order: cls.order,
        });
      }
      out.sort((a, b) => a.order - b.order || a.ticker.localeCompare(b.ticker));
      return out.map(({ order: _order, ...row }) => row);
    },

    getAvailablePeriods: (gameKey: string) => {
      void get().version;
      const spread = new Set<Period>();
      const total = new Set<Period>();
      const moneyline = new Set<Period>();
      const btts = new Set<Period>();
      // Soccer knockout demotion: once an advance market TAKES the headline the
      // regulation 1X2 (a full-game moneyline) moves from the top section into
      // Game Lines under "90 min". Whether it takes the headline is the ONE
      // promotion rule (lib/advancePromotion.ts) over the ONE evidence object
      // the store builds in getAdvanceGame / getAdvanceEvents — read back off
      // the advance row here, so the page can never list the 1X2 as demoted
      // while it still heads the page (DNL rounds 1-2). Group-stage games have
      // no advance → unchanged.
      let hasFullMoneyline = false;
      for (const m of marketsForGameKey(gameKey)) {
        // gameKey is the doubleheader-safe v2 row key — match each market by its
        // own v2 key so a doubleheader's two games keep separate period markets.
        if (gameKeyV2(m, m.canonicalGameKey ?? '') !== gameKey) continue;
        const parsed = parseTicker(m);
        // Advance markets (hydra field authoritative; parsed as cross-check)
        // aren't themselves a period bet; their promotion evidence is read
        // through getAdvanceGame below.
        if (m.marketType === 'advance' || parsed?.marketType === 'advance') continue;
        if (!parsed) continue;
        // Same authoritative-ticker-then-hydra classification as getGameEvents.
        const p = resolveMarketPeriod(m, parsed);
        if (p == null) continue;
        if (parsed.marketType === 'btts') {
          // BTTS has no top section, so its Full Game line belongs here too.
          btts.add(p);
          continue;
        }
        // spread/total/moneyline full game live in the top sections — period-only
        // here, EXCEPT the soccer full-game 1X2 demoted below.
        if (p === 'full') {
          if (parsed.marketType === 'moneyline') hasFullMoneyline = true;
          continue;
        }
        if (parsed.marketType === 'spread') spread.add(p);
        else if (parsed.marketType === 'total') total.add(p);
        else if (parsed.marketType === 'moneyline') moneyline.add(p);
      }
      // Surface the demoted 1X2 as a "90 min" (full) moneyline period for soccer
      // knockouts. GameLinesSection relabels 'full' → "90 min" for soccer.
      const sport = SPORT_NORMALIZE[gameKey.split('|', 1)[0] ?? ''];
      if (sport === 'soccer' && hasFullMoneyline) {
        const adv = get().getAdvanceGame(gameKey);
        if (adv?.advancePromotion && advancePromotes(adv.advancePromotion)) moneyline.add('full');
      }
      const ORDER: Period[] = ['full', '1H', '2H', 'F5', '1Q', '2Q', '3Q', '4Q'];
      const sortP = (s: Set<Period>) => ORDER.filter((p) => s.has(p));
      return {
        spread: sortP(spread),
        total: sortP(total),
        moneyline: sortP(moneyline),
        btts: sortP(btts),
      };
    },

    getFeedLog: (source?: FeedLogSource): FeedLogEntry[] => {
      void get().version;
      if (source === 'kalshi') return [..._feedLogKalshi].reverse();
      if (source === 'polymarket') return [..._feedLogPoly].reverse();
      if (source === 'polymarket_us') return [..._feedLogPolyUs].reverse();
      if (source === 'unabated') return [..._feedLogUnabated].reverse();
      if (source === 'oddspapi') return [..._feedLogOp].reverse();
      // All sources merged, newest first
      return [..._feedLogKalshi, ..._feedLogPoly, ..._feedLogPolyUs, ..._feedLogUnabated, ..._feedLogOp]
        .sort((a, b) => b.ts - a.ts)
        .slice(0, FEED_LOG_MAX);
    },

    getOrderbookRaw: (ticker: string): HydraOrderbook | null => {
      void get().version; // re-derive on book updates
      return sourceBookUsable(ticker) ? _orderbooks.get(ticker) ?? null : null;
    },
    getOrderbookDisplay: (ticker) => { void get().version; return readBookDisplay(ticker); },

    getOrderbookDiag: (ticker: string) => {
      const book = _orderbooks.get(ticker);
      const ms = _lastOrderbookMs.get(ticker);
      if (!book || ms == null) return null;
      const yes = book.yes ?? [];
      const no = book.no ?? [];
      return {
        yesLevels: yes.length,
        noLevels: no.length,
        ageMs: Date.now() - ms,
        sourceFeedId: _orderbookSourceFeed.get(ticker) ?? null,
        needed: _activeOrderbookTickers.has(ticker),
        rawBestYesBid: yes.length ? Math.max(...yes.map((l) => l.price)) : null,
        rawBestNoBid: no.length ? Math.max(...no.map((l) => l.price)) : null,
        lastPrice: _markets.get(ticker)?.lastPrice ?? null,
      };
    },
    getOrderbookAgeMs: (ticker: string): number | null => {
      const ms = _lastOrderbookMs.get(ticker);
      return ms === undefined ? null : Date.now() - ms;
    },

    getMarketAgeMs: (ticker: string): number | null =>
      marketEvidenceAgeMs(ticker, _markets.get(ticker), Date.now()),

    getMarketPrice: (ticker: string): number | null => {
      void get().version; // re-derive on price updates
      const market = _markets.get(ticker);
      if (!market) return null;
      // Kalshi (and edge-parity) frames encode an EMPTY book side as
      // bid 0 / ask 100, and a never-traded market as lastPrice 0 — real
      // prices only exist strictly inside (0, 100). Averaging against an
      // empty side craters the mid to ~half the ask (or spikes it toward
      // 100) for the frame or two where a side clears mid-game — the
      // positions page's 20¢ "Now" jitter (2026-07-12). Treat those
      // sentinels as absent; a one-sided book marks at last trade.
      // Strict inequalities, not >=1/<=99: Polymarket sub-cent prices
      // (0.5¢ longshots) are legitimate and must pass.
      const bid = market.yesBid;
      const ask = market.yesAsk;
      if (bid != null && bid > 0 && ask != null && ask > 0 && ask < 100) {
        return midCents(bid, ask);
      }
      const last = market.lastPrice;
      return last != null && last > 0 ? last : null;
    },

    getMarket: (ticker: string): HydraMarket | null => {
      // Not subscribed to `version` — describeMarket is called from
      // synchronous render code (PositionTracker, TraderDetail, …) and
      // doesn't need the value to update reactively on every tick. The
      // title field is effectively static for a given ticker.
      return _markets.get(ticker) ?? null;
    },

    getCanonicalGameKey: (ticker: string): string | null => {
      const m = _markets.get(ticker);
      if (!m || !(m.canonicalGameKeyV2 || m.canonicalGameKey)) return null;
      return gameKeyV2(m, m.canonicalGameKey ?? '');
    },

    getSubtitleMatchup: (ticker: string): SubtitleMatchupView | null => {
      // Non-reactive, same contract as getMarket: this is render-path code and
      // the answer is stable for a ticker until the producer renames a club.
      const market = _markets.get(ticker);
      if (!market) return null;
      for (const key of subtitleKeysForMarket(market)) {
        const view = subtitleMatchupForKey(key);
        if (view) return view;
      }
      return null;
    },

    getSubtitleName: (ticker: string, code: string): string | null => {
      const market = _markets.get(ticker);
      if (!market || !code) return null;
      const wanted = code.toUpperCase();
      for (const key of subtitleKeysForMarket(market)) {
        const name = subtitleNameHint(key, wanted);
        if (name) return name;
      }
      return null;
    },

    getExchangeIndexDiagnostic: (ticker: string): KalshiExchangeIndexDiagnostic =>
      _kalshiExchangeIndexDiagnostics.get(ticker) ?? { state: 'missing' },

    getMarketByPmusSlug: (slug: string): HydraMarket | null => {
      const ticker = _tickerByPmusSlug.get(slug);
      return ticker ? _markets.get(ticker) ?? null : null;
    },
    getTickerByConditionId: (conditionId: string): string | null => {
      // Same non-reactive contract as getMarket: callers are render-path
      // helpers that re-run on the tracker's own (throttled) clock.
      return _tickerByConditionId.get(conditionId) ?? null;
    },

    getTickerByTokenId: (tokenId: string): string | null => {
      return _tickerByTokenId.get(tokenId) ?? null;
    },

    getEventStartTimeMsByTicker: (ticker: string): number | null => {
      void get().version;
      const market = _markets.get(ticker);
      if (!market) return null;
      // Resolve through the precedence cache under the doubleheader-safe key.
      // An ambiguous frame gets NO cache lookup at all — neither the v2 key
      // (it has none) nor the legacy key, which on a doubleheader day is the
      // shared key whose slots belong to the numbered games. It falls through
      // to its own `eventStartTime`: the frame's own data, never a cross-game
      // join.
      const v2 = market.canonicalGameKey ? gameKeyV2(market, market.canonicalGameKey) : null;
      if (v2) {
        const cached = _startTimeMsByGameKey.get(v2);
        if (cached != null) return cached;
        // Rollout fallback (TIME-BOXED — see the repo-local handoff's Phase-2
        // debt note): a mixed-version hydra may still have written slots under
        // the raw legacy key. Safe only because we know this frame is NOT
        // ambiguous. Remove once both engines stamp v2 on every frame.
        const ck = market.canonicalGameKey;
        if (ck && ck !== v2) {
          const legacy = _startTimeMsByGameKey.get(ck);
          if (legacy != null) return legacy;
        }
      }
      if (!market.eventStartTime) return null;
      const ms = Date.parse(market.eventStartTime);
      return Number.isFinite(ms) ? ms : null;
    },

    /**
     * The one place a consumer asks "which game does this ticker belong to?".
     * Returns the doubleheader-safe v2 key, or **null** when the frame is
     * ambiguous (DNL-001) or carries no canonical key at all.
     *
     * Null means "unknown game" and callers must treat it as such — never
     * promote the legacy key themselves, which is the gameNo=1 trap this
     * exists to close. Re-derived from the CURRENT record on every call, so an
     * unambiguous → ambiguous transition takes effect immediately (DNL-002).
     */
    getGameKeyV2ForTicker: (ticker: string): string | null => {
      void get().version;
      const market = _markets.get(ticker);
      if (!market) return null;
      if (!market.canonicalGameKeyV2 && !market.canonicalGameKey) return null;
      return gameKeyV2(market, market.canonicalGameKey ?? '') || null;
    },

    getConsensusFor: (canonicalGameKey): HydraConsensus | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      // Result bucket (period 1, no marketType) — the regulation 1X2 / moneyline.
      return _consensusByGameKeyAndPeriod.get(consensusCacheKey(canonicalGameKey, 1)) ?? null;
    },

    getOpLaneStatus: (canonicalGameKey): OpLaneStatus => {
      void get().version;
      const active = _opActiveFeedId == null ? undefined : _feedClients.find((f) => f.id === _opActiveFeedId);
      const source = _feedLabelOf(_opActiveFeedId) ?? null;
      const slice = canonicalGameKey
        ? _opOddsByGameKey.get(opOddsCacheKey(oddsGameKeyV2(canonicalGameKey), 1, 'moneyline')) ?? null
        : null;
      const stamp = slice?.market.consensus?.lastUpdateMs ?? slice?.lastUpdateMs ?? null;
      return {
        source,
        // "Fallback" = the OG moneyline-only standby is what feeds OP now. A
        // firehose-role feed that carries the full feed (the gateway) is not.
        fallback: active?.role === 'firehose' && active.opFullFeed !== true,
        lines: _opLinesByGame.size > 0,
        coverage: slice ? 'ok' : 'none',
        stale: stamp != null && Date.now() - stamp > OP_LANE_STALE_MS,
      };
    },

    getOpOddsFor: (canonicalGameKey): OpOddsSlice | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      // Full Game moneyline bucket — the same grain the U column reads.
      // Promote so legacy 4-seg keys and v2 row keys land on one slot.
      return _opOddsByGameKey.get(
        opOddsCacheKey(oddsGameKeyV2(canonicalGameKey), 1, 'moneyline'),
      ) ?? null;
    },

    getOpMarketFor: (canonicalGameKey, periodKey, marketType, points, prop) => {
      void get().version;
      if (!canonicalGameKey) return null;
      const gameKeyV2 = oddsGameKeyV2(canonicalGameKey);
      // op-player-props: a prop bucket is keyed by (stat, wire playerKey) —
      // a read without that identity (or without a line) resolves nothing.
      let who: { stat: string; who: string } | undefined;
      if (marketType === 'player-prop') {
        if (!prop || !prop.stat || !prop.playerKey || points == null) return null;
        who = { stat: prop.stat, who: prop.playerKey };
      }
      if (points == null) {
        return _opOddsByGameKey.get(opOddsCacheKey(gameKeyV2, periodKey, marketType)) ?? null;
      }
      if (!Number.isFinite(points)) return null;
      const exact = _opOddsByGameKey.get(opOddsCacheKey(gameKeyV2, periodKey, marketType, points, who));
      // op-player-props: an `unpaired` entry (no Kalshi ladder matched — the
      // bots' narrowing feed) is enumerable but NEVER a pane's join result,
      // whatever identity the read carries (rule: g1 pins wire playerKeys only).
      if (exact) return exact.market.pairing === 'unpaired' ? null : exact;
      // A nearby strike is a different contract. Missing exact coverage stays absent.
      return null;
    },

    getOpMarketsFor: (canonicalGameKey) => {
      void get().version;
      if (!canonicalGameKey) return [];

      const out: OpOddsSlice[] = [];
      for (const [, slice] of opGameEntries(oddsGameKeyV2(canonicalGameKey))) out.push(slice);
      out.sort(
        (a, b) =>
          a.market.marketType.localeCompare(b.market.marketType) ||
          String(a.market.period ?? a.market.periodTypeId ?? '').localeCompare(
            String(b.market.period ?? b.market.periodTypeId ?? ''),
          ) ||
          (a.market.points ?? 0) - (b.market.points ?? 0),
      );
      return out;
    },

    getOpSpreadLineFor: (canonicalGameKey, rowHomeCode, rowLine) => {
      void get().version;
      if (!canonicalGameKey || rowLine == null) return null;
      const entry = _opLinesByGame.get(oddsGameKeyV2(canonicalGameKey));
      if (!entry) return null;
      // Orientation (rule 4): the provider's teams are canonical codes but
      // its away/home MAY be flipped vs the row. The wire `points` is the
      // PROVIDER-home handicap; g1's row line is signed from the ROW-away
      // perspective (negative = away favored). Aligned ⇒ points = −rowLine;
      // swapped ⇒ provider-home IS row-away ⇒ points = rowLine. Value slots
      // swap with it (buildOpLineFairView applies the same flag).
      const any = entry.spreads.values().next().value as OpOddsSlice | undefined;
      if (!any) return null;
      const swapped = rowHomeCode != null && any.awayTeam === rowHomeCode;
      const slice = opLineLookup(entry.spreads, swapped ? rowLine : -rowLine);
      return slice ? { slice, swapped } : null;
    },

    getOpTotalLineFor: (canonicalGameKey, line) => {
      void get().version;
      if (!canonicalGameKey || line == null) return null;
      const entry = _opLinesByGame.get(oddsGameKeyV2(canonicalGameKey));
      if (!entry) return null;
      // Totals never swap: Over rides the away slot by sideIndex convention
      // regardless of team orientation.
      return opLineLookup(entry.totals, line);
    },

    getOpTeamTotalLineFor: (canonicalGameKey, teamCode, line, periodKey = 1) => {
      void get().version;
      if (!canonicalGameKey || !teamCode || line == null || !Number.isFinite(line)) return null;
      const gameKeyV2 = oddsGameKeyV2(canonicalGameKey);
      // Suffixes use producer orientation; match the named team on the exact
      // slice. The other team's same strike is never a substitute.
      for (const marketType of ['teamtotal-home', 'teamtotal-away'] as const) {
        const exact = _opOddsByGameKey.get(opOddsCacheKey(gameKeyV2, periodKey, marketType, line));
        if (exact && opTeamTotalTeamCode(exact) === teamCode) return exact;
      }
      return null;
    },

    hasOpLinesFor: (canonicalGameKeys): boolean => {
      void get().version;
      if (_opLinesByGame.size === 0) return false;
      return canonicalGameKeys.some((key) => _opLinesByGame.has(oddsGameKeyV2(key)));
    },

    hasOpOddsForAny: (canonicalGameKeys): boolean => {
      void get().version;
      if (_opOddsByGameKey.size === 0) return false;
      return canonicalGameKeys.some((key) =>
        _opOddsByGameKey.has(opOddsCacheKey(oddsGameKeyV2(key), 1, 'moneyline')),
      );
    },

    getAnyOpBookmaker: (): string | null => {
      void get().version;
      for (const slice of _opOddsByGameKey.values()) {
        // Match the actual PNCL display; a field book is not an anchor.
        const book = selectOpAnchorBook(slice.market);
        if (book) return book.bookmaker;
      }
      return null;
    },

    getAnyOpConsensusBooks: (): number | null => {
      void get().version;
      for (const slice of _opOddsByGameKey.values()) {
        const n = slice.market.consensus?.contributingBooks;
        if (n != null) return n;
      }
      return null;
    },

    getOpTrendFor: (canonicalGameKey): ConsensusTrendState | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      return _opTrendByBucket.get(
        opOddsCacheKey(oddsGameKeyV2(canonicalGameKey), 1, 'moneyline'),
      ) ?? null;
    },

    getOpLastAnchorFairFor: (canonicalGameKey): OpLastAnchorFair | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      return _opLastAnchorFair.get(
        opOddsCacheKey(oddsGameKeyV2(canonicalGameKey), 1, 'moneyline'),
      ) ?? null;
    },

    getAdvanceConsensusFor: (canonicalGameKey): HydraConsensus | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      // Knockout "To Advance" 2-way (period 1, marketType 'advance'). Null when
      // hydra hasn't quoted an advance line for this game (group stage / pre-
      // quote) — callers render "—", never the 1X2 value.
      return _consensusByGameKeyAndPeriod.get(consensusCacheKey(canonicalGameKey, 1, 'advance')) ?? null;
    },

    getSourceAge: (ticker) => {
      const market = _marketSourceAge.get(ticker);
      const book = _bookSourceAge.get(ticker);
      if (!market && !book) return null;
      return { marketAgeMs: sourceAgeNow(market), bookAgeMs: sourceAgeNow(book) };
    },
    getConsensusTrendFor: (canonicalGameKey): ConsensusTrendState | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      return _consensusTrendByBucket.get(consensusCacheKey(canonicalGameKey, 1)) ?? null;
    },

    getAdvanceConsensusTrendFor: (canonicalGameKey): ConsensusTrendState | null => {
      void get().version;
      if (!canonicalGameKey) return null;
      return _consensusTrendByBucket.get(consensusCacheKey(canonicalGameKey, 1, 'advance')) ?? null;
    },

    seedEventStartTimeMsByKey: (gameKey, ms, statusId) => {
      // Update policy lives in `updateStartTimeCache`. Once Unabated says
      // the game has STARTED, it outranks the venues (which often keep
      // streaming the original scheduled tip through live delays);
      // pre-start it ranks below Kalshi so venue schedule corrections
      // still propagate — the games-state poll re-seeds this slot every
      // cycle where it's mounted.
      // Normalize to the v2 grain the venue slots now use. Live callers
      // (useGameStatesPoll → toHydraGameKeyLive) already pass v2 keys; a
      // legacy key promotes to the same gameNo=1 form a single game's venue
      // frames hash to, so both land in ONE slot set. Pre-fix these were two
      // namespaces and the post-start Unabated lock never actually applied to
      // a venue-written key.
      if (updateStartTimeCache(promoteToGameKeyV2(gameKey), ms, 'unabated', statusId)) {
        touch(sportOfGameKey(gameKey) != null ? [sportOfGameKey(gameKey)!] : 'all');
      }
    },

    getLastTradeYesCents: (ticker: string): number | null => {
      void get().version; // re-derive on tick updates
      return _markets.get(ticker)?.lastPrice ?? null;
    },
  };
});

/** Reactive read of the latest Full Game consensus for a canonical game key.
 *  Re-renders only when the cached snapshot reference changes (i.e. on a real
 *  prob shift) — not on every market-frame tick — because Zustand compares by
 *  reference and the cache only writes when probs actually move. */
export function useConsensusFor(canonicalGameKey: string | null | undefined): HydraConsensus | null {
  return useMarketStore((s) => s.getConsensusFor(canonicalGameKey));
}

/** Reactive read of the knockout "To Advance" 2-way consensus for a game.
 *  Null until hydra quotes an advance line — the advance row renders "—" then
 *  (never the 1X2 value). Same reference-stable re-render behavior as
 *  `useConsensusFor`. */
export function useAdvanceConsensusFor(canonicalGameKey: string | null | undefined): HydraConsensus | null {
  return useMarketStore((s) => s.getAdvanceConsensusFor(canonicalGameKey));
}

/** Reactive read of the latest Full Game OddsPapi slice for a game key.
 *  Null = no OP coverage (missing / unmatched / tombstoned / feature off) —
 *  the cell renders "—" and the panel row hides. Reference-stable: buckets
 *  are replaced only on a real change, so consumers re-render exactly when
 *  their game's OP state moves. */
/** Trend-state counterpart of `useOpOddsFor` — the OP/PNCL column's
 *  velocity arrows + recency brightness (OWN feed, never Unabated's). */
export function useOpTrendFor(
  canonicalGameKey: string | null | undefined,
): ConsensusTrendState | null {
  return useMarketStore((s) => s.getOpTrendFor(canonicalGameKey));
}

/** Last-known anchor fair values — the phase-3 strikethrough's number. */
export function useOpLastAnchorFairFor(
  canonicalGameKey: string | null | undefined,
): OpLastAnchorFair | null {
  return useMarketStore((s) => s.getOpLastAnchorFairFor(canonicalGameKey));
}

/** Transport health of the active OddsPapi lane — see MarketState.opFeedHealth. */
export function useOpFeedHealth(): OpFeedHealth {
  return useMarketStore((s) => s.opFeedHealth);
}

/** Reactive OP lane status for the GameDetail header badge. The selector
 *  returns a primitive key so the badge re-renders only when the state it
 *  prints changes (a fresh object per tick would re-render every frame). */
export function useOpLaneStatus(canonicalGameKey: string | null | undefined): OpLaneStatus {
  const key = useMarketStore((s) => opLaneStatusKey(s.getOpLaneStatus(canonicalGameKey)));
  return opLaneStatusFromKey(key);
}

export function useOpOddsFor(canonicalGameKey: string | null | undefined): OpOddsSlice | null {
  return useMarketStore((s) => s.getOpOddsFor(canonicalGameKey));
}

/** Full-feed B: reactive read of one arbitrary OP bucket (period-scoped or
 *  full-game). Pass the vendor `period` string verbatim, or 1 for
 *  full-game; line families require the exact declared `points`. */
export function useOpMarketFor(
  canonicalGameKey: string | null | undefined,
  periodKey: number | string,
  marketType: string,
  points?: number | null,
  prop?: { stat: string; playerKey: string } | null,
): OpOddsSlice | null {
  return useMarketStore((s) => s.getOpMarketFor(canonicalGameKey, periodKey, marketType, points, prop));
}

/** Reactive read of the Full Game result bucket's sub-cent trend state —
 *  feed it to `useConsensusGlow` for the recency-brightness / trend-arrow /
 *  sharp-anchor treatments. Re-renders when the bucket ingests a material
 *  frame (tenth-only moves and sharpBookUsed flips included — the state
 *  object is replaced immutably per frame). */
export function useConsensusTrendFor(
  canonicalGameKey: string | null | undefined,
): ConsensusTrendState | null {
  return useMarketStore((s) => s.getConsensusTrendFor(canonicalGameKey));
}

/** Trend-state counterpart of `useAdvanceConsensusFor` — the knockout
 *  "To Advance" bucket. */
export function useAdvanceConsensusTrendFor(
  canonicalGameKey: string | null | undefined,
): ConsensusTrendState | null {
  return useMarketStore((s) => s.getAdvanceConsensusTrendFor(canonicalGameKey));
}
